// Do NOT add any extraneous logic to this file. It should be treated as a mostly static JSON file, and is only in TypeScript for type-checking purposes.

import dedent from 'dedent';

import {withComment, withDetail} from 'common/i18n/util';
import {LocaleCode} from 'common/i18n/locales';

/** This text gets added to translation context every time. */
export const GLOBAL_CONTEXT = `
Translate with the Mixpanel product in mind. Mixpanel is a SaaS analytics product that helps businesses understand user behavior and make data-driven decisions.

Here are some common terms used in Mixpanel:
- Event: An action that a user takes in your product, like signing up or making a purchase.
- Property: Additional information about an event, like the price of a purchase.
- User: A person who interacts with your product.
- Cohort: A group of users who share a common characteristic, like signing up in the same week.

The following are core Mixpanel apps; they should translated consistently wherever they are referenced:
- Insights
- Funnels
- Flows
- Retention
- Lexicon
- Signal
- Impact
- Experiments
`;

export const LOCALE_CONTEXTS: Partial<Record<LocaleCode, string>> = {
  [LocaleCode.Indonesian]: dedent(`
    At Mixpanel, our Support team provides technical support and customer service. For Indonesian translations, translate any references to Support with that context. Avoid using "dukungan", which implies emotional support.
    Translate any references to a Mixpanel "event" as "aksi".
  `),
  [LocaleCode.Japanese]: dedent(`
    For Japanese translations, in the Impact app, translate "Adopters" as "利用者" and "Non-Adopters" as "非利用者".
    Mixpanel "boards" are short for "dashboards". Treat any references to a Mixpanel "board" as shorthand for "dashboard".
    Translate any instances of "Identity Merge" as if it were "ID Merge", which is the canonical name.
  `),
};

export const MIXOLOGY_KEY = `mixology`;

/* eslint-disable sort-keys */
// oxlint-disable-next-line import/no-default-export
export default {
  'nav-upsell': {
    'enterprise-badge': `Available on the Enterprise plan`,
  },
  'api-errors': {
    dqs: {
      params: {
        unauthorized_classified_property_access: {
          detail: `This export includes classified properties ({{properties}}) you don't have access to. Remove them from the export, or request access to classified data, and try again.`,
        },
      },
    },
  },
  sidenav: {
    discover: `Discover`,
    'new-board': `New Board`,
    'new-canvas': `New $t(mixology.metric-tree)`,
    'new-experiment': `New Experiment`,
    'new-feature-flag': `New Feature Flag`,
    experiments: `Experiments`,
    experimentation: `Experimentation`,
    'feature-flags': `Feature Flags`,
    'favorite-success': `Board favorited successfully.`,
    'unfavorite-success': `Board unfavorited successfully.`,
    'favorite-error': `Failed to add board to favorites.`,
    'unfavorite-error': `Failed to remove board from favorites.`,
    'favorite-content-success': `{{entityTypeName}} favorited successfully.`,
    'unfavorite-content-success': `{{entityTypeName}} unfavorited successfully.`,
    'favorite-content-error': `Failed to add {{entityTypeName}} to favorites.`,
    'unfavorite-content-error': `Failed to remove {{entityTypeName}} from favorites.`,
    'pin-success': `Board pinned successfully.`,
    'unpin-success': `Board unpinned successfully.`,
    'pin-error': `Failed to add board to pinned.`,
    'unpin-error': `Failed to remove board from pinned.`,
    'pin-content-success': `{{entityTypeName}} pinned successfully.`,
    'unpin-content-success': `{{entityTypeName}} unpinned successfully.`,
    'pin-content': `Pin for Project`,
    'unpin-content': `Unpin for Project`,
    'pin-content-error': `Failed to add {{entityTypeName}} to pinned.`,
    'unpin-content-error': `Failed to remove {{entityTypeName}} from pinned.`,
    'visit-board': `Visit Board`,
    'board-moved': `Board has been moved.`,
    'delete-error': `Error deleting board.`,
    'delete-content': `Deleted {{entityTypeName}} "{{name}}".`,
    'delete-content-error': `Error deleting {{entityTypeName}} "{{name}}".`,
    'duplicate-error': `Error duplicating board.`,
    'duplicate-content': `Duplicated {{entityTypeName}} "{{name}}".`,
    'duplicate-content-error': `Error duplicating {{entityTypeName}} "{{name}}".`,
    'move-error': `Error moving board.`,
    moving: `Moving...`,
    'delete-confirm-title': `Delete Content`,
    'delete-confirm-body': `Are you sure you want to delete the "{{boardName}}" board? Please note that this action will also delete any reports on this board.`,
    'delete-confirm-body-content': `Are you sure you want to delete "{{name}}" {{entityTypeName}}?`,
    'archive-confirm-title': `Archive Content`,
    'archive-confirm-body': `Are you sure you want to archive "{{name}}" {{entityTypeName}}?`,
    'children-preserved': `However, any child boards will be preserved and moved to the root level.`,
    'access-override-staff': `You do not have granted access to this board. However, as a staff user, you may proceed.`,
    'access-override-owner': `You do not have granted access to this board. However, as a project Owner, you may proceed.`,
    'permanent-action': `This action is permanent and cannot be undone.`,
    favorites: `Favorites`,
    pinned: `Pinned`,
    'your-boards': `Your Boards`,
    'your-content': `Your Content`,
    unpin: `Unpin from Project`,
    pin: `Pin for Project`,
    'manage-sharing': `Manage Sharing`,
    unfavorite: `Remove from Favorites`,
    favorite: `Favorite`,
    move: `Move to`,
    'rename-board': `Rename Board`,
    'rename-content': `Rename {{entityTypeName}}`,
    'rename-content-error': `Failed to rename {{entityTypeName}}`,
    'no-nested-content': `Only boards can be nested inside other boards.`,
    'url-copy-success': `URL copied!`,
    'url-copy-fail': `Something went wrong`,
    'url-copy-none': `No URL to copy`,
    'delete-experiment-on-trial-error': `Cannot delete free trial experiment`,
    'board-move-error-kept-safe': `Something went wrong moving a board. We've kept it safely in its old location.`,
    'delete-flag-enabled-error': `Cannot delete enabled feature flag`,
  },
  'sidenav-canvas': {
    'favorite-success': `$t(mixology.metric-tree) favorited successfully.`,
    'unfavorite-success': `$t(mixology.metric-tree) unfavorited successfully.`,
    'pin-success': `$t(mixology.metric-tree) pinned successfully.`,
    'unpin-success': `$t(mixology.metric-tree) unpinned successfully.`,
    'visit-board': `Visit $t(mixology.metric-tree)`,
    'board-moved': `$t(mixology.metric-tree) has been moved.`,
    'delete-error': `Error deleting $t(mixology.metric-tree).`,
    'duplicate-error': `Error duplicating $t(mixology.metric-tree).`,
    'move-error': `Error moving board.`,
    moving: `Moving...`,
    'delete-confirm-title': `Delete $t(mixology.metric-tree)`,
    'delete-confirm-body': `Are you sure you want to delete the "**{{canvasName}}**" $t(mixology.metric-tree)?`,
    'access-override-staff': `You do not have granted access to this $t(mixology.metric-tree). However, as a staff user, you may proceed.`,
    'access-override-owner': `You do not have granted access to this $t(mixology.metric-tree). However, as a project Owner, you may proceed.`,
    'permanent-action': `This action is permanent and cannot be undone.`,
    favorites: `Favorite $t(mixology.metric-tree_plural)`,
    pinned: `Pinned $t(mixology.metric-tree_plural)`,
    'your-canvases': `Your $t(mixology.metric-tree_plural)`,
    unpin: `Unpin from Project`,
    pin: `Pin for Project`,
    'manage-sharing': `Manage Sharing`,
    unfavorite: `Remove from Favorites`,
    favorite: `Favorite`,
    move: `Move to`,
  },
  'global-sidenav': {
    'show-side-nav': `Show Side Nav`,
    'collapse-side-nav': `Collapse Side Nav`,
    'create-new': `Create New`,
    'create-new-dropdown': {
      report: `REPORT`,
      analysis: `ANALYSIS`,
      experimentation: `EXPERIMENTATION`,
    },
    'ask-ai': {
      title: `Build with Mixpanel Agent`,
      description: `Describe what you want to learn from your data and Mixpanel Agent will create a report for you.`,
      placeholder: `e.g. Show me signup trends by country over the last 30 days`,
      creating: `Creating your report, please don't close this window...`,
      'create-report': `Create Report`,
      'menu-label': `Build with Mixpanel Agent`,
      dogfooding: `Dogfooding`,
    },
    'ask-ai-board': {
      'menu-label': `Build board with Mixpanel Agent`,
    },
    'create-with-agent': {
      'menu-label': `Mixpanel Agent`,
      title: `Mixpanel Agent`,
      description: `Describe what you want to learn`,
      placeholder: `e.g. Show me signup trends by country, or create a dashboard of engagement metrics`,
      'create-button': `Create`,
    },
    'support-widget': {
      'submit-error': `Error submitting ticket, please email support@mixpanel.com`,
      'attach-logs-error': `Error attaching logs, your ticket will be sent without them`,
    },
  },
  'chart-actions': {
    exclude: `Exclude`,
    filter: `Filter`,
    'root-cause-analysis': `Root Cause Analysis`,
    'view-report': `View Report`,
    'view-users': `View Users`,
    'view-users-variant-only': `{{variant}} only`,
    'view-users-all-variants': `Both variants`,
    'view-group': `View {{group}}`,
    'view-group-maybe-plural': `View {{group}}(s)`,
    'view-events': `View Events`,
    'view-replays': `View Replays`,
    'zoom-in': `Zoom In`,
    'hide-weekends': `Hide Weekends`,
    'show-weekends': `Show Weekends`,
    'add-annotation': `Add Annotation`,
    annotations: {
      'tag-label': `Tags`,
      'select-tag': `Select Tag`,
      'create-new': `Create New Tag`,
      'no-tags-help': `Start typing to create tags`,
      'one-annotation': `1 Annotation`,
      'multiple-annotations': `{{number}} Annotations`,
      'more-annotations': `+ {{number}} more...`,
      'description-needed': `Please enter a description`,
      'select-date': `Select Date`,
      'invalid-permissions': `You don't have the requisite permissions to save an annotation. Please contact your project owner.`,
      'failed-create': `Failed to create annotation`,
      'failed-update': `Failed to update annotation`,
      'failed-delete': `Failed to delete annotation`,
      'annotation-delete-warning': `Deleting this annotation will delete it from the current project for everyone. Are you sure you want to delete?`,
      'tag-length-error': `Tag must be less than {{limit}} characters ({{length}})`,
    },
    tooltip: {
      'above-expected': `above expected range`,
      'below-expected': `below expected range`,
      'converted-users': `converted`,
      'dropped-off-users': `drop-off`,
      events: `events`,
      high: `high`,
      low: `low`,
      'not-available': `N/A`,
      totals: `total times`,
      sessions: `sessions`,
      'sum-of': `sum of {{property}}`,
      uniques: `uniques`,
      users: `users`,
      'maybe-plural-groups': `{{group}}(s)`,
      'from-previous-period': `from previous period`,
      trend: `Trend`,
      'from-previous-time-unit': `from previous {{timeUnit}}`,
      'of-overall': `of overall`,
      'of-total': `of total`,
      'incomplete-time-unit': `Incomplete {{timeUnit}}`,
    },
    metrics: {
      'average-ttc': `average time to convert`,
      'median-ttc': `median time to convert`,
      'minimum-ttc': `minimum time to convert`,
      'maximum-ttc': `maximum time to convert`,
      'p25-ttc': `p25 time to convert`,
      'p75-ttc': `p75 time to convert`,
      'p90-ttc': `p90 time to convert`,
      'p99-ttc': `p99 time to convert`,
      converted: `converted`,
      event_one: `event`,
      event: `events`,
      formula: `formula`,
      group_user_one: `user`,
      group_user: `users`,
      group_one: `{{group}}`,
      group: `{{group}}(s)`,
      profile_one: `profile`,
      profile: `profiles`,
      session_one: `session`,
      session: `sessions`,
      'total-conversion_one': `total conversion`,
      'total-conversion': `total conversions`,
      'total-session_one': `total session`,
      'total-session': `total sessions`,
      per_user: `per user`,
      per: `per {{group}}`,
      'event-per_user_one': `event per user`,
      'event-per_user': `events per user`,
      'event-per_one': `event per {{group}}`,
      'event-per': `events per {{group}}`,
      dau_user_one: `daily active users`,
      dau_user: `daily active user`,
      dau: `daily active {{group}}`,
      dau_average: `average daily active {{group}}`,
      wau_user_one: `weekly active user`,
      wau_user: `weekly active users`,
      wau: `weekly active {{group}}`,
      wau_average: `average weekly active {{group}}`,
      mau_user_one: `monthly active user`,
      mau_user: `monthly active users`,
      mau: `monthly active {{group}}`,
      mau_average: `average monthly active {{group}}`,
    },
    disabled: {
      generic: `Action not supported`,
      attribution: `"Attributed by" breakdown not supported.`,
      behaviors: `Behaviors not supported.`,
      'clause-missing': `Clause not found.`,
      cohorts: `Cohorts not supported.`,
      'count-users-once': `Count Users Once option not supported.`,
      'cohort-filters': `Inline cohort filters are not supported.`,
      'duration-property': `Duration property not supported.`,
      'history-tables': `History table properties not supported`,
      profiles: `Profiles not supported.`,
      'report-type': `{{reportType}} Reports do not support View Users with Cohort Breakdowns`,
      'replay-retention': `Replay data is only retained for the last {{retentionDays}} days.`,
      'funnel-with-attribution': `Viewing replays is only supported when segmenting and filtering on a specific step.`,
      'funnel-attribution-wrong-step': `Viewing replays is only supported on the step you are segmenting or filtering on.`,
      'save-for-rca': `Save this report to generate Root Cause Analysis`,
      'rca-incomplete-period': `Root Cause Analysis is not available for incomplete time periods`,
      'time-granularity': `{{timeUnit}} granularity not supported`,
      formulas: `{{viewType}} is not supported for formulas`,
    },
    funnels: {
      'completed-current-period': `completed from current period`,
      'completed-past-period': `completed from past period`,
      'completed-from-previous': `completed from previous step`,
      'completed-this-step': `completed this step`,
      'dropped-off': `dropped off`,
      'dropped-off-first': `dropped off from step 1`,
      'dropped-previous': `dropped from previous step`,
      'droppoff-current-period': `droppoff in current period`,
      'droppoff-past-period': `droppoff in past period`,
      'droppoff-previous-step': `droppoff from previous step`,
      'who-completed-step': `who completed this step`,
      'who-dropped-off': `who dropped off from step 1`,
      'view-users': `View Users`,
      'view-as-flow': `View as Flow`,
      'view-as-flow-comparison': `View as Flow from Current Period`,
      'step-from-to': `(Step {{from}} -> {{to}})`,
      'view-replays': `View Replays`,
      'replays-step-tooltip': `View replays of users completing this step`,
      'replays-previous-tooltip': `View replays of users completing the previous step that dropped off`,
      disabled: {
        'view-users-top-path_users': `View users is disabled when breaking down by top path`,
        'view-users-top-path': `View {{group}} is disabled when breaking down by top path`,
        'view-users-cohort_users': `View users is disabled when breaking down by cohorts`,
        'view-users-cohort': `View {{group}} is disabled when breaking down by cohorts`,
        'view-users-other_users': `View users is not supported for segments grouped into $other.`,
        'view-users-other': `View {{group}} is not supported for segments grouped into $other.`,
        'flows-top-path': `View as Flow is disabled when breaking down by top path`,
        'flows-numeric': `View as Flow is disabled when breaking down by a numeric property`,
        'flows-date': `View as Flow is disabled when breaking down by a date property`,
        'flows-cohort': `View as Flow is disabled when breaking down by cohorts`,
        'flows-comparison': `View as Flow is disabled when viewing event comparisons`,
        'flows-pqa': `View as Flow is disabled when viewing PQA style query`,
        'flows-reentry': `View as Flow is disabled when using Optimized Reentry`,
      },
    },
    flows: {
      'expand-by-property': `Expand by property`,
      'hide-event': `Hide Event`,
      'add-event-row': `Add a row of events`,
      'remove-event-row': `Remove a row of events`,
      'view-users': `View users`,
      'event-hidden': `{{event}} is hidden`,
      'segmented-event-by-property': `Segmented "{{event}}" by "{{property}}"`,
    },
    frequency: {
      name: `engaged`,
      'weighted-average': `Weighted Average Frequency`,
    },
    retention: {
      name: `retention`,
      description_users: `Weighted average of users who retained during a complete interval.`,
      description: `Weighted average of {{group}} who retained during a complete interval.`,
      'description-incomplete_users': `Weighted average of users who retained during any interval.`,
      'description-incomplete': `Weighted average of {{group}} who retained during any interval.`,
      'entered-with_users': `For users who entered with {{segment}}`,
      'entered-with': `For {{group}} who entered with {{segment}}`,
      'entered-on_users': `For users who entered on {{point}}`,
      'entered-on': `For {{group}} who entered on {{point}}`,
      'view-retained_users': `View retained users`,
      'view-retained': `View retained {{group}}`,
      'view-dropped-off_users': `View dropped off users`,
      'view-dropped-off': `View dropped off {{group}}`,
      'in-completed-intervals': `in completed intervals`,
      'users-entered-on': `Users entered on {{point}}`,
      average: `average`,
      'weighted-average': `Weighted Average Value`,
      'incomplete-time-unit': `Incomplete {{timeUnit}}`,
      disabled: {
        'view-cohort_users': `View users is disabled when breaking down by cohort`,
        'view-cohort': `View {{group}} is disabled when breaking down by cohort`,
        'view-numeric-segment_users': `View users is disabled when breaking down by a numeric property`,
        'view-numeric-segment': `View {{group}} is disabled when breaking down by a numeric property`,
        'no-segment_users': `Can only view users when there is at least 1 eligible {{group}}`,
        'no-segment': `Can only view {{group}} when there is at least 1 eligible {{group}}`,
      },
    },
  },
  'chart-customization': {
    abbreviation: `Abbreviation`,
    'apply-theme-tooltip': `Apply to this report only`,
    axis: `Axis`,
    'axis-assignment': `Axis Assignment`,
    'axis-unit': `Unit`,
    'confirm-delete-theme': `Do you want to delete {{themeName}}?`,
    'custom-theme': `Custom Theme`,
    decimals: `Decimals`,
    'delete-theme': `Delete Theme`,
    'duplicate-name-error': `A theme with that name already exists`,
    'error-deleting-theme': `Error deleting theme`,
    'hide-weekends': `Weekends`,
    'hide-weekends-tooltip': `Whether or not to hide weekends from the chart.`,
    'left-short': `L`,
    'left-y-axis': `Left Y-Axis`,
    max: `Max`,
    min: `Min`,
    'new-theme': `New Theme`,
    'no-themes-available': `No themes available`,
    prefix: `Prefix`,
    'prefix-placeholder': `Add Prefix...`,
    preview: `Preview`,
    'project-share-label': `Give access to everyone in the project`,
    reset: `Reset`,
    'right-short': `R`,
    'right-y-axis': `Right Y-Axis`,
    'save-theme-modal-description': `Modal for sharing theme in the project`,
    'save-theme-tooltip': `Save for re-use`,
    'save-theme': `Save Theme`,
    scale: `Scale`,
    'search-themes-placeholder': `Find a theme`,
    'set-color-for-all-checkbox': `Set segment color for all {{n}} reports on this board`,
    suffix: `Suffix`,
    'suffix-placeholder': `Add Suffix...`,
    'strict-threshold': `Out of Bounds`,
    'strict-threshold-hide': `Hide`,
    'strict-threshold-show': `Show`,
    'strict-threshold-tooltip': `Whether or not to display data outside the defined min and max threshold values.`,
    'theme-deleted': `Theme deleted`,
    'theme-name': `Theme Name`,
    'theme-save-error': `Error saving theme, please try again`,
    'theme-saved': `Theme saved`,
    'theme-update-error': `Error updating theme`,
    'theme-updated': `Theme updated`,
    themes: `Themes`,
    theme: `Theme`,
    threshold: `Threshold Bounds`,
    'trendline-tooltip-title': `Trendline`,
    trendlines: `Trendline`,
    'trendline-slope-per-minute': `{{value}} per minute`,
    'trendline-slope-per-hour': `{{value}} per hour`,
    'trendline-slope-per-day': `{{value}} per day`,
    'trendline-slope-per-week': `{{value}} per week`,
    'trendline-slope-per-month': `{{value}} per month`,
    'trendline-slope-per-quarter': `{{value}} per quarter`,
    'trendline-slope-per-year': `{{value}} per year`,
    'x-axis': `X-Axis`,
  },
  'annotations-chart': {
    'add-annotation': `Add Annotation`,
    'untagged-label': `Untagged`,
    'annotations-hidden': `Annotations are hidden on this report. Control annotation visibility in the annotations side panel.`,
    'no-annotations': `There are no annotations in the selected date range. Try adjusting your date range or creating a new annotation.`,
  },
  'bar-chart': {
    'truncated-download': `Results have been truncated to the first {{count}} segments for download.`,
  },
  'custom-event': {
    header: {
      create: `Create Custom Event`,
      edit: `Edit Custom Event - {{eventName}}`,
      delete: `Delete custom event`,
    },
    'delete-description': `Reports and cohorts using this custom event will be affected.`,
    name: `Name`,
    'match-description': `Match my custom event when any of the following events happen`,
    'save-and-share': `Save and Share`,
    'delete-tooltip': `Delete custom event`,
    errors: {
      'no-name': `Please name your custom event.`,
      'name-exists': `A custom event with that name already exists.`,
      'invalid-character': `Please refrain from using " / " (forward slash with spaces) in your custom event's name.`,
      'confirm-unsaved': `Changes made will not be applied.`,
      'error-saving': `Your custom event could not be saved. If this issue persists please [contact support](https://mixpanel.com/get-support).`,
      'error-deleting-with-support': `Your custom event could not be deleted. If this issue persists please [contact support](https://mixpanel.com/get-support).`,
    },
  },
  'custom-property': {
    header: {
      create: `Create a Custom Property`,
      edit: `Edit Custom Property`,
      update: `Update Custom Property`,
      delete: `Delete Custom Property`,
      create_behavior: `Create a Behavior`,
      edit_behavior: `Edit Behavior`,
      update_behavior: `Update Behavior`,
    },
    'name-title': `Name`,
    'name-placeholder': `Custom formula {{number}}`,
    'description-title': `Description`,
    'description-optional': `Optional`,
    'condition-title': `Define condition`,
    'formula-title': `Formula`,
    'formula-placeholder': `Transform properties by typing a formula here or select an example above`,
    'formula-tooltip': `Click to view documentation`,
    'help-text': `Press <period /> to insert a property and <ctrlSpace /> to see a list of formula functions.`,
    'help-text-period': `Period (.)`,
    'insert-example': `Insert Example`,
    'delete-confirmation': `Are you sure you want to delete "{{customPropertyName}}"? This action is permanent and cannot be undone.`,
    'upgrade-cta': `Upgrade to Save`,
    'upgrade-cta-update': `Upgrade to Update`,
    'upgrade-description': `When you upgrade your plan, custom properties can be created and saved to use across all reports in a project.`,
    example: {
      'channel-classifier': `Channel Classifier`,
      'url-domain': `URL Domain`,
      'resize-range': `Resize range`,
      'merge-values': `Merge values`,
      'extract-regex': `Extract regex`,
      'replace-regex': `Replace regex`,
      'days-since-signup': `Days since sign-up`,
      'email-domain': `Email Domain`,
    },
    error: {
      required: `Required`,
      description: `Error in custom property formula`,
      'name-exists': `A property by this name already exists. Are you sure you want to use this?`,
      'recursive-variable': `Variable {{name}} refers to this property. Properties cannot refer to themselves.`,
      'loop-variable': `Variable {{name}} cannot refer to property '{{other}}' because '{{other}}' already refers to this property.`,
      'borrowed-property-use': `To use a Borrowed Property with any other function (e.g. IFS), [learn more here](https://docs.mixpanel.com/docs/features/custom-properties#borrowed-properties).`,
      'borrowed-property-limit': `You can borrow a remaining {{remaining}} of {{limit}} total properties for the current project. [$t(common.learn-more)](https://docs.mixpanel.com/docs/features/custom-properties#borrowed-properties).`,
      'borrowed-property-role-create': `Cannot create borrowed property due to your project role ({{role}}). [$t(common.learn-more)](https://docs.mixpanel.com/docs/features/custom-properties#borrowed-properties).`,
      'borrowed-property-role-update': `Cannot update borrowed property due to your project role ({{role}}). [$t(common.learn-more)](https://docs.mixpanel.com/docs/features/custom-properties#borrowed-properties).`,
      'select-value': `Select a value for this property`,
      'commented-property': `Properties cannot be referenced inside of comments.`,
      'quoted-property': `Properties cannot be referenced inside of quotes.`,
    },
  },
  'nav-header': {
    'mixpanel-apps': `Mixpanel Apps`,
    'data-management': `Data Management`,
    applications: `Applications`,
    help: `Help`,
    settings: `Settings`,
    projects: `Projects`,
    'rollup-projects': `Rollup Projects`,
    'create-project': `Create Project`,
    'search-projects': `Search Projects`,
    'data-views': `Data Views`,
    'create-data-view': `Create Data View`,
    'search-data-views': `Search Data Views`,
    'select-data-view': `Select a Data View`,
    'data-view-created-by': `Created by {{creator}}`,
    'data-view-more-details': `More details`,
    'data-view-library': `Data View Library`,
    'sample-datasets': `Sample Datasets`,
    'mixpanel-demo': `Mixpanel Demo`,
    'insights-description': `Explore your data by segmenting and filtering event and user counts`,
    'funnels-description': `Understand the conversion and drop-off of critical sequences by measuring user progress at each step`,
    'flows-description': `Analyze how users move through your product by exploring paths between key actions`,
    'retention-description': `Assess stickiness of your product by identifying how many users come back over time`,
    'signal-description': `Uncover insights by finding correlations between user actions`,
    'impact-description': `Determine the impact of a new feature by comparing behavior before and after launch`,
    'experiments-description': `View an A/B test's effect by comparing metrics between test variants`,
    'users-description': `Understand each user`,
    'lexicon-description': `Document and transform your events and properties`,
    'data-audit-description': `View possible issues with your implementation`,
    'events-description': `View the stream of your events and individual event details`,
    'cohorts-description': `Manage your saved cohorts`,
    'integrations-description': `Connect Mixpanel to a data pipeline or customer engagement tool`,
    'integrations-disabled': `Integration settings are only available in the global Data View`,
    'jql-description': `Write powerful queries to analyze your data in Mixpanel.`,
    'jql-disabled': `JQL Console is only available in the global Data View`,
  },
  content: {
    page: {
      settings: {
        collapse: `Collapse settings`,
        expand: `Expand settings`,
        environment: {
          'push-disabled': `Cannot push to project with unsaved changes.`,
          'duplicate-disabled': `Cannot duplicate to project with unsaved changes.`,
          remove: `Remove`,
          open: `Open`,
          duplicate: `Duplicate to Project`,
          none: `None`,
          push: `Push Changes`,
        },
      },
      exposures: {
        title: `Exposures`,
        'no-results': `No data detected`,
        error: `Error loading data`,
        srm: {
          detected: `Sample Ratio Mismatch detected (p={{pValue}}). There is a significant difference between observed exposure rates and expected target rates set in your feature flag settings. This may indicate incorrect assignment code implementation. If you recently adjusted rollout rates, allow time for exposure rates to stabilize.`,
          'not-detected': `No Sample Ratio Mismatch detected (p={{pValue}}). Observed exposure rates match the expected target rates configured in your feature flag settings.`,
        },
      },
    },
  },
  'content-page': {
    tabs: {
      overview: `Overview`,
      configuration: `Configuration`,
      diagnostics: `Diagnostics`,
    },
  },
  'agent-flows': {
    label: `Automation`,
    new: `New $t(agent-flows.label)`,
    'type-setting': `$t(agent-flows.label) Type`,
    'name-setting': `Name`,
    'name-placeholder': `Untitled $t(agent-flows.label)`,
    'not-activatable': `Metric must be configured first.`,
    'one-metric': `Only one metric is supported per $t(agent-flows.label).`,
    actions: {
      activate: `Activate`,
      create: `Create`,
      pause: `Pause`,
      'run-now': `Run Now`,
      running: `Running...`,
      'running-tooltip': `This might take up to a couple of minutes.`,
      'run-complete-toast': `$t(agent-flows.label) run completed.`,
      'disabled-while-running': `Wait for the current run to finish`,
      'save-first': `Save your changes first`,
      'activated-toast': `$t(agent-flows.label) Activated`,
      'paused-toast': `$t(agent-flows.label) Paused`,
      'saved-toast': `$t(agent-flows.label) Saved`,
    },
    'agent-type': {
      'metric-monitor': {
        title: `KPI Monitor`,
        description: `Analyzes a metric on a recurring schedule and sends a digest with key insights to email or Slack.`,
      },
      'experiment-watcher': {
        title: `Experiment Watcher`,
        description: `Track running experiments and alert when they reach significance or show guardrail violations.`,
      },
      'data-quality-guardian': {
        title: `Data Quality Guardian`,
        description: `Monitor data pipelines for missing events, property drift, and unexpected data volume changes.`,
      },
      'disabled-reason': `Future`,
    },
    error: {
      'failed-load': `Failed to load $t(agent-flows.label)`,
      'failed-load-run-history': `Failed to load run history`,
      'failed-action': `Failed to {{action}} $t(agent-flows.label)`,
      'failed-action-with-detail': `Failed to {{action}} $t(agent-flows.label): {{errorMessage}}`,
      'unknown-type': `Unknown $t(agent-flows.label) Type: {{type}}`,
      'not-found': `$t(agent-flows.label) not found`,
      'failed-load-project-members': `Failed to load project members`,
      'metric-required': `Metric must be configured`,
      'name-required': `Name is required`,
      'frequency-required': `Trigger frequency is required`,
      'run-failed': `$t(agent-flows.label) run did not complete. Please try again or contact support if the problem persists.`,
      'run-status-unavailable': `The status of this run is currently unavailable.`,
      'create-limit-reached': `You've reached the maximum number of $t(agent-flows.label)s for this project on your current plan.`,
      'run-daily-limit-reached': `You've reached the daily run limit for $t(agent-flows.label)s in this project. Try again tomorrow.`,
      'run-debounced': `You've triggered a run in the last 30 seconds. Please wait a few moments before trying again.`,
    },
    'form-fields': {
      metric: `Metric`,
      'delete-metric': `Delete Metric`,
      'delete-metric-confirm-title': `Delete Metric?`,
      'delete-metric-confirm-body': `The current metric configuration will be removed.`,
      'select-metric-placeholder': `Select Metric`,
      'loading-metric-placeholder': `Loading metric…`,
      instructions: `Instructions`,
      'instructions-placeholder': `How should the agent analyze and summarize the results? For example: "Focus on week-over-week change, not absolute values."`,
      'instructions-tooltip': `Instructions guide how the digest is written and what gets flagged. They don't change the metric or time range, run new queries, or pull in other data.`,
      frequency: `Frequency`,
      email: `Email`,
      slack: `Slack`,
      'analysis-window': `Analysis window`,
      'analysis-window-tooltip': `Analysis window sets how far back the agent looks when analyzing trends in your KPI.`,
      'analysis-window-preset-7d': `Last 7 days`,
      'analysis-window-preset-14d': `Last 14 days`,
      'analysis-window-preset-21d': `Last 21 days`,
      'analysis-window-preset-30d': `Last 30 days`,
      'analysis-window-preset-60d': `Last 60 days`,
      'analysis-window-preset-90d': `Last 90 days`,
      'analysis-window-preset-180d': `Last 180 days`,
      'analysis-window-preset-365d': `Last 365 days`,
      'repeat-every': `Repeat every`,
      'at-time': `At`,
      'on-days': `On`,
    },
    trigger: {
      'unit-day': `day`,
      'unit-day-plural': `days`,
      'unit-week': `week`,
      'unit-week-plural': `weeks`,
      'unit-month': `month`,
      'unit-month-plural': `months`,
      am: `AM`,
      pm: `PM`,
      'monthly-mode-day-of-month': `Day of the month`,
      'monthly-mode-nth-weekday': `Day of the week`,
      'nth-week-first': `First`,
      'nth-week-second': `Second`,
      'nth-week-third': `Third`,
      'nth-week-fourth': `Fourth`,
      'nth-week-last': `Last`,
      'day-mon': `Monday`,
      'day-tue': `Tuesday`,
      'day-wed': `Wednesday`,
      'day-thu': `Thursday`,
      'day-fri': `Friday`,
      'day-sat': `Saturday`,
      'day-sun': `Sunday`,
      'day-plural-mon': `Mondays`,
      'day-plural-tue': `Tuesdays`,
      'day-plural-wed': `Wednesdays`,
      'day-plural-thu': `Thursdays`,
      'day-plural-fri': `Fridays`,
      'day-plural-sat': `Saturdays`,
      'day-plural-sun': `Sundays`,
      'timezone-tooltip': `$t(agent-flows.label) runs are scheduled based on this project's timezone setting ({{timezone}}).`,
      'timezone-tooltip-no-tz': `$t(agent-flows.label) runs are scheduled based on this project's timezone setting.`,
      'schedule-preview-heading': `Schedule preview`,
      'analysis-window-preview-heading': `Analysis window preview`,
      'calendar-tooltip-today': `Today`,
      'calendar-tooltip-future-run': `Scheduled run`,
      'calendar-tooltip-no-run': `No run on this day`,
      'calendar-tooltip-next-run': `Next run`,
      'calendar-tooltip-analysis-window': `Analysis window`,
      'calendar-tooltip-today-and-analysis-window': `Today, Analysis window`,
    },
    status: {
      active: `Active`,
      paused: `Paused`,
      draft: `Draft`,
      archived: `Archived`,
    },
    banner: {
      draft: {
        title: `Draft`,
        body: `Activate to start running this $t(agent-flows.label).`,
      },
      active: {
        title: `Active`,
        body: `Last Ran: {{lastRun}}`,
        'body-no-runs': `This $t(agent-flows.label) has not run yet`,
      },
      paused: {
        title: `Paused`,
        body: `This $t(agent-flows.label) is not currently running. Activate it to resume.`,
      },
      archived: {
        title: `Archived`,
      },
    },
    sections: {
      trigger: `Trigger`,
      'trigger-subheader': `When should this automation run?`,
      metric: `Metric`,
      analysis: `Analysis`,
      'analysis-subheader': `Which metric should the automation monitor, how do you want it to interpret the results, and how far back should it look for comparison?`,
      delivery: `Delivery`,
      'delivery-subheader': `Where should the output go?`,
    },
    unsaved: {
      title: `You have unsaved changes`,
      description: `Save your changes before leaving this page, or they will be lost.`,
    },
    feedback: {
      title: `Automations Feedback`,
      description: `Rate your satisfaction with Automations`,
      placeholder: `Additional feedback (Optional)`,
    },
    'latest-run': {
      title: `Latest Run`,
      'no-run-msg': `This automation has not completed any runs yet. You can click Run Now to trigger a run manually and see the latest results here.`,
    },
    'run-output': {
      'key-findings': `Key Findings`,
      'next-steps': `Next Steps`,
      'view-report-cta': `View Report`,
      'rca-cta': `Root Cause Analysis`,
      'chart-alt': `Metric analysis trendline chart`,
      'delivered-via': `Delivered via {{deliveries}}`,
      'unparseable-msg': `We are unable to render the output from this run at this time. You can try triggering another run manually. If this issue persists, please contact support.`,
      'failed-msg': `We were unable to complete this automation run at this time. We will try running it again at the next scheduled window. In the meantime, you can review its configuration or trigger a run manually. If this issue persists, please contact support.`,
      'unparseable-summary': `Results unavailable...`,
      'failed-summary': `Run failed...`,
      'rca-unavailable-tooltip': `RCA unavailable for this run`,
      'report-unavailable-tooltip': `Report unavailable for this run`,
    },
    'run-history': {
      title: `Run History`,
      'empty-msg': `Results from past runs will appear here`,
      'error-msg': `Something went wrong while loading run history. Please refresh the page to try again.`,
      'col-when': `When`,
      'col-summary': `Summary`,
      'load-more': `Load More`,
    },
  },
  experiments: {
    upsell: {
      headline: `Make every release a confident bet.`,
      subhead: `Mixpanel Experiments brings A/B testing, statistical rigor, and deep behavioral analysis together so you test with the same data you use to build.`,
      'talk-to-sales': `Talk to Sales`,
      'see-how-it-works': `See How It Works`,
    },
    'running-change-warning': {
      title: `Confirm Settings Change`,
      body: `Modifying the testing model after starting an experiment can compromise the statistical validity of your results and increase the risk of incorrect conclusions. Consider these changes carefully.`,
      confirm: `Proceed`,
    },
    'experiment-extension-warning': {
      title: `Confirm experiment extension`,
      body: `Warning: Extending a Frequentist experiment after viewing results may inflate your false positive rate. Consider declaring the experiment inconclusive and start a new one, or use Sequential testing for future experiments if you need flexibility to extend.`,
      confirm: `Continue`,
    },
    'experiment-version': `Experiment Version`,
    'startup-blocked': {
      'mutation-error': `This action is restricted because your account has exceeded the Startup Plan quota. All experiment viewing and editing is locked down. Please upgrade to a paid plan or contact support@mixpanel.com.`,
    },
    actions: {
      create: {
        success: `Experiment created successfully`,
        fail: `Failed to create experiment`,
        'fail-limit': `Enterprise customers get some free experiments, but you have reached the limit.`,
        'fail-feature-flag-linked': `This feature flag is already linked to another experiment. Unlink the other experiment first.`,
      },
      delete: {
        label: `Delete`,
        success: `Experiment deleted successfully`,
        fail: `Failed to delete experiment`,
      },
      archive: {
        label: `Archive`,
        success: `Experiment archived successfully`,
        fail: `Failed to archive experiment`,
        'archive-modal': {
          title: `Archive Experiment`,
          description: `Are you sure you want to archive "{{experimentName}}"?`,
        },
      },
      restore: {
        label: `Restore`,
        success: `Experiment restored successfully`,
        fail: `Failed to restore experiment`,
      },
      'force-conclude': {
        fail: `Failed to force conclude experiment`,
      },
      duplicate: {
        label: `Duplicate`,
        success: `Experiment duplicated successfully`,
        fail: `Failed to duplicate experiment`,
        'flag-confirm': {
          title: `Duplicate Experiment`,
          description: `This experiment has a linked feature flag. Duplicating the experiment will also duplicate the feature flag.`,
        },
        'flag-mode-modal': {
          title: `This experiment has a linked feature flag. How do you want to handle the flag?`,
          redo: {
            label: `Transfer Flag`,
            description: `Run this experiment again with the same feature flag. The duplicate experiment takes over the flag connection. The original experiment is disconnected from the flag.`,
            'useful-for': `Useful for: re-running this experiment under a new draft while keeping the same rollout.`,
          },
          analyze: {
            label: `Duplicate without Flag`,
            description: `Create a copy of this experiment without its flag connection. The original experiment keeps the flag.`,
            'useful-for': `Useful for: reviewing results separately, or adding and modifying metrics without affecting the original.`,
          },
          template: {
            label: `Duplicate Flag`,
            description: `Create copies of both the experiment and flag. The originals remain unchanged.`,
            'useful-for': `Useful for: starting a fresh experiment using this one as a template.`,
            'flag-key-label': `New flag key`,
            'flag-name-label': `New flag name`,
          },
        },
      },
      favorite: {
        label: `Favorite`,
        success: `Experiment favorited successfully.`,
        fail: `Failed to add experiment to favorites.`,
      },
      unfavorite: {
        label: `Favorited`,
        success: `Experiment unfavorited successfully.`,
        fail: `Failed to remove experiment from favorites.`,
      },
      pin: {
        label: `Pin for Project`,
        success: `Experiment pinned for project successfully.`,
        fail: `Failed to add experiment to project pins.`,
      },
      unpin: {
        label: `Unpin for Project`,
        success: `Experiment unpinned for project successfully.`,
        fail: `Failed to remove experiment from project pins.`,
      },
      update: {
        success: `Successfully updated experiment`,
        fail: `Failed to update experiment`,
      },
      'create-metric': {
        fail: `Failed to create metric`,
      },
      'update-metric': {
        fail: `Failed to update metric`,
      },
      'remove-metric': {
        fail: `Failed to remove metric`,
      },
      'reorder-metric': {
        fail: `Failed to reorder metrics`,
      },
      launch: {
        fail: `Failed to launch experiment`,
      },
      decide: {
        fail: `Failed to set experiment decision`,
      },
      'no-edit-access': `You have view-only access`,
    },
    status: {
      draft: `Draft`,
      configured: `Configured`,
      'pending-data': `Pending Data`,
      active: `In Progress`,
      concluded: `Ready for Review`,
      success: `Decision: {{variant}}`,
      'success-none': `Decision: None`,
      'success-deferred': `Decision Deferred`,
      fail: `Abandoned`,
      archived: `Archived`,
    },
    feedback: {
      title: `Experiments Feedback`,
      description: `Rate your satisfaction with experiments`,
      placeholder: `Additional feedback (Optional)`,
    },
    'data-view-mismatch': {
      title: `Switch to Linked Data View`,
      body: `This experiment is tied to the {{dataViewName}} Data View.`,
      instructions: `Select the {{dataViewName}} Data View via the top left dropdown or click Switch Data Views.`,
      'learn-more': `Learn more`,
      'switch-data-view': `Switch Data View`,
    },
    'data-view-link': {
      'body-mixpanel': `The selected Data View, {{dataViewName}}, will act as a permanent filter on this Experiment and Feature Flag.`,
      'body-third-party': `The selected Data View, {{dataViewName}}, will act as a permanent filter on this Experiment.`,
      'learn-more': `Learn more`,
      dismiss: `Dismiss`,
    },
    'switch-data-view': {
      title: `Switch to Linked Data View`,
      content: `This experiment is tied to the <bold>{{workspaceName}}</bold> Data View. <break /><break /> Select the {{workspaceName}} Data View via the top left dropdown or click Switch Data View.`,
      'content-private': `This experiment is tied to a <bold>Private Data View</bold> owned by <bold>{{owner}}</bold>. <break /><break /> You do not have permission to access this Data View.`,
      'content-private-unknown': `This experiment is tied to a <bold>Private Data View</bold>. <break /><break /> You do not have permission to access this Data View.`,
      'learn-more': `Learn More`,
      'switch-data-view': `Switch Data View`,
      'view-all-experiments': `View All Experiments`,
      'navigating-to': `Navigating to {{workspaceName}} Data View...`,
    },
    banner: {
      common: {
        // all these "significant-metrics" keys are used in the following helper to format significant metrics messages
        // iron/apps/experiments/src/components/page/summary/_banner/helper.ts::formatSignificantResults
        'significant-metrics': `No significant primary metrics.`,
        'significant-metrics-up': `↑ 1 positive primary metric`,
        'significant-metrics-up-plural': `↑ {{up}} positive primary metrics`,
        'significant-metrics-down': `↓ 1 negative primary metric`,
        'significant-metrics-down-plural': `↓ {{down}} negative primary metrics`,
        'significant-metrics-up-down': `↑ 1 positive and ↓ 1 negative primary metric`,
        'significant-metrics-up-plural-down': `↑ {{up}} positive and ↓ 1 negative primary metrics`,
        'significant-metrics-up-down-plural': `↑ 1 positive and ↓ {{down}} negative primary metrics`,
        'significant-metrics-up-plural-down-plural': `↑ {{up}} positive and ↓ {{down}} negative primary metrics`,
        'exposures-make-decision': {
          label: `Make Decision`,
          'cta-end': `Make Decision`,
          'cta-end-info': `End the analysis and log which experiment variant was shipped`,
          'cta-end-ff': `Make Decision`,
          'cta-end-ff-info': `End the analysis and decide which experiment variant to ship`,
          'cta-restart': `Restart Analysis`,
          'cta-restart-info': `Restart the analysis with today set as the start date`,
          'cta-restart-ff': `Restart Experiment`,
          'cta-restart-ff-info': `Restart the experiment with today as the start date`,
          'cta-reset': `Reset Experiment`,
          'cta-reset-info': `Resets the experiment to not started and reverts users back to the default experience`,
          'message-do-not-ship': `Reverted to control variant and logged decision`,
          'message-ship-variant': `Shipped variant {{variantKey}} to production`,
          'message-abandon': `Abandoned experiment and disabled feature flag`,
        },
        'edit-log': `Edit Log`,
        'finalize-decision': `Finalize Decision`,
      },
      configure: {
        title: `Draft`,
        'body-exposure': `Follow our guide to customize this experiment's configuration`,
        'body-feature-flag': `Select goals, variants and rollouts to ready this experiment for launch`,
        cta: `Configure`,
        dismiss: `Dismiss`,
      },
      running: {
        title: `In Progress`,
        'title-no-peeking': `In Progress: Avoid Peeking`,
        'title-early-significance': `In Progress: Early Significance`,
        cta: `Make Decision`,
        'cta-end': `End Analysis`,
        'cta-end-info': `End the analysis and log which experiment variant was shipped`,
        'cta-restart': `Restart Analysis`,
        'cta-restart-info': `Restart the analysis with today set as the start date`,
      },
      'prepped-for-launch': {
        title: `Configured`,
        body: `Launch to turn on the feature flag and begin the experiment`,
        test: `Test`,
        launch: `Launch`,
        'test-popover': {
          title: `Enable for Testers`,
          subtitle: `Turn on for testers only`,
        },
        'launch-error': `Failed to launch experiment. Please try again.`,
        'unsaved-changes-title': `Unsaved changes detected`,
        'unsaved-changes-description': `Before proceeding you must either save these changes or reset to the last saved state.`,
        'reset-changes': `Reset Changes`,
      },
      review: {
        title: `Ready for Review`,
        'post-hoc-segmentation-warning': `Segmenting after an experiment has ended is exploratory and may introduce bias. Interpret these results with caution.`,
      },
      succeeded: {
        title: `Decision: {{name}}`,
        'title-none': `Decision: None`,
      },
      'succeeded-deferred': {
        title: `Decision Deferred`,
      },
      failed: {
        title: `Abandoned`,
      },
      archived: {
        title: `Archived`,
        description: `This experiment is archived and inactive`,
      },
      decide: {
        title: `Make Decision`,
        decision: `Decision`,
        'keep-cohort-targeting': `Keep Rollout Group Targeting after Shipping?`,
        'no-description': `All users will receive shipped variant`,
        'yes-description': `Only users matching rollout groups will receive shipped variant`,
        'message-placeholder': `Due to X, Y, Z, we are shipping...`,
        'message-label': `Document Your Decision`,
        'message-info': `What did you learn and how did it influence this decision?`,
        'control-label': `Control`,
        'variant-label': `Variant #{{num}}`,
        none: `None Shipped`,
        defer: `Defer Decision`,
      },
      'restart-modal': {
        title: `Restart Experiment`,
        description: `Do you want to reset variant assignments when restarting?`,
        'keep-assignments-label': `Keep current assignments`,
        'keep-assignments-description': `Users will remain in their current variant groups`,
        'reset-assignments-label': `Reset assignments`,
        'reset-assignments-description': `All users except ones in sticky variants will get shuffled`,
        restart: `Restart`,
        restarting: `Restarting...`,
      },
      message: {
        shipped: `Variant - {{variant}}`,
        updated: `{{updatedBy}} on {{updatedAt}}`,
        'no-peeking': `Avoid peeking early at the results, it can compromise the analysis. [Learn more](https://docs.mixpanel.com/docs/experiments#when-do-we-say-the-experiment-is-ready-to-review)`,
        'pending-days-remaining': `More data required to estimate the end date.`,
        'days-remaining_one': `{{count}} day remaining.`,
        'days-remaining_other': `{{count}} days remaining.`,
        'duration-range': `{{startDate}} - {{endDate}}`,
        'duration-estimate': `(estimate)`,
        'duration-pending-data': `Pending Data`,
        duration: `Duration`,
        summary: `Summary`,
        findings: `Findings`,
        author: `Author`,
      },
    },
    results: {
      draft: `None`,
      active: `Not Ready`,
      concluded: `Ready for Review`,
      'no-significance': `No Significance`,
    },
    rollout: {
      list: {
        rollout: `Rollout`,
        'variant-split': `Variant Split`,
        or: `or`,
        add: `Add Rollout Group`,
        'group-name': `Group #{{num}}`,
        edit: `Edit Group`,
        delete: `Delete Group`,
        rename: `Rename Group`,
        duplicate: `Duplicate Group`,
      },
      modal: {
        title: `Edit Rollout Group`,
        'open-feature-flag': `Open Feature Flag`,
      },
    },
    date: {
      na: `n/a`,
    },
    list: {
      title: `Experiments`,
      new: `New Experiment`,
      'limit-banner': `Enterprise customers may create up to {{limit}} experiments for free`,
      'limit-reached': `You've used all free experiments included in your plan — upgrade to create more.`,
      'column-name': `Name`,
      'column-status': `Status`,
      'column-results': `Primary Results`,
      'column-creator': `Creator`,
      'column-start-date': `Start Date`,
      'column-end-date': `End Date`,
      'column-tags': `Tags`,
      'column-created': `Created`,
      'column-experiment-type': `Experiment Type`,
      'column-feature-flag': `Feature Flag`,
      'column-number-of-variants': `Variants`,

      'search-placeholder': `Search...`,
      variants: `{{count}} variants`,
      'no-description': `New experiment`,
      'data-view-indicator': `👁️  Data View: {{dataViewName}}`,
      'change-data-view': `Change Data View`,
      'end-date-estimated': withComment(`"est" is short for estimated`, `{{date}} (est)`),
      'end-date-no-data': `Pending Data`,
      'significant-positive': `{{count}} positive`,
      'significant-negative': `{{count}} negative`,
      'significant-positive-short': withComment(`"pos" is short for positive`, `{{count}} pos`),
      'significant-negative-short': withComment(`"neg" is short for negative`, `{{count}} neg`),
      'no-significance': `No significance`,
      'free-trial': {
        title: `Introducing $t(mixology.experiment_plural) in Mixpanel`,
        body: `Make smarter decisions, faster. Run experiments in minutes and track impact on key metrics. Enterprise plans let you **create three experiments for free.**`,
        'title-limit': `$t(mixology.experiment) Limit Reached`,
        'body-limit': `You've used all free experiments included in your plan — upgrade to create more.`,
        'body-empty': `Make smarter decisions, faster. Run experiments in minutes and track impact on key metrics.`,
      },
      'filter-statuses': {
        draft: `Draft`,
        configured: `Configured`,
        'pending-data': `Pending Data`,
        active: `In Progress`,
        concluded: `Ready for Review`,
        success: `Made Decision`,
        'success-deferred': `Decision Deferred`,
        fail: `Abandoned`,
        archived: `Archived`,
      },
      'show-archived': `All`,
      'hide-archived': `Active`,
      'show-archived-only': `Archived`,
      archival: `Archival`,
    },
    'unsaved-changes': {
      title: `Unsaved Changes`,
      description: `This experiment has unsaved changes, would you like to save before leaving to avoid losing these changes?`,
    },
    main: {
      tabs: {
        summary: `Summary`,
        settings: `Settings`,
      },
      summary: {
        hypothesis: `Hypothesis`,
        'hypothesis-placeholder': `Introducing [feature] will lead to [impact] among [user segment] due to [mechanism], causing [desired outcome]...`,
        metrics: `Metrics`,
      },
      metrics: {
        primary: `Primary Metrics`,
        'primary-tooltip': `Metrics used to evaluate the key goals for this experiment. These metrics define success and are included in statistical adjustments like Bonferroni correction (if enabled).`,
        'primary-metrics-warning': `We recommend 3 or fewer Primary Metrics to maintain statistical power and reduce false positives. <learnMoreLink>Learn more</learnMoreLink>`,
        'limit-reached': `You've reached the maximum number of metrics allowed for an Experiment ({{metricLimit}}).`,
        guardrail: `Guardrail Metrics`,
        'guardrail-tooltip': `Metrics used to monitor for regressions or unintended negative effects. Guardrail metrics help assess risk and can influence a decision if they degrade.`,
        other: `Other Metrics`,
        'other-tooltip': `Metrics included for context and learning. These metrics are visible in results but typically are not used to make a decision.`,
        'category-metric': `Metric`,
        'category-variant': `Variant Name`,
        'category-control': `Control`,
        'category-lift': `Lift`,
        'category-treatment': `Treatment`,
        'category-p-value': `P-value`,
        'category-prob-beat-control': `Chance to Beat Control`,
        lift: `Lift: {{lift}} ± {{confidence}}`,
        'p-value': `(P-Value: {{pValue}})`,
        'add-metric': `Add {{metricType}}`,
        'no-results': `No results for "{{searchTerm}}"`,
        'clear-search': `Clear Search`,
        'control-error': `There is no control data.  Either you haven't started sending $experiment_started events or the control key is not set correctly.`,
        error: `There was an error fetching data for your metric. We have been notified and are working to resolve issues.`,
        'lift-unsupported': `This metric is not yet supported.`,
        'cuped-error': `We couldn't apply CUPED variance reduction for this metric. We're aware of this issue and working to fix it.`,
        'server-error': `Something went wrong while fetching metric data. Please try again later.`,
        'segment-count_one': `{{count}} segment`,
        'segment-count_other': `{{count}} segments`,
        filters: {
          'all-variants': `All Variants`,
          variant: `Variant #{{num}}: {{variant}}`,
          search: `Search Metrics`,
          'exclude-qa': withComment(
            `Choose whether QA test users should be included in or excluded from the results.`,
            `Exclude QA Testers`,
          ),
        },
      },
    },
    'metrics-modal': {
      title: `Selected Metric`,
      'one-metric': `can only select one metric`,
    },
    setup: {
      title: `Customization Guide`,
      'primary-metric': `Primary Metric`,
      'primary-metric-info': `Main measure to determine experiment's success`,
      'primary-metric-btn': `Select Metric`,
      'test-duration': `Test Duration`,
      'test-duration-info': `Use our <a target="_blank" href="https://mixpanel.com/platform/experiments/sample-size-calculator/?hypothesis=two-sided">sample size calculator</a> to determine duration based on the current volume of your primary metric.`,
      'advanced-settings': `Advanced Settings`,
      'test-type': `Test Type`,
      'test-type-info': `Sequential is the default model for quick results.`,
      confidence: `Confidence Threshold`,
      'confidence-info': `Probability test results aren't due to random chance.`,
      'decision-threshold': `Decision Threshold`,
      'decision-threshold-info': `The minimum probability to beat control required to highlight a variant as a winner.`,
      variants: `Choose variants`,
      'variants-info': `Variants are the different versions you intend to include in the experiment. You can name variants as you see fit and customize the distribution.`,
      qa: `QA Testers`,
      'qa-info': `QA testers won't be counted towards the experiment's results. Add testers individually or by cohort.`,
      'qa-users': `Users`,
      'variant-control-label': `Control`,
      'variant-label': `Variant #{{num}}`,
      'rollout-groups': `Who should be eligible for this experiment?`,
      'rollout-groups-info': {
        line1: `Cohorts are evaluated top-to-bottom.`,
        line2: `If a user doesn’t match any cohort:`,
        'list-item1': `They will not receive any variant`,
        'list-item2': `They will see the default experience`,
        'list-item3': `They will not be included in the experiment's results`,
        line3: `These settings will not apply until the experiment is launched in a separate step.`,
      },
      'step-goals-title': `Goals`,
      'step-variants-title': `Variants`,
      'step-qa-title': `QA`,
      'step-rollout-title': `Rollout Groups`,
      'missing-primary-metric': `Select a primary metric`,
      'invalid-variant-errors': {
        'not-enough': `Must have at least {{num}} variants`,
        'too-many': `At most {{num}} variants are allowed`,
        'invalid-split': `Variant splits must sum to 100%`,
        'missing-key': `All variants must have a key`,
        'duplicate-key': `Variant keys must be unique`,
        'invalid-payload': `Payload must be valid JSON`,
        'payload-too-long': `Payload must be <= 32KB`,
        'invalid-runtime-rule': `Runtime evaluation properties cannot have empty keys or values`,
      },
      'missing-rollout': `You must have at least one rollout group`,
    },
    configuration: {
      settings: `Settings`,
      rollout: `Rollout`,
      testing: `Testing`,
      'rollout-groups': `Rollout Groups`,
      details: `Details`,
      'core-configurations': `Core Configurations`,
      variants: `Variants`,
      analysis: `Analysis`,
      'control-label': `Control`,
      'variant-label': `Variant #{{num}}`,
      'hide-variant': `Hide {{variant}} from results`,
      'show-variant': `Show {{variant}} in results`,
      hidden: `Hidden`,
      creator: `Creator`,
      tags: `Tags`,
      date: `Date Range`,
      'date-info': `This experiment has concluded and any events before or after the range will be excluded from the analysis.`,
      'end-condition': `Duration`,
      'end-condition-info': `How long this experiment should be expected to run to achieve desired statistical power. <sampleSizeLink>Try our sample size calculator</sampleSizeLink> to determine this value. <learnMoreLink>Learn more</learnMoreLink>`,
      'end-condition-options': {
        'sample-size': `Exposures`,
        'sample-size-placeholder': `10,000`,
        'sample-size-info': `The test will end when the total exposures reach this number`,
        days: `Days`,
        'days-placeholder': `14`,
        'days-info': `The test will end after this number of days`,
      },
      'start-date': `Start Date`,
      'start-date-info': `Any events before this date will be excluded from the analysis`,
      'end-date': `End Date`,
      'data-collection-options': {
        'exposure-events': `Exposure Events`,
        'feature-flag': `Mixpanel Feature Flag`,
      },
      'testing-model': `Experiment Type`,
      'testing-model-info': `<strong>Sequential</strong>: A Frequentist method that allows mid-test checks with less bias. <strong>Frequentist</strong>: Tests once with a fixed sample for strict validity. <learnMoreLink>Learn more</learnMoreLink>`,
      'exclude-qa-info': `Enable this to exclude QA testers from experiment analysis for experiments managed by Mixpanel $t(sidenav.feature-flags).`,
      'exclude-qa-options': {
        include: withComment(`Feature is turned off, QA testers will be included in the results`, `Disabled`),
        exclude: withComment(`Feature is turned on, QA testers will be excluded from the results`, `Enabled`),
      },
      'testing-model-options': {
        frequentist: `Frequentist`,
        'frequentist-info': `Frequentist model is capable of detecting smaller lifts, but requires you to keep experiments running for the full duration.`,
        sequential: `Sequential`,
        'sequential-info': `Sequential Frequentist allows you to detect lift and conclude experiments quickly, but may fail to reach significance for very small lifts.`,
        bayesian: `Bayesian`,
        'bayesian-info': `Bayesian model reports the probability that each variant beats control, making results easier to interpret and act on. Supported for average numeric metrics only.`,
      },
      'confidence-level': `Confidence Threshold`,
      'confidence-level-info': `The level of certainty required to consider a result significant. <learnMoreLink>Learn more</learnMoreLink>`,
      'decision-threshold': `Decision Threshold`,
      'decision-threshold-info': `The minimum probability to beat control required to highlight a variant as a winner. <learnMoreLink>Learn more</learnMoreLink>`,
      cohort: `Cohort Scoped Results`,
      'cohort-info': `Includes only this cohort in results. Useful if your system logs exposure for all users, but not needed in most cases.`,
      'cohort-empty': `Disabled`,
      'cohort-present': `Enabled`,
      cuped: `CUPED (Variance Reduction)`,
      'cuped-info': `CUPED (Controlled-experiment Using Pre-Experiment Data) reduces variance in experiment results by using pre-exposure data as a control variate, leading to faster experiment conclusions. <learnMoreLink>Learn more</learnMoreLink>`,
      'cuped-options': {
        disabled: `Disabled`,
        'disabled-default': `Disabled (Default)`,
        enabled: `Enabled`,
      },
      'cuped-preset': `Pre-Exposure Period`,
      'cuped-preset-info': `The period before the experiment start date to use for CUPED variance reduction. The pre-exposure period should be representative of user behavior before the experiment began.`,
      'cuped-preset-placeholder': `Select pre-exposure period`,
      'cuped-presets': {
        '1-week': `1 Week`,
        '2-weeks': `2 Weeks`,
        '4-weeks': `4 Weeks`,
        '60-days': `60 Days`,
        '90-days': `90 Days`,
      },
      'cuped-variance-reduction': `CUPED Variance Reduction`,
      'cuped-pre-exposure-period': `CUPED Pre-Exposure Period`,
      'bonferroni-correction': `Bonferroni Correction`,
      'bonferroni-info': `Bonferroni correction adjusts the significance threshold to account for multiple comparisons. When enabled, the threshold is made more strict based on the number of experiment variants and primary metrics you have. This reduces the chance of false positives, but requires more data to reach significance.`,
      'bonferroni-options': {
        disabled: `Disabled`,
        'disabled-default': `Disabled (Default)`,
        enabled: `Enabled`,
      },
      'multiple-testing-correction': `Multiple Testing Correction`,
      'multiple-testing-correction-info': `Controls how significance thresholds are adjusted when testing multiple metrics and variants. Without correction, the chance of false positives increases with more comparisons.`,
      'multiple-testing-correction-options': {
        off: `Off`,
        'off-default': `Off (Default)`,
        bonferroni: `Bonferroni`,
        'bonferroni-description': `Strictest correction. Every individual significant result can be trusted on its own, but it becomes harder to reach significance with many metrics and variants.`,
        'benjamini-hochberg': `Benjamini-Hochberg`,
        'benjamini-hochberg-description': `Balanced correction. A small proportion of significant results may be false positives, but you are much more likely to detect real effects. Recommended when testing many metrics.`,
      },
      winsorization: `Winsorization`,
      'winsorization-info': `Winsorization caps extreme values at specified percentiles to reduce the impact of outliers and data spikes. Enable this if your metric data has extreme values that may skew results. Does not apply to binary metrics (unique users, conversion rates, retention rates).`,
      'winsorization-options': {
        disabled: `Disabled`,
        'disabled-default': `Disabled (Default)`,
        enabled: `Enabled`,
      },
      'winsorization-percentile': `Winsorization Percentile`,
      'winsorization-percentile-info': `Values above the top percentile or below the bottom percentile will be clamped to stay within this range. For example, 5% means values below the 5th percentile or above the 95th percentile will be capped.`,
      'winsorization-percentile-error': `Must be greater than 0, less than 50`,
      advanced: `Advanced`,
      'advanced-analysis': `Advanced Analysis`,
      health: `Health`,
      srm: `Sample Ratio Mismatch (SRM)`,
      'srm-info': `Sample Ratio Mismatch detection checks if the observed traffic distribution matches the expected allocation, helping identify data collection issues.`,
      'srm-options': {
        disabled: `Disabled`,
        'disabled-default': `Disabled (Default)`,
        enabled: `Enabled`,
      },
      'srm-allocations': `SRM Target Allocations`,
      'srm-allocations-info': `The expected percentage of traffic for each variant. These values should match your experiment's intended split.`,
      'srm-allocations-not-set': `Not configured`,
      'srm-allocations-modal': {
        title: `SRM Target Allocations`,
        description: `Enter the expected percentage of traffic for each variant. The total must equal 100%.`,
        total: `Total`,
        error: `The percentages must add up to 100%.`,
      },
      'pre-experiment-bias': `Pre-Experiment Bias`,
      'pre-experiment-bias-info': `Checks for pre-existing biases between variant groups before the experiment started, helping validate that user assignment was truly random.`,
      'pre-experiment-bias-options': {
        disabled: `Disabled`,
        enabled: `Enabled`,
        'enabled-default': `Enabled (Default)`,
      },
      'pre-experiment-bias-duration': `Pre-Experiment Duration`,
      'pre-experiment-bias-duration-info': `The period before the experiment start date to check for pre-experiment biases. This period should be representative of user behavior before the experiment began.`,
      'pre-experiment-bias-presets': {
        '1-week': `1 Week`,
        '2-weeks': `2 Weeks`,
        '4-weeks': `4 Weeks`,
      },
      'exposure-events': {
        'experiment-name': `Experiment Key`,
        'experiment-name-info': `Identifies the experiment-started events from this experiment`,
        'control-key': `Control Variant Key`,
        'control-key-info': `Identifies the control variant in this experiment`,
        'date-change-disabled': `The Start Date and Duration of an experiment cannot be modified after the experiment has ended and a decision has been logged.`,
      },
      'feature-flag': {
        'feature-flag-id': `Feature Flag`,
        'feature-flag-id-info': `The Mixpanel Feature Flag orchestrating this experiment`,
        'feature-flag-key': `Feature Flag Key`,
        'feature-flag-key-info': `The key used to identify this feature flag in your code base`,
        'control-key': `Control Variant`,
        'control-key-info': `Identifies the control variant in this experiment`,
        'date-change-disabled': `The Start Date and Duration of an experiment cannot be modified after the experiment has ended and a decision has been made.`,
      },
      'enter-key': `Enter key`,
      'enter-new-key': `Enter new key`,
      'no-experiment-results': `No experiment event results found`,
      'no-experiment-variant-results': `No variant results found`,
      'no-feature-flag-results': `No feature flag results found`,
      'no-feature-flag-variant-results': `No variant results found`,
      'how-it-works': `How it works`,
      'rollout-groups-info': `Choose who you want in this experiment. These users are included in the experiment's results.`,
      'list-environments-failed': `Failed to load linked experiments.`,
      'copy-to-environment-success': `Successfully copied experiment.`,
      'copy-to-environment-failed': `Failed to copy experiment.`,
      'push-to-environment-success': `Successfully pushed changes to linked experiment.`,
      'push-to-environment-failed': `Failed to push changes to linked experiment.`,
      'unlink-environment-success': `Successfully unlinked experiment.`,
      'unlink-environment-failed': `Failed to unlink experiment.`,
      'environment-help': `Experiments may be duplicated to other Projects used as development environments. Duplicates are managed independently.`,
      'other-environments': `Other Environments`,
      'multi-env-modal': {
        'include-flag': `Include Flag`,
        'no-flag': `Don't Include Flag`,
      },
      'copy-to-project-modal': {
        title: `Copy Experiment To Project`,
        description: `This experiment is linked to the Mixpanel feature flag <strong>{{flagName}}</strong>. Do you want to copy the flag to the project as well?`,
      },
      'push-to-project-modal': {
        title: `Push Experiment Changes To Project`,
        description: `This experiment is linked to the Mixpanel feature flag <strong>{{flagName}}</strong>. Do you want to push changes to the feature flag as well?`,
      },
      'screenshot-upload-failed': `Failed to upload screenshot.`,
    },
    'config-tab': {
      experiment: `Experiment`,
      analysis: `Analysis`,
      'health-checks': `Health Checks`,
      'pre-experiment-bias': `Pre-Experiment Bias`,
      more: `More`,
      variants: `Variants`,
      'feature-flag': `Feature Flag`,
      'feature-flag-banner': `This experiment is powered by a <bold>Mixpanel Feature Flag</bold>. Most flag settings can be managed directly below.`,
      'open-flag': `Open Flag`,
      'cohorts-and-rollouts': `Cohorts & Rollouts`,
      'qa-testers': `QA Testers`,
    },
    new: {
      header: `New Experiment`,
      source: `Choose Experiment Source`,
      'source-options': {
        'feature-flag-title': `Mixpanel Feature Flag`,
        'feature-flag-description': `Use a Mixpanel Feature Flag to run your experiment`,
        'exposure-events-title': `3rd Party Feature Flag`,
        'exposure-events-description': `Manually send exposure events from other flag tools to Mixpanel`,
      },
      'feature-flag-limit-reached': `Free Limit Reached`,
      name: `Experiment Name`,
      'name-placeholder': `e.g. New Signup Flow`,
      hypothesis: `Hypothesis`,
      'feature-flag': `Feature Flag Key`,
      'feature-flag-placeholder': `Select Feature Flag`,
      'feature-flag-more-info': `Generated a new flag key for this experiment`,
      'feature-flag-already-exists': `A feature flag with this key already exists. Choose a different key`,
      'feature-flag-already-linked-to-experiment': `This feature flag is already linked to <experimentLink>another experiment</experimentLink>. If you proceed, it will be unlinked from that experiment.`,
      'find-feature-flag': `or Find Existing Flag`,
      'add-feature-flag': `or Specify New Feature Flag`,
      experiment: `Experiment Key`,
      'experiment-info': `Experiment key is the value for the property \`{{nameProperty}}\` on the \`{{eventName}}\` event that identifies this experiment.<br/><br/>You can also choose a different event for experiments in <settingsLink>Project Settings.</settingsLink>`,
      'experiment-info-use-any': `Experiment key is the property name on the \`{{eventName}}\` event that identifies this experiment.<br/><br/>You can also choose a different event for experiments in <settingsLink>Project Settings.</settingsLink>`,
      'experiment-placeholder': `Select Experiment Key`,
      'add-experiment': `or Specify New Key`,
      'find-experiment': `or Find Existing Key`,
      'add-experiment-placeholder': `e.g. New Signup Flow`,
      control: `Control Variant`,
      'control-placeholder': `Select control`,
      'add-control-placeholder': `Control variant`,
      'experiment-display-name': `Experiment Display Name`,
      'experiment-display-name-more-info': `Generated using experiment key`,
      create: `Create Experiment`,
      'experiment-type': `Experiment type`,
      'experiment-type-default-title': `Default experiment`,
      'experiment-type-default-description': `Run a standard A/B test backed by a feature flag or exposure events.`,
      'experiment-type-visual-title': `Visual experiment`,
      'experiment-type-visual-description': `Use the no-code overlay editor to author UI changes on your live site.`,
      'experiment-type-visual-lock-required': `Required for visual experiments`,
      'experiment-type-visual-lock-must-feature-flag': `Visual experiments must use a Mixpanel feature flag`,
    },
    'edit-on-site': {
      button: `Edit on site`,
      'modal-title': `Open overlay editor`,
      'modal-description': `Enter the URL of the page where you want to author visual changes for this experiment.`,
      'url-placeholder': `https://example.com/landing`,
      'open-editor': `Open editor`,
      opening: `Opening…`,
      'detecting-extension': `Detecting Mixpanel Data Inspector extension…`,
      'extension-not-installed': `The Mixpanel Data Inspector Chrome extension is required for the visual editor. Install it, then refresh this page.`,
      'install-extension': `Install extension →`,
      requesting: `Requesting editor session…`,
      'opening-tab': `Opening editor in a new tab…`,
      'last-attempt-failed': `Last attempt failed.`,
      'last-attempt-failed-detail': `Last attempt failed: {{error}}`,
      error: `Error: {{error}}`,
    },
    'metric-hover-card': {
      tabs: {
        results: `Results`,
        chart: `Chart`,
        totals: `Totals`,
      },
      summary: {
        title: `Summary`,
        'statsig-not-significant': `Not Statistically Significant`,
        'statsig-negative': `Statistically Significant`,
        'statsig-positive': `Statistically Significant`,
        'text-lift-inconclusive': `The lift of <lift/> is inconclusive and could be due to chance, so it shouldn't be used for decision-making.`,
        'text-lift-neutral': `The lift is <lift/> compared to <control/> in <direction/>, with a margin of error of <confidence>±{{confidence}}</confidence>.`,
        'text-lift-above': `The lift is <lift/> over <control/> in <direction/>, with a margin of error of <confidence>±{{confidence}}</confidence>.`,
        'text-lift-below': `The lift is <lift/> under <control/> in <direction/>, with a margin of error of <confidence>±{{confidence}}</confidence>.`,
        'text-default': `The p-value is {{pValueAboveBelow}} the Significance Threshold, indicating a <statsig/> result. <liftStatement/>`,
        'text-no-peeking': `The current data shows a <statsig/> result. However, checking Frequentist results early increases the chance of seeing a false "{{statsig}}" outcome. <noPeekLearnMoreLink>Learn more</noPeekLearnMoreLink>`,
        'text-bayesian': `This variant is <prob>{{probability}}</prob> likely to beat <control/>. The posterior mean of lift is <lift/>, with a credible interval of <credibleInterval/>.`,
        'direction-positive': `the target direction`,
        'direction-negative': `the wrong direction`,
        'direction-neutral': `a neutral direction`,
        'p-value-above': `above`,
        'p-value-below': `below`,
        'text-no-results': `No data was found for this metric.`,
      },
      'target-direction-up': `↑ Up`,
      'target-direction-down': `↓ Down`,
      'target-direction-unknown': `Unknown`,
      'significance-threshold-tooltip': `The Significance Threshold is calculated based on the Confidence Level: ST = 1 - CL.<br/><br/>The Confidence Level can be changed in this experiment's settings.<br/><br/><learnMoreLink>Learn More</learnMoreLink>`,
      'significance-threshold-tooltip-bonferroni': `The Significance Threshold is calculated based on the Confidence Level: ST = 1 - CL.<br/><br/>Bonferroni correction is applied, dividing the threshold by the number of primary metrics ({{metricCount}}) × variants ({{variantCount}}) = {{comparisons}} comparisons.<br/><br/>The Confidence Level can be changed in this experiment's settings.<br/><br/><learnMoreLink>Learn More</learnMoreLink>`,
      'prob-beat-control': `Probability to Beat Control`,
      'credible-interval': `Credible Interval`,
      'bh-threshold': `BH Adjusted Threshold`,
      'bh-threshold-tooltip': `Benjamini-Hochberg assigns each metric its own threshold based on its rank among all {{comparisons}} primary metric comparisons.<br/><br/>All p-values are sorted from smallest to largest. Each p-value is compared against (rank / total tests) × significance threshold. A result is significant if its p-value is below its rank-based threshold.`,
      'target-direction-tooltip': `Target Direction is the ideal direction for this metric to move, either Up or Down.<br/><br/>Target Direction determines the color of the result: green when moving in the target direction, red when moving against it.`,
      'target-direction-edit': `Edit Direction`,
    },
    health: {
      'checks-passed-one': `1 Check Passed`,
      'checks-passed': `{{passed}} Checks Passed`,
      'checks-failed': `{{failed}} of {{total}} Checks Failed`,
      'checks-configure': `Configure Health Checks`,
      'check-passed': `Passed`,
      'check-failed': `Issue Detected`,
      'check-configure': `Configure`,
      'check-not-enough-data': `Not Enough Data`,
      'view-details': `View Details`,
      'retro-aa': {
        label: `Pre-Experiment Bias`,
        description: `Checks to confirm there are no imbalances in user behaviors for the primary and guardrail metrics included in this experiment. <learnMoreLink>Learn more</learnMoreLink>`,
        disabled: `This check is not enabled. Click Configure to enable it.`,
        'no-data': `No pre-experiment exposure data was found, so there is no indication of pre-existing bias for this metric.`,
        warning: `An imbalance was detected in {{metricName}} for variant "{{variantName}}" (p-value {{pValue}}).`,
        'warning-other': `An imbalance was detected in {{metricName}} for variant "{{variantName}}" (p-value {{pValue}}) and {{other}} other metric/variant pairs.`,
        'warning-bayesian': `An imbalance was detected in {{metricName}} for variant "{{variantName}}" (probability to beat control {{probBeatControl}}).`,
        'warning-bayesian-other': `An imbalance was detected in {{metricName}} for variant "{{variantName}}" (probability to beat control {{probBeatControl}}) and {{other}} other metric/variant pairs.`,
      },
      srm: {
        label: `Sample Ratio Mismatch`,
        description: `Checks that the observed distribution of users across variants matches the expected allocation. <learnMoreLink>Learn more</learnMoreLink>`,
        disabled: `This check is not enabled. Click Configure to enable it.`,
        passed: `No sample ratio mismatch detected (p-value {{pValue}}).`,
        warning: `A sample ratio mismatch was detected (p-value {{pValue}}). The observed variant distribution differs significantly from expected.`,
      },
    },
    'select-board': `Select board`,
    'select-experiment': `Select experiment`,
    'custom-experiment': `Custom experiment`,
    'saved-experiments': `Saved Experiments`,
    'tracked-experiments': `Tracked Experiments`,
    'mobile-ab-tests': `Mobile A/B Tests`,
    'filter-by-prop': `Filter by a property`,
    'filter-by-cohort': `Filter by a cohort`,
    'filter-by-event': `Filter by an event`,
    'sort-board-order': `Board Order`,
    'sort-weighted-delta': `Highest Weighted Delta`,
    'qb-impact': `<label>Show how</label> <experiment></experiment> <label>impacts</label> <board></board>`,
    'qb-treatment': `<control></control> <label>compares to</label> <variant></variant>`,
    'qb-breakdown': `<label>breakdown by</label> <segment></segment>`,
    'board-no-experiments': `The selected board does not have any cards for experiment reporting.`,
    'upsell-header': `Quickly learn how your experiments and their variants impact your metrics`,
    'upsell-description': `Learn how your experiments and their variants affect your KPIs across any of your boards — all in one place, in seconds. Track changes in your metrics over time, and confirm the validity of the results with confidence ratings to see which experiments are worth continuing.`,
    'upsell-bold': `Experiments is only available with the Enterprise plan. When you upgrade, you also get:`,
    'upsell-callout1': `Access to the Signal report & all advanced analysis`,
    'upsell-callout2': `SSO & automated provisioning`,
    'upsell-callout3': `Advanced access controls, & more...`,
    'upsell-cta': `Contact Sales`,
    'not-supported': `Not supported`,
    delta: `Delta`,
    lift: `Lift`,
    'p-value': `p-value`,
    'significance-threshold': `Significance Threshold`,
    'adjusted-significance-threshold': `Adjusted Significance Threshold`,
    'target-direction': `Target Direction`,
    unknown: `Unknown`,
    'sort-by': `Sort by`,
    where: `where`,
    'compares-to': `compares to`,
    control: `Control`,
    variant: `Variant`,
    'select-filter': `Select filter`,
    board: `Board`,
    experiment: `Experiment`,
    exposures: `Exposures`,
    total: `Total`,
    'funnel-conversion': `Funnel conversion`,
    'funnel-conversion-tooltip': `Percent of users who entered the funnel and completed it. This differs from the denominator used by Lift, which is calculated per exposure.`,
    'lift-denominator-note': `Lift is calculated per exposure (conversions ÷ users assigned to the variant), not per funnel entry. It can disagree with the conversion rate shown in the Totals and Chart tabs. <docsLink>Learn more</docsLink>`,
    'no-experiments': `No experiment variants`,
    'no-ab-tests': `No A/B tests`,
    'no-variants': `No variants detected`,
    segment: `Segment`,
    rate: `Rate`,
    confidence: `Confidence`,
    'goal-metric': `Goal Metric`,
    loading: `Loading...`,
    'deprecated-header': `The new $t(mixology.experiment_plural) report is here!`,
    'deprecated-body': `Smarter, faster, easier to use—the new Experiments report replaces this legacy report on {{date}}.`,
    'deprecated-action': `Go to $t(mixology.experiment_plural)`,
    significance: `Significance`,
    'not-significant': `Not Significant`,
    'significant-positive': `Significant Positive`,
    'significant-negative': `Significant Negative`,
    'save-error': `Error saving experiment: {{message}}`,
    toast: {
      'multiple-testing-correction-auto-enabled': `Benjamini-Hochberg correction has been enabled to adjust for multiple comparisons across segments.`,
    },
    diagnostics: {
      'users-card-title': `Users`,
      'users-card-count': `Showing {{shown}} of {{total}} total exposed users`,
      'search-placeholder': `Search users`,
      'filter-variant': `Variant`,
      'filter-variant-all': `All variants`,
      'filter-variant-tooltip': `Filters the table to users who have been exposed to the selected variant at any point during the experiment. A user's most recent variant may differ if they were re-bucketed.`,
      'column-name': `Name`,
      'column-variant': `Most Recent Variant`,
      'column-variant-tooltip': `The variant value from this user's most recent exposure event.`,
      'column-variant-mismatch-tooltip': `This user was exposed to {{filter}} during the experiment, but their most recent exposure was to {{latest}}. This usually happens when allocations change or sticky variants are off.`,
      'column-email': `Email`,
      'column-last-exposed': `Last Exposed`,
      'column-first-exposed': `First Exposed`,
      'column-country': `Country`,
      'column-total-exposures': `Total Exposures`,
      'control-suffix': `(Control)`,
      'empty-state': `No exposed users yet`,
      'users-card-error': `Could not load exposed users. Please try again.`,
      'health-checks-card-title': `Health Checks`,
      'health-checks-section-traffic': `Traffic`,
      'health-checks-srm-title': `Sample Size Ratio`,
      'health-checks-srm-summary-passed': `Variants are within expected exposure proportions.`,
      'health-checks-srm-summary-failed': `Variant exposure proportions don't match the configured split.`,
      'health-checks-srm-summary-disabled': `Sample ratio mismatch alerts are turned off for this experiment.`,
      'health-checks-srm-summary-loading': `Checking variant exposure proportions…`,
      'health-checks-srm-summary-no-data': `Will run once exposures are recorded.`,
      'health-checks-srm-status': `Within expected proportions`,
      'health-checks-srm-detail': `Sample size ratio mismatch (SRM) compares the observed exposure counts in each variant against the expected split. A statistically significant mismatch is a strong signal that traffic is not being assigned correctly — common causes include broken eligibility rules, SDK initialization race conditions, or one variant failing silently before exposure can be tracked. We run a chi-square goodness-of-fit test against the configured allocation and surface a warning when the p-value falls below 0.001.`,
      'health-checks-bias-title': `Pre-Experiment Bias`,
      'health-checks-bias-summary-passed': `No meaningful baseline differences detected.`,
      'health-checks-bias-summary-failed': `Baseline differences were detected between variant groups.`,
      'health-checks-bias-summary-disabled': `Pre-experiment bias alerts are turned off for this experiment.`,
      'health-checks-bias-summary-loading': `Analyzing pre-experiment behavior…`,
      'health-checks-bias-summary-no-data': `Will run once exposures are recorded.`,
      'health-checks-bias-status': `No bias detected`,
      'health-checks-bias-detail': `Pre-experiment bias compares each variant's behavior on key metrics during a calibration window before the experiment started. Large differences in pre-period behavior suggest the assignment is correlated with user characteristics (e.g. only power users are bucketed into a particular variant), which can confound the post-launch results. We summarize the baseline gap per variant and surface a warning when any variant deviates by more than two standard errors from the control's pre-period mean.`,
      'health-checks-status-passed': `Passed`,
      'health-checks-status-failed': `Issue Detected`,
      'health-checks-status-disabled': `Disabled`,
      'health-checks-status-loading': `Checking…`,
      'health-checks-status-no-data': `No data yet`,
      'health-checks-section-explanation': `Explanation`,
      'health-checks-section-meaning': `What This Means`,
      'health-checks-srm-explanation': `This check compares the actual number of users exposed to each variant against your intended traffic split. It uses a chi-squared test to determine whether any difference in sample sizes is due to random chance or points to a problem with the experiment setup.`,
      'health-checks-srm-meaning-passed': `Your experiment's actual traffic split matches your intended split. The p-value is {{pValue}}, which means the difference in sample sizes is within the range of random chance. No action needed.`,
      'health-checks-srm-meaning-failed-line1': `Your experiment is configured for a {{configuredSplit}} split, but the actual split is {{observedSplit}} ({{variantBreakdown}}). The p-value is {{pValue}}.`,
      'health-checks-srm-meaning-failed-line2': `A low p-value means the difference in sample sizes is unlikely to be random. Something in your experiment setup is probably routing users unevenly across variants.`,
      'health-checks-srm-meaning-failed-line3': `When SRM is detected, all other metric results should be considered unreliable until the root cause is identified and resolved. The same issue causing the uneven split is likely also biasing your metrics.`,
      'health-checks-srm-dismiss': `Disable alert`,
      'health-checks-srm-control': `Control`,
      'health-checks-srm-treatment': `Treatment`,
      'health-checks-srm-users': `users`,
      'health-checks-section-next-steps': `Next Steps`,
      'health-checks-srm-next-step-1-title': `Check your traffic allocation history.`,
      'health-checks-srm-next-step-1-body-with-flag': `If you changed variant splits during the experiment in a way that wasn't purely additive (e.g., going from 50/50 to 70/30 and back), users may have been rebucketed across variants, creating uneven counts. <flagHistoryLink>View flag history</flagHistoryLink> to review allocation changes. If you're targeting cohorts, using <stickyVariantsLink>sticky variants</stickyVariantsLink> can prevent reassignment when cohort membership refreshes.`,
      'health-checks-srm-next-step-1-body': `If you changed variant splits during the experiment in a way that wasn't purely additive (e.g., going from 50/50 to 70/30 and back), users may have been rebucketed across variants, creating uneven counts. Review allocation changes in your flag's audit trail. If you're targeting cohorts, using <stickyVariantsLink>sticky variants</stickyVariantsLink> can prevent reassignment when cohort membership refreshes.`,
      'health-checks-srm-next-step-2-title': `Check for bot traffic.`,
      'health-checks-srm-next-step-2-body': `Mixpanel's JavaScript SDK automatically filters common bots, but other bots can still send exposure events, inflating one variant's count if they only hit that variant's experience. Conversely, if a variant changes user behavior enough, real users could be misclassified as bots and filtered out. Review whether bot filtering is removing users unevenly across variants. Use <sessionReplayLink>Session Replay</sessionReplayLink> to verify whether suspicious users are real or automated, and see <botTrafficLink>Bot Traffic Best Practices</botTrafficLink> for mitigation options.`,
      'health-checks-srm-next-step-3-title': `Check your trigger/exposure event.`,
      'health-checks-srm-next-step-3-body': `Make sure the exposure event fires at the same point in the user flow for all variants. A common issue: redirect experiments where the treatment variation loads a different URL, and some users drop off during the redirect before the exposure event fires. Only users who complete the redirect get counted, creating an SRM. Use <sessionReplayLink>Session Replay</sessionReplayLink> to observe whether one variant introduces extra friction before exposure.`,
      'health-checks-srm-next-step-4-title': `Check for residual effects from a restarted experiment.`,
      'health-checks-srm-next-step-4-body': `If you restarted this experiment after a bug and moved the analysis start date forward rather than re-randomizing users, users who churned during the buggy period are still assigned to their variant but won't appear in post-restart data. If the bug affected one variant more than the other, this could create an SRM. Consider starting a fresh experiment with new randomization instead.`,
      'health-checks-srm-next-step-5-title': `Check for filters that exclude users from other experiments.`,
      'health-checks-srm-next-step-5-body': `If you've filtered out users exposed to another experiment (or a specific variant of another experiment), verify that the filter removes roughly equal numbers of users from each variant. Uneven removal could create an SRM.`,
      'health-checks-bias-explanation': `This check analyzes your experiment's metrics using {{weeks}} weeks of data from before the experiment started, with the same variant assignments applied retroactively. It runs the same statistical significance analysis you'd see in your experiment results, but on pre-experiment data. If it finds a statistically significant difference between variant groups before the experiment began, the groups may not have been equivalent at baseline. If there is no pre-experiment data available for a metric, this check will not trigger for that metric.`,
      'health-checks-bias-meaning-passed': `No statistically significant differences were found between variant groups on any metrics in the two weeks before the experiment started. Your variant groups were equivalent at baseline. No action needed.`,
      'health-checks-bias-meaning-failed': `Users in different variants were already behaving differently on these metrics before the experiment started. For example, one variant may have been assigned more highly-active users than the other. Any lift you observe during the experiment on these metrics could reflect that pre-existing difference rather than the effect of your treatment. Metrics without pre-experiment bias can be interpreted normally.`,
      'health-checks-bias-affected-summary': `Pre-experiment bias was detected on {{affected}} of {{total}} metrics over the past {{weeks}} weeks:`,
      'health-checks-bias-dismiss': `Disable alert`,
      'health-checks-bias-next-step-1-title': `Check whether SRM is also present.`,
      'health-checks-bias-next-step-1-body': `Pre-experiment bias and SRM often co-occur. If you also have an SRM, fix that first, since the root cause is likely in your assignment mechanism. Pre-experiment bias caused by a systematic assignment bug won't be fully corrected by CUPED.`,
      'health-checks-bias-next-step-2-title': `Check for carry-over effects from prior experiments.`,
      'health-checks-bias-next-step-2-body': `If users in this experiment recently participated in another experiment, the prior treatment may have permanently changed their behavior. If those users aren't evenly distributed across your current variants, you'll see bias on related metrics. Check whether your experiment population overlaps with a recently concluded experiment.`,
      'health-checks-bias-next-step-3-title': `Check whether your experiment setup is filtering out users unevenly.`,
      'health-checks-bias-next-step-3-body': `If your experiment involves redirects, additional page loads, or other friction before the exposure event fires, users who drop off during that process are lost from that variant's population. The users who remain are self-selected and likely more engaged, so that variant skews toward higher baseline engagement. Similarly, users entering via bookmarks or shared links may bypass the normal entry point and behave differently. Use <sessionReplayLink>Session Replay</sessionReplayLink> to observe how users in each variant are experiencing the entry flow.`,
      'health-checks-bias-next-step-4-title': `Enable CUPED and compare adjusted vs. unadjusted results.`,
      'health-checks-bias-next-step-4-body': `If you've investigated the above and want to proceed with the experiment, enable CUPED to help reduce the impact of the imbalance. CUPED can mitigate mild pre-experiment bias but won't fully correct severe cases. Compare adjusted vs. unadjusted results to gauge how much the bias is affecting your metrics, then dismiss the warning if you're comfortable proceeding.`,
    },
    page: {
      config: {
        settings: `Settings`,
        history: `History`,
        'diff-header': `Changes Diff`,
        'next-history-page': `Load more results`,
      },
      'history-event': {
        'experiment-created': `Created experiment`,
        'started-experiment': `Started experiment`,
        'paused-experiment': `Paused experiment`,
        'concluded-experiment': `Concluded experiment`,
        'archived-experiment': `Archived experiment`,
        'decided-experiment': `Decided experiment`,
        'changed-status': `Changed status to`,
        'added-metric': `Added metric`,
        'removed-metric': `Removed metric`,
        'updated-metric': `Updated metric`,
        'added-variant': `Added variant`,
        'removed-variant': `Removed variant`,
        'updated-variant': `Updated variant`,
        'linked-feature-flag': `Linked feature flag`,
        'unlinked-feature-flag': `Unlinked feature flag`,
        'changed-feature-flag': `Changed linked feature flag`,
        'changed-name': `Changed name`,
        'changed-description': `Changed description`,
        'changed-hypothesis': `Changed hypothesis`,
        'changed-tags': `Changed tags`,
        'changed-start-date': `Changed start date`,
        'changed-end-date': `Changed end date`,
        'changed-field': `Changed`,
        'changed-settings': `Changed settings`,
        'changed-sample-size': `Changed sample size`,
        'changed-end-condition': `Changed end condition`,
        'changed-end-after-days': `Changed end after (days)`,
        'changed-testing-model': `Changed testing model`,
        'changed-multiple-testing-correction': `Changed multiple testing correction`,
        'changed-confidence-level': `Changed confidence level`,
        'changed-collection-method': `Changed data collection method`,
        'changed-cuped': `Changed CUPED setting`,
        'changed-winsorization': `Changed winsorization setting`,
        'changed-srm': `Changed SRM check setting`,
        'updated-settings': `Updated settings`,
        'no-change': `No change`,
      },
    },
  },
  reports: {
    header: {
      'title-placeholder': `Untitled`,
      'description-placeholder': `Add description...`,
      'created-by': `Created by`,
    },
    actions: {
      'copy-url': `Copy URL`,
      close: `Close`,
      'close-tooltip': `Close Report`,
      'close-entity-tooltip': `Close {{entity}}`,
      comments: `View Comments`,
      'version-history': `Version History`,
      saving: {
        'save-new': `Save As New`,
        save: `Save`,
        saved: `Saved`,
        'save-to-board': `Save to New Board`,
        'save-to-board-placeholder': `Save to board...`,
        tooltips: {
          'save-new': `Save a duplicate of this report`,
          'save-new-entity': `Save a duplicate of this {{entity}}`,
          'save-entity-changes': `Save changes to this {{entity}}`,
          'invalid-report': `Current report is invalid`,
          'invalid-entity': `Current {{entity}} is invalid`,
          cta: `Save changes to this report`,
          'needs-event': `Please select at least one event to save your insights report`,
          'no-changes': `No unsaved changes`,
          'view-only': `You have view-only access and cannot save changes`,
        },
      },
      delete: `Delete`,
      rename: `Rename`,
      duplicate: `Duplicate`,
      'duplicate-disabled-tooltip': `Duplicating is disabled because this report is over the metrics limit.`,
      undo: `Undo`,
      redo: `Redo`,
      'move-to': `Move to`,
      open: `Open`,
      'manage-sharing': `Manage Sharing`,
      new: `New`,
      'move-to-board': `Move to New Board`,
      'move-to-board-placeholder': `Move report to another board...`,
      'new-report': `New Report`,
      alerts: `Alerts`,
      'create-alert': `Create Alert`,
      'save-alert': `Save Alert`,
      'test-alert': `Test Alert`,
      'enable-alerts': `Enable Alerts`,
      'manage-alerts': `Manage Alerts`,
      'max-alerts-exceeded': `Manage Alerts`,
      'alerts-need-data': `To create a custom alert, your report must have data.`,
      'alerts-report-data-loading': `Loading data for report. You can create alerts once the report has finished loading.`,
      'alerts-need-permission': `You do not have permission to create alerts in this project. You must be an Owner, Admin or Analyst to create alerts.`,
      'alerts-need-save': `To enable alerts, save your report`,
      'retention-alerts-disabled': `Custom alerts are not available for Retention reports.`,
      'funnel-conversion-window-alerts-disabled': `Custom alerts are not available when funnels conversion window exceeds the report's date range.`,
      'alerts-modal': {
        title: `Alerts for {{reportName}}`,
        name: `Name`,
        status: `Status`,
        project: `Project`,
        'data-view': `Data View`,
        'select-data-view': `Select data view`,
        history: `History`,
        when: `When`,
        criteria: `Criteria`,
        frequency: `Frequency`,
        notify: `Notify`,
        'no-alerts': `No alerts for this report.`,
        'no-create-permission': `You don't have permission to create alerts for this report`,
        valid: `Valid`,
        'out-of-sync': `Out of sync`,
        'out-of-sync-tooltip': `This alert has become out of sync due to a change to the underlying report. Edit and save the alert to make it valid again.`,
        paused: `Paused`,
        invalid: `Invalid`,
        'invalid-default-tooltip': `This alert has become invalid. Please check the underlying report and alert criteria settings.`,
        'delivery-failure-tooltip': `This alert has been paused due to consecutive failures to deliver notifications. Please check the alert notification settings.`,
        'query-failure-tooltip': `This alert has been paused due to consecutive failures to run the data query. Please check the alert criteria settings and ensure the underlying report is valid.`,
        'paused-sensitive-tooltip': `This alert is paused because the report contains classified or sensitive properties. Remove the sensitive properties from the report to re-enable this alert.`,
        tooltip_one: `This report has {{count}} custom alert`,
        tooltip_other: `This report has {{count}} custom alerts`,
        'criteria-type-threshold': `Custom threshold met`,
        'criteria-type-anomaly': `Anomaly detected`,
        'loading-tooltip': `Loading custom alerts...`,
        'manage-alerts': `Manage Alerts`,
        'unsaved-changes-warning': `This report is currently used to query these alerts. Saving changes to the report may affect the alert output or cause the alerts to stop running. If you wish to avoid invalidating these alerts, please save as a new report.`,
        'dashboard-filters-warning': `This report's dashboard has global filters or breakdowns applied. Alerts ignore dashboard-level filters and breakdowns. To include them, add filters or breakdowns directly to the report.`,
        'view-alert-history': `View Alert History`,
        'notification-windows': {
          'any-time': `Any time`,
          'during-selected-windows': `During Selected Windows`,
          'invalid-time-format': `Invalid time format`,
          from: `from`,
          until: `until`,
          'add-day-window': `Add Day Window`,
          'add-single-day-window': `Add Single Day Window`,
          'add-weekdays': `Weekdays`,
          'add-weekends': `Weekends`,
          'replace-with-weekdays': `Replace existing windows with weekdays`,
          'replace-with-weekends': `Replace existing windows with weekends`,
          'add-time-window-action': `Add Time Window`,
          'add-window': `Add Window`,
          'delete-day-window': `Delete Day Window`,
          'duplicate-day-window': `Duplicate Day Window`,
          'remove-time-window': `Remove Time Window`,
          to: `to`,
          'error-start-before-end': `Start day/time must be before end day/time`,
          'error-exceeds-week': `Window cannot span more than 7 days`,
          'error-windows-overlap': `Notification windows cannot overlap`,
          'error-incomplete-fields': `Please fill in all fields for each notification window`,
          'error-at-least-one-required': `At least one complete notification window is required`,
          'timezone-tooltip': `Time windows use this project's timezone setting ({{timezone}})`,
          'timezone-tooltip-no-tz': `Time windows use this project's timezone setting`,
        },
        operators: {
          'surges-or-drops': `Surges or drops`,
          surges: `Surges`,
          drops: `Drops`,
          'is-above': `is above`,
          'is-below': `is below`,
          increases: `increases by more than`,
          decreases: `decreases by more than`,
        },
        'test-criteria': {
          button: `Test Criteria`,
          description: `Test the criteria against recent past data.`,
          result: `This alert would have triggered during {{count}} of the past {{timeBucketCount}} {{timeUnit}}s.`,
          'result-no-unit_one': `This alert would have triggered {{count}} time recently.`,
          'result-no-unit_other': `This alert would have triggered {{count}} times recently.`,
          'result-tooltip': `Notification frequency preferences are ignored.`,
        },
        'test-alert-errors': {
          'generic-error': `Failed to test alert. Please try again.`,
          'invalid-alert': `Alert configuration is invalid. Please review your settings.`,
          'query-error': `Failed to query the report data. Please try again.`,
          'http-error': `Request failed. Please try again.`,
          'sensitive-data': `This report contains classified or sensitive properties. Remove them to test this alert.`,
        },
        'test-notification': {
          button: `Test Notification Configuration`,
          'condition-met': `Test condition met.`,
          'condition-not-met': `Test condition not met.`,
          'notifications-sent': `Test notifications were sent.`,
          'notification-error': `Some notifications failed to send: {{failedTypes}}`,
        },
        'anomaly-detection-controls': {
          'alert-when-any-segment': `Alert when any top 50 segment`,
          'alert-when-this-segment': `Alert when this segment`,
          'approaching-limit-banner-body': `Your organization is approaching the limit of active anomaly detection alerts. Delete old anomaly detection alerts to make room for new ones.`,
          'below-limit-banner-body': `Your organization has reached the limit for anomaly detection alerts. Delete old anomaly detection alerts to make room for new ones.`,
          confidence: `Confidence`,
          'disabled-tooltip': `Anomaly detection is only supported on Insights and Funnels line charts without comparisons.`,
          'limit-banner-header': `Cannot create alert with anomaly detection criteria`,
          'outside-range': `outside of the expected range with`,
        },
        'frequency-select-label': `At most once per`,
        'hourly-warning-banner': `Currently, hourly notifications may result in more false positives due to reduced alert accuracy.`,
        'out-of-sync-banner': `This alert is out of sync with the originating report and may not perform as expected. Check that the alert definition below is as expected and save it again to revalidate it.`,
      },
      exports: {
        export: `Export`,
        'export-png': `Export PNG`,
        'export-pdf': `Export PDF`,
        'export-csv': `Export CSV`,
        'export-csvclipboard': `Copy CSV`,
        'segment-limit': `Export results are limited to {{limit}} segments`,
        'enable-cta': `Contact your project administrator to enable`,
        'upgrade-cta': `Upgrade your plan to export reports as {{format}}. [$t(common.learn-more)](https://mixpanel.com/pricing)`,
        'csv-copied-to-clipboard': `CSV copied to clipboard`,
        'csv-copy-error': `There was an error copying the CSV data to your clipboard.`,
        'exporting-format': `Exporting {{format}}...`,
        'exceeded-limit': `Export results exceeded the limit you selected. You can export up to {{limit}} rows`,
        'export-complete': `Export Complete`,
        'export-failed': `Export Failed`,
      },
      'refresh-data': `Refresh Data`,
      'data-age-recent': `Data from < 1 min ago`,
      'data-age-hour': `Data from {{minutes}} min ago`,
      'data-age-old': `Data from {{time}}`,
      'switching-report-types': {
        'over-metrics-limit-toast': `You have more than {{metricsLimit}} metrics selected. Only the first {{metricsLimit}} will be used in the report.`,
      },
      'ai-summary': {
        tooltip: `Summarize using AI`,
      },
      oda: {
        'analysis-scheduled': `Analysis scheduled.`,
        'analysis-scheduling-error': `There was an error scheduling your analysis.`,
      },
      errors: {
        'error-loading-report': `Error loading report`,
        'report-not-found': `Report does not exist`,
        'error-loading-from-url': `Error loading report from URL`,
        'board-filters-incompatible': `Board filters cannot be applied to this report.`,
        'save-board-changes-failed': `Failed to save your board changes.`,
        'bookmark-not-found': `Bookmark not found`,
        'save-report-failed': `Failed to save report: {{errorMessage}}`,
        'error-moving-board': `Error moving board`,
        'metrics-api-default': `Sorry, something went wrong. Please contact support if this problem persists.`,
      },
    },
    'day-of-week': {
      monday: `Monday`,
      tuesday: `Tuesday`,
      wednesday: `Wednesday`,
      thursday: `Thursday`,
      friday: `Friday`,
      saturday: `Saturday`,
      sunday: `Sunday`,
      'next-monday': `Next Monday`,
      'next-tuesday': `Next Tuesday`,
      'next-wednesday': `Next Wednesday`,
      'next-thursday': `Next Thursday`,
      'next-friday': `Next Friday`,
      'next-saturday': `Next Saturday`,
      'next-sunday': `Next Sunday`,
    },
    'time-unit': {
      minute: `Minute`,
      minute_other: `Minutes`,
      hour: `Hour`,
      hour_other: `Hours`,
      day: `Day`,
      day_other: `Days`,
      week: `Week`,
      week_other: `Weeks`,
      month: `Month`,
      month_other: `Months`,
      quarter: `Quarter`,
      quarter_other: `Quarters`,
      year: `Year`,
      year_other: `Years`,
      'hour-of-day': `Hour of Day`,
      'day-of-week': `Day of Week`,
      session: `Session`,
      session_other: `Sessions`,
      second: `Second`,
      second_other: `Seconds`,
      'x-units': `{{count}} {{timeUnit}}`,
      'disabled-reason': {
        behavioral: `Not available for behavioral segmentation.`,
        cohorts: `Not available for cohorts and people timeseries.`,
        'cohorts-over-time-intervals-day': `Not available for cohorts timeseries with time windows over {{max}} days.`,
        'cohorts-over-time-intervals-week': `Not available for cohorts timeseries with time windows over {{max}} weeks.`,
        'day-range': `Not available for time windows over {{max}} days`,
        'hour-range': `Not available for time windows over {{max}} days`,
        'minute-range': `Not available for time windows over {{max}} hours`,
        'minute-range-funnel': `Not available for funnels with time windows over {{max}} hours`,
        retention: `Not available for retention.`,
        xau: `Not available when {{xau}} is selected`,
      },
    },
    'date-range': {
      'display-label': {
        between: `Between {{fromDate}} — {{toDate}}`,
        since: `Since {{date}}`,
        last: `Last {{value}} {{unit}}`,
        'in-the-last': `In the last {{value}} {{unit}}`,
        next: `Next {{value}} {{unit}}`,
        ending: `ending {{date}}`,
        'ending-ago': `ending {{value}} {{unit}} ago`,
      },
      presets: {
        title: `Presets`,
        today: `Today`,
        yesterday: `Yesterday`,
        '1-day': `1D`,
        '96-hours': `96H`,
        '7-days': `7D`,
        '30-days': `30D`,
        '3-months': `3M`,
        '24-weeks': `24W`,
        '6-months': `6M`,
        '12-months': `12M`,
        'period-to-date': {
          'end-of': `End of current day`,
          'selector-header': `Current`,
          title: `Period to date`,
          tooltip: `Track progress from the start of the current week, month, quarter, or year through today.`,
          week: `Week to date`,
          month: `Month to date`,
          quarter: `Quarter to date`,
          year: `Year to date`,
          'current-week': `Current week`,
          'current-month': `Current month`,
          'current-quarter': `Current quarter`,
          'current-year': `Current year`,
        },
        'previous-period': {
          'end-of': `End of previous {{unit}}`,
          'selector-header': `Previous`,
          title: `Previous period`,
          tooltip: `Look back at the last complete week, month, quarter, or year.`,
          week: `Previous week`,
          month: `Previous month`,
          quarter: `Previous quarter`,
          year: `Previous year`,
        },
        default: `Default`,
        description: {
          '1-day': `Last 1 day`,
          '96-hours': `Last 96 hours`,
          '7-days': `Last 7 days`,
          '30-days': `Last 30 days`,
          '3-months': `Last 3 months`,
          '24-weeks': `Last 24 weeks`,
          '6-months': `Last 6 months`,
          '12-months': `Last 12 months`,
          default: `Date selection set by report`,
        },
        tooltip: {
          'unit-start': `{{unit}} start is set to {{start}}`,
          'reset-selection': `Reset selection and navigation to default`,
          'time-ranges-disabled-mode': `"{{mode}}" mode does not support time ranges`,
        },
      },
      exclusion: {
        title: `Exclude`,
        today: {
          'menu-item-label': `Today`,
          'selected-label': `Excluding today`,
        },
        'current-week': {
          'menu-item-label': `Current week`,
          'selected-label': `Excluding current week`,
        },
        'current-month': {
          'menu-item-label': `Current month`,
          'selected-label': `Excluding current month`,
        },
        'current-quarter': {
          'menu-item-label': `Current quarter`,
          'selected-label': `Excluding current quarter`,
        },
        'incomplete-period': {
          'menu-item-label': `Incomplete periods`,
          'selected-label': `Excluding incomplete periods`,
          tooltip: `Incomplete periods will be excluded based on the selected time unit of the chart. Example: an hour-based chart will exclude the current hour as it is incomplete.`,
        },
        custom: {
          'selected-label': `Excluding last {{value}} {{unit}}`,
        },
        disabled: {
          'non-line-incomplete-period': `Excluding incomplete periods is only available for over time analysis (ie line charts).`,
          'exclusion-exceeds-date-range': `This exclusion period would exceed the selected date range.`,
          'between-date-range': `Between date ranges are incompatible with exclusion periods.`,
          'default-dashboard-date-range': `Default date ranges are incompatible with exclusion periods.`,
        },
        'exclusion-removed': `"Exclude incomplete periods" was removed because it's incompatible with your chart type.`,
        'context-menu-label': `Exclude incomplete {{unit}}`,
      },
      custom: {
        mode: {
          between: `Fixed`,
          on: `On`,
          since: `Since`,
          'in-the-last': `Last`,
          tooltip: {
            between: `Pick a specific start and end date on the calendar.`,
            since: `Track progress from a specific date through today.`,
            last: `Set a rolling window, like last 3 weeks or last 30 days.`,
          },
        },
        'date-placeholder': `Describe a date`,
        'date-and-time-placeholder': `Describe date and time`,
        start: `Start`,
        starts: `Starts`,
        end: `End`,
        ends: `Ends`,
        'time-placeholder': `Time`,
        to: `To`,
        'since-date-placeholder': `Since date`,
        'value-placeholder': `Enter a value`,
        'ending-date': `Ending`,
        'duration-ago': `{{duration}} ago`,
        tooltip: {
          'change-unit': `Change {{units}} to days to select ending date`,
          'change-unit-warning': `Changing the unit from "days" will remove the ending date`,
          'time-limit': `Time window may be up to {{count}} days ago.`,
          'history-limit': `Upgrade your plan to query more than {{count}} days of data.`,
        },
        'advanced-options': `$t(common.advanced)`,
        advanced: {
          'enable-time-ranges': `Enable Time Ranges`,
          'disable-time-ranges': `Disable Time Ranges`,
        },
      },
      type: {
        between: `between`,
        'not-between': `not between`,
        on: `on`,
        'not-on': `not on`,
        'relative-after': `in the last`,
        'relative-before': `before the last`,
        since: `since`,
        'starts-on': `starts on`,
        'ends-on': `ends on`,
        'relative-future': `in the next`,
        'not-relative-after': `not in the last`,
        before: `before`,
      },
      rolling: {
        range: `Rolling range`,
        date: `Rolling date`,
        tooltips: {
          range: `Dates adjust relative to current day`,
          date: `Date adjusts relative to current day`,
        },
      },
      calendar: {
        'select-month': `Navigate to {{firstMonth}} and {{secondMonth}}.`,
        'select-year': `Navigate to {{year}}.`,
        'select-title': `Viewing {{selectedValue}}.`,
        'nav-out-of-bounds': `Navigation disabled: out of supported date bounds.`,
      },
      comparison: {
        ending: `date range ending on {{date}}`,
        starting: `date range starting on {{date}}`,
        'rolling-dates': `rolling`,
        'compared-to': `compared to {{timePeriod}}`,
      },
      disabled: {
        'all-events': `All Events queries only allow for {{count}} days of data.`,
        'date-beyond-data-retention': `Data is only available from {{date}} onwards.`,
        'data-history-label': `Data unavailable for selected date`,
        'beyond-data-retention-detailed-tooltip': `$t(reports.date-range.disabled.date-beyond-data-retention) Reduce the time range to match your data retention period.`,
        dau: `DAU queries only allow for {{count}} days of data.`,
        'max-days': `This type of query only allows for {{count}} days of data.`,
        'max-query-label': `Date range too long`,
      },
      info: {
        'last-import': `Data last imported {{date}}`,
      },
      invalid: {
        'generic-invalid-label': `Invalid date range`,
        'inverted-dates-tooltip': `Correct the dates or disable time ranges.`,
        'inverted-dates': `Date start is after end`,
        'last-non-integer-input': `Input must be an integer`,
        'last-range-min': `Value below {{min}} {{unit}} minimum`,
        'last-range-max': `Value exceeds {{max}} {{unit}} maximum`,
      },
      'warn-only': {
        'auto-corrected-for-data-retention': `$t(reports.date-range.disabled.date-beyond-data-retention) The selected date range has been adjusted in accordance with your data lookback period.`,
        'fixed-date-tooltip': `End dates are auto-calculated in "{{mode}}" mode. Switch to Fixed or Last to set your own.`,
        'future-date': `Future date selected`,
        'future-date-tooltip': `We enable future dates up to 6 months for building reports ahead of scheduled events. Your report may return no data or an abridged series for now.`,
      },
      copilot: {
        title: `Recently applied`,
        tooltip: `Select date ranges with natural language or from your "Recents" list.`,
        placeholder: `Describe a date range, e.g. "last 7 days"`,
        loading: `Resolving date range...`,
      },
    },
    'analysis-types': {
      linear: `Linear`,
      logarithmic: `Logarithmic`,
      rolling: `Rolling`,
      cumulative: `Cumulative`,
      tooltips: {
        linear: `Linear is the standard calculation of counts over time.`,
        logarithmic: `Logarithmic nonlinear scale is best used for  wide ranges of values.`,
        rolling: `Rolling smooths trends by calculating the average value over past periods.`,
        cumulative: `Cumulative adds each new period’s value to the sum of previous periods.`,
      },
    },
    'lift-comparison': {
      compare: `Compare`,
      period: `Time Period`,
      'period-description': `percent change over time`,
      segment: `Segment`,
      'segment-description': `segment comparison`,
      overall: `Overall`,
      'overall-description': `overall comparison`,
      experiment: `Experiment`,
      'experiment-description': `experiment comparison`,
      'compare-overall': `Compare to Overall`,
      'compare-segment': `Compare to {{segment}}`,
      'compare-period': `Compare to {{period}}`,
      'compare-experiment': `Experiment: {{control}} as control`,
      'previous-period': {
        day: `Previous day`,
        week: `Previous week`,
        month: `Previous month`,
        quarter: `Previous quarter`,
        year: `Previous year`,
        custom: `Custom`,
        'with-value': `Previous {{count}} {{unit}}`,
        units: {
          second: `seconds`,
          minute: `minutes`,
          hour: `hours`,
          day: `days`,
          week: `weeks`,
          month: `months`,
          quarter: `quarters`,
          year: `years`,
          session: `sessions`,
          day_of_week: `days of week`,
          hour_of_day: `hours of day`,
        },
      },
      'custom-date': {
        title: `Compare to Date`,
        apply: `Apply`,
      },
      tooltips: {
        period: `Compare to Past Time Periods`,
        segmented: `Compare to Segmented Values`,
        unsegmented: `Compare to Unsegmented Value`,
        'compare-info': `Compare change over time using segments as baseline. [Learn more.](https://help.mixpanel.com/hc/en-us/articles/4485419360916)`,
        disabled: `You must select a breakdown to enable {{liftType}}`,
        'lift-absolute': `Absolute`,
        'lift-percent': `Percent Change over Baseline`,
        experiment: `Compare experiment variants with statistical significance`,
        'experiment-disabled': `Add an experiment breakdown to enable experiment comparison`,
      },
    },
    'select-table-column': {
      'selector-tooltip': `Pick which metrics are shown in the table below.`,
      'stat-sig': `Statistical Significance`,
      'step-count': `Step Count`,
      'time-prev-step': `Average time from previous step`,
      'time-first-step': `Average time from first step`,
      'convert-prev-step': `Conversion from previous step`,
      'convert-first-step': `Conversion from first step`,
      tooltips: {
        'stat-sig': `Display if a segment has a statistically significant difference compared to overall or any other segment.`,
        'step-count': `Display the absolute number of conversions at a particular step.`,
        'time-prev-step': `Display the average time from the previous step, for users who converted.`,
        'time-first-step': `Display the average time from the first step, for users who converted.`,
        'convert-prev-step': `Display the percent that reached a step from the prior step.`,
        'convert-first-step': `Display the percent that reached a step from the first step.`,
      },
    },
    'chart-types': {
      bar: `Bar`,
      line: `Line`,
      pie: `Pie`,
      metric: `Metric`,
      'stacked-bar': `Stacked Bar`,
      'stacked-line': `Stacked Line`,
      column: `Column`,
      'stacked-column': `Stacked Column`,
      sankey: `User Flows`,
      table: `Table`,
      'funnel-steps': `Funnel Steps`,
      'funnel-trend': `Funnel Trends`,
      'funnel-frequency-line': `Funnel Frequency Line`,
      'funnel-frequency-bar': `Funnel Frequency Bar`,
      'funnel-ttc-bar': `Funnel Time To Convert Bar`,
      'funnel-ttc-line': `Funnel Time To Convert Line`,
      'funnel-median-ttc': `Time to Convert`,
      'retention-curve': `Retention Curve`,
      'retention-line': `Line`,
      'retention-trends': `Retention Trends`,
      frequency: `Frequency`,
      'frequency-curve': `Frequency Curve`,
      'funnel-top-paths': `Top Paths`,
      'flows-top-paths': `Top Paths`,
      flows: `User Flows`,
      'impact-adoption': `Adoption Over Time`,
      'impact-trends': `Impact Trends`,
      'impact-propensity': `Causal Impact`,
      labels: {
        chart: `{{chartType}} chart`,
        'profile-chart': `Profile {{chartType}} chart`,
        'retention-chart': `Retention {{chartType}} chart`,
        'retention-metric': `Retention Metric chart`,
        stacked: `Stacked {{chartType}}`,
      },
      tooltips: {
        bar: `Bars are best to display comparisons between categories.`,
        line: `Line charts display trends over time.`,
        pie: `Pie charts illustrate composition of a whole in pie form.`,
        metric: `Metric chart displays a single value for each metric.`,
        'stacked-bar': `Stacked bar is best when comparing composition of multiple counts.`,
        'stacked-line': `Stacked line charts display compositions of counts over time.`,
        column: `Column charts display trends over time.`,
        'stacked-column': `Stacked column charts display compositions of counts over time.`,
        sankey: ``,
        table: `Tables display data in a tabular view.`,
        'funnel-steps': `View conversion rates between funnel steps.`,
        'funnel-trend': `View changes in conversion rate over time.`,
        'funnel-frequency-line': ``,
        'funnel-frequency-bar': ``,
        'funnel-ttc-bar': ``,
        'funnel-ttc-line': ``,
        'funnel-median-ttc': `View a distribution of the time it takes users to convert through the funnel.`,
        'retention-curve': `View retention rates of individual cohorts of users.`,
        'retention-line': ``,
        'retention-trends': `View the retention trend over a period of time.`,
        frequency: `The number of times a user completes a step before converting or dropping off.`,
        'funnel-top-paths': `When using an Any Order Funnel, view which paths were most common.`,
        'flows-top-paths': `Specifically analyze the most often followed sequences of events.`,
        flows: `Explore a fluid diagram of various user paths.`,
        'impact-adoption': ``,
        'impact-trends': ``,
        'impact-propensity': ``,
      },
      'disabled-reasons': {
        'multiple-or': `{{first}} or {{second}}`,
        'cohort-breakdown': `{{chartType}} is not available with cohort breakdowns alone`,
        formula: `{{chartType}} is not available with formula applied`,
        xau: `{{chartType}} is not available with daily, weekly, or monthly active users.\nRemove daily, weekly, or monthly active users to enable`,
        'funnel-conversion-rate': `{{chartType}} is disabled when a funnel conversion rate metric is present`,
        'retention-rate': `{{chartType}} is disabled when a retention rate metric is present`,
        rolling: `{{chartType}} is disabled while Rolling is active.\nRemove Rolling to enable`,
        cumulative: `{{chartType}} is disabled while Cumulative is active.\nRemove Cumulative to enable`,
        'not-any-order': `{{chartType}} is only available with any order funnels`,
        attribution: `{{chartType}} is not available with attribution breakdown`,
        'sub-day-granularity-with-any-order': `Any-order funnels are not available with sub-day time granularities.`,
        'time-to-convert': `{{chartType}} is not available with Time to Convert`,
        'funnel-ttc': `{{chartType}} is disabled when a funnel TTC metric is present`,
        'not-single-funnel': `{{chartType}} is only available when there is one funnel metric only`,
        'not-only-retention': `{{chartType}} is only available when all metrics are retention metrics`,
        'time-comparison': `Time comparison is not available for {{chartType}}`,
        'percent-change': `{{chartType}} is not available with percent change over time`,
        'overall-comparison': `Overall comparison is not available for {{chartType}}`,
        'segment-comparison': `Segment comparison is not available for {{chartType}}`,
        'experiment-comparison': `Experiment comparison is not available for {{chartType}}`,
        'top-paths-funnels': `Top paths is only available with any order funnels`,
        'comparison-restriction': `Cannot compare baselines when both distributions and segmentations are selected`,
        'scd-option-any': `{{chartType}} is not available when "Any" option is selected`,
      },
    },
    oda: {
      cta: `Find Interesting Segments`,
      'retention-description': `Receive an email of statistically significant segments impacting retention.`,
      'funnel-description': `Receive an email of statistically significant segments impacting this funnel.`,
      'upsell-cta': `Upgrade to Find Interesting Segments`,
      'upsell-cta-header': `Find statistically significant segments`,
      'upsell-retention-description': `Rather than searching through multiple property and cohort breakdowns to find significant data, receive an email of statistically significant segments with higher or lower retention.`,
      'upsell-funnel-description': `Rather than searching through multiple property and cohort breakdowns to find significant data, receive an email of statistically significant segments with higher or lower conversion for any funnel you create.`,
    },
    sorting: {
      'menu-button': `Sort`,
      'group-segments': `Group segments`,
      tooltip: `Set the grouped or ungrouped\nsort order. [$t(common.learn-more)](https://help.mixpanel.com/hc/en-us/articles/13271005313556#sort-order)`,
      'grouped-sort-tooltip': `You are seeing top performing segments\nUncheck this option to show top metric values\n[$t(common.learn-more)](https://help.mixpanel.com/hc/en-us/articles/13271005313556#view-n-segments)`,
      'flat-sort-tooltip': `You are seeing top metric values.\nCheck this option to show top performing segments\n[$t(common.learn-more)](https://help.mixpanel.com/hc/en-us/articles/13271005313556#view-n-segments)`,
    },
    'unsaved-changes': {
      header: `Unsaved Changes`,
      description: `Changes made will not be saved. Would you like to continue?`,
    },
  },
  'view-users': {
    title_user: `View users`,
    title: `View {{group}}`,
    expand: `Expand`,
    'create-cohort': `Create Cohort`,
    'export-csv': `Export CSV`,
    'export-toast': `Exporting CSV...`,
    'error-toast': `Sorry, something went wrong. Please contact support if this problem persists.`,
    'error-unauthorized': `You don't have permission to export CSV`,
    'count-label_user_one': `user`,
    'count-label_user': `users`,
    'count-label': `{{group}}`,
    'in-segment': `{{group}} in segment`,
    'in-segment_user': `users in segment`,
    'in-segment_user_one': `user in segment`,
    'in-cohort': `{{group}} in cohort: {{cohort}}`,
    'in-cohort_user': `users in cohort: {{cohort}}`,
    'in-cohort_user_one': `user in cohort: {{cohort}}`,
    'not-in-cohort': `{{group}} not in cohort: {{cohort}}`,
    'not-in-cohort_user': `users not in cohort: {{cohort}}`,
    'not-in-cohort_user_one': `user not in cohort: {{cohort}}`,
    'who-dropped-off': `{{group}} who dropped off`,
    'who-dropped-off_user': `users who dropped off`,
    'who-dropped-off_user_one': `user who dropped off`,
    'who-converted': `{{group}} who converted`,
    'who-converted_user': `users who converted`,
    'who-converted_user_one': `user who converted`,
    'who-not-converted': `{{group}} who did not convert`,
    'who-not-converted_user': `users who did not convert`,
    'who-not-converted_user_one': `user who did not convert`,
    'retained-date': `{{group}} who retained on {{date}}`,
    'retained-date_user': `users who retained on {{date}}`,
    'retained-date_user_one': `user who retained on {{date}}`,
    'retained-date-unit': `{{group}} who retained on {{dateUnit}} of {{date}}`,
    'retained-date-unit_user': `users who retained on {{dateUnit}} of {{date}}`,
    'retained-date-unit_user_one': `user who retained on {{dateUnit}} of {{date}}`,
    'dropped-date': `{{group}} who dropped on {{date}}`,
    'dropped-date_user': `users who dropped on {{date}}`,
    'dropped-date_user_one': `user who dropped on {{date}}`,
    'dropped-date-unit': `{{group}} who dropped on {{dateUnit}} of {{date}}`,
    'dropped-date-unit_user': `users who dropped on {{dateUnit}} of {{date}}`,
    'dropped-date-unit_user_one': `user who dropped on {{dateUnit}} of {{date}}`,
    'retained-any-interval': `{{group}} who retained during any interval`,
    'retained-any-interval_user': `users who retained during any interval`,
    'retained-any-interval_user_one': `user who retained during any interval`,
    'retained-complete-interval': `{{group}} who retained during a complete interval`,
    'retained-complete-interval_user': `users who retained during a complete interval`,
    'retained-complete-interval_user_one': `user who retained during a complete interval`,
    'dropped-any-interval': `{{group}} who dropped during any interval`,
    'dropped-any-interval_user': `users who dropped during any interval`,
    'dropped-any-interval_user_one': `user who dropped during any interval`,
    'dropped-complete-interval': `{{group}} who dropped during a complete interval`,
    'dropped-complete-interval_user': `users who dropped during a complete interval`,
    'dropped-complete-interval_user_one': `user who dropped during a complete interval`,
    'search-placeholder': `Search...`,
  },
  search: {
    placeholder: `Search`,
    templates: `Templates`,
    'boards-and-reports': `Boards & Reports`,
    'recently-searched': `Recently Searched`,
    'recently-viewed': `Recently Viewed`,
    'most-viewed': `Most Viewed`,
    'no-recent': `No recent history`,
    'show-all-results': `Show all results`,
    'allow-storage-prompt': `Personalize your Mixpanel experience in your user settings`,
    error: `Something went wrong`,
    'no-items-matched': `No items match your search`,
    'try-again': `Try another search, or contact support if this error persists.`,
    'sort-options': {
      relevance: `Relevance`,
      'most-viewed': `Most Viewed`,
      'recently-viewed': `Recently Viewed`,
      'recently-modified': `Recently Modified`,
      'title-asc': `Title A-Z`,
      'title-desc': `Title Z-A`,
    },
  },
  help: {
    'ask-questions': `Ask Questions`,
    'community-faqs': `Community FAQs`,
    'contact-us': `Contact Us`,
    'contact-sales': `Contact Sales`,
    'contact-support': `Contact Support`,
    'support-documentation': `Support Documentation`,
    support: {
      'pending-title': `Our Support Team is ready to help!`,
      'submitted-title': `Message sent`,
      'request-type': `Request type`,
      'request-type-prompt': `Select request type...`,
      'request-type-product': `I need help using Mixpanel`,
      'request-type-account': `I need help with my account`,
      'request-type-issue': `I need to report an issue`,
      'select-sub-type': `Select {{subType}}...`,
      priority: `Priority`,
      'product-help': `Product area`,
      'product-boards': `Boards`,
      'product-analysis': `Analysis`,
      'product-users': `Users`,
      'product-data-management': `Data Management`,
      'product-integrations': `Integrations`,
      'product-pipelines': `Pipelines`,
      'product-implementation': `Implementation`,
      'product-other': `Other`,
      'account-help': `Account area`,
      'account-billing': `Billing, Payments, and Pricing`,
      'account-management': `Account Management`,
      'account-data': `Personal Data and GDPR`,
      'account-other': `Other`,
      'issue-help': `Issue`,
      'issue-bug': `Bug`,
      'issue-reports': `Reports not Loading`,
      'issue-messages': `Messages not Sending`,
      'issue-ingestion': `Data Ingestion Issues`,
      'issue-other': `Other`,
      subject: `Subject`,
      'free-plan-warning': `Your plan includes support for critical issues only. Visit our [help center](https://help.mixpanel.com/?utm_source=in-product&utm_campaign=help-widget), ask the Mixpanel [community](https://experiences.mixpanel.com/mixpanel-slack-community?&utm_campaign=help-widget&utm_content=support-widget&utm_medium=help-widget&utm_source=in-product), or [upgrade](https://mixpanel.com/pricing/) to a paid plan for additional support.`,
      'issue-prompt': `How can we help?`,
      'thank-you': `Thanks for reaching out`,
      'thank-you-followup': `We'll get back to you at {{userEmail}} soon.`,
      'need-help': `Need help faster?`,
      'visit-docs': `Visit our help docs.`,
      'docs-tip': `Try searching for your question or using Ask AI for instant answers.`,
      attachments: `Attachments`,
      'screenshots-none': `Add up to {{maxUploads}} screenshots`,
      'screenshots-add_one': `Add up to 1 more screenshot`,
      'screenshots-add_other': `Add up to {{count}} more screenshots`,
      'screenshots-limit-reached': `Screenshot limited reached`,
      done: `Done`,
      send: `Send`,
      uploading: `Uploading...`,
      sending: `Sending...`,
      cancel: `Cancel`,
    },
    'open-help-chat': `Open Help Chat`,
    sendbird: {
      'chat-title': `Mixpanel Help Chat`,
      'chat-failed': `Help Chat Error, redirecting to Contact Support`,
      'loading-chat': `Loading Help Chat...`,
    },
    'demo-projects': `Demo Projects`,
    'demo-loading': `While you wait...`,
    'demo-select-dataset': `Select Sample Dataset`,
    'demo-switch-tip': `You can easily switch between datasets in the Demo view`,
    'demo-request-title': `Feel free to check out our demo projects!`,
    'demo-request-owner': `If you know {{ownerEmail}}, you can try to contact them directly for faster approval. You will receive an email confirmation once your request has been approved.`,
    'demo-cta': `Explore Demo`,
    documentation: `Help Docs`,
    'mixpanel-university': `Mixpanel University`,
    'submit-feedback': withComment(`submit feedback for Mixpanel product`, `Submit Feedback`),
    feedback: {
      submit: `Submit`,
      cancel: `Cancel`,
      'submit-pending': `Sending...`,
      'schedule-pm-call': `Would a call be easier? Grab time with a Mixpanel PM [here](https://scheduler.zoom.us/d/51qajncn/talk-to-a-mixpanel-pm).`,
      'thank-you': `Thank you for submitting feedback`,
      placeholder: `Your feedback...`,
      'sentiment-prompt': `Was this feature helpful?`,
      'email-prompt': `Customer email (optional)`,
      'email-placeholder': `Customer's email if submitting feedback on their behalf.`,
      'sfdc-prompt': `SFDC Opportunity ID (optional)`,
      'sfdc-placeholder': `Useful to file gaps for prospects. Just the ID, eg: 006Nt000005QwdRIAS.`,
      'feedback-type': `Feedback type`,
      positive: `Yes`,
      negative: `No`,
      'type-prompt': `Feedback type`,
      'type-unspecified': `Unspecified`,
      'type-dealbreaker': `Dealbreaker`,
      'type-pain-point': `Pain-point`,
      'type-alpha-beta': `Alpha/beta`,
    },
    'getting-started': `Getting Started`,
    'quick-start': `Quick Start`,
    changelog: `Changelog`,
    'changelog-modal': {
      'watch-loom': `Watch the Loom`,
      'read-more': `Read more`,
      'see-full-changelog': `See the Full Changelog`,
      'whats-new': `What's New`,
    },
    'full-changelog': `Full Changelog`,
    'visit-community': `Visit Community`,
    'hire-an-expert': `Hire an Expert`,
    'your-privacy-rights': `Your Privacy Rights`,
    'onboarding-guide': `Onboarding Guide`,
  },
  settings: {
    language: `Language`,
    settings: `Settings`,
    appearance: `Appearance`,
    organization: `Organization Settings`,
    'organization-short': withComment(`shortened version of organization`, `Org`),
    'organization-name-settings': `{{organization}} Settings`,
    'organization-no-access': `Only users with an owner or admin role can access {{organization}} Organization Settings`,
    project: `Project Settings`,
    'project-tooltip': `{{projectName}} Settings`,
    personal: `Personal Settings`,
    'admin-org-menu': `Admin Organizations`,
    shortcuts: withComment(`quick menu access to other apps`, `Shortcuts`),
    setup: `Set Up Mixpanel`,
    'billing-plan': `Plan Details and Billing`,
    'invite-users': `Invite Users`,
    'leave-demo': `Leave Demo`,
    'back-to-signup': `Back to Sign Up`,
    logout: `Log Out`,
    'add-to-role': {
      'email-outside-org': `This email is not a part of your organization`,
      role: `Role`,
      'select-item': `Please select a {{itemType}}`,
      'add-item': `Add another {{itemType}}`,
      'add-action': `Add`,
    },
    'user-settings': {
      'your-profile': {
        'verify-email': `Verify your email`,
        'resend-email': `Resend Verification Email`,
        'delete-profile': `Delete Your Profile`,
        name: `Name`,
        'name-placeholder': `Your Name`,
        email: `Email`,
        'email-placeholder': `Email address`,
        password: `Password`,
        'password-placeholder': `Password`,
        'two-factor-auth': `Two-factor Authentication`,
        'two-factor-method': `Two-factor method`,
        'two-factor-change': `Change method`,
        'two-factor-setup': `Set Up method`,
        'delete-account': `Delete Account`,
        'confirm-delete-title': `Are you sure you want to delete your user account?`,
        'confirm-delete-body': `You will be immediately logged out and lose access to your account. This cannot be undone.`,
        'change-two-factor-title': `Are you sure you want to change your two-factor method?`,
        'change-two-factor-body-p1': `Changing your two-factor method will clear your current method and immediately log you out.`,
        'change-two-factor-body-p2': `You will need to log back in to set up your new two-factor method.`,
        'setup-two-factor-title': `Set up your two-factor method`,
        'setup-two-factor-body': `To set up your two-factor method, you will need to log back in to start the setup process.`,
        'recovery-code-title': `You’ve used your last recovery code`,
        'recovery-code-body': `You have used your last recovery code. The next time you log in, you will be asked to reset your two-factor authentication.`,
        cancel: `Cancel`,
        'learn-more': `Learn More`,
        'log-out': `Log Out`,
        close: `Close`,
        'name-updated': `Name updated`,
        'email-verification-sent': `Email verification sent`,
        'password-updated': `Password updated`,
        'password-reset-sent': `Password reset email sent to {{email}}`,
        'password-reset-error': `There was a problem sending a password reset email`,
      },
      orgs: {
        id: `Organization ID`,
        name: `Organization Name`,
        role: `Organization Role`,
        leave: `Leave`,
        member: `Member`,
        'leave-org': `Leave Organization`,
        'leave-confirm-title': `Are you sure you want to leave {{organizationName}}?`,
        'leave-confirm-body': `You will lose access to all projects associated with {{organizationName}}.`,
      },
      privacy: {
        generate: `Generate`,
        'click-to-generate': `Click the generate button to create an API token`,
        'your-activity': `Your Activity`,
        'cookies-title': `Use cookies to personalize your Mixpanel experience`,
        'cookies-description': `Mixpanel uses cookies to deliver a personalized experience for our users. Storing this information allows us to keep you logged in to Mixpanel, and returns you to where you were when you last left the site, along with additional benefits.`,
        'usage-title': `Help Mixpanel improve`,
        'usage-description': `Understanding how our customers use Mixpanel is how we improve our products & services. Help us make Mixpanel better by allowing us to log your product usage.`,
        'your-data': `Your Mixpanel Data`,
        'usage-data-title': `Personal usage data`,
        'usage-data-description': `Mixpanel may use your personal information, such as how you use our products and services, to improve our products. Users in some jurisdictions, such as users residing in the [European Union](https://mixpanel.com/legal/mixpanel-gdpr/) and [California](https://mixpanel.com/cpra-preference-center), can exercise their rights to their data here, or by contacting [compliance@mixpanel.com](mailto:compliance@mixpanel.com).`,
        'gdpr-request': `Request Data`,
        'gdpr-delete': `Delete Data`,
        'gdpr-request-sent': `Your request has been sent. A support engineer will contact you at: {{email}}`,
        'gdpr-delete-confirm-title': `Erase Personal Data`,
        'gdpr-delete-confirm-body': `This will delete all usage data that Mixpanel has collected for product research associated with your account. Your account will remain operational.`,
        'gdpr-reset-confirm-title': `Reset GDPR API Token`,
        'gdpr-reset-confirm-body': `Are you sure you want to reset the API token? WARNING: changing this token will invalidate any application using it. ARE YOU SURE?`,
        'gdpr-api': `GDPR API`,
        'gdpr-api-title': `OAuth Token for GDPR APIs`,
        'gdpr-reset-token-error': `Error trying to reset GDPR API token.`,
      },
      projects: {
        'api-reset-success': `API Key and Secret reset successfully`,
        'all-projects': `All Projects`,
        'your-projects': `Your Projects`,
        'not-part-of-any': `You aren't part of any projects`,
        'havent-created-any': `You haven't created any projects`,
        'api-no-perms': `You do not have permission to view project access keys`,
        'access-url': `Access URL`,
        org: `Organization`,
        token: `Token`,
        'api-key': `API Key`,
        'api-secret': `API Secret`,
        'reset-description': `Reset the API Key and API Secret for this project`,
        'reset-confirm-title': `Reset API Credentials`,
        'reset-confirm-body': `Are you sure you want to reset the API Key and API Secret for this project? This may affect projects that are currently in production.`,
        'api-reset-error': `Error trying to reset API credentials`,
      },
      alerts: {
        'alert-history': `Alert History`,
        'alert-history-permission-denied': `You do not have permission to view this alert's history.`,
        'delete-alert': `Delete Alert`,
        'edit-alert': `Edit Alert`,
        'learn-more': `Learn more`,
        manage: `Manage Alerts`,
        'moved-to-project-alerts': `Alert settings has moved to project alert settings. [See Project Alerts]({{link}}){:target="_parent"}`,
        usage: `{{count}}/{{max}} alerts used. {{percentage}} capacity.`,
        'reached-title': `You've reached the anomaly detection alert limit.`,
        'cant-create': `Alert cannot be created with anomaly detection criteria`,
        'reached-limit': `Your organization has reached the limit for active anomaly detection alerts.`,
        'approaching-limit': `Your organization is approaching the limit for active anomaly detection alerts.`,
        'delete-hint': `Delete old anomaly detection alerts to make space for new ones.`,
        'no-access': `You don't have access to Alerts.`,
        'contact-admin-or-upgrade': `Contact your Admin or upgrade your plan.`,
        'no-alerts': `You do not have any alerts.`,
        'manage-webhooks': `Manage Webhooks`,
        name: `Name`,
        creator: `Creator`,
        project: `Project`,
        'criteria-type': `Criteria Type`,
        report: `Report`,
        status: `Status`,
        'last-triggered': `Last Triggered`,
        'last-modified': `Last Modified`,
        triggers: `Triggers`,
        'sub-status': `Subscription Status`,
        'rca-long': `Root Cause Analysis`,
        rca: `RCA`,
        'all-subs': `All Subscriptions`,
        'all-projects': `All Projects`,
        'all-reports': `All Reports`,
        'all-creators': `All Creators`,
        subscribed: `Subscribed`,
        unsubscribed: `Unsubscribed`,
        'all-criteria': `All Criteria Types`,
        'custom-threshold': `Custom Threshold`,
        'anomaly-detection': `Anomaly Detection`,
        search: `Search alert settings`,
        'x-alerts_one': `{{count}} alert`,
        'x-alerts_other': `{{count}} alerts`,
        'desync-warning': `This alert is out of sync with the originating report and may not perform as expected. To ensure alert success, delete this alert and create a new one.`,
        updated: `Custom alert updated.`,
        deleted: `Custom alert deleted.`,
        deleted_one: `Deleted {{count}} custom alert.`,
        deleted_other: `Deleted {{count}} custom alerts.`,
        'delete-failed': `Failed to delete custom alert.`,
        'delete-failed_one': `Failed to delete {{count}} custom alert`,
        'delete-failed_other': `Failed to delete {{count}} custom alerts`,
        'update-failed': `Failed to update custom alert`,
        'create-success': `Custom alert created.`,
        'create-failed': `Failed to create custom alert.`,
        'create-failed-reason': `Failed to create custom alert, {{reason}}.`,
        'create-failed-unauthorized': `Failed to create custom alert, Unauthorized.`,
        'delete-confirm-title': `Delete Custom Alert?`,
        'bulk-delete-confirm-body': `Are you sure you want to delete these {{count}} custom alerts? This will affect all users subscribed to these alerts.`,
        'single-delete-confirm-body': `Are you sure you want to delete {{name}}? This will affect all users subscribed to this alert.`,
        'delete-no-permission_one': `You do not have permission to delete this alert.`,
        'delete-no-permission_other': `You do not have permission to delete some of the selected alerts.`,
        history: {
          screenshot: `Screenshot`,
          'alert-name': `Alert name`,
          'data-view': `Data View`,
          metric: `Metric`,
          breakdown: `Breakdown`,
          'current-value': `Current Value`,
          condition: `Condition`,
          'notify-at-most': `Notify at most`,
          'email-notification-sent-to': `Email notification sent to`,
          'slack-notification-sent-to': `Slack notification sent to`,
          'slack-notification-channel-sent-to': `Slack notification channel sent to`,
          'project-webhook': `Project Webhook`,
          'webhook-notification-sent-to': `Webhook notification sent to`,
          validity: `Validity`,
          valid: `Valid`,
          invalid: `Invalid`,
          'empty-state': `Triggers and modifications to this alert will appear here`,
          loading: `Loading...`,
          'load-more': `Load More`,
        },
        created: `Created`,
        frequency: `Frequency`,
        'frequency-hourly': `Hourly`,
        'frequency-daily': `Daily`,
        'frequency-weekly': `Weekly`,
        'frequency-monthly': `Monthly`,
        'frequency-unknown': `Unknown`,
      },
      rollup: {
        new: `New Rollup Project`,
        add: `Add Rollup Project`,
        edit: `Edit Rollup Project`,
        'no-rollups': `You don't have any Rollup projects. Click '$t(settings.user-settings.rollup.new)' to create one.`,
        owner: `Owner`,
        projects: `Projects`,
        description: `Rollup projects provide the ability to combine multiple projects into a single view and query them together in insights.`,
        name: `Rollup Project Name`,
        org: `Organization`,
        'org-description': `Your Rollup Project will be created under "{{organizationName}}"`,
        'choose-projects': `Choose Projects`,
        'search-projects': `Search Projects`,
        'delete-confirm-title': `Delete Rollup Project?`,
        'delete-confirm-body': `Are you sure you want to delete "{{projectName}}"?`,
      },
      sidenav: {
        'your-profile': `Your Profile`,
        organizations: `Organizations`,
        projects: `Projects`,
        'data-and-privacy': `Data & Privacy`,
        passkeys: `Passkeys`,
        'rollup-projects': `Rollup Projects`,
        alerts: `Alerts`,
        'session-replay': `Session Replay`,
      },
    },
    passkeys: {
      name: `Name`,
      created: `Created`,
      'last-used': `Last Used`,
      'never-used': `Never`,
      'unnamed-passkey': `Unnamed Passkey`,
      'your-passkeys': `Your Passkeys`,
      'delete-confirm-title': `Delete Passkey?`,
      'delete-confirm-description': `Are you sure you want to delete "{{name}}"? You will no longer be able to use this passkey to sign in.`,
      'delete-success': `Passkey deleted successfully`,
      'delete-error': `Failed to delete passkey`,
      'load-error': `Failed to load passkeys`,
      'no-passkeys': `You have no passkeys yet. Create one to enable passwordless sign-in.`,
      register: `Register Passkey`,
      'register-success': `Passkey registered successfully`,
      'register-error': `Failed to register passkey`,
      registering: `Registering passkey`,
      'rename-title': `Rename Passkey`,
      'rename-success': `Passkey renamed successfully`,
      'rename-error': `Failed to rename passkey`,
      'enable-passkeys': `Enable Passkeys`,
      'enable-passkeys-description': `You can sign in using a passkey as a second factor`,
      'enable-passkeys-description-no-passkeys': `You can sign in using a passkey as a second factor (register a passkey to enable)`,
      'skip-password': `Skip password`,
      'skip-password-description': `You can sign in using a passkey without needing to enter an email or password`,
      'skip-password-description-no-password': `You can sign in using a passkey without needing to enter an email or password (always on because you don't have a password)`,
      'settings-update-success': `Passkey settings updated`,
      'settings-update-error': `Failed to update passkey settings`,
      'recovery-codes': `Recovery codes`,
      'recovery-codes-description': `Use a recovery code to sign in if you ever lose access to your passkey`,
      'regenerate-recovery-codes': `Regenerate codes`,
      'recovery-codes-title': `Save your recovery codes`,
      'recovery-codes-warning': `These recovery codes will be required if you are unable to access your passkey. This is the only time you are able to save them.`,
      'recovery-codes-download': `Download`,
      'recovery-codes-acknowledge': `I acknowledge I have saved my recovery codes`,
      'recovery-codes-copied': `Recovery codes copied to clipboard`,
      'recovery-codes-copy-error': `Failed to copy recovery codes`,
    },
    'project-settings': {
      overview: {
        'load-error': `Project details failed to load`,
        'domain-mismatch': `This project exists in the {{regionName}} data center`,
        'domain-mismatch-description': `Some information may be hidden unless viewing from {{domain}}`,
        'domain-mismatch-cta': `Switch View`,
        'usage-stats': `Usage Statistics`,
        'mtu-count': `MTU Count`,
        'event-count': `Event Count`,
        'user-count': `User Profile Count`,
        'access-keys': `Access Keys`,
        'project-token': `Project Token`,
        'project-token-tooltip': `Use your Project Token to track data to your Mixpanel project via our SDKs or Ingestion API`,
        'api-secret': `API Secret`,
        'api-secret-tooltip': `API Secret is a deprecated form of authentication and will be removed in the future. Service Account authentication is preferred.`,
        'group-keys': `Group Keys`,
        'group-keys-help-text': `Group Keys enable data analysis at the group level. Groups are fully customizable by you. [$t(common.learn-more)](https://help.mixpanel.com/hc/en-us/articles/360025333632)`,
        'group-key': `Key`,
        'group-id': `Data Group ID`,
        'group-name': `Display Name`,
        'add-group': `Add Group Key`,
        'group-max': `Maximum of {{count, number}} group keys. Delete one to add a new group key.`,
        'group-name-updated': `Group key name updated successfully`,
        'group-name-update-error': `Failed to update Group key display name`,
        'group-metadata-updated': `Group key metadata updated successfully`,
        'group-metadata-update-error': `Failed to update Group key metadata`,
        'group-deleted': `Group key deleted successfully`,
        'group-delete-error': `Failed to delete group key`,
        'group-created': `Group created successfully: it may take up to 3 minutes to take effect`,
        'group-create-error': `Failed to create a new group`,
        'sharing-settings': `Sharing Settings`,
        'sharing-settings-tooltip': `Change the default visibility when creating new boards and reports.`,
        'default-share-mode': `Default Share Mode`,
        'homepage-dashboard': `Homepage Board`,
        'homepage-dashboard-tooltip': `Set a Board as the Homepage for the Project. All users will have access to this Board.`,
        'homepage-dashboard-visibility': `This Board is the Project's Homepage. Project Admins can remove as Homepage Board to change.`,
        'color-theme-settings': `Color Theme Settings`,
        'default-theme': `Default Theme`,
        'b2b-company-settings': {
          label: `B2B Company settings`,
          'name-key': `Name Key`,
          'revenue-key': `Revenue Key`,
          'not-set': `Not Set`,
          'not-applicable': `Not Applicable`,
          'not-configured': `Not Configured`,
        },
        'select-dashboard': {
          dashboard: `Dashboard`,
          'suggested-dashboard': `Suggested Boards`,
          'select-dashboard': `Select Homepage Board`,
          'select-dashboard-no-results': `No Results. Try different keywords`,
          'select-dashboard-instructions': `Choose a Board that all users in this project will see as their homepage. This will replace the default Mixpanel Home.`,
        },
        details: {
          title: `Project Details`,
          name: `Name`,
          'name-placeholder': `New project name`,
          org: `Organization`,
          'org-placeholder': `No organization`,
          id: `Project ID`,
          'id-placeholder': `No project ID`,
          residency: `Data Residency`,
          'residency-placeholder': `Cannot determine residency`,
          timezone: `Timezone`,
          'created-by': `Created By`,
          'date-created': `Date Created`,
          'access-url': `Access URL`,
          unspecified: `Unspecified`,
        },
        'project-transferred': `Project transferred successfully`,
        'view-settings': `View Project Settings`,
        transfer: `Transfer`,
        'transfer-tooltip': `You must have permission to create projects in another organization to transfer this project.`,
        session: {
          title: `Session Settings`,
          'def-type': `Definition Type`,
          timeout: `Session Timeout`,
          'timeout-units': `Session Timeout Units`,
          'start-event': `Session Start Event`,
          'select-start-event': `Select Start Event`,
          'end-event': `Session End Event`,
          'select-end-event': `Select End Event`,
          'id-prop': `Session ID Property Name`,
          exclusions: `Excluded Events`,
          props: `Session Properties`,
          'custom-props': `Session Custom Properties`,
          'custom-props-info': `Session-scoped custom properties must be created here. To reuse an existing custom property, reference it inside your new definition.`,
          'new-custom-prop': `New Custom Property`,
        },
        'time-period': {
          title: `Time Period Settings`,
          'start-week': `Start of Week`,
          'start-week-placeholder': `New start of week`,
          'start-quarter': `Start of Quarter`,
          'start-quarter-placeholder': `New start of quarter`,
          'start-weekend': `Start of Weekend`,
          'start-weekend-placeholder': `New start of weekend`,
          'duration-weekend': `Duration of Weekend`,
          'duration-weekend-placeholder': `New duration of weekend`,
        },
        'go-to-experimentation-banner': `Experiment settings have moved to the [experimentation]({{link}}) settings tab.`,
      },
      users: {
        title: `Users`,
        'load-error': `Project users failed to load`,
        'all-users': `All Users`,
        'all-users-count': `All Users ({{count}})`,
        'all-users-banner-text': `All users in {{orgName}} have the {{role}} role for this project.`,
        'all-users-tooltip-row': `All users in {{orgName}}`,
        'all-users-tooltip-row-no-org': `All users in this organization`,
        'team-tooltip-row': `{{teamName}} (team)`,
        'team-tooltip-row-unnamed': `Team grant`,
        'accept-access-request': `Accept Access Request`,
        'user-requested-access': `{{userName}} requested access to this project`,
        'assign-permissions': `Assign Permissions`,
        'not-in-org-note': `{{email}} is not in your organization. Invite them to add to this project.`,
        'edit-all-users-description': `Edit permissions for all users in the organization`,
        'set-role-for-all': `Set Role for All Users`,
        'remove-base-permissions': `Remove Base Permissions`,
        'remove-base-permissions-desc': `This will remove base permissions for all users in the organization from this project.`,
        'access-requests': `Access Requests`,
        updated: `Users updated successfully`,
        deleted: `Users removed successfully`,
        'update-users-error': `Error trying to update users: {{error}}`,
        'remove-users-error': `Error trying to remove users: {{error}}`,
        'add-users-error': `Error trying to add users to project: {{error}}`,
        'invite-error': `Error sending invitation`,
        'permission-change-error': `Error processing the permission change. Try again and please contact support if issue persists.`,
        'no-users-added': `No users added`,
        'access-request-not-found': `This request has already been approved or has expired`,
        'load-next-page-error': `Error trying to load next page of project members`,
        'access-request-accepted': `Access request accepted`,
        'access-request-expired': `This access request no longer exists`,
        'granted-by': `Granted by`,
        'classified-data': `Classified Data`,
        role: `Role`,
        expires: `Expires`,
        grants: {
          all: `All Grant Types`,
          'all-alt': `All grant types`,
          team: `Team Grant`,
          'team-alt': `Team grant`,
          teamFrom: `Team grant from `,
          'team-tooltip': `This user has access via a team. Organization admins can edit this in the org's team settings.`,
          individual: `Individual Grant`,
          'individual-alt': `Individual grant`,
          org: `Organization Role`,
          'org-alt': `Organization grant`,
          orgFrom: `Organization grant from `,
          'org-tooltip': `This user has access via their organization role. Organization admins can edit this in this user's detail in org settings.`,
          'all-users': `All Users Grant`,
          'all-users-alt': `All users grant`,
          projectGrantsTitle: `Project Grants`,
          projectGrantsDescription: `{{displayName}} has access to this project from {{grantCount}} grant(s)`,
          manage: `Manage`,
        },
        'can-view': `Can View`,
        search: `Search project users`,
        'x-users_one': `{{count}} user`,
        'x-users_other': `{{count}} users`,
        'load-more': `Load More Users`,
        loading: `Loading...`,
        'showing-x-of-y': `Showing {{current}} of {{total}} users`,
        'all-roles': `All Roles`,
        'role-all-users': `All Users in the Organization`,
        'role-all-users-description': `Gives the chosen role to all current and future users and service accounts of your organization`,
        'classification-all': `All Data Permissions`,
        'classification-can-view': `Can View Classified Data`,
        'classification-cant-view': `Cannot View Classified Data`,
        'resend-invite': `Resend invite`,
        'delete-invite': `Delete invite`,
        'send-invite': `Send invite`,
        'delete-request': `Delete request`,
        'user-search-placeholder': `Select users or enter an email to invite`,
        'new-invite-label': `Invite to organization via email`,
        'copy-to-another-project-label': `Copy role to another project`,
        invite: `Invite`,
        'last-active': `Last Active`,
        'cant-modify-org-grant-role': `You can't modify roles assigned through the organization grant.`,
        'no-permission-edit-user-role': `You do not have permission to edit users with this project role.`,
        'empty-search': `No users found matching "{{searchText}}"`,
        'empty-role-filter': `No users with {{role}} role`,
        'empty-grant-filter': `No users with {{grantType}} grant type`,
        'empty-sensitive-with-access': `No users with classified data access`,
        'empty-sensitive-without-access': `No users without classified data access`,
        'custom-roles-tab-header': `Project Roles`,
        'default-mixpanel-project-role-label': `Default Mixpanel Project Role`,
        'custom-role-label': `Custom Role`,
        'created-by': `Created by`,
        'users-with-role': `Users with this role`,
        'new-role-button-text': `New Role`,
        'create-custom-role-title': `Create Custom Role`,
        'edit-custom-role-title': `Edit Custom Role`,
        'role-name-label': `Role Name`,
        'role-name-placeholder': `Role Name`,
        'role-name-required': `Role name is required`,
        'role-description-label': `Description`,
        'role-description-placeholder': `Add a description for the role...`,
        'create-from-existing-role-label': `Create from Existing Role`,
        'select-role-placeholder': `Select Role`,
        'select-role': `Select role`,
        'permissions-title': `Permissions`,
        permissions: {
          'save-analysis': {
            title: `Save Analysis Reports and Boards`,
            description: `Allows members to save analysis and custom data definitions.`,
          },
          experimentation: {
            title: `Experimentation`,
            description: `Allows members to create and manage Mixpanel Experiments.`,
          },
          'feature-flags': {
            title: `Feature Flags`,
            description: `Allows members to create and manage Feature Flags.`,
          },
          'feature-flag-creation': {
            title: `Feature Flag Creation`,
            description: `Allows members to create and manage Feature Flags.`,
          },
          'full-feature-flag-access': {
            title: `Full Feature Flag Access`,
            description: `Allows members to create and manage all Feature Flags within the project.`,
          },
          'metadata-editing': {
            title: `Metadata Editing Only`,
            description: `Allows members to edit event and property details only such as description and image.`,
          },
          'full-data-management': {
            title: `Full Data Management Access`,
            description: `Allows members to edit events and property details in Lexicon, hide events, mark as sensitive, and manage other data settings.`,
          },
          'access-sensitive-data': {
            title: `Access to Sensitive Data`,
            description: `Allows access to properties that have been marked sensitive.`,
          },
          'exporting-data': {
            title: `Exporting Data`,
            description: `Allows access to download dashboards, reports, and data from Lexicon.`,
          },
          'cohorts-sync-only': {
            title: `Cohort Syncs Only`,
            description: `Access to create and manage Cohort syncs from Mixpanel to other destinations.`,
          },
          'full-data-sources': {
            title: `Full Data Sources & Destinations Access`,
            description: `Access to send data into and out of Mixpanel. Including creating and managing Data Warehouses, Pipelines, and Cohort syncs.`,
          },
          'data-management': {
            title: `Data Management`,
            description: `Control access to data management features.`,
          },
          'data-sources': {
            title: `Data Sources & Destinations`,
            description: `Access to send data into and out of Mixpanel.`,
          },
        },
        'permissions-v2': {
          'select-all': `Select all`,
          'deselect-all': `Deselect all`,
          'disabled-permission-tooltip': `Your current role does not have this permission`,
          'requires-parent-permission-tooltip': `Requires "Edit metadata" to be enabled`,
          analysis: {
            title: `Analysis`,
            'save-reports-and-boards': {
              title: `Save Reports and Boards`,
              description: `Allows members to save and edit reports and boards`,
            },
            'create-custom-data-definitions': {
              title: `Create Custom Data Definitions`,
              description: `Allows members to create, edit, and manage custom data definitions such as Cohorts, Metrics, Custom Events and Properties.`,
            },
            'manage-all-analysis-and-custom-definitions': {
              title: `Manage All Analysis and Custom Data Definitions`,
              description: `Allows members to edit and manage any analysis or custom data definition in the project`,
            },
            'pin-dashboards': {
              title: `Pin Dashboards`,
              description: `Allows members to pin dashboards for the project.`,
            },
          },
          'feature-flags-and-experimentation': {
            title: `Feature Flags and Experimentation`,
            'experimentation-creation': {
              title: `Experimentation Creation`,
              description: `Allows members to create and manage Mixpanel Experiments.`,
            },
            'feature-flag-creation': {
              title: `Feature Flag Creation`,
              description: `Allows members to create and manage Feature Flags.`,
            },
            'full-feature-flag-access': {
              title: `Full Feature Flag Access`,
              description: `Allows members to create and manage all Feature Flags within the project.`,
            },
          },
          'data-management': {
            title: `Data Management`,
            'edit-metadata': {
              title: `Edit Metadata`,
              description: `Allows members to edit event and property details such as description, images, and tags, and owners.`,
            },
            'set-event-and-property-visibility': {
              title: `Set Event and Property Visibility`,
              description: `Allows members to hide or unhide events and properties in Lexicon.`,
            },
            'data-verification': {
              title: `Data Verification`,
              description: `Allows members to control data verification in Lexicon.`,
            },
            'manage-schemas': {
              title: `Manage Schemas`,
              description: `Allows members to upload and edit schemas in Lexicon.`,
            },
            'manage-lookup-tables': {
              title: `Manage Lookup Tables`,
              description: `Allows members to create and edit lookup tables`,
            },
            'mark-sensitive-data': {
              title: `Mark Sensitive Data`,
              description: `Allows members to control which properties are classified. Does not grant access to view. `,
            },
            'edit-profiles': {
              title: `Edit Profiles`,
              description: `Allows members to edit user or group profiles.`,
            },
            'data-deletion-dropping-and-merging': {
              title: `Data Deletion, Dropping, and Merging`,
              description: `Allows members to control advanced data deletion, merge and drop events. (Recommended for Project Owners/Admins only)`,
            },
            'data-settings-access': {
              title: `Data Settings Access`,
              description: `Allows members to edit Data Settings such as Data Volume Monitoring, Data Standards, and Event Approval.`,
            },
            'edit-project-autotrack': {
              title: `Edit Autotrack Settings`,
              description: `Allows editing the autotrack settings in project settings.`,
            },
          },
          'data-access-exports-and-alerts': {
            title: `Data Access, Exports, and Alerts`,
            'public-boards': {
              title: `Public Boards`,
              description: `If enabled in project settings, allows members to create public boards to share outside of their Mixpanel project.`,
            },
            'sensitive-data-access': {
              title: `Sensitive Data Access`,
              description: `Allows access to properties that have been marked sensitive.`,
            },
            'exporting-lexicon': {
              title: `Export Lexicon`,
              description: `Allows exporting data definitions from Lexicon`,
            },
            'exporting-reports-csv': {
              title: `Export Reports as CSV`,
              description: `Allows downloading report data (Insights, Funnels, etc.) as CSV`,
            },
            'exporting-users-csv': {
              title: `Export Users as CSV`,
              description: `Allows downloading user data from the Users page as CSV`,
            },
            'exporting-events-csv': {
              title: `Export Events as CSV`,
              description: `Allows downloading event data from the Events page as CSV`,
            },
            'create-alerts': {
              title: `Create Alerts`,
              description: `Allows members to create and manage their own alerts on reports and boards.`,
            },
            'create-board-subscriptions': {
              title: `Create Board Subscriptions`,
              description: `Allows members to create and manage board subscriptions.`,
            },
            'manage-all-alerts': {
              title: `Manage All Alerts`,
              description: `Allows members to create, edit, and delete any alert in the project, including alerts created by other members.`,
            },
          },
          'data-sources-and-definitions': {
            title: `Data Sources and Definitions`,
            'create-and-manage-destinations': {
              title: `Create and Manage Destinations`,
              description: `Access to create and manage destination tools such as Cohort syncs and Data Pipelines to send data from Mixpanel to other destinations.`,
            },
            'create-and-manage-data-warehouses': {
              title: `Create and Manage Data Warehouses`,
              description: `Access to create and manage Data Warehouse Connector Syncs into Mixpanel.`,
            },
          },
          settings: {
            title: `Settings and Notifications`,
            'view-and-edit-project-settings': {
              title: `View and Edit Project Settings`,
              description: `Allows members to edit project settings, create data views, view audit logs, create service accounts, and manage project members`,
            },
          },
        },
        'copy-to-another-project': `Copy to Another Project`,
        'clone-to-another-project': `Copy to Another Project`,
        'delete-role-title': `Delete Custom Role`,
        'delete-role-description-has-assignees': `Are you sure you want to delete this custom role?\n\n{{count}} users are currently assigned to this role. They will be reassigned to the Consumer role after deletion.`,
        'delete-role-description-no-assignees': `Are you sure you want to delete this custom role?\n\nNo users are currently assigned to this role.`,
        'delete-role-error': `There was an error deleting the role`,
        'role-cloned': `Role cloned successfully`,
        'clone-role-error': `There was an error cloning the role`,
        'fetch-role-error': `Failed to fetch role details`,
        'fetch-permissions-error': `Failed to fetch permissions switches`,
        'create-role-success': `Custom role created successfully`,
        'create-role-error': `There was an error creating the role`,
        'edit-role-success': `Custom role updated successfully`,
        'edit-role-error': `There was an error updating the role`,
        'no-permissions-selected-error': `You need to select at least one permission to create a role.`,
        'custom-role-limit-error': `You have reached the maximum number of custom roles for this project.`,
        'generic-role-error': `Something went wrong. If this keeps happening, please contact support.`,
        owner: `Project Owner`,
        'owner-description': `Full access to everything.`,
        admin: `Project Admin`,
        'admin-description': `Administrative controls, but no project deletion privileges.`,
        analyst: `Analyst`,
        'analyst-description': `View and save reports.`,
        consumer: `Consumer`,
        'consumer-description': `View and create reports.`,
        'role-override-modal': {
          title: `This user has a {{grantSourceType}} role`,
          'body-has-role': `{{displayName}} has a {{grantSourceType}} role granted from `,
          'body-cannot-be-edited': ` which can't be edited here.`,
          'body-layer-role': `You can layer on {{newRole}} as an individual role for this project member. When two project roles exist, the permissions from each role are combined.`,
          'body-change-for-everyone': `To change this for everyone in `,
          'body-go-to': `, go to `,
          'org-settings': `Org Settings`,
          cancel: `Cancel`,
          'add-individual-role': `Add Individual Role`,
          'toast-updated': `Project role updated.`,
          'toast-view-details': `View user details`,
          'a-team': `a team`,
          'grant-source-team': `team`,
          'grant-source-organization': `organization`,
        },
      },
      'service-accounts': {
        'load-error': `Service accounts failed to load`,
        'add-no-perms': `Only project owners and admins can create service accounts.`,
        'granted-by': `Granted by`,
        'classified-data': `Classified data`,
        role: `Role`,
        'permission-updated': `Permission updated`,
        'no-service-accounts': `This project does not currently have any service accounts`,
        'never-used': `never used`,
        'no-permission-to-edit': `You do not have permission to edit this service account`,
        'permission-change-error': `Failed to change service account permissions`,
        'update-error': `Failed to update service account: {{error}}`,
        'fetch-error': `Failed to fetch service accounts: {{error}}`,
      },
      'moving-boards': {
        'dont-allow': `Do Not Allow`,
        'allow-within-org': `Allow Within Organization`,
        'allow-across-orgs': `Allow Across Organizations`,
        updated: `Successfully set Moving Boards setting to {{setting}}`,
        'allow-moving': `Allow moving boards to other projects`,
        'allow-moving-description': `This allows users with edit access to move boards between projects. You may choose to allow moving to other organizations or disable this functionality entirely.`,
        'enabled-history': `Enabled by {{user}} on {{date}}`,
        'disabled-history': `Disabled by {{user}} on {{date}}`,
        'update-error': `Unable to set Moving Boards setting`,
      },
      'public-boards': {
        enabled: `Public Boards enabled`,
        disabled: `Public Board disabled`,
        description: `Public Boards allow you to share your analysis with people outside of your Mixpanel organization. \n A Project Owner must enable the use of Public Boards and can disable Public Boards at any point in time.`,
        'learn-more': `Learn more about Public Boards in the Help Center`,
        name: `Board Name`,
        'data-view': `Data View`,
        'url-details': `Public URL Details`,
        url: `Public URL`,
        'url-activated': `Public URL activated`,
        'url-deactivated': `Public URL deactivated`,
        'active-boards': `Active Public Boards`,
        'no-boards': `Your project currently has no Public Boards`,
        'no-perms': `Only Project Owners can change this setting`,
        enable: `Enable for Project`,
        disable: `Disable for Project`,
        'enable-confirm-title': `Enable Public Boards`,
        'enable-confirm-body': `By enabling Public Boards you agree that Mixpanel shall have no liability for the disclosure of confidential sensitive or other information published via Public Boards. \nFor additional security, you have the option of creating a password that will be required prior to viewing your Public Board.  Without password protection your Public Board will be visible to anyone who receives a URL, including the general public. \nPublic Boards can be disabled at any point in time.`,
        'disable-confirm-title': `Turn off Public Boards`,
        'disable-confirm-body': `This will remove the ability to create public boards and deactivate all current public links for this Project.`,
        'deactivate-confirm-title': `Deactivate this Public URL?`,
        'deactivate-confirm-body': `People will no longer be able to view the public facing URL associated with this URL.`,
        'status-change-error': `An issue was encountered changing status`,
        'activate-url-error': `An issue was encountered activating URL.`,
        'deactivate-url-error': `An issue was encountered deactivating URL.`,
      },
      'data-views': {
        'cannot-delete-global': `Global or default data views cannot be deleted`,
        'delete-tooltip': `Delete selected data views`,
        'set-default-error-multiple': `Only one data view can be set as default`,
        'set-default-error-already': `Data view is already default`,
        'set-default': `Set Default`,
        'set-default-tooltip': `New project members are automatically added to the default data view`,
        name: `Name`,
        description: `Description`,
        'x-data-views_one': `{{count, number}} data view`,
        'x-data-views_other': `{{count, number}} data views`,
        search: `Search Data Views`,
        'no-data-views': `There are no data views for this project`,
        'create-new': `Create New Data View`,
        global: `Global Data View`,
        default: `Default Data View`,
        'delete-no-perms': `Failed to delete workspaces. You do not have permissions to delete the selected workspaces.`,
        'delete-confirm-title': `Delete Data Views`,
        'delete-confirm-body_one': `Are you sure you want to delete {{count, number}} workspace?\n\n Any Boards, Reports, Cohorts, Custom Properties, or Custom Events created with this workspace will still be available.`,
        'delete-confirm-body_other': `Are you sure you want to delete {{count, number}} workspaces?\n\n Any Boards, Reports, Cohorts, Custom Properties, or Custom Events created with these workspaces will still be available.`,
        'create-success': `Data view created successfully.`,
        'create-error': `Something went wrong while creating the data view. Please try again later.`,
        'delete-success': `Data views deleted successfully.`,
        'delete-error': `Something went wrong while deleting the data views. Please try again later.`,
        'create-data-view-error': `Failed to create data view`,
        'delete-data-view-error': `Failed to delete data view`,
        'set-default-error': `Failed to set default data view`,
        'update-filters-error': `Failed to update filters`,
        'add-service-accounts-error_one': `Failed to add service account`,
        'add-service-accounts-error_other': `Failed to add service accounts`,
        'add-users-error_one': `Failed to add user`,
        'add-users-error_other': `Failed to add users`,
        'remove-service-accounts-error_one': `Failed to delete service account`,
        'remove-service-accounts-error_other': `Failed to delete service accounts`,
        'remove-users-error_one': `Failed to delete user`,
        'remove-users-error_other': `Failed to delete users`,
        'remove-teams-error_one': `Failed to delete team`,
        'remove-teams-error_other': `Failed to delete teams`,
        'update-error': `Data View Update Failed`,
        'get-error': `Unable to get data view details`,
      },
      autotrack: {
        description: `We collect a set of events that are valuable across all mobile applications such as app opens, app crashes, and session length. A full list of events can be found [here](https://help.mixpanel.com/hc/en-us/articles/115004596186).`,
        'deprecation-notice-pre': `DEPRECATED: This setting has been deprecated from the following SDK versions:`,
        'deprecation-notice-post': `In the versions noted above, this project setting has no effect, instead the value is specified in code when initializing the SDK.`,
        'no-perms': `An owner of {{organizationName}} organization must enable the collection of common mobile events.`,
        'disable-pretrack-confirm-title': `Disable pre-track`,
        'disable-pretrack-confirm-body': `Are you sure about turning off pre-track?`,
      },
      experimentation: {
        title: `Experiment Event Format`,
        'title-info': `The defined format of your Experiment exposure events such that Mixpanel can correctly identify them.`,
        description: `Define the format of your experiment exposure events to help Mixpanel correctly detect your data`,
        'event-name': `Exposure Event Name`,
        'event-name-info': `The name used to identify all exposure events sent to Mixpanel`,
        'number-of-experiments': `Experiments in each Exposure Event`,
        'number-of-experiments-info': `Defines whether each exposure event reports one or several experiments`,
        'number-of-experiments-one': `One`,
        'number-of-experiments-multiple': `More than one`,
        format: `Experiment and Variant format`,
        'format-info': `Choose how the experiment and variant information inside the event is formatted`,
        'format-separate': `Separate values`,
        'format-combined': `Combined value`,
        'format-dictionary': `Dictionary (Key-value pairs)`,
        'format-list': `List (Ordered values)`,
        'experiment-property': `Experiment Property Key`,
        'experiment-property-info': `Name of the event property that identifies the experiment`,
        'variant-property': `Variant Property Key`,
        'variant-property-info': `Name of the event property that identifies which variant the user is in`,
        'combined-property': `Combined Property Key`,
        'combined-property-info': `Name of the event property that stores both experiment and variant`,
        'list-property': `List Property Key`,
        'list-property-info': `Name of the event property that identifies the list containing experiments and variants`,
        separator: `String Separator`,
        'separator-info': `The symbol used to separate the experiment name from variant name.<br/><br/>e.g. "Sign-Up:New-Flow" has separator of ":"<br/><br/>Mixpanel assumes the experiment name is first.`,
        example: `Example Event`,
        'example-comment': `// Example event using the above configuration`,
        'example-experiment-key': `[Experiment Key]`,
        'example-variant-key': `[Variant Key]`,
        'example2-experiment-key': `[Another Experiment Key]`,
        'example2-variant-key': `[Another Variant Key]`,
        banner: `{{count}} active experiments are using these settings. Saving changes may hide experiment data, if currently visible.`,
        'event-label': `"event"`,
        'properties-label': `"properties"`,
        updated: `Experiment event settings updated successfully`,
        failed: `Failed to update experiment event settings`,
      },
      sidenav: {
        overview: `Overview`,
        'overview-tooltip': `Project details, access keys, session and time period settings`,
        users: `Project Users`,
        'users-tooltip': `View, manage, and invite users`,
        'data-governance': `Data Governance`,
        'data-governance-tooltip': `Manage data governance settings`,
        'ai-settings': `AI Settings`,
        'ai-settings-tooltip': `Configure how Mixpanel AI tools and MCP interact with this project's data`,
        'data-views': `Data Views`,
        'data-views-tooltip': `Filter your project data and share them with others`,
        experimentation: `Experimentation`,
        'experimentation-tooltip': `Manage experiment event settings`,
        'service-accounts': `Service Accounts`,
        'service-accounts-tooltip': `Manage programmatic access`,
        'public-boards': `Public Boards`,
        'public-boards-tooltip': `Share Boards with others outside your Project`,
        'moving-boards': `Moving Boards`,
        'moving-boards-tooltip': `Move Boards to other projects`,
        'identity-merge': `Identity Merge`,
        'identity-merge-tooltip': `Connect pre-authentication activity across platforms and devices`,
        'mixpanel-usage': `Mixpanel Usage`,
        'mixpanel-usage-tooltip': `See how your project is used`,
        'linked-organizations': `Linked Organizations`,
        'warehouse-data': `Warehouse Data`,
        'warehouse-data-tooltip': `Configure data coming from warehouse sources.`,
        'warehouse-sources': `Warehouse Sources`,
        'warehouse-sources-tooltip': `Configure data warehouses as data sources.`,
      },
      create: {
        title: `Create Project`,
        name: `Project Name`,
        'name-placeholder': `e.g. Production Analytics`,
        'name-help': `You may change your project name in settings at any time.`,
        'us-option': `United States`,
        'eu-option': `European Union`,
        'in-option': `India`,
        'eu-info': `If you are a Mixpanel customer in the EU, you have the option to send your data to Mixpanel's EU data center. You do not lose any product capability if you choose to store your data in the EU.`,
        'store-data': `Select Where To Store Your Data`,
        timezone: `Select Your Timezone`,
        organization: `Select Organization`,
        create: `Create`,
        cancel: `Cancel`,
        'enable-b2b': `Enable B2B`,
      },
      'add-service-account': {
        'create-new': `Create new service account`,
        'account-created': `Service account created successfully`,
        'account-added': `Service account added successfully`,
        'select-placeholder': `Select service account or enter name to create`,
        'account-create-failed': `There was an error creating the service account.`,
        'service-account': `Service Account`,
        'project-role': `Project Role`,
        'select-project-role': `Select Project Role`,
        expires: `Expires`,
        'save-secret-msg': `Make sure you save the service account's secret. You will never be able to access it again.`,
        'no-default-csv-access': `Starting June 28, new service accounts with Consumer roles no longer have CSV download access for the Export and Engage APIs; Analyst or higher is required. Read more [here](https://docs.mixpanel.com/docs/orgs-and-projects/roles-and-permissions#project-roles).`,
        username: `Username`,
        secret: `Secret`,
        'add-service-account': `Add Service Account`,
      },
      'warehouse-data': {
        banner: {
          monitoring: {
            header: `One or more syncs were automatically paused.`,
            body: `Your admin opted in to sync pauses when overages or anomalies are detected. Check with your Project Admin or Owner to unpause the syncs.`,
          },
        },
        monitoring: {
          'sync-paused': {
            title: `Sync Automatically Paused`,
            description: `This project has warehouse monitoring enabled, and automatically pauses syncs when issues are detected. Contact your Mixpanel admin to unpause.`,
          },
          'anomaly-detected': `Anomaly Detected`,
          'days-to-unpause': `{{days}} days to unpause`,
          'confirm-unpause': {
            title: `Unpause Sync`,
            description: `This sync was automatically paused by warehouse monitoring due to high volume. Unpausing will disable monitoring for the next run. Are you sure you want to continue?`,
          },
        },
        title: `Warehouse Data`,
        create: `Create`,
        'test-import': `Test Import`,
        inactive: `Inactive`,
        'deleted-sync': `Deleted Sync`,
        'deleted-data': `Deleted Data`,
        paused: `Paused`,
        active: `Active`,
        last: `Last:`,
        next: `Next:`,
      },
    },
    'warehouse-sources': {
      setup: `Setup`,
      'source-name': `Source Name`,
      'add-source-name-placeholder': `Add Source Name...`,
      'postgres-custom-name': `A custom name to identify this Postgres connection in Mixpanel.`,
      'postgres-hostname': `This is the hostname of your Postgres database`,
      'add-hostname-placeholder': `Add hostname or IP address`,
      'postgres-port': `This is the port your database server is listening to`,
      'add-port-placeholder': `Add port`,
      database: `Database`,
      'postgres-database': `This is the name of the database you want to connect to`,
      'add-database-placeholder': `Add database`,
      'user-name': `User Name`,
      'add-user-name-placeholder': `Add user name`,
      password: `Password`,
      'enter-password-placeholder': `Enter password`,
      'postgres-ssl-mode': `Postgres SSL Mode`,
      'postgres-ssl-mode-help': `To keep your data secure, Mixpanel requires encryption for Postgres sources`,
      'server-ca-certificate': `Server CA Certificate`,
      'server-ca-certificate-help': `Upload a certificate to authenticate your server`,
      'ssh-tunnel': `SSH Tunnel`,
      disabled: `Disabled`,
      enabled: `Enabled`,
      'bastion-hostname': `Bastion Hostname`,
      'bastion-hostname-help': `The hostname or IP address of your SSH bastion host`,
      'bastion-hostname-placeholder': `bastion.example.com`,
      'bastion-port': `Bastion Port`,
      'bastion-port-help': `The SSH port on your bastion host`,
      'bastion-port-placeholder': `22`,
      'ssh-username': `SSH Username`,
      'ssh-username-help': `Username for SSH authentication to the bastion host`,
      'ssh-username-placeholder': `ssh-user`,
      'ssh-private-key': `SSH Private Key`,
      'ssh-private-key-help': `Private key for SSH authentication`,
      'gcp-project': `GCP Project`,
      'add-project-id-placeholder': `Add Project ID...`,
      location: `Location`,
      'dataset-region-auto-detect': `Most dataset regions are identified automatically. Use this field only if BigQuery fails to detect your specific location.`,
      'grant-permissions': `Grant Permissions`,
      'service-account-generated': `Mixpanel automatically generated a service account for your new BigQuery connection.`,
      'grant-access-instructions': `Either run the following commands in your Google Cloud Shell to grant access permissions for this account on this project, or go directly into BigQuery to grant specific access to datasets and tables.`,
      'staging-dataset': `Staging Dataset`,
      'add-staging-dataset-placeholder': `Add the name of a dataset for staging changes...`,
      'bigquery-sql-instructions': `You can grant access by running this SQL command in BigQuery:`,
      'cold-starts-header': `Cold Starts can be Slow`,
      'cold-starts-body': `If your Databricks cluster is not currently running, it may take a while for us to validate that the connection is configured correctly. Retry the connection after a few minutes if timing out.`,
      'databricks-custom-name': `A custom name to identify this Databricks connection in Mixpanel.`,
      'server-hostname': `Server Hostname`,
      'add-server-hostname-placeholder': `Add Server Hostname...`,
      'databricks-hostname-help': `This is the hostname of your Databricks cluster. This can be found in your workspace URL, or by navigating to [JDBC/ODBC connection settings](https://docs.databricks.com/aws/en/integrations/compute-details)`,
      'access-token': `Access Token`,
      'access-token-placeholder': `************************`,
      'databricks-access-token-help': `Personal access token used to authenticate with your Databricks cluster. [Learn how to create an access token](https://docs.databricks.com/en/dev-tools/auth.html#databricks-personal-access-tokens-for-workspace-users)`,
      'personal-access-token': `Personal Access Token`,
      'oauth-client-credentials': `OAuth Client Credentials`,
      'oauth-client-id': `Client ID`,
      'oauth-client-id-placeholder': `Add Client ID...`,
      'databricks-oauth-client-id-help': `The OAuth client ID for your Databricks service principal. [Learn how to create a service principal](https://docs.databricks.com/en/dev-tools/auth/oauth-m2m.html)`,
      'oauth-client-secret': `Client Secret`,
      'oauth-client-secret-placeholder': `************************`,
      'databricks-oauth-client-secret-help': `The OAuth client secret for your Databricks service principal.`,
      'http-path': `HTTP Path`,
      'add-http-path-placeholder': `Add HTTP Path...`,
      'databricks-http-path-help': `The HTTP path of the cluster you would like to connect to. This can be found in your cluster [JDBC/ODBC connection settings](https://docs.databricks.com/aws/en/integrations/compute-details)`,
      'data-import-preview-header': `Info about data import preview`,
      'data-import-preview-body': `Jobs compute uses the Unity Catalog API to display catalogs, schemas, and table names, providing a more cost-efficient solution. However, data previews are not available in this mode.`,
      compute: `Compute`,
      'mirror-mode': `Mirror Mode`,
      'mirror-description': `Mirror is an optional but recommended mode that captures updates and deletions to underlying warehouse data. To enable Mirror, you must grant access to a dataset where Mixpanel can create tables for tracking changes.`,
      'learn-more-mirror': `Learn more about Mirror`,
      'mirror-billing': `Mirror billing.`,
      delete: `Delete`,
      step: `Step`,
      of: `of`,
      back: `Back`,
      'aws-account-id': `AWS Account ID`,
      'add-aws-account-id-placeholder': `Add AWS Account ID`,
      type: `Type`,
      'cluster-id': `Cluster ID`,
      'add-cluster-id-placeholder': `Add cluster identifier`,
      'workgroup-arn': `Workgroup ARN`,
      'add-workgroup-arn-placeholder': `Add workgroup ARN`,
      'region-staging-bucket': `Region & Staging Bucket`,
      'aws-region': `AWS Region`,
      'add-region-placeholder': `Add region`,
      's3-staging-bucket': `S3 Staging Bucket`,
      's3-staging-bucket-help': `Create an S3 staging bucket in your region and provide the name. [Learn how to create an S3 bucket](https://docs.aws.amazon.com/AmazonS3/latest/userguide/create-bucket-overview.html).`,
      's3-bucket-name-placeholder': `S3 bucket name`,
      'using-aws-cli': `Using the AWS CLI:`,
      'database-policy': `Database & Policy`,
      'database-name': `Database Name`,
      'database-name-help': `Add the name of your connected database for authentication.`,
      'add-database-name-placeholder': `Add database`,
      'policy-name': `Policy Name`,
      'policy-name-help': `Define the name of a policy to grant Mixpanel access. Mixpanel doesn't store this, but the permissions below need to be granted to the role you provide.`,
      'create-policy': `Create Policy`,
      role: `Role`,
      'role-name': `Role Name`,
      'role-name-help': `Define the name of a role for Mixpanel to assume.`,
      'create-role': `Create Role`,
      'attach-policy-to-role': `Then attach the policy to the role:`,
      'grant-access': `Grant Access`,
      'grant-access-to-schema': `Grant Access to Schema`,
      'schema-name-placeholder': `Your schema name...`,
      'schema-name-help': `Enter a schema name for suggested SQL commands to grant access.`,
      'create-schema': `Create Schema`,
      'grant-access-to-staging-schema': `Grant Access to Staging Schema`,
      'staging-schema-name-placeholder': `Staging schema name...`,
      'staging-schema-help': `Add the name of your staging schema to enable Mirror mode.`,
      'create-staging-schema': `Create Staging Schema`,
      'redshift-source-created': `Redshift Source Successfully Created`,
      'redshift-grant-access-final': `Finally, go to Redshift and grant access to the schemas and tables you want Mixpanel to access.`,
      'redshift-custom-name': `A custom name to identify this Redshift connection in Mixpanel.`,
      'navigate-iam-create-policy': `Navigate to IAM in AWS to create the following policy. [Learn how to create an IAM policy](https://docs.aws.amazon.com/IAM/latest/UserGuide/access_policies_create-console.html#access_policies_create-start)`,
      'create-role-attach-trust-policy': `Create a role and attach the following trust policy. You will also need to attach the policy created in the previous step.\n[Learn how to create a role](https://docs.aws.amazon.com/IAM/latest/UserGuide/access_policies_create-console.html#access_policies_create-start).`,
      'staging-schema-different-from-database': `Staging schema name must be different from the database name.`,
      'redshift-mirror-mode-intro': `To enable Mirror Mode, here are the steps you need to perform:`,
      'redshift-mirror-step-1': `Update the form below with a staging schema name`,
      'redshift-mirror-step-2': `Create the staging schema in Redshift if not already and grant necessary permissions. You can use the auto-populated command in the form below.`,
      'redshift-mirror-step-3': `Make sure to update your existing policy with the latest policy snippet above. Specifically, "redshift-data:list tables"`,
      'redshift-mirror-step-4': `You can find the policy name attached to the role {{roleName}} in IAM console.`,
      'redshift-mirror-edit-mode-instructions': `To enable Mirror Mode, here are the steps you need to perform:\n  - Update the form below with a staging schema name\n  - Create the staging schema in Redshift if not already and grant necessary permissions. You can use the command in the form below.\n  - Make sure to update your existing policy with the latest policy snippet above. Specifically, "redshift-data:ListTables". You can find the policy name attached to the role "{{roleName}}" in the IAM console.`,
      'snowflake-account-name': `Snowflake Account Name`,
      'snowflake-account-name-placeholder': `orgname-accountname`,
      'snowflake-custom-name': `A custom name to identify this Snowflake connection in Mixpanel.`,
      'snowflake-account-help': `This is the URL prefix of your snowflake account (`,
      'snowflake-account-help-suffix': `This can also normally be found via the `,
      'snowflake-account-help-suffix-2': ` function.)`,
      'snowflake-allowlist': `SYSTEM$ALLOWLIST()`,
      'see-snowflake-documentation': `See Snowflake documentation`,
      and: `and`,
      username: `Username`,
      authentication: `Authentication`,
      'authentication-type': `Authentication Type`,
      'key-pair': `Key Pair`,
      'rotate-key': `Rotate Key`,
      'snowflake-create-user-help': `Running the following commands in Snowflake will create the user Mixpanel uses to unload data. While Mixpanel uses pre-defined user names and roles, you can use any name you like.`,
      'warehouse-and-storage-integration': `Warehouse & Storage Integration`,
      warehouse: `Warehouse`,
      'warehouse-best-practice': `While not mandatory, it is best to create a warehouse specifically for Mixpanel. Be sure to grant your roles access to the warehouse.`,
      'storage-integration': `Storage Integration`,
      'storage-integration-help': `The storage integration grants access to unload data into a Google Cloud Storage bucket managed by Mixpanel.`,
      'grant-access-to-tables': `Grant Access to Tables`,
      'grant-access-to-tables-help': `Be sure to grant access to any tables you wish to import. The following is an example of how to grant access to all tables in a database.`,
      'table-database': `Table Database`,
      'fine-tune-permissions': `You can fine tune the granularity of these permissions.`,
      'stream-database': `Stream Database`,
      'stream-database-placeholder': `Stream database name...`,
      'stream-schema': `Stream Schema`,
      'stream-schema-placeholder': `Stream schema name...`,
      'snowflake-streams-help': `You may create a new database / schema or reuse an existing one if you wish. Mixpanel will ping your Snowflake instance to make sure the right permissions are granted`,
      'field-mandatory': `This field is mandatory.`,
      'stream-fields-required': `Both stream database and stream schema are required to enable Mirror mode.`,
      'compute-not-editable': `Compute type is not editable after source creation.`,
      'sql-warehouse-not-supported': `SQL Warehouse is not supported. Mixpanel requires a dedicated cluster to run Spark jobs.`,
      'enable-mirror-cdf': `To enable Mirror, make sure the tables imported have [Change Data Feed](https://docs.databricks.com/en/delta/delta-change-data-feed.html#enable-change-data-feed) enabled.`,
      port: `Port`,
      'service-account': `Service Account`,
      'http-path-label': `HTTP Path`,
      'role-arn': `Role ARN`,
      'date-connected': `Date Connected`,
      'connected-by': `Connected By`,
      'number-of-imports': `Number of Imports`,
      mirror: `Mirror`,
      'additional-setup-required': `Additional Setup Required`,
      'no-sources-found': `No sources found.`,
      'connect-warehouse': `Connect your Warehouse to Mixpanel`,
      'connect-warehouse-body': `Connecting a warehouse source to Mixpanel will allow you to create events, users, groups or lookup tables to analyze in any report or board.`,
      'warehouse-sources': `Warehouse Sources`,
      edit: `Edit`,
      create: `Create`,
      connection: `Connection`,
      'add-connection': `Add Connection`,
      'delete-source-first-delete-syncs': `To delete this source, first delete all recurring syncs.`,
      'step-x-of-y': `Step {{step}} of {{total}}`,
      saved: `Saved`,
      save: `Save`,
      done: `Done`,
      'create-source': `Create Source`,
      next: `Next`,
      'source-created-success': `Successfully created {{warehouseType}} source.`,
      'source-updated-success': `Successfully updated {{warehouseType}} source.`,
      'source-deleted-success': `Successfully deleted source.`,
      'certificate-too-large': `Certificate file is too large. Maximum size is {{maxSizeKB}} KB.`,
      'certificate-read-error': `Reading certificate failed with error {{error}}`,
    },
    'search-projects': `Search projects`,
    'x-projects_one': `{{count, number}} project`,
    'x-projects_other': `{{count, number}} projects`,
    'no-projects-found': `No projects found for given filters`,
    'id-merge': {
      'in-waiting-list': `You are now in the waiting list for id merge.`,
      'status-fetch-error': `Error getting current status of ID merge: {{error}}`,
      'enable-error': `Error trying to enable ID merge: {{error}}`,
      'set-mode-error': `Error setting ID merge mode: {{error}}`,
      modes: {
        simplified: `Simplified API`,
        original: `Original API`,
        disabled: `Disabled`,
      },
      name: `Name`,
      'created-on': `Created On`,
      'users-30-days': `Users (30d)`,
      'users-30-days-tooltip': `The number of Mixpanel users who ran at least one query in the project in the last 30 days`,
      'id-merge': `Identity Merge`,
      enable: `Enable ID Merge`,
      'enable-id-merge': `Enable ID merge`,
      'enable-id-merge-question': `Enable ID merge?`,
      'enable-description': `ID Merge makes it possible to connect pre-authentication activity with an authenticated user across devices.`,
      'learn-more': `Learn More about ID Merge`,
      'simplified-info': `Mixpanel has released a [simplified ID Merge API](https://docs.mixpanel.com/docs/tracking/how-tos/identifying-users#simplified-vs-original-id-merge), read about the changes from the original API [here](https://docs.mixpanel.com/docs/tracking/how-tos/identifying-users#key-changes-from-the-original-api).`,
      'new-project-default': `New projects in this organization will default to`,
      'using-simplified': `This project is using the **simplified** ID Merge API.`,
      'using-original': `This project is using the **original** ID Merge API.`,
      'switch-to-simplified': `Switch this project to the Simplified API`,
      'switch-to-original': `Switch this project to the Original API`,
      'switch-note': `**Note:** This setting can only be changed before the project has received data. Please read the linked documentation for details. If you are unsure which API is appropriate choose the one that matches your organization's default.`,
      'matches-org-defaults': `This matches your organization's default for new projects.`,
      'doesnt-match-org-defaults': `This is not your organization's default for new projects.`,
      'not-enabled': `This project does not have ID Merge enabled.`,
      search: `Search projects`,
      'migration-info': `All projects in your org will be migrated to the new ID Merge system. This can take up to 4 days.`,
      'migration-warning': `This cannot be undone.`,
    },
    'org-settings': {
      updated: `Org updated successfully`,
      overview: {
        title: `Manage Your Organization`,
        'mcp-enable': `Enable MCP`,
        'mcp-enable-tooltip': `Enable or disable Model Context Protocol (MCP) access for projects in your organization.`,
        name: `Name`,
        'spark-enable': `Enable Mixpanel AI`,
        'spark-enable-tooltip': `Control your organization's access to generative AI in Mixpanel`,
        'spark-opted-out': `Your organization has opted out of Mixpanel AI and it cannot be enabled from the UI. Please contact support if this is in error.`,
        'spark-no-perms': `You must have the Admin or Owner role in order to change AI settings.`,
        'spark-description': `Toggle access to all AI features in Mixpanel`,
        'delete-title': `Delete Organization`,
        'delete-tooltip': `Close your organization and delete all associated data`,
        'undelete-title': `Cancel Deletion`,
        'undelete-tooltip': `Cancel the deletion process for this organization`,
        'undelete-description': `Are you sure you wish to cancel the deletion of {{organizationName}}?`,
        'undelete-cta': `Cancel Deletion Request`,
        'undelete-success': `Deletion request canceled`,
      },
      'verify-password': {
        title: `Verify Mixpanel password`,
        description: `For your security, please enter the password for {{user}}`,
        'current-password': `Your current password`,
        'invalid-password': `Invalid Password`,
      },
      projects: {
        name: `Name`,
        teams: `Teams`,
        'all-teams': `All Teams`,
        'created-by': `Created by`,
        'queries-30-days': `Queries (30d)`,
        'queries-30-days-tooltip': `The total number of queries ran in the project in the last 30 days`,
        'users-30-days': `Users (30d)`,
        'users-30-days-tooltip': `The number of Mixpanel users who ran at least one query in the project in the last 30 days`,
        mtu: `MTU (this month)`,
        'mtu-tooltip': `"Monthly tracked users" since \n the beginning of the month`,
        events: `Events`,
        'events-tooltip': `Event tallies since the \n beginning of the month`,
        profiles: `Profiles`,
        'profiles-tooltip': `People tallies since the \n beginning of the month`,
        'delete-project': `Delete Project`,
        'delete-project-disabled-tooltip': `You cannot perform this action because it will delete the final project in the organization. If you wish to delete everything in this organization, you can delete the organization in the Overview section of Organization Settings.`,
        'project-created': `Project "{{projectName}}" created successfully`,
        'project-restored': `Project restored successfully`,
        'project-restored_other': `Projects restored successfully`,
        'project-deleted': `Project deleted successfully`,
        'project-deleted_other': `Projects deleted successfully`,
        'create-project-error': `Error trying to create company project`,
        'delete-project-error': `Error trying to delete company project`,
        'project-updated': `Project updated successfully`,
        'update-project-error': `Error trying to update project`,
        'update-project-failed': `Failed to update project`,
        'project-session-updated': `Project session settings updated successfully`,
        'project-time-updated': `Project time period settings updated successfully`,
        'homepage-dashboard-updated': `Updated homepage dashboard successfully`,
        'homepage-dashboard-update-error': `Failed to update homepage dashboard`,
        'export-csv': `Export CSV`,
      },
      'access-security': {
        'sso-disabled': `Single sign-on is disabled for your organization.`,
        'sso-header': `Single Sign-On`,
        'sso-updated': `SSO settings successfully updated`,
        'sso-enabled-success': `Single sign-on enabled for your organization.`,
        'sso-updated-success': `Single sign-on updated for your organization.`,
        'domain-verified': `Your domain has been verified`,
        'domain-not-verified': `Your domain has not been verified yet`,
        'domain-claiming': `Domain Claiming`,
        'two-factor-header': `Two-Factor Authentication`,
        'shared-sso': `Shared SSO`,
        'claim-domain-header': `Action Required to Enable SSO`,
        'claim-domain-body': `In order to use SSO, you must have verified domains. You either have no claimed domains or your domains have expired.`,
        'claim-domains': `Claim Domains`,
        sso: `Single Sign-On`,
        'sso-description': `Enable users of your organization to log in with single sign-on.`,
        'postback-url': `Postback URL`,
        'id-provider-url': `Identity Provider Sign-In URL`,
        'id-provider-url-description': `Supplied by your identity provider. Verifies where users enter their credentials.`,
        'issuer-url': `Issuer URL`,
        'issuer-url-description': `Supplied by your identity provider. Used to check where the SAML assertion is coming from.`,
        'saml-cert': `SAML Certificate`,
        'saml-cert-description': `SAML certificate supplied by your identity provider.`,
        'select-cert': `Select SAML Certificate`,
        'require-sso-checkbox': `Require users to log in using SSO?`,
        'require-sso': `Require Single Sign-On for members`,
        'require-sso-description': `This option requires organization members to use single sign-on to log in; they will not be able to use other login methods like username and password. By default, users with administrator permissions are exempted from this requirement (further configuration available below).`,
        'require-sso-for-all': `Require Single Sign-On for administrators`,
        'require-sso-for-all-description': `If enabled, users with administrator permissions are not exempted from the Single Sign-On requirement to log in.`,
        'require-sso-for-all-warning': `If your SSO is misconfigured, no one with administrator permissions can sign in to fix it. Please ensure your SSO is configured correctly before enabling this option.`,
        idp: `IDP Managed Access`,
        'idp-description': `When enabled, Mixpanel treats your Identity Provider (IDP) as a source of truth for who belongs in the organization. Users who log into Mixpanel through SSO will be given accounts if they do not have them, and anyone trying to use Mixpanel using one of your claimed domains will be routed to your IDP if SSO is enabled.`,
        'delete-config': `Remove SSO configuration`,
        'delete-config-description': `This will remove all saved single sign-on configuration. You will need to re-enter all configuration values.`,
        'delete-config-confirm-title': `Are you sure you want to remove all single sign-on configuration?`,
        'delete-config-success-message': `SSO configuration successfully removed`,
        'delete-config-error-message': `Could not remove SSO configuration`,
        '2fa': `Two-Factor Authentication`,
        '2fa-description': `Whenever users sign in with a username and password, they also need to enter a security code generated on their mobile device. Users do not need a security code when signing in through the organization's identity provider (SSO).`,
        discoverability: `Looking to automate adding new users to your organization without using SSO? Check out Organization Discoverability.`,
        'domain-claiming-description': `Claim a domain and enforce the login requirements for all users with email from that domain.<br/><br/>To complete the process of claiming a domain, you will need to add a TXT record to the domain's DNS records with the provided verification key. Consult your DNS provider for instructions.<br/><br/>You can refresh the status to manually verify once the records are done propagating, or it will be verified via a periodic check.`,
        'add-domain': `Add a Domain`,
        'add-domain-disabled-tooltip': `Add domains to the admin org linked to this org.`,
        'no-domains': `No domains currently claimed`,
        'domain-name': `Domain Name`,
        status: `Status`,
        verified: `Verified`,
        pending: `Pending`,
        'verification-token': `Verification Token`,
        scim: `SCIM Provisioning`,
        'scim-description': `Provision and manage users from your directory service.`,
        'scim-token': `SCIM Token`,
        'scim-token-description': `Make sure to copy this token. You will not be able to see it again.`,
        'generate-token': `Generate Token`,
        'submit-claim': `Submit Claim`,
        'verify-now': `Verify Now`,
        'claim-domain': `Claim a Domain`,
        'claim-domain-description': `To complete the verification after submitting, you will need to add a TXT record with your generated verification token to the domain's root. Consult your DNS provider for instructions.\n\nDNS verification may take up to 24 hours to succeed.`,
        'generate-token-confirm-title': `Are you sure you want to generate a SCIM token?`,
        'generate-token-confirm-description': `Regenerating this token will invalidate any existing SCIM tokens for this organization, which could disrupt existing provisioning flows.`,
        'disable-sso': `Disable SSO`,
        'disable-sso-confirm-title': `Are you sure you want to disable single sign-on?`,
        'disable-sso-confirm-description': `All users will now begin using the built-in Mixpanel authentication and may need assistance establishing or resetting their password. You can not undo this action, but you can re-enable single sign-on.`,
        'withdraw-claim': `Withdraw Claim`,
        'withdraw-claim-confirm-title': `Are you sure you want to withdraw your claim for this domain?`,
        'withdraw-claim-confirm-description': `If you have sso enabled, all users with emails from that domain will be able to log in with a username and password instead of the existing flow.`,
        'withdraw-claim-success-toast': `Your claim to {{domain}} has been withdrawn.`,
        'withdraw-claim-error-toast': `Error trying to withdraw claim`,
        'reset-two-factor-table-column-name-name': `Name`,
        'reset-two-factor-table-column-name-email': `Email`,
        'reset-two-factor-table-column-name-organization-role': `Organization Role`,
        'reset-two-factor-table-column-name-two-factor-method': `Two-Factor Method`,
        'reset-two-factor-table-filter-placeholder': `Search`,
        'reset-two-factor-table-no-users-place-holder': `No users`,
        'reset-two-factor-table-title': `User Configuration`,
        'reset-two-factor-reset-button-title': `Reset Two-Factor Authorization`,
        'reset-two-factor-methods': `Reset Two-Factor Method`,
        'reset-two-factor-confirm-title': `Are you sure you want to reset two-factor authorization methods for the {{count}} selected users?`,
        'reset-two-factor-confirm-description': `By resetting their two-factor authentication method, they will be required to set up a new two-factor authentication method the next time they log in to Mixpanel. This action cannot be undone.`,
        'reset-two-factor-success-toast-message': `You have reset two-factor authorization methods for {{count}} users`,
        'reset-two-factor-error-toast': `Error trying to reset two-factor authentication methods for the selected users. If you are still experiencing problems, please [contact support](https://mixpanel.com/get-support). `,
        'transfer-domain': `Transfer Domain`,
        'transfer-domain-confirm-title': `Are you sure you want to transfer this domain?`,
        'transfer-domain-confirm-description': `This action will transfer the claimed domain to the admin org connected to this current org.`,
        'transfer-domain-success-toast': `Your claim to {{domain}} has been transferred.`,
        'transfer-domain-error-toast': `Error trying to transfer domain`,
        'optional-sso': `Make SSO Optional for members`,
        'optional-sso-confirm-title': `Are you sure you want to make SSO optional for members?`,
        'optional-sso-confirm-description': `Members will be able to sign in using their email and password.`,
        'required-sso': `Make SSO Required for members`,
        'required-sso-confirm-title': `Are you sure you want to make SSO required for members?`,
        'required-sso-confirm-description': `Members will no longer be able to sign in using their email and password and instead must use SSO to log in, exempting users with administrator permissions.`,
        'optional-sso-all': `Make SSO Optional for administrators`,
        'optional-sso-all-confirm-title': `Are you sure you want to allow users with administrator permissions to bypass SSO requirements?`,
        'optional-sso-all-confirm-description': `Users with administrator permisions will be able to sign in using their email and password.`,
        'required-sso-all': `Make SSO Required for administrators`,
        'required-sso-all-confirm-title': `Are you sure you want to make SSO required for users with administrator permissions?`,
        'required-sso-all-confirm-description': `If your SSO is misconfigured, no one with administrator permissions can sign in to fix it. Please ensure your SSO is configured correctly before enabling this option.`,
        'session-settings': `Session Settings`,
        'session-settings-description': `Configure session timeout and expiry settings for all users in your organization.`,
        'session-settings-inactivity-timeout': `Inactivity Timeout (minutes)`,
        'session-settings-inactivity-timeout-description': `Users will be logged out after this period of inactivity. Leave empty for no limit.`,
        'session-settings-max-duration': `Maximum Session Duration (hours)`,
        'session-settings-max-duration-description': `Users will be required to log in again after this duration. Leave empty for no limit.`,
        'session-settings-inactivity-label': `Inactivity Timeout:`,
        'session-settings-max-duration-label': `Maximum Session Duration:`,
        'session-settings-no-limit': `Default`,
        'session-settings-updated': `Session settings updated.`,
        'session-settings-error': `Error updating session settings: {{error}}`,
        'request-access-settings': `Request Access Settings`,
        'request-access-settings-description': `Customize the help message and link shown to users on the request access page.`,
        'request-access-settings-help-message': `Help Message`,
        'request-access-settings-help-message-label': `Help Message:`,
        'request-access-settings-help-link': `Help Link`,
        'request-access-settings-help-link-label': `Help Link:`,
        'request-access-settings-not-set': `Not set`,
        'request-access-settings-updated': `Request access settings updated.`,
        'request-access-settings-error': `Error updating request access settings.`,
        'cert-file-formats': `(.pem, .cert, .cer)`,
        'shared-sso-link-orgs-title': `Link Organizations`,
        'shared-sso-link-orgs-description': `Enter the invitation code from the Admin Organization`,
        'shared-sso-invitation-code-placeholder': `Invitation code`,
        'shared-sso-link-organization': `Link Organization`,
        'shared-sso-create-admin-title': `Create a shared SSO Admin Organization`,
        'shared-sso-create-admin-description': `Enter a name for the Admin Organization`,
        'shared-sso-new-org-name-placeholder': `New Organization name`,
        'shared-sso-done': `Done`,
        'shared-sso-unlinked-description': `This organization is not sharing SSO settings. To share settings, Link to an existing Admin Organization using a code, or create a new Admin Organization to hold shared SSO configuration.`,
        'shared-sso-linked-settings-title': `This organization is sharing SSO settings.`,
        'shared-sso-scim-name-label': `SCIM Name`,
        'shared-sso-scim-name-placeholder': `SCIM name`,
        'shared-sso-admin-org-label': `Admin Organization`,
        'shared-sso-admin-org-placeholder': `Admin Organization`,
        'shared-sso-create-admin-organization': `Create Admin Organization`,
        'shared-sso-create-admin-error': `Could not save Admin Organization.`,
        'shared-sso-accept-invitation-error': `Could not accept invitation.`,
        'shared-sso-scim-fetch-error': `Could not fetch SCIM configuration`,
        'shared-sso-fetch-related-orgs-error': `Could not fetch related Organizations`,
        'shared-sso-update-scim-error': `Could not update SCIM name.`,
      },
      'linked-orgs': {
        'linking-code': `Linking Code`,
        'linking-code-description': `This code should be entered on the settings page of the organization you wish to link. You may need to provide it to an admin of that organization to do so. Please note that this code expires in 3 days.`,
        token: `Token`,
        title: `Linked Organizations`,
        'invite-error': `Invitation to link org couldn't be created.`,
        'scim-name-updated': `Successfully updated linked org {{orgName}} SCIM name to {{scimName}}.`,
        'scim-name-error': `Could not update SCIM name: {{error}}`,
      },
      'resend-invite-modal': {
        title: `Resend Invitation`,
        description: `Use the following URL if the person you want to invite is having difficulty receiving the invitation email.`,
        loading: `Loading...`,
        'send-another-email': `Send another invitation email to`,
        error: `Error resending invite.`,
      },
      'confirm-delete-users': {
        'delete-invitations': `Delete`,
        'delete-requests': `Delete`,
        'delete-invitations-confirm': `Are you sure you want to delete the`,
        'invitations-for': `for`,
        'to-join': `to join`,
        'requests-from': `from`,
        'to-access': `to access`,
        'revoke-service-accounts': `Are you sure you want to revoke`,
        'service-accounts-removed': `will be removed from`,
        'action-non-reversible': `This action is non-reversible.`,
        'delete-users-confirm': `Are you sure you want to delete`,
        'users-removed': `will be removed from`,
        'delete-project-users-error': `Error trying to delete project users: {{error}}`,
        'delete-company-users-error': `Error trying to delete company users: {{error}}`,
      },
      'service-accounts': {
        add: `Add Service Account`,
        'add-no-perms': `Only owners and admin can create service accounts across an Organization`,
        updated: `Service account updated successfully`,
        deleted: `Service account deleted successfully`,
        deleted_other: `Service accounts deleted successfully`,
        'x-service-accounts_one': `{{count, number}} service account`,
        'x-service-accounts_other': `{{count, number}} service accounts`,
        search: `Search service accounts`,
        'no-service-accounts': `This organization does not currently have any service accounts`,
        username: `Username`,
        expires: `Expires`,
        expired: `Expired`,
        'created-at': `Created At`,
        'created-by': `Created By`,
        never: `Never`,
        'last-used': `Last Used`,
        'never-used': `never used`,
        'org-role': `Organization Role`,
        'remove-service-account': `Remove service account`,
        'fetch-error': `Error trying to fetch service accounts: {{error}}`,
      },
      gdpr: {
        'not-owner': `Your account is currently not an owner of a project`,
        'no-token': `You have not yet generated a Token to use this page. See instructions above.`,
        'export-user-data': `Export user data from a project`,
        'export-user-data-description': `Submit a request to export user data from one of your Mixpanel projects.`,
        'request-export': `Request Export`,
        'delete-user-data': `Delete user data from a project`,
        'delete-user-data-description': `Submit a request to permanently delete user data from one of your Mixpanel projects.`,
        'request-delete': `Request Deletion`,
        'select-compliance': `Select Compliance`,
        gdpr: `GDPR`,
        ccpa: `CCPA`,
        'learn-more-compliance': `Learn more about [GDPR](https://gdpr.eu/) and [CCPA](https://www.caprivacy.org/).`,
        project: `Project`,
        'select-project': `Select a project`,
        'dont-see-project': `Don't see the project you're thinking of here? [Ask the owner](https://help.mixpanel.com/hc/en-us/articles/115004505106-Project-Ownership) to submit a request, have them [transfer the project](https://help.mixpanel.com/hc/en-us/articles/115004505106-Project-Ownership#transferring-project-ownership) to you, or add you as an admin.`,
        'disclosure-type': `Disclosure Type`,
        'distinct-id-placeholder': `Enter distinct_id of this user`,
        'select-csv': `Select CSV file`,
        'csv-max-distinct-ids': `File must not contain more than the maximum of {{count, number}} distinct IDs.`,
        'learn-more-distinct-id': `Learn more about [how to locate users by distinct_ids](https://help.mixpanel.com/hc/en-us/articles/115004509406#locating-the-distinct_id).`,
        'submit-request': `Submit Request`,
      },
      'plan-details': {
        'plan-details': `Plan Details`,
        'plan-description': `{{pricingPlanName}} at {{numDataUnits}} {{dataUnitType}}`,
        'plan-description-long': `{{pricingPlanName}} Plan at {{numDataUnits}} {{dataUnitType}}`,
        'term-window': {
          'total-per': `Total Per {{termWindow}}`,
          month: `Month`,
          'month-count': `{{count}} Months`,
          quarter: `Quarter`,
          year: `Year`,
        },
        'renews-on': `Renews: {{date}}`,
        'before-tax': `before tax`,
        'data-usage': `Data Usage`,
        'session-recording-usage': `Session Recording Usage`,
        'spark-usage': `Spark Usage`,
        'view-history': `View Historical Usage`,
        'manage-billing': `Manage Billing & Payment`,
        event: `Event`,
        event_other: `Events`,
        profiles: `Profiles`,
        mtu: `MTU`,
        mtu_other: `MTUs`,
        'session-replays': `Session Replays`,
        experiments: `Monthly Experiment Users`,
        'feature-flags': `Feature Flag Requests`,
        'mtus-long': `Monthly Tracked Users`,
        'mtus-details': `{{count, number}} since the beginning of the month`,
        'view-usage': `View All Usage`,
        'modify-plan': `Modify Plan`,
        'upgrade-or-modify-plan': `Upgrade or Modify Plan`,
        'upgrade-or-modify-tooltip': `Make changes to your plan`,
        'upgrade-or-modify': `Upgrade or Modify`,
        cancel: `Cancel Plan`,
        'switch-to-free': `Switch to Free`,
        'receipt-updated': `Your receipt message has been successfully updated!`,
        'billing-updated': `Your billing info has been successfully updated!`,
        'plan-updated': `Plan successfully updated!`,
        downgrade: `Submit a request to cancel your plan and switch to Free plan`,
        'downgrade-scheduled': `A downgrade to our Free plan has been scheduled`,
        'payment-success': `Your payment was successful`,
        'payment-declined': `Your payment was declined`,
        'payment-in-progress': `A previous payment is still being processed. Please wait a moment before retrying.`,
        'address-updated': `Billing address updated`,
        'address-update-error': `Error updating billing address`,
        'remove-add-on-update-error': `Error removing add on`,
        month: `Month`,
        'project-details': `Project Details`,
        'session-recordings': `Session recordings this period`,
        'spark-responses': `Spark responses this period`,
        'this-billing-period': `This billing period`,
        'additional-data': `Additional data rate: \${{overageRate}} / {{unit}}`,
        'additional-data-tooltip': `Additional data will be charged at this rate and billed the following month.`,
        'credits-in-account_one': `{{count}} valid credit in your account`,
        'credits-in-account_other': `{{count}} valid credits in your account`,
        'event-volume': `Event Volume`,
        'event-updates': `Event Updates`,
        tax: {
          title: `Tax Information`,
          manage: `Manage Tax Info`,
          'no-info': `No Tax Information on File`,
          'no-info-description': `Please take a moment to add your tax information to your billing account`,
          add: `Add Tax Info`,
          country: `Country`,
          address: `Street Address`,
          'tax-id': `Tax ID Number`,
        },
      },
      'cancel-account': {
        title: `Downgrade Plan`,
        plan: `Plan`,
        'active-free-plan-message': `Your account will remain active and will automatically be moved to a Free plan after this billing period.`,
        'delete-warning': `Users will maintain access to your organization for {{daysToChurn}} days, after which it will be deleted. After {{daysToChurn}} days, organization projects will be permanently deleted.`,
        'reasons-for-cancellation': `Reasons for cancellation (select all that apply):`,
        'cancellation-reason-title': `Why do you wish to no longer pay for Mixpanel?`,
        'missing-features-prompt': `What features were missing?`,
        'analytics-alternative-title': `What will you do for analytics going forward?`,
        'vendor-title': `Which vendor?`,
        'vendor-input-title': `Please enter the name of the vendor here:`,
        'additional-info-title': `Would you like to add anything else?`,
        'select-one-placeholder': `Select one`,
        back: `Back`,
        cancel: `Cancel`,
        submit: `Submit`,
        reasons: {
          'too-difficult-to-implement': `Too difficult to implement`,
          'missing-features': `Missing features`,
          'team-stopped-using': `Team stopped using it / Not delivering expected insights`,
          'too-hard-to-use': `Too hard to use day-to-day`,
          'inadequate-support': `Inadequate support/documentation`,
          'pricing-too-expensive': `Pricing too expensive`,
          'performance-issues': `Performance issues`,
          'data-quality-issues': `Data quality/trust issues`,
          'consolidating-with-other-tools': `Consolidating with other tools`,
          other: `Other`,
        },
        'sub-questions': {
          'missing-features-prompt': `What features are missing:`,
          'other-prompt': `Please specify:`,
        },
        alternatives: {
          'continue-free': `I will continue using Mixpanel's free product`,
          'selected-another-vendor': `I have selected another vendor`,
          'no-need': `I no longer have a need for product analytics`,
          'internal-resources': `I have internal resources which will satisfy our analytics needs`,
        },
        validation: {
          'select-reason': `Select at least one reason`,
          'select-alternative': `Please select what you'll use for analytics`,
          'select-vendor': `Please select a vendor`,
          'enter-vendor-name': `Please enter the vendor name`,
        },
        errors: {
          'submit-error': `Error trying to cancel plans. Please try again or contact support.`,
        },
      },
      'plan-details-new': {
        'forgiveness-success-modal': {
          'header-monthly': `Cha-ching! You just saved {{amountToBeForgivenText}} on your Mixpanel spend.`,
          'header-annual': `Smart move. You've optimized your plan and avoided future fees.`,
          'body-monthly': `Last month's additional data fees totaling {{amountToBeForgivenText}} have been waived. Your plan volume is now {{monthlyEventVolume}} monthly events. That's a savings of {{savingsAmountText}}.`,
          'body-annual': `We've waived your additional data fees, and your plan now covers {{annualEventVolume}} annual events. This upgrade should save you an estimated {{savingsAmountText}} in additional data fees for the rest of your annual plan. This change keeps your costs predictable - and your analytics uninterrupted.`,
          confirm: `Got It`,
          'billing-details': `View Billing Details`,
        },
        'purchase-success-modal': {
          heading: `You're Primed for Growth`,
          'sub-heading': `Welcome to the Growth plan: everything you need to grow your business & make your competition sweat.`,
          'feature-1-text': `Unlimited reports, boards, & cohorts`,
          'feature-2-text': `20K session replays per month`,
          'feature-3-text': `Advanced sharing controls`,
          'feature-4-text': `Real-time email & Slack alerts`,
          'get-started': `Get Started`,
          'view-my-invoice': `View My Invoice`,
        },
        'legacy-to-current-warning-modal': {
          heading: `You're on a Legacy Mixpanel plan`,
          'sub-heading': `By updating to our latest Growth plan, you’ll be able to manage your plan more fully and access the latest Mixpanel features.`,
          'custom-session-replay-volumes': `Custom Session Replay Volumes`,
          'first-million-events': `Pay $0 for your first 1M monthly events`,
          'manage-add-ons': `Manage add-ons like Data Pipelines & Group Analytics`,
          'qualify-for-ai-features': `Qualify for beta access to AI features`,
          'not-right-now': `Not Right Now`,
          'update-my-plan': `Update My Plan`,
        },
        'plan-builder-enterprise-warning': `You are on an Enterprise plan. To make changes to your plan, please contact sales.`,
        'plan-details-text': `Plan Details`,
        'my-plan': `My Plan`,
        'data-usage': `Data Usage`,
        'title-experiment': `Experiment Monthly Users`,
        'title-feature-flags': `Feature Flag Requests`,
        'title-total-events': `Total Events`,
        'title-event-volume': `Event Volume`,
        'title-event-updates': `Event Updates`,
        'title-historical-events': `Historical Events`,
        'title-adjusted-events': `Adjusted Events`,
        'title-profiles': `Profiles`,
        'title-session-replays': `Session Replays`,
        'title-mtus': `MTUs`,
        total: ` Total`,
        'project-title-suffix': `by Project`,
        'data-usage-for-org': `Data Usage for {{orgName}}`,
        'how-are-mtus-calculated': `How are MTUs calculated?`,
        'export-csv': `Export CSV`,
        'event-source': `Event Source`,
        status: `Status`,
        creator: `Creator`,
        'event-volume': `Event Volume`,
        'event-updates': `Event Updates`,
        adjustments: `Adjustments`,
        'non-warehouse': `Non Warehouse`,
        'manual-adjustments': `Manual Adjustments`,
        deleted: `Deleted`,
        active: `Active`,
        'billing-details': `Billing Info`,
        'add-to-plan': `Add to Plan`,
        monthly: `monthly`,
        annual: `annual`,
        'add-ons': {
          'added-to-plan': `Added to Plan`,
          'ending-date': `Ending {{endDate}}`,
        },
        'talk-to-sales': `Talk to Sales`,
        'learn-more': `Learn More`,
        'adjust-plan-volume': `Adjust Plan Volume`,
        pricing: `Pricing varies depending on plan`,
        'upgrade-plan': `Upgrade Plan`,
        cancel: `Cancel`,
        'remove-from-plan': `Remove from Plan`,
        'plan-renews': `Renews on {{startDate}}`,
        'error-retrieving-account-info': `Error retrieving account information.`,
        'error-retrieving-alert-info': `Error retrieving alert information.`,
        'error-retrieving-pipeline-info': `Error retrieving pipeline information.`,
        'error-retrieving-group-analytics-info': `Error retrieving group analytics information.`,
        'experiment-usage-card': {
          header: `Experiment Events`,
          subheader: `Your plan provides an additional {{experimentEventCap}} monthly experiment events for free. Events beyond this threshold count toward your overall event volume.`,
          'events-tracked': `{{experimentEventCount}} Experiment Events Tracked`,
        },
        downgrade: {
          'days-left': `{{daysLeft}} Days Left`,
          'plan-downgrade-incoming': `Pending change takes effect {{startDate}}`,
          'usage-downgrade-pending': `Pending change to `,
          'usage-downgrade-takes-effect': ` takes effect {{startDate}}`,
          'event-downgrade': `{{upcomingVolume}} events`,
          'session-replay-downgrade': `{{upcomingVolume}} sessions`,
          'mtu-downgrade': `{{upcomingVolume}} monthly tracked users`,
          'add-on-downgrade-incoming': `Pending removal takes effect {{startDate}}`,
        },
        'current-plan-card': {
          'free-header': `Free Plan`,
          'growth-header': `Growth Plan`,
          'enterprise-header': `Enterprise Plan`,
          'startup-header': `Startup Plan`,
          'whats-included': `What’s included in this plan?`,
          'modify-plan': `Modify Plan`,
          'adjust-plan-volume': `Adjust Plan Volume`,
          'downgrade-to-free': `Downgrade to Free Plan`,
          'overage-rates': {
            header: `Overage rates`,
            events: `Events: \${{overageRate}} per thousand events`,
            mtus: `Monthly tracked users: \${{overageRate}} per thousand users`,
            'session-replay': `Session replays: \${{overageRate}} per thousand replays`,
            experiments: `Experiment users: \${{overageRate}} per thousand users`,
            'feature-flags': `Feature flag requests: \${{overageRate}} per thousand requests`,
          },
        },
        'overage-card': {
          'mtu-header-free': `You have reached your monthly tracked user volume. To regain access to reports, `,
          'event-header-free': `You have reached your event volume. To regain access to reports, `,
          'upgrade-to-growth': `upgrade to the Growth plan`,
          'wait-until-next-billing-period': ` or wait until the next billing period.`,
          'mtu-header': `Heads up: You’re over your monthly tracked user limit.`,
          'event-header': `Heads up: You’re over your event limit`,
          'mtu-subheader': `Your additional data fees currently total {{overageAmount}}. To avoid these fees, increase your plan's monthly tracked user volume to cover your usage. These fees will continue to accrue until you increase your volume or your billing period ends.`,
          'event-subheader': `Your additional data fees currently total {{overageAmount}}. To avoid these fees, increase your plan's event volume to cover your usage. These fees will continue to accrue until you increase your volume or your billing period ends.`,
        },
        'event-usage-card': {
          'event-limit': `Up to {{eventLimit}} tracked events`,
          'unlimited-events': `Unlimited events included in plan`,
          'events-tracked': `{{eventsCount}} Events Tracked`,
          'mtu-limit': `Up to {{eventLimit}} Monthly Tracked Users`,
          'unlimited-mtu': `Unlimited Monthly Tracked Users included in plan`,
          'mtu-tracked': `{{eventsCount}} MTUs`,
          'period-ends': `Period ends {{periodEndDate}}`,
          'trial-ends': `Trial ends {{periodEndDate}}`,
          'volume-exceeded': `Volume exceeded {{percentage}}%`,
        },
        'growth-feature-card': {
          'current-header': `Get More out of your Data`,
          'upgrade-header': `Power Up your Analysis`,
          'upgrade-startup-header': `Don’t Lose Your Premium Features`,
          'current-subheader': `Choose from the add-ons below to unlock valuable new features for your team.`,
          'upgrade-subheader-annual-save': `Mixpanel’s Growth plan is $0 for your first 1M monthly events. `,
          'upgrade-subheader-compare-plans': `Compare plans`,
          'upgrade-subheader-learn-more': ` to learn more about the right plan for you.`,
          'included-in-growth': {
            header: `Included in This Plan`,
            'reports-and-cohorts': `Unlimited Reports & Cohorts`,
            'session-replay': `Increased Session Replay Volumes`,
            'spark-reports': `Increased Spark AI Report Limits`,
            alerts: `Real-time Slack & Email Alerts`,
          },
          'add-ons': {
            'header-upgrade': `Unlock Access to Powerful Add-Ons`,
            'pipelines-header': `Data Pipelines`,
            'pipelines-usage-text': `{{pipelinesCreated}} active data connections using Pipelines`,
            'pipelines-detail': `Spend less time cleaning and preparing your data, and export events and user profiles created in Mixpanel to your cloud storage for custom analysis.`,
            'pipelines-manage-text': `Manage Pipelines`,
            'group-analytics-header': `Group Analytics`,
            'group-analytics-usage-text': `{{analyticGroups}}/{{analyticGroupsLimit}} analytic groups used`,
            'group-analytics-detail': `Measure KPIs at the account or company level. B2B companies can use this add-on to prevent churn and discover upsell opportunities based on group-level product usage.`,
            'group-analytics-manage-text': `Manage Groups`,
            'remove-add-on-header': `Remove {{addOnName}}`,
            'remove-add-on-body': `We’re sorry that {{addOnName}} didn’t work out for you. These changes will take effect at the start of your next billing period ({{startDate}}). `,
          },
        },
        'enterprise-feature-card': {
          header: `Unlock Insights at Scale with the Enterprise Plan`,
          subheader: `Access warehouse connectors, enterprise SSO, custom volumes, and more to transform your data into company-wide insights.`,
          'included-in-enterprise': {
            'header-upgrade': `Included in This Plan`,
            'header-included': `What you get`,
            'compare-plan': `Compare Plan`,
            'event-volumes': `Custom event volumes of any size`,
            warehouse: `Mirror mode & data warehouse sync`,
            'session-replay-and-spark': `Customizable Session Replay limits`,
            'data-government': `Data governance & access controls`,
            sso: `Automated provisioning & SSO`,
          },
          'add-ons': {
            'header-upgrade': `Unlock even more add-ons`,
            'header-included': `Add ons`,
            'warehouse-header': `Warehouse Connectors`,
            'warehouse-detail': `Unify product and business data, all in Mixpanel. Analyze and understand the end-to-end customer experience seamlessly, without hopping between tools or bugging your data team.`,
            'premium-support-header': `Premium Support`,
            'premium-support-detail': `With Premium Support, our team is available 24/7 with 1-hour guaranteed response times for critical issues. We'll troubleshoot technical problems, answer product questions, and share best practices to keep you unblocked.`,
            'metric-tree-header': `$t(mixology.metric-tree_plural)`,
            'metric-tree-detail': `Hold your product accountable to business goals with a living, interactive framework of your growth strategy.`,
            'experiments-header': `Experiments`,
            'experiments-detail': `Make smarter decisions, faster. Run experiments in minutes and track impact on key metrics.`,
            'feature-flags-header': `Feature Flags`,
            'feature-flags-detail': `Safely roll out changes, test ideas in production, and control who sees what—all without code deploys.`,
          },
        },
        'premium-support-card': {
          header: `Premium Support`,
          subheader: `Experience priority assistance from our support team to help you succeed. You'll receive faster responses to your inquiries. For more details, please refer to your support agreement.`,
          'contact-support': `Contact Support`,
          'footer-need-help': `Need help faster? `,
          'footer-visit-docs': `Visit our help docs.`,
          'footer-try-searching': ` Try searching for your question or Ask AI for instant answers.`,
        },
        'usage-card': {
          'adjust-plan-volume': `Adjust Plan Volume`,
          'period-ends': `Period ends {{periodEndDate}}`,
          'view-historical-usage': `View Historical Usage`,
          event: {
            header: `Event Volume`,
            'subheader-free': `Your plan provides up to {{limit}} tracked events. To track more events please upgrade to the Growth plan.`,
            'subheader-limited': `Your plan provides up to {{limit}} tracked events. Additional events are billed at \${{overageRate}} per thousand events.`,
            'subheader-unlimited': `Your plan provides unlimited tracked events.`,
            footer: `{{usage}} Events Tracked`,
          },
          'session-replay': {
            header: `Session Replay`,
            'subheader-free': `Your plan provides up to {{limit}} session replays, which are retained for {{retention}}. To track more sessions please upgrade to the Growth plan.`,
            'subheader-upsell-retention': `Your plan provides up to {{limit}} session replays, which are retained for {{retention}}. To extend your retention period, please talk to sales.`,
            subheader: `Your plan provides up to {{limit}} session replays, which are retained for {{retention}}.`,
            'subheader-with-overage': `Your plan provides up to {{limit}} session replays, which are retained for {{retention}}. Additional replays are billed at \${{overageRate}} per thousand replays.`,
            footer: `{{usage}} Replays`,
          },
          'spark-ai': {
            header: `Spark AI`,
            subheader: `Your plan provides up to {{limit}} monthly Spark AI reports.`,
            footer: `{{usage}} Spark AI Reports`,
          },
          'saved-alert': {
            header: `Saved Alerts`,
            subheader: `Your plan provides up to {{limit}} saved alerts.`,
            footer: `{{usage}} Saved Alerts`,
          },
          mtu: {
            header: `Monthly Tracked Users`,
            subheader: `Your plan provides up to {{limit}} monthly tracked users. Additional users are billed at \${{overageRate}} per 1,000 users.`,
            footer: `{{usage}} Monthly Tracked Users`,
          },
          people: {
            header: `People`,
            subheader: `Your plan provides up to {{limit}} people.`,
            footer: `{{usage}} People`,
          },
          experiments: {
            header: `Experiments`,
            subheader: `Your plan provides {{limit}} MEUs. Additional users above total contract allotment are billed at \${{overageRate}} per thousand users.`,
            'subheader-startup': `Your plan provides {{limit}} MEUs. To increase your volume, please talk to sales.`,
            footer: `{{usage}} Users`,
          },
          'feature-flags': {
            header: `Feature Flagging`,
            subheader: `Your plan provides up to {{limit}} feature flag API requests and {{enabledFlagsLimit}} feature flags. Additional requests are billed at \${{overageRate}} per thousand requests.`,
            'subheader-startup': `Your plan provides up to {{limit}} feature flag API requests and {{enabledFlagsLimit}} feature flags. To increase your volume, please talk to sales.`,
            footer: `{{usage}} requests`,
          },
          retention: {
            label: `Retention Period`,
          },
          disclaimers: {
            'ingestion-time-billing': `Your plan bills events when they're processed by Mixpanel, not when they occurred. This means backfilled historical data and events that get deleted later (like GDPR deletions) will still count toward your usage quota.`,
          },
        },
        'billing-info': {
          'base-plan': `Base Plan`,
          'add-ons': `Add Ons`,
          other: `Other`,
          unlimited: `Unlimited`,
          'hide-cost-breakdown': `Hide Cost Breakdown`,
          'show-cost-breakdown': `Show Cost Breakdown`,
          'manage-billing-and-payment': `Manage Billing & Payment`,
          'cost-this-period': `Cost this Period ({{contractPeriod}})`,
          'percentage-of-base-price': `{{percentage}}% of your base price`,
          'reseller-managed-pricing': `Contact your Mixpanel seller for pricing details`,
          'warehouse-connectors': `Warehouse Connectors`,
          'business-associate-agreement': `Business Associate Agreement`,
          'baa-badge': `Business Associate Agreement (BAA) signed`,
          'signature-success-package': `Signature Success Package`,
          'premier-success-package': `Premier Success Package`,
          'essential-success-package': `Essential Success Package`,
          'gcp-plan-credit': `GCP Plan Credit`,
          'one-time-credit': `One Time Credit`,
          'silver-support': `Silver Support`,
          'standard-onboarding-package': `Standard Onboarding Package`,
          'strategic-onboarding-package': `Strategic Onboarding Package`,
          'monthly-to-annual-discount': {
            header: `Monthly to Annual Discount`,
            subheader: `You’re saving on your plan by paying annually.`,
          },
          event: {
            header: `Event Volume`,
            'subheader-free': `Your plan provides up to {{limit}} tracked events. To track more events please upgrade to the Growth plan.`,
            'subheader-limited': `Your plan provides up to {{limit}} tracked events. Additional events are billed at \${{overageRate}} per thousand events.`,
            'sub-line-item-header': `{{limit}} Events`,
            'overage-header': `{{quantity}} Additional Events`,
          },
          'session-replay': {
            header: `Session Replay`,
            'subheader-free': `Your plan provides up to {{limit}} session replays. To track more replays, please upgrade to the Growth plan.`,
            'subheader-overage': `Your plan provides up to {{limit}} session replays. Additional replays are billed at \${{overageRate}} per thousand replays.`,
            subheader: `Your plan provides up to {{limit}} session replays.`,
            'sub-line-item-header': `{{limit}} Replays`,
            'overage-header': `{{quantity}} Additional Replays`,
          },
          mtu: {
            header: `Monthly Tracked Users`,
            subheader: `Your plan provides up to {{limit}} monthly tracked users. Additional users are billed at \${{overageRate}} per thousand users.`,
            footer: `{{usage}} Monthly Tracked Users`,
            'sub-line-item-header': `{{limit}} Monthly Tracked Users`,
            'overage-header': `{{quantity}} Additional Monthly Tracked Users`,
          },
          pipeline: {
            header: `Data Pipelines`,
          },
          'group-analytic': {
            header: `Group Analytics`,
          },
          experiments: {
            header: `Experimentation`,
            subheader: `Your plan provides {{limit}} MEUs each month. Additional users above total contract allotment are billed at \${{overageRate}} per thousand users.`,
            'subheader-startup': `Your plan provides {{limit}} MEUs each month. To increase your volume, please talk to sales.`,
            'sub-line-item-header': `{{limit}} Monthly Experiment Users`,
            'overage-header': `{{quantity}} Additional Monthly Experiment Users`,
          },
          'feature-flags': {
            header: `Feature Flagging`,
            subheader: `Your plan provides up to {{limit}} feature flag API requests and {{enabledFlagsLimit}} feature flags. Additional requests are billed at \${{overageRate}} per thousand requests.`,
            'subheader-startup': `Your plan provides up to {{limit}} feature flag API requests and {{enabledFlagsLimit}} feature flags. To increase your volume, please talk to sales.`,
            'sub-line-item-header': `{{limit}} Monthly Requests`,
            'overage-header': `{{quantity}} Additional Requests`,
          },
          'enabled-feature-flags': {
            'sub-line-item-header': `{{limit}} Feature Flags`,
          },
        },
      },
      'billing-and-payment': {
        title: `Billing & Payment`,
        tabs: {
          'payment-and-invoices': `Payment & Invoices`,
          'invoice-preferences': `Invoice Preferences`,
          credits: `Credits`,
          'tax-information': `Tax Information`,
        },
        loading: {
          'please-wait': `Please wait while we process your payment...`,
          'check-later': `You can check back later, your payment will not be interrupted`,
        },
        'receipt-information': {
          title: `Receipt Information`,
          description: `Add your full business name, VAT number, or address of record here. This information will appear on all your receipts. For your security, do not include any confidential or financial information (like credit card numbers).`,
          placeholder: `Enter any additional information you would like to include on your receipts here.`,
        },
        'send-receipts-to': {
          title: `Send Receipts To`,
          recipients: `Recipients`,
          placeholder: `Enter a comma separated list of emails to receive copies of your receipts.`,
        },
        payment: {
          title: `Payment`,
          'enter-new-card': `Enter a new card to use for your Mixpanel payments`,
          'payment-declined': `Your payment for`,
          'was-declined': `was declined.`,
          on: `on`,
          'please-update': `Please update your payment method.`,
          'next-payment-for': `Your next payment is for`,
          'rerun-payment': `Rerun Payment`,
        },
        'invoice-history': {
          title: `Invoice History`,
          'no-invoices': `No Invoices`,
        },
        credits: {
          'no-credits': `No credits exist for your account`,
          'applies-to': `Applies To`,
          'initial-value': `Initial Value`,
          'remaining-balance': `Remaining Balance`,
          'valid-through': `Valid Through`,
        },
      },
      'plan-builder': {
        'failed-to-parse-url': `Failed to update your payment builder page. Please contact support.`,
        'error-retrieving-quote': `Error retrieving quote price. Please contact support.`,
        'failed-to-fetch-necessary-info': `Failed to retrieve the necessary information for your plan builder. Please contact support.`,
        'no-permissions-modify-plan': `You do not have permission to modify your plan. Please contact your billing admin to make changes.`,
        'plan-cannot-be-modified-online': `Your plan cannot be modified online. Contact sales to modify your plan.`,
        'per-year': `per year`,
        'per-month': `per month`,
        year: `year`,
        month: `month`,
        'go-back': `Go Back`,
        continue: `Continue`,
        'continue-to-add-ons': `Continue to Add-ons`,
        'continue-to-session-replay': `Continue to Session Replay`,
        'continue-to-payment': `Continue to Payment`,
        'complete-purchase': `Complete Purchase`,
        'complete-purchase-with-price': `Complete Purchase {{purchaseAmount}}`,
        'contact-us-for-pricing': `Contact us for pricing`,
        'add-ons': {
          'data-pipelines': {
            name: `Data Pipelines`,
            description: `Spend less time cleaning and preparing your data, and export events and user profiles created in Mixpanel to your cloud storage for custom analysis.`,
          },
          'group-analytics': {
            name: `Group Analytics`,
            description: `Measure KPIs at the account or company level. B2B companies can use this add-on to prevent churn and discover upsell opportunities based on group-level product usage.`,
          },
          'visit-our-docs': `Visit our docs to learn more.`,
          'percentage-of-base-price': `{{percentage}}% of your base price`,
        },
        'volume-chart': {
          recommended: `Recommended`,
          events: `{{events}} events`,
          'no-historical-data-header': `It looks like you haven’t set up Mixpanel yet`,
          'no-historical-data-body': `Check back after you’ve sent data for a few days to see your recommended monthly event volume.`,
        },
        'monthly-volume-card': {
          header: `Select monthly event volume`,
          'sub-header': `Your first 1M events per month are on us. If you need more, we offer volume discounts: the more you buy, the more you save.`,
          'how-to-estimate-volume': `How to estimate my event volume`,
        },
        'session-replay-volume-card': {
          header: `Select session replay volume`,
          'sub-header': `Growth plans include 20,000 monthly replays at no additional cost.`,
          'view-docs': `View Docs`,
        },
        'session-replay-selector': {
          'play-video': `Play Video`,
          'session-replays': `{{sessionReplays}} Session Replays`,
          'free-session-replays': `First {{freeSessionReplays}} Replays Free`,
        },
        'select-add-ons-card': {
          header: `Select add-ons`,
          'sub-header': `Unlock even more Mixpanel power with these additional features.`,
        },
        'add-on-card': {
          'learn-more': `Learn More`,
          'add-to-plan': `Add to Plan`,
          'added-to-plan': `Added to Plan`,
        },
        'payment-details-card': {
          header: `Select payment details`,
          'billing-cycle': `Billing cycle`,
          'billing-cycle-subtitle': `Save {{annualPlanSavingsAmount}} on your total plan cost when you choose annual billing.`,
          'billing-method': `Billing method`,
          'change-payment': `Change Payment Method`,
          'add-credit-card': `Add Credit Card`,
          'overage-forgiveness': {
            header: `Complete your purchase`,
            banner: {
              title: `You're one click away from saving`,
              'body-monthly': `By increasing your plan volume to {{monthlyEventVolume}} monthly events, you're avoiding additional data fees and saving money on last month's bill. Act now, this offer expires on {{formattedExpirationDate}}.`,
              'body-annual': `By increasing your plan volume to {{monthlyEventVolume}} monthly ({{annualEventVolume}} annual) events, you're avoiding additional data fees and saving money on last month's bill. Act now, this offer expires on {{formattedExpirationDate}}.`,
              'waived-monthly': `Additional data fees waived:`,
              'waived-annual': `Current additional data fees waived:`,
              'waived-projected': `Projected additional data fees waived:`,
              'price-increase': `{{monthlyOrAnnual}} price increase:`,
              'summary-monthly': `Your savings:`,
              'summary-annual': `Projected annual savings:`,
            },
          },
          monthly: {
            header: `Billed {{price}} monthly`,
            description: `Make recurring payments on the first of each month.`,
          },
          annual: {
            header: `Billed {{price}} annually`,
            description: `Pay once per year and lock in annual savings of {{annualPlanSavingsAmount}} compared to monthly plans.`,
          },
          'best-value': `Best Value`,
          'free-banner': {
            header: `Why is a credit card required if my plan costs $0?`,
            'body-credit-card-on-file': `A credit card on file ensures that we are able to bill for any additional data fees that are incurred beyond your 1M monthly volume. `,
            'body-review-docs': `Please review our help docs`,
            'body-learn-more-additional-fees': `to learn more about additional data fees.`,
          },
          'credit-card': {
            'card-number': `Card Number`,
            expiration: `Expiration`,
            cvv: `CVV`,
            country: `Country`,
            'postal-code': `Postal Code`,
            'use-card-on-file': `Use Card On File`,
            'change-payment-method': `Change Payment Method`,
          },
          'payment-failed': `Your payment failed. Please try again, or contact support.`,
          'payment-in-progress': `A previous payment is still being processed. Please wait a moment before retrying.`,
          'vat-gst-failed': `Your card was added, but we couldn't save your VAT/GST number. Please verify it and try again.`,
        },
        'plan-breakdown-card': {
          header: `Growth Plan Summary`,
          'change-volume': `Change Volume`,
          recommended: `Recommended`,
          events: {
            header: `{{monthlyEventVolume}} Monthly Events`,
            'header-annual': `({{annualEventVolume}} Annual)`,
            subheader: `Events ingested above your volume are billed at {{overageRate}} per thousand events`,
            'free-banner': `First {{eventVolume}} Events Free`,
          },
          'session-replay': {
            header: `{{monthlySessionReplayVolume}} Monthly Replays`,
            'header-annual': `({{annualSessionReplayVolume}} Annual)`,
            'free-banner': `First {{sessionReplayVolume}} Replays Free`,
          },
          'remove-add-on': `Remove add on`,
          'percentage-of-base-price': `{{percentage}}% of your base price`,
          'annual-subtotal': `Annual Subtotal`,
          'monthly-subtotal': `Monthly Subtotal`,
          'change-to-monthly-billing': `Change to monthly billing`,
          'change-to-annual-billing': `Change to annual billing`,
          'prorated-due-today': `Prorated Total Due Today`,
          'monthly-billing-switch-discount': `Save {{annualPlanSavingsAmount}} when you`,
          'monthly-billing-switch-discount-cta': `Select annual billing.`,
          'annual-billing-discount': `You’re saving {{annualPlanSavingsAmount}} with annual billing.`,
          'annual-billing-switch-cta': `Change to monthly.`,
          'renews-next-year': `Renews every year starting {{renewalDate}}`,
          'renews-next-month': `Renews every month starting {{renewalDate}}`,
          'contact-sales-header': `Contact Sales for a bigger plan`,
          'contact-sales-body-events': `Our plan options don’t stop at 20M events. Higher volume plans are just better served with a human touch and volume discounts. Get in touch for pricing that best serves your business goals.`,
          'contact-sales-body-session-replays': `Our plan options don’t stop at 500K session replays. Higher volume plans are just better served with a human touch and volume discounts. Get in touch for pricing that best serves your business goals.`,
          'startup-purchase-banner-title': `This purchase won’t affect your Startup Plan`,
          'startup-purchase-banner-body': `To ensure continuity of service, any plan purchased will take effect only when your Startup Plan concludes on {{endDate}}.`,
          'startup-purchase-banner-body-without-date': `To ensure continuity of service, any plan purchased will take effect only when your Startup Plan concludes.`,
          'including-sales-tax': `including sales tax`,
          'error-loading-quote': `Error Loading Quote`,
        },
        'wrong-size-modal': {
          header: `Heads up, Your selection may result in additional data fees`,
          events: `{{eventVolume}} events`,
          selected: `Selected`,
          recommended: `Recommended`,
          'recommendations-based-on-previous-usage': `Recommendations are based on previous data usage.`,
          'body-part-1': `Events ingested above your selected volume are billed at {{overageRate}} per 1,000 events. `,
          'body-save-estimate': `Save an estimated {{estimatedSavings}} per month `,
          'body-part-2': `by adjusting your monthly event volume.`,
          'keep-current-selection': `Keep My Current Selection`,
          'adjust-volume': `Adjust My Event Volume`,
        },
        disclaimers: {
          'ingestion-time-billing-switch-1': `Note: Mixpanel bills events at `,
          'ingestion-time-billing-switch-2': `ingestion time`,
          'ingestion-time-billing-switch-3': `. Backfilled historical imports and events later deleted (e.g., GDPR deletions) still count toward your event volume. Please review your volume before importing data to prevent unexpected charges.`,
        },
      },
      'session-replay': {
        'add-filter': `Add Filter`,
        'add-global-rule': `Add Global Rule`,
        'filter-value-placeholder': `Value`,
        'loading-properties': `Loading...`,
        'all-platforms': `All platforms`,
        cancel: `Cancel`,
        'add-conditional-rule': `Add Conditional Rule`,
        'add-rule': `Add Rule`,
        'rule-added': `Rule Added`,
        'recommendations-label': `Sampling Health`,
        'recommendations-subtitle': `Monitor sampling coverage for your most important events and URLs (last 30 days).`,
        'current-project': `Current Project`,
        'no-recommendations': `No event recommendations available for this project.`,
        'recommendations-fetch-error': `Failed to load event recommendations. Please try again later.`,
        'top-events-title': `Top Events`,
        'top-events-subtitle': `We recommend at least 10k replays monthly for your most popular events, so that your reports and playlists always have enough replays to watch even after filtering.`,
        'top-url-views-title': `Top URLs`,
        'top-url-views-subtitle': `We recommend at least 10k replays monthly for your most popular URLs, so that your heatmaps always have enough click data for statistically significant insights, even after filtering.`,
        'enable-url-views-message': `No page views detected. Enable page views to use URL-based sampling rules.`,
        'learn-more': `Learn more`,
        'conditional-sampling-label': `Conditional Sampling ({{amount}})`,
        'conditional-sampling-subtitle': `Capture critical flows with more precision.`,
        'conditional-start-recording': `START RECORDING IN`,
        'conditional-once': `once`,
        'hide-details': `Hide Details`,
        'platform-in-project': `{{platform}} in {{project}}`,
        'view-details': `View Details`,
        'export-csv': `Export CSV`,
        'global-sampling-label': `Global Sampling ({{amount}})`,
        'global-sampling-subtitle': `Capture all flows for general diagnosis.`,
        'loading-usage-data': `Retrieving Usage Data...`,
        'no-plan-data': `No plan data found for session replay.`,
        'usage-info-unavailable': `Usage Information Unavailable: {{error}}`,
        'usage-info-contact-owner': `This information is restricted to organization Owners. Please contact an Owner for access.`,
        'unknown-error': `Unknown error`,
        'no-project': `No project selected`,
        'no-edit-permission': `You do not have permission to edit these settings. Please contact your organization admin.`,
        'no-project-error': `Project not found.`,
        'row-sampling-prefix': `at`,
        'remote-settings-unavailable': `Remote settings are not available on your current plan. Please contact sales to upgrade.`,
        'row-sampling-suffix': `sampling`,
        'sales-button': `Talk to Sales`,
        save: `Save`,
        saving: `Saving...`,
        'property-filter-where': `where`,
        'select-project': `Select project`,
        'select-event': `Select event`,
        'select-platforms': `Select platforms`,
        'select-project-and-platforms': `Select a project and platforms`,
        'select-property': `Select property`,
        'session-replay-historical-usage': `Session Replay Historical Usage`,
        summary: `Summary`,
        total: `Total {{total}}`,
        'total-session-replays': `Total Session Replays`,
        'total-replays-by-project': `Total Session Replays by Project`,
        usage: `Usage`,
        'usage-approaching-limit': `Replay capture is approaching the current plan limit.`,
        'usage-over-limit': `Replay capture is over the current plan limit.`,
        'usage-contact-sales': `Contact Sales`,
        'usage-total': `{{usage}} replays`,
        'usage-term-end': `Period ends {{date}}`,
        'warning-exceeds-limit-title': `Projected Session Replay Capture exceeds your limit`,
        'warning-exceeds-limit-description': `Increase your quota to avoid losing valuable session replays`,
        'replay-count-label': `{{formattedCount}} / 10K replays`,
        'replay-count-label-approximate': `(approximately)`,
        'replay-count-label-approximate-tooltip': `This count is estimated from a shorter time range because the full query took too long to complete.`,
        'replay-count-error': `Could not get number of replays`,
        'settings-save-success': `Settings saved successfully.`,
        advanced: {
          'min-duration-title': `Min Duration`,
          'min-duration-description': `The minimum duration that each replay must be in order to be recorded.`,
          'max-duration-title': `Max Duration`,
          'max-duration-description': `The maximum duration that each replay can be in order to be recorded.`,
          'timeout-title': `Timeout`,
          'timeout-description': `The amount of inactivity before a recording is stopped.`,
          'record-heatmap-data-title': `Record Heatmap Data`,
          'record-heatmap-data-description': `When enabled, Mixpanel will capture click events during replays to populate Heatmaps.`,
          'remote-config-tooltip': `Enable Remote Config`,
          'no-settings': `No advanced settings available for this platform.`,
          'invalid-filters': `One or more property filters have invalid JSON logic. Please contact support for assistance.`,
          minutes: `minutes`,
          seconds: `seconds`,
        },
      },
      users: {
        users: `Users`,
        teams: `Teams`,
        'resend-invite': `Resend invite`,
        'project-role': `Project Role`,
        'granted-by': `Granted By`,
        'add-project': `Add Project`,
        'user-has-org-role-access': `This user has access to all organization projects with the`,
        'role-due-to-org-role': `role due to their organization role`,
        team: `Team`,
        projects: `Projects`,
        'user-not-in-teams': `This user is currently not a part of any teams`,
        'add-to-team': `Add to Team`,
        'all-users-grant': `All Users Grant`,
        'individual-grant': `Individual Grant`,
        discoverability: `Organization Discoverability`,
        'delete-no-perms': `You do not have permission to \n delete a user with the given role`,
        'all-roles': `All Roles`,
        'role-deleted': `Role on project deleted successfully`,
        'role-updated': `Role on project updated successfully`,
        'org-role-updated': `Organization role updated successfully`,
        'org-role-no-change': `No change in organization role`,
        'perm-updated': `Permission updated`,
        'users-added': `Users added successfully`,
        'projects-added': `Projects added successfully`,
        'add-user-to-projects-error': `Error trying to add user to projects: {{error}}`,
        'invitation-resent': `Invitation re-sent successfully`,
        'users-deleted': `Users deleted successfully`,
        'change-role-error': `Error trying to change role: {{error}}`,
        'remove-user-error': `Error trying to remove user: {{error}}`,
        'update-org-role-error': `Error trying to update org role: {{error}}`,
        'team-deleted': `Teams deleted successfully`,
        'removed-from-team': `User successfully removed from team`,
        'remove-team-error': `Error trying to remove team: {{error}}`,
        'add-team-error': `Error trying to add team: {{error}}`,
        'added-to-team_one': `User successfully added to {{count}} team`,
        'added-to-team_other': `User successfully added to {{count}} teams`,
        'export-csv': `Export CSV`,
        'current-users': `Current Users`,
        details: `Details`,
        'invited-users': `Invited Users`,
        'search-current-users': `Search current users`,
        'search-invited-users': `Search invited users`,
        name: `Name`,
        project: `Project`,
        description: `Description`,
        email: `Email`,
        role: `Organization Role`,
        'last-activity': `Last Activity`,
        status: `Status`,
        invite: `Invite Users`,
        'x-users_one': `{{count}} user`,
        'x-users_other': `{{count}} users`,
        'x-teams_one': `{{count}} team`,
        'x-teams_other': `{{count}} teams`,
        'no-users': `No users found for given filters`,
        'create-team': `Create Team`,
        'search-teams': `Search {{organizationName}} teams`,
        'invite-sent': `Invitation sent`,
        'invite-sent_other': `Invitations sent`,
        'admin-org-banner': `This is an Admin Organization. To manage individual user permissions, visit the corresponding organization's settings.`,
      },
      teams: {
        'no-assign-role-perm': `You do not have permission to \n assign the role of at least one \n project in this team`,
        'send-invite-error': `Error trying to send invitation`,
        'permission-change-error': `Error processing the permission change. Try again and please contact support if issue persists.`,
        'add-projects-error': `Error trying to add projects to team: {{errorMessage}}`,
        'add-users-error': `Error trying to add users to team: {{errorMessage}}`,
        'remove-user-error': `Error trying to remove user: {{errorMessage}}`,
        'project-role-changed': `Project role changed`,
        'change-role-error': `Error trying to change role: {{errorMessage}}`,
        'project-removed': `Project removed successfully`,
        'project-remove-error': `Error trying to remove project: {{errorMessage}}`,
        'team-created': `Team created successfully`,
        'create-team-error': `Error trying to create company team: {{errorMessage}}`,
        'delete-teams-error': `Error trying to delete company teams: {{errorMessage}}`,
        'service-account-added': `Service account added successfully`,
        'team-name-updated': `Team name updated successfully`,
        'update-team-name-error': `Error trying to update team name: {{errorMessage}}`,
        'export-downloaded': `Export Downloaded`,
        'x-team': `{{teamName}} Team`,
        'team-name': `Team Name`,
        'project-role': `Project Role`,
        'classified-data': `Classified Data`,
        'add-projects-and-roles': `Add projects and set the team's roles`,
        'no-projects-yet': `No projects yet for this team`,
        'add-projects': `Add Projects`,
        'no-roles-selected': `No Role Selected`,
        'can-view': `Can View`,
        'no-data-view-access': `This team is not granted access to any data view`,
        'give-projects-access': `Give users access to the team's projects`,
        'no-users-yet': `No users yet for this team`,
        'add-users': `Add Users`,
        'service-accounts-x': `Service Accounts {{suffix}}`,
        'last-used': `Last Used`,
        'give-service-accounts-access': `Give service accounts access to the team's project`,
        'no-service-accounts-yet': `No service accounts yet for this team`,
        'add-service-accounts': `Add Service Accounts`,
        'never-used': `never used`,
        'no-teams-for-filters': `No teams found for given filters`,
        'no-team': `-no team-`,
        'team-count': `{{count}} teams`,
        'invite-user': `Invite user`,
        'no-users-found': `No users found`,
        'search-users': `Search users...`,
        'invite-service-account': `Invite service account`,
        'no-service-accounts-found': `No service accounts found`,
        'search-service-accounts': `Search service accounts...`,
        'create-new-team': `Create New Team`,
        name: `Name`,
        'delete-team': `Delete team`,
        'delete-team_plural': `Delete teams`,
        'confirm-delete-team': `Are you sure you want to delete {{teamTitle}}?`,
        'permanent-delete-info': `This team will be permanently deleted, and all users within the team will lose the permissions granted by the deleted team`,
        'permanent-delete-info_plural': `These teams will be permanently deleted, and all users within the team will lose the permissions granted by the deleted teams`,
        'project-search-placeholder': `Select a project`,
      },
      discoverability: {
        'verification-sent': `Verification email sent`,
        'updated-email-domains': `Successfully updated email domains`,
        'header-description': `Help your teammates find your company when signing up with Mixpanel for the first time.`,
        'sso-warning': `This feature does not work for organizations that have SSO enabled.`,
        'email-verified-error': `Your email address must be verified to manage this setting.`,
        'verify-email': `Send Verification Email`,
        'open-org': `Open Organization`,
        'discoverable-description': `Your organization is discoverable and joinable to anyone signing up only with domains you specify.`,
        'discoverable-domain-error': `Open Organization access is not enabled for public domains: {{publicDomains}}`,
        'admin-approval': `Admin Approval`,
        'admin-approval-description': `Your organization is discoverable by anyone signing up with domains you specify, but they request to join this organization. You will receive an email notification allowing you to authorize user access.`,
        'admin-approval-domain-error': `Admin Approval access is not enabled for public domains: {{publicDomains}}`,
        'invite-only': `Invite-Only`,
        'invite-only-description': `Your organization will not be discoverable. New users must be invited by an administrator.`,
        'access-security-hint': `Looking to edit SSO or SCIM? Check out Access Security`,
        'email-domains': `Email Domains`,
        'email-domains-placeholder': `mixpanel.com, mix.com`,
        'verification-email-error': `Unable to send verification email. Try again later.`,
        'update-domains-error': `Failed to update email domains!`,
        'update-discoverability-error': `Failed to update organization discoverability!`,
      },
      'data-privacy': {
        'no-token-warning': `Our records show you have not yet generated a token to use this page. Please go to [Personal Data & Privacy](https://mixpanel.com/settings/account#data-privacy) to generate one, and then refresh this page. If you are still experiencing problems, please [contact support](https://mixpanel.com/get-support).`,
        'job-cancelled': `Job cancelled successfully`,
        'job-cancel-not-allowed': `Job cannot be cancelled at this point`,
        'job-cancel-unknown-error': `Could not cancel job for unknown reasons`,
        'job-cancel-error': `There was an error cancelling the job`,
        'job-details-error': `There was an error fetching the details for this specific Job.`,
        'jobs-fetch-error': `There was an error fetching Compliance Jobs`,
        'token-required-for-details': `Please generate a token to use this page to fetch job details. You can do this from personal data & privacy settings.`,
        'request-sent': `Your request was successfully sent. Your Tracking ID is: {{taskId}}.`,
        'request-error': `There was an error sending your request: {{error}}. If this continues, please contact support.`,
        'load-more': `Load More Rows`,
        'no-more-rows': `No more rows to load`,
        tabs: {
          all: `All Requests`,
          'user-data-export': `User Data Export Requests`,
          'user-deletion': `User Deletion Requests`,
        },
        'request-type': {
          'user-deletion': `User Deletion`,
          'data-export': `Data Export`,
        },
        status: {
          all: `All Statuses`,
          pending: `Pending`,
          running: `Running`,
          revoked: `Revoked`,
          success: `Success`,
          error: `Error`,
          errored: `Errored`,
          expired: `Expired`,
          failure: `Failure`,
        },
        'all-compliance-types': `All Compliance Types`,
        'request-log': `Request Log`,
        'search-requests': `Search requests`,
        'no-requests-found': `No requests found.`,
        'requesting-user': `Requesting User: {{requestingUser}}`,
        'project-id': `Project ID: {{projectId}}`,
        'disclosure-type-label': `Disclosure Type: {{disclosureType}}`,
        'disclosure-type': {
          sources: `Sources`,
          categories: `Categories`,
          data: `Data`,
        },
        'expired-at': `Expired: {{expirationTime}}`,
        export: `Export`,
        expiration: `Expiration:`,
        columns: {
          'task-id': `Task ID`,
          type: `Type`,
          compliance: `Compliance`,
          'user-count': `User Count`,
          'request-date': `Request Date`,
          status: `Status`,
        },
        details: {
          status: `Status`,
          'requesting-user': `Requesting User`,
          'date-requested': `Date Requested`,
          'request-type': `Request Type`,
          compliance: `Compliance`,
          project: `Project`,
          'disclosure-type': `Disclosure Type`,
          'requested-users': `Requested Users`,
          'distinct-id': `Distinct ID`,
        },
      },
      usage: {
        'active-users': `Active Users`,
        'explore-usage': `Explore Usage`,
        'report-usage': `Popular Reports and Boards`,
        range: {
          '12-weeks': `Last 12 weeks`,
          '30-days': `Last 30 days`,
          '6-months': `Last 6 months`,
        },
        'weekly-active': `Weekly Active Users`,
        'most-active': `Most Active Users`,
        'project-active': `Active Users by Project`,
        'most-used-features': `Most Used Features`,
        'most-popular-content': `Most Popular Content in this Project`,
        'popular-by-project': `Most Popular Content by Project`,
        'popular-in-org': `Most Popular Content in this Org`,
        columns: {
          'average-weekly-active': `Average Weekly Active Users`,
          queries: `Queries`,
          'queries-tooltip': `Any user action that retrieved data from Mixpanel. For example, loading an event or property, or opening a report.`,
          name: `Name`,
          type: `Type`,
          'created-by': `Created By`,
          views: `Views`,
          email: `Email`,
        },
        explore: {
          'breakdown-header': `Breakdown:`,
          country: `Country`,
          'count-header': `Count:`,
          'data-view-header': `Data View:`,
          'event-name': `Event Name`,
          'no-breakdown': `None`,
          'overall-comparison': `Compare to Overall`,
          'select-breakdown': `Select Breakdown`,
          'select-data-view': `Select Data View`,
          'total-events': `Total Events`,
          'unique-users': `Unique Users`,
        },
      },
      sidenav: {
        overview: `Overview`,
        users: `Users`,
        'linked-organizations': `Linked Organizations`,
        'overview-tooltip': `Overview`,
        'plan-builder-tooltip': `Plan Builder`,
        'plan-details-and-billing': `Plan Details & Billing`,
        'plan-details-and-billing-tooltip': `View your plan and data usage; update your payment information`,
        'users-and-teams': `Users & Teams`,
        'users-and-teams-tooltip': `Manage users, teams, and organization discoverability`,
        projects: `Projects`,
        'projects-tooltip': `View and manage your Organization's Projects`,
        'service-accounts': `Service Accounts`,
        'service-accounts-tooltip': `Manage programmatic access to your Organization`,
        'access-security': `Access Security`,
        'access-security-tooltip': `Security settings such as 2FA`,
        'identity-merge': `Identity Merge`,
        'identity-merge-tooltip': `Connect pre-authentication activity across platforms and devices`,
        'data-and-privacy': `Data & Privacy`,
        'data-and-privacy-tooltip': `Export or delete user data from your Projects`,
        'mixpanel-usage': `Mixpanel Usage`,
        'mixpanel-usage-tooltip': `View adoption throughout your Organization`,
        'mcp-connections': `MCP Connections`,
        'mcp-connections-tooltip': `Manage connections to external Model Context Protocol (MCP) servers`,
        'custom-api-domains': `Custom API Domains`,
        'custom-api-domains-tooltip': `Configure custom domains for Mixpanel APIs`,
        'ai-settings': `AI Settings`,
        'ai-settings-tooltip': `Configure Mixpanel AI access and context for your organization`,
      },
      'invite-users': {
        'already-invited': `A user with this email is already invited. Go to the Invited Users tab to resend an invite.`,
        'invite-users': `Invite Users`,
        invite: `Invite`,
        'invite-new-users': `Invite New Users`,
        name: `Name`,
        'email-address': `Email Address`,
        'enter-email': `Please enter an email address`,
        'enter-valid-email': `Please enter a valid email address`,
        'org-role': `Organization Role`,
        'select-role': `Select role`,
        'please-select-role': `Please select a role`,
        'select-projects': `Select projects`,
        'please-select-project': `Please select at least one project`,
        'project-role': `Project Role`,
        expires: `Expires`,
        hours: `Hours`,
        days: `Days`,
        months: `Months`,
        years: `Years`,
        never: `Never`,
        specify: `Specify...`,
        teams: `Teams`,
        'team-count_one': `1 team`,
        'team-count_other': `{{count}} teams`,
        'select-teams': `Select teams`,
        'cant-assign-owner-role': `You do not have permission to assign the Project Role of Owner for all the selected projects. Admin will be assigned for {{numProjects}} projects:`,
        'see-all': `See All`,
        'create-service-account': `Create Service Account`,
        'create-service-account-error': `There was an error creating the service account.`,
        'save-secret-info': `Make sure you save the service account's secret. You will never be able to access it again.`,
        username: `Username`,
        secret: `Secret`,
        'invite-to-projects': `Invite to projects`,
        'invite-to-teams': `Invite to teams`,
        'bulk-invite': `Bulk invite`,
        'select-org': `Select organization`,
        'out-of-seats-upsell': `You have run out of seats in your organization!`,
        'upgrade-plan-link': `Click <ctaLink>here</ctaLink> to upgrade your plan.`,
        'contact-admin-to-upgrade': `Contact your organization's billing admin or owner to upgrade your plan.`,
        'add-another-user': `Add another user`,
        'file-name': `File name`,
        'select-csv': `Select CSV File`,
        'please-select-csv': `Please select a CSV file`,
        'csv-format-info': `Please follow the format of our <ctaLink>CSV template</ctaLink>`,
        upload: `Upload`,
        'service-account-created': `Service account created successfully`,
        'not-in-org-email': `This email is not a part of your organization`,
        'error-inviting-users': `Error inviting users: {{errorMessage}}`,
        'email-invite-sent_one': `1 email invitation has been sent.`,
        'email-invite-sent_other': `{{count}} email invitations have been sent.`,
        'user-added-to-org_one': `1 user has been added to the organization.`,
        'user-added-to-org_other': `{{count}} users have been added to the organization.`,
        'user-access-updated_one': `Access updated for 1 existing user.`,
        'user-access-updated_other': `Access updated for {{count}} existing users.`,
        'cant-invite-error': `Can't invite {{email}}, because {{error}}`,
      },
      billing: {
        'billing-payment': `Billing & Payment`,
        'payment-invoices': `Payment & Invoices`,
        'invoice-preferences': `Invoice Preferences`,
        'receipt-info': `Receipt Information`,
        'receipt-info-description': `Add your full business name, VAT number, or address of record here. This information will appear on all your receipts. For your security, do not include any confidential or financial information (like credit card numbers).`,
        'receipt-placeholder': `Enter any additional information you would like to include on your receipts here.`,
        'send-receipts-to': `Send Receipts To`,
        recipients: `Recipients`,
        'recipients-placeholder': `Enter a comma separated list of emails to receive copies of your receipts.`,
        payment: `Payment`,
        'enter-new-card': `Enter a new card to use for your Mixpanel payments`,
        'payment-for': `Your payment for`,
        'was-declined': `was declined.`,
        on: `on`,
        'please-update-payment': `Please update your payment method.`,
        'next-payment-for': `Your next payment is for`,
        expires: `Expires`,
        'rerun-payment': `Rerun Payment`,
        'invoice-history': `Invoice History`,
        'no-invoices': `No Invoices`,
        'applies-to': `Applies To`,
        'initial-value': `Initial Value`,
        'remaining-balance': `Remaining Balance`,
        'valid-through': `Valid Through`,
        'no-credits': `No credits exist for your account`,
        'select-country': `Select Country`,
        'vat-gst-label': `Add your GST/VAT Number`,
        'vat-gst-placeholder': `VAT/GST`,
      },
      tally: {
        'data-usage-for': `Data Usage for {{orgName}}`,
        'how-mtus-calculated': `How are MTUs calculated?`,
        'export-csv': `Export CSV`,
        'event-source': `Event Source`,
        status: `Status`,
        creator: `Creator`,
        'event-volume': `Event Volume`,
        'event-updates': `Event Updates`,
        adjustments: `Adjustments`,
        'non-warehouse': `Non Warehouse`,
        'manual-adjustments': `Manual Adjustments`,
        deleted: `Deleted`,
        active: `Active`,
      },
      'session-replay-overview': `Session Replay Overview`,
      'usage-error-message': `We were unable to load your Mixpanel usage data. If this issue continues please`,
      'contact-support': `contact support`,
      'no-data-message': `No data for this report`,
      'invalid-tab': `Invalid tab option`,
      screenshot: `Screenshot`,
    },
    'user-role': {
      'project-description': {
        owner: `Full access to everything`,
        admin: `Administrative controls, but no project deletion privileges`,
        'billing-admin': `This role does nothing at the project level`,
        messenger: `View and save reports and access messages`,
        analyst: `View and save reports`,
        consumer: `View and create reports`,
      },
      'org-description': {
        owner: `Full access to everything in the organization including all projects`,
        admin: `Access and administrative controls of the organization and its projects. Admins cannot delete projects nor edit billing information`,
        'billing-admin': `View and manage billing for an organization`,
        messenger: `Access to all projects in the organization as a Messenger`,
        analyst: `Access to all projects in the organization as an Analyst`,
        consumer: `Access to all projects in the organization as a Consumer`,
        member: `No special permissions tied to the organization`,
      },
    },
    'audit-logs': `Audit Log`,
    'business-context': {
      save: `Save`,
      saving: `Saving…`,
      'save-success': `Business context saved.`,
      'save-error': `Failed to save business context.`,
      'save-hint-mac': `\u2318+Enter to save`,
      'save-hint': `Ctrl+Enter to save`,
      placeholder: `Describe your business, key metrics, user journey, and any other context that would help Mixpanel Agent understand your data...`,
      'char-count': `{{current}} / 50,000`,
      generate: `Generate`,
      generating: `Generating\u2026`,
      'generate-complete': `Complete`,
      'generate-cancel': `Cancel`,
      'generate-section-title': `Generate with AI`,
      'generate-section-subtitle': `Use Mixpanel AI's web research to generate your organization's business context.`,
      'generate-section-title-project': `Generate with AI`,
      'generate-section-subtitle-project': `Synthesize project-specific context from your organization's information.`,
      'generate-url-label': `Organization URL`,
      'generate-url-placeholder': `https://\u2026`,
      'generate-notes-label': `Project Notes (optional)`,
      'generate-notes-placeholder': `Add notes about team conventions, focus areas, key workflows, or anything specific to this project...`,
      'generate-hint': `Mixpanel AI will use your URL to generate your Business Context File`,
      'generate-hint-project': `Mixpanel AI will create project-specific context. Add optional notes to guide the synthesis.`,
      'generate-notes-char-count': `{{current}} / 2,000`,
      'generate-success': `Business context generated. Review and save.`,
      'generate-warning-truncated': `Your project is large; some details were omitted from the generated context. Review the result carefully before saving.`,
      'generate-error': `Failed to generate business context.`,
      'generate-timeout': `Generation timed out. Please try again.`,
      'generate-replace-title': `Replace Business Context?`,
      'generate-replace-body': `This will replace the current Business Context with newly generated content. You'll be able to review and edit before saving.`,
    },
    'ai-settings-title': `AI Settings`,
    'ai-settings-description': `Configure how AI tools interact with your project data.`,
    'ai-settings-verified-ai-mode-title': `Enable Verified Mode`,
    'ai-settings-verified-ai-mode-description': `When enabled, Mixpanel AI tools and MCP will only use verified data for this project`,
    'ai-settings-no-permission-tooltip': `You need the Manage Event Approval permission to change this setting.`,
    'ai-settings-success-toast': `AI settings updated successfully.`,
    'ai-settings-error-toast': `Failed to update AI settings.`,
    'ai-settings-load-error': `Failed to load AI settings. Please refresh the page to try again.`,
    'ai-settings-skills-rca-title': `Root Cause Analysis`,
    'ai-settings-skills-rca-description': `Add instructions about which properties to look at when Mixpanel does Root Cause Analysis`,
    'ai-settings-skills-rca-added-by': `Added by`,
    'ai-settings-skills-rca-added-by-builtin': `Mixpanel`,
    'ai-settings-skills-rca-trigger': `Trigger`,
    'ai-settings-skills-rca-enable-label': `Enable for this project`,
    'ai-settings-skills-rca-custom-instructions-label': `Custom instructions`,
    'ai-settings-skills-rca-custom-instructions-placeholder': `Example (replace with your own):

# Breakdown Properties

## Default
1. Platform ($os, $browser)
2. Geography (mp_country_code, region)
3. User Segment (plan_type, company_size)

## Metric-Specific
### Conversion (Signup → Activation)
- Focus: signup_method, onboarding_flow_version
- Ignore: $browser`,
    'ai-settings-skills-rca-save': `Save`,
    'ai-settings-skills-rca-cancel': `Cancel`,
    'ai-settings-skills-rca-saving': `Saving...`,
    'ai-settings-skills-rca-save-success': `RCA settings saved.`,
    'ai-settings-skills-rca-save-error': `Failed to save RCA settings.`,
    'ai-settings-skills-rca-save-hint': `Ctrl+Enter to save`,
    'ai-settings-skills-rca-save-hint-mac': `⌘+Enter to save`,
    'ai-settings-skills-rca-char-count': `{{current}} / 5,000`,
    'ai-settings-no-verified-data-warning': `No verified data in this project — verify in Lexicon.`,
    'ai-settings-sections': {
      functionality: {
        title: `Functionality`,
        description: `Control access to Mixpanel AI functionality across the current project.`,
        'description-org': `Control access to Mixpanel AI functionality across all projects in your organization.`,
      },
      'org-context-docs': {
        title: `Organization Context Documents`,
        description: `Manage context documents to improve AI accuracy across all projects.`,
      },
      'company-context': {
        title: `Company Context`,
        description: `Add a markdown file with company context like business information, team structure, key metrics, or terminology.`,
      },
      'project-context-docs': {
        title: `Project Context Documents`,
        description: `Manage context documents to improve AI accuracy for this project.`,
      },
      'project-context': {
        title: `Project Context`,
        description: `Add a markdown file with project context like product information, team structure, key metrics, or terminology.`,
      },
      skills: {
        title: `Skills`,
        description: `Manage skills for this project.`,
      },
    },
    'ai-settings-verified-confirm-modal': {
      title: `Enable Verified Mode for this Project?`,
      description: `Mixpanel's AI tools, including Copilot and MCP, can only access data that was verified in Lexicon. Events, Cohorts, Behaviors, and Metrics that have not been verified will be restricted. This may limit what your team can do with Mixpanel’s AI features. `,
      'confirm-button-label': `Enable for this Project`,
      cancel: `Cancel`,
    },
    'share-settings': {
      'update-success': `Updated share settings successfully.`,
      'update-failure': `Failed to update share settings.`,
    },
    'bump-agreement': {
      included: `Included`,
      'add-on': `Add-on`,
      'non-recurring': `Non-recurring`,
      'usage-based': `Usage Based`,
      grandfathered: `Grandfathered`,
      trial: `Trial`,
      baa: `Business Associate Agreement`,
      unlimited: `Unlimited`,
      'fpa-amortized': `FP&A Amortized`,
      'plan-total': `Plan Total`,
      'before-tax': `before tax`,
      renewal: `Renewal`,
      pending: `Pending`,
      'billing-date': `Billing Date:`,
    },
    'classify-in-lexicon': {
      title: `Classify Data in Lexicon`,
      'description-granted': `You have granted users access to Classified Data, but nothing has been classified yet.`,
      'description-properties': `User Properties and Event Properties can be classified in Lexicon.`,
    },
    'confirm-delete-projects': {
      'lose-access': `Project members will lose access.`,
      'delete-title': `This will delete:`,
      'metrics-note': `(metrics since beginning of month)`,
      'data-retention-policy': `data retention policy`,
    },
    'data-group-update': {
      cancel: `Cancel`,
    },
    'tax-info': {
      'manage-title': `Manage Tax Info`,
      'name-placeholder': `First Last`,
      country: `Country`,
      'street-address': `Street Address`,
      'address-line-2': `Address Line 2 (Optional)`,
      city: `City`,
      'city-or-town': `City or Town`,
      'state-or-province': `State or Province`,
      'postal-code': `Postal Code`,
      yes: `Yes`,
      no: `No`,
      exempt: `Exempt`,
      'vat-placeholder': `XXXXXXXXXXXX`,
      'add-tax-info': `Add Tax Info`,
      cancel: `Cancel`,
      name: `Name`,
      'submit-success': `Tax info submitted successfully`,
      'submit-error': `Tax info submission was not successful`,
    },
    'update-billing-dialog': {
      title: `Update Billing Information`,
    },
    'update-receipt-dialog': {
      title: `Edit Receipt Information`,
      cancel: `Cancel`,
      submit: `Submit`,
      messagePlaceholder: `Add your full business name, VAT number or address of record`,
    },
    'project-overview': {
      'transfer-project': `Transfer Project`,
      'available-organizations': `Available Organizations`,
      'transfer-permission-info': `You may only transfer this project to an organization in which you have permission to create projects.`,
      'some-users': `Some users`,
      'users-via-team': `• Users who only have access via a team`,
      'users-not-in-dest-org': `• Users who do not belong to the destination organization`,
      'may-lose-access': `may lose access to the project!`,
      'reset-project-error': `Error trying to reset project.`,
      'restore-project-error': `Project could not be restored.`,
      'transfer-project-error': `Error trying to transfer project.`,
    },
    'public-dashboards': {
      'by-user': `by {{user}}`,
      'enabled-date': `Enabled {{date}}`,
      'no-edit-access': `You don't have access to edit this board or links`,
      'passcode-protected': `passcode protected`,
    },
    'usage-analytics': {
      'failed-to-load': `We were unable to load your Mixpanel usage data. If this issue continues please `,
      'contact-support': `contact support`,
      'no-data': `No data for this report`,
      'chart-load-error': `Something went wrong`,
    },
    'warehouse-data': {
      'delete-sync': `Delete Sync`,
      'delete-sync-and-data': `Delete Sync and Data`,
      'remaining-deletions': `Your team has <b>{{count}} Data Deletions</b> available this month.`,
      'delete-stop-syncing': `Deleting this import will stop Mixpanel from syncing new data.`,
      'delete-confirm-message': `Are you sure you want to delete this import? Mixpanel will stop syncing data from this table, but data that has already been imported will not be deleted.`,
      'also-delete-data': `Also Delete Existing Data?`,
      'deletion-permanent-7-days': `Deletion will be permanent in 7 days, until then you may undo this action by contacting support.`,
      'historical-property-exempt': `Note that the latest historical property is exempt from deletion.`,
      'user-tables-not-available-b2b': `User tables are not available for B2B projects. Use Group tables instead.`,
      'create-group-key-first': `Create a group key in your [project settings]({{url}}) to import a group table.`,
      'import-deleted': `Import successfully deleted.`,
      'sync-and-data-deleted': `'{{table}}' sync and data have been deleted.`,
      'sync-deleted': `'{{table}}' sync has been deleted.`,
      'delete-import-error': `Error deleting import: {{error}}`,
      'sync-not-found': `This warehouse sync does not exist, or has been deleted.`,
      'syncs-fetch-error': `Error getting current status of warehouse syncs: {{error}}`,
    },
    workspaces: {
      'individual-grant': `Individual Grant`,
      'team-grant': `{{team}} Team`,
      'add-service-accounts-to-view': `Add Service Accounts to Data View`,
      'search-service-accounts': `Search for service accounts`,
      'no-service-account-found': `No service account found`,
      'add-teams-to-view': `Add Teams to Data View`,
      'search-teams': `Search Teams`,
      'no-teams-added': `No teams have been added`,
      'add-users-to-view': `Add Users to Data View`,
      'add-all-org-users-to-view': `Add all current and future organization users to this data view`,
      'search-users': `Search for users`,
      'no-user-found': `No user found`,
      'loading-details': `Loading Data View Details...`,
      'details-title': `Data View Details`,
      name: `Name`,
      description: `Description`,
      'editing-controls': `Editing Controls`,
      'editing-controls-info': `If unrestricted, any Admin can edit data view settings`,
      visibility: `Visibility`,
      'visibility-info': `Public data views are visible and joinable by all project members`,
      'created-by': `Created By`,
      'date-created': `Date Created`,
      'url-access': `URL Access`,
      'no-access-edit': `You don't have access to edit this field`,
      'apply-filters-header': `Apply the following data filters to the data view:`,
      events: `Events`,
      'event-properties': `Event Properties`,
      'user-profile-properties': `User Profile Properties`,
      'user-profile-disclaimer': `Applying a User Profile Property filter will remove the ability to analyze by all Group Identifiers except User`,
      and: `and`,
      'all-users-access': `All Users ({{count}}) within the {{projectName}} project have access to this data view.`,
      'add-user': `Add User`,
      'add-service-account': `Add Service Account`,
      'add-team': `Add Team`,
      'cannot-add-restricted': `Cannot add {{entityType}} in a restricted data view`,
      'no-users-added': `No users have been added to the data view`,
      'no-service-accounts-added': `No service accounts have been added to the data view`,
      'no-teams-added-table': `No teams have been added to the data view`,
    },
  },
  boards: {
    'board-save-error': `Error saving board, please try again`,
    'board-title-save-error': `New report title exceeds the {{maxLength}} limit`,
    'board-url-invalid-error': `URL's board ID was invalid, you were redirected to Home`,
    'board-does-not-exist-error': `Board does not exist or has been deleted, you were redirected to Home.`,
    'board-unexpected-error': `Unexpected error: board failed to load`,
    'dashboard-deleted-error': `Changes could not be saved because the dashboard has been deleted`,
    'rca-daily-limit-reached': `You've hit the daily AI Root Cause Analysis limit for this project. Try again tomorrow.`,
    'rca-rate-limited': `AI Root Cause Analysis is busy right now. Please wait a moment and try again.`,
    'rca-trigger-failed': `Failed to trigger Root Cause Analysis`,
    'rca-poll-failed-default': `Root Cause Analysis failed`,
    'rca-create-board-failed': `Failed to generate the Root Cause Analysis board`,
    'refresh-page-error': `Something went wrong, please refresh the page and try again.`,
    'save-changes-error': `Failed to save board changes: {{errorMessage}}`,
    'report-linked-to-board': `Report has been linked to board "{{boardTitle}}."`,
    'board-moved': `Board has been moved.`,
    'report-moved': `Report has been moved.`,
    empty: {
      body: `Learn [Board Basics]({{basicLink}}) or explore [Example Boards]({{exampleLink}})`,
      header: `Add a report to your board.`,
    },
    header: {
      'title-placeholder': `Untitled`,
      'description-placeholder': `Add description...`,
      saving: `Saving`,
      public: `Public`,
      'public-board-description': `Public Boards are accessible to anyone with a URL. People outside your project will see see a read-only version.`,
      'view-public-links': `View public links for this Board`,
      draft: `Draft`,
      home: `Home`,
      'mixpanel-template': `Mixpanel Template`,
      'save-report-changes': `Save Report Changes`,
      'save-as-new-report': `Save as new report`,
      'agent-draft': `Agent Draft`,
      'copilot-draft-tooltip': `This board was drafted by Mixpanel Agent. Save it to keep it, or discard to remove it from your project.`,
      'copilot-draft-save': `Save draft`,
      'copilot-draft-saving': `Saving…`,
      'copilot-draft-discard': `Discard draft`,
      'copilot-draft-discarding': `Discarding…`,
      'copilot-draft-save-success': `Board saved`,
      'copilot-draft-save-error': `Failed to save draft, please try again`,
      'copilot-draft-discard-confirm-title': `Discard this draft?`,
      'copilot-draft-discard-confirm-body': `This draft and any reports on it will be permanently deleted. This action cannot be undone.`,
      'copilot-draft-discard-confirm-action': `Discard`,
      'copilot-draft-leave-confirm-title': `You have an unsaved Agent draft`,
      'copilot-draft-leave-confirm-body': `Save to keep this board in your project, or discard to permanently delete it.`,
      'copilot-draft-leave-confirm-save': `Save`,
      'copilot-draft-leave-confirm-discard': `Discard`,
      'copilot-draft-leave-confirm-continue': `Continue editing`,
    },
    'empty-hero': {
      'add-report': `Add to Board`,
      title: `Looking a little empty...`,
      description: `Get started by adding a report or some text to your board.`,
      'report-description': `Get started with a report, text, or media`,
      'use-template': `Use a Template`,
      'template-description': `Choose from eight Mixpanel templates`,
      'help-text': `Need help? View our <demoLink>Demo Boards</demoLink>`,
    },
    'board-summary': {
      'add-to-board': `Add to Board`,
      dismiss: `Dismiss`,
      'dismiss-tooltip': `Don't show again on this board`,
      title: `Summary`,
    },
    'rca-feedback': {
      prompt: `How was this Root Cause Analysis?`,
      'thumbs-up-tooltip': `This analysis was helpful`,
      'thumbs-down-tooltip': `This analysis needs improvement`,
      'modal-title': `Tell us about this Root Cause Analysis`,
      'modal-sentiment-heading': `Was this analysis helpful?`,
      'modal-placeholder': `What worked well or what was missing? (optional)`,
    },
    row: {
      'option-tooltip': `Drag to move\nClick to open menu`,
      'option-and-add-tooltip': `Drag to move\nClick to open menu or add content`,
      'full-row-drop-error': `The row is full. Card cannot be dropped.`,
      'combine-exceeds-limit': `Cannot combine these rows—they would have more than {{maxCells}} cells`,
    },
    cards: {
      'drag-tooltip': `Drag to move`,
      'option-tooltip': `Rename, Chart Type, and more...`,
      actions: {
        'add-to-new-board': `Place in New Board`,
        'chart-type': `Chart Type`,
        'copy-url': `Copy URL`,
        'full-screen': `Full Screen`,
        'duplicate-placeholder': `Add report to another board...`,
        'duplicate-disabled-tooltip': `$t(reports.actions.duplicate-disabled-tooltip)`,
        'duplicate-disabled-tooltip-updating': `Duplicating is disabled while the row is updating.`,
        'delete-disabled-tooltip-updating': `Delete is disabled while the row is updating.`,
        'move-disabled-tooltip-updating': `Move is disabled while the row is updating.`,
        'drag-disabled-tooltip-updating': `Dragging is disabled while the board is updating.`,
        delete: `$t(common.delete)`,
        move: `Move to`,
        'move-placeholder': `Move report to another board...`,
        refresh: `Refresh Data`,
        rename: `Rename`,
        reset: `Reset to original`,
        'summarize-with-ai': `Summarize with AI`,
        'use-copilot-instead': `Use Mixpanel Agent to get deeper insights from this playlist`,
      },
      error: {
        'filter-data-group-id-mismatch': `This report does not support filtering by selected group property. Please remove filters to view data.`,
        'user-filter-on-data-group': `This report does not support filtering by user properties or cohorts. Please remove the filters on the board to see the
            data.`,
        'unknown-filter': `This report does not support filtering for unknown reason. Please remove the filters on the board to see the data.`,
      },
      'report-header': {
        'entering-funnel': `$t(query-builder.step-selection.entering-funnel)`,
        'funnel-step-from-to': `Step {{from}}-{{to}}`,
        'multi-step-funnel': `{{count}}-step Funnel`,
        'multi-step-flow': `{{count}}-step Flow`,
        'total-count': `Total`,
        'unique-users': `Unique`,
      },
      playlist: {
        title: `Playlist`,
        'recording-count-total': `{{total}} recordings`,
        'recording-count-new': `{{new}} new today`,
      },
      'time-range': `Time range`,
      heatmap: `Heatmap`,
      'heatmap-collection': `Heatmap Collection`,
      'expand-to-view': `Expand to View`,
      'visualization-not-supported': `This visualization is not supported.`,
      benchmark: {
        benchmark: `Benchmark`,
        you: `You*`,
      },
      media: {
        'hosted-video-placeholder': `Hosted Video goes here.`,
        'embedded-image-placeholder': `Embeded Image goes here.`,
      },
      'metric-with-trend': {
        'add-report': `Add Report`,
        'change-report': `Change Report`,
        'insights-reports': `Insights Reports`,
        'suggested-reports': `Suggested Reports`,
        'insights-only-tooltip': `Currently Home can only support Insights reports`,
        'kpi-description': `You can power your Core KPIs with the following reports. {{action}} the KPIs will save for only you.`,
        adding: `Adding`,
        changing: `Changing`,
      },
      text: {
        'remove-link': `Remove Link`,
        link: `Link`,
      },
    },
    confirmation: {
      rhs: {
        title: `Discard Board?`,
        description: `You haven't finished setting up your {{templateType}} for Mixpanel Template. Would you like to discard your template Board or continue editing?`,
        'confirm-btn-label': `Discard`,
        'cancel-btn-label': `Continue Editing`,
      },
      delete: {
        title: `Delete Board`,
        'confirm-btn-label': `Delete`,
        'msg-header': `Are you sure you want to delete the "{{boardTitle}}" board? Please note that this action will also delete any reports on this board.`,
        'msg-has-child': `However, any child boards will be preserved and moved to the root level.`,
        'msg-footer': `This action is permanent and cannot be undone.`,
        'permission-staff': `You do not have granted access to this board. However, as a staff user you may proceed.`,
        'permission-owner': `You do not have granted access to this board. However, as a project Owner you may proceed.`,
      },
    },
    actions: {
      duplicate: `Duplicate`,
      'duplicate-row': `Duplicate row`,
      'duplicate-row-disabled-tooltip': `This row contains cards that cannot be duplicated`,
      'linked-copy': `Create Linked Copy`,
      move: `Move to`,
      'move-top': `Move to top`,
      'move-bottom': `Move to bottom`,
      'copy-link': `Copy link to row`,
      export: `Export`,
      'to-email': `to Email`,
      'to-slack': `to Slack`,
      'tv-mode-enter': `Enter TV Mode`,
      'tv-mode-exit': `Exit TV Mode`,
      manage: `Manage`,
      subscribe: `Subscribe`,
      filter: `Filter`,
      'show-filter': `Show Filter`,
      'hide-filter': `Hide Filter`,
      // Same as above, just added temporarily until pinned filters is GA
      'filter-breakdown': `Filter/Breakdown`,
      'show-filter-breakdown': `Show Filter/Breakdown`,
      'hide-filter-breakdown': `Hide Filter/Breakdown`,
      favorite: `Favorite`,
      favorited: `Favorited`,
      unpin: `Unpin for Project`,
      pin: `Pin for Project`,
      'create-new-board': `Create New Board`,
      'board-placeholder': `Select a board`,
      'create-new-project': `Create a New Project`,
      'project-placeholder': `Select Destination Project`,
    },
    tooltip: {
      'save-to-board': `Saving to Board makes changes visible to everyone.`,
    },
    'tooltip-tour': {
      subscribe: {
        title: `Subscribe to this Board`,
        description: `Mixpanel can push important data to you! It takes just a few seconds to set up recurring email or Slack updates on this board.`,
        confirm: `Subscribe`,
      },
    },
    content: {
      add: `Add`,
      'add-content': `Add content`,
      'badge-beta': `beta`,
      'insert-below': `Insert content below`,
      'insert-row': `Add content to row`,
      'insert-row-below': `Add content to row below`,
      'insert-between': `Insert content between`,
      'row-full-tooltip': `The row is full. Content cannot be added.`,
      spark: `Launch Spark`,
      'spark-sublabel': `Use natural language to generate reports with AI`,
      'existing-report': `Existing Report`,
      'existing-report-sublabel': `Start from a previously saved report`,
      insights: `Insights Report`,
      'insights-sublabel': `Explore product usage in detail`,
      funnels: `Funnels Report`,
      'funnels-sublabel': `Analyze conversion and drop-off`,
      flows: `Flows Report`,
      'flows-sublabel': `Understand steps in the user journey`,
      heatmaps: `Heatmap`,
      'heatmaps-sublabel': `Explore interactions by users`,
      playlist: `Playlist`,
      'playlist-sublabel': `Curate a collection of session replays`,
      retention: `Retention Report`,
      'retention-sublabel': `Measure product stickiness`,
      text: `Text`,
      'text-sublabel': `Use text to tell a story`,
      'text-placeholder': `Write about your data…`,
      media: `Media`,
      'media-sublabel': `Upload an image or embed a video`,
      'no-data': `No data for this report`,
      'error-loading': `Error loading report`,
      'chart-has-error': `This chart has an error`,
      'incomplete-data': `Incomplete data`,
    },
    'template-setup': {
      title: `Use a Template`,
      description: `Mixpanel uses your event data to generate a number of out-of-the-box, fully customizable reports that make it easy for anyone to get started. Have a template that you'd like to see?`,
      'alternative-description': `Use your event data to generate a number of out-of-the-box, fully customizable reports.`,
      toggle: {
        open: `Browse all Templates`,
        close: `Collapse Templates`,
      },
      'open-template': `Open Template`,
      'leave-feedback': `Leave us feedback`,
      'view-preview': `View Preview`,
      'view-with-demo-data': `View with Demo Data`,
      creating: `Creating`,
      'start-template': `Start from a Template`,
      'num-reports': `{{count}} Reports`,
      kpi: {
        title: `Company KPIs`,
        description: `Nine reports based on core metrics applicable to most products: engagement, retention, and growth.`,
        'alternative-description': `Track core growth metrics applicable to most products`,
      },
      'feature-launch': {
        title: `Feature Launch`,
        description: `Fourteen reports to help you evaluate adoption, retention and overall product impact of your new feature.`,
        'alternative-description': `Evaluate adoption, retention, and impact of your feature`,
      },
      lifecycle: {
        title: `Lifecycle Cohort Analysis`,
        description: `Ten reports to help you breakdown different users and their journey using your product.`,
        'alternative-description': `Breakdown different users’ journeys using your product`,
      },
      ecommerce: {
        title: `Ecommerce`,
        description: `Twenty five reports to provide common strategic plans and goals of an ecommerce business.`,
        'alternative-description': `Evaluate your sales and segment by cart properties`,
      },
      marketing: {
        title: `Marketing KPIs`,
        description: `Fifteen reports to help you optimize your marketing strategy.`,
        'alternative-description': `Optimize your marketing strategy`,
      },
      'web-analytics': {
        title: `Web Analytics`,
        description: `Sixteen reports to evaluate core user, session and page metrics.`,
        'alternative-description': `Evaluate core user, session, and page metrics`,
      },
      b2b: {
        title: `B2B Account KPIs`,
        description: `Eight reports to help you understand your accounts ARR and logo mix.`,
      },
      'saas-company-kpi': {
        title: `SaaS Company KPIs`,
        description: `Sixteen reports to help you evaluate your overall SaaS business health`,
        'alternative-description': `Evaluate your overall SaaS business health`,
      },
      'ai-company-kpi': {
        title: `AI Company KPIs`,
        description: `Nineteen reports to help you evaluate your overall AI business health`,
        'alternative-description': `Evaluate your overall AI business health`,
      },
    },
    rhs: {
      'see-all-templates': `See all templates`,
      setup: {
        header: `Set Up Board`,
        'two-events': `Add two events to get started with this template.`,
        'two-events-details': `Add two events and some details about your launch to get started with this template.`,
        'one-event': `Add just one event to get started with this template.`,
        'some-events': `Add some events to get started with this template.`,
        saas: `Select Group Profile to get started with this template.`,
        ecommerce: `This template involves both events and properties to use.`,
        'why-events-link': `[Why these events?]({{urlLink}})`,
        'value-moment-link': `Learn about your [value moment]({{urlLink}}).`,
        'learn-more-link': `[Learn More]({{urlLink}})`,
      },
      'all-events': `All Events`,
      'starter-board-title': `All Events`,
      'starter-board-description': `This board will automatically begin showing your data once events are sent to Mixpanel`,
      'select-date': `Select Date`,
      'add-event': `Add Event`,
      'add-property': `Add Property`,
      'event-list-property-placeholder': `Select Events and Properties`,
      'value-moment-tooltip': `For a social media platform, it could be creating a post; for a streaming service, it's watching a video; for e-commerce, it might be completing a purchase. [Learn More](https://docs.mixpanel.com/docs/what-to-track)`,
      'reminder-tooltip': `Enter in your events on the set up panel and click Save in order to view these reports.`,
      tutorial: {
        'info-text': `Need help setting up this template? Try out a walkthrough.`,
        'show-me': `Show me how`,
        'not-now': `Not now`,
        completed: `Completed`,
        'progress-count': `Step {{currentStep}} of {{totalStep}}`,
      },
      kpi: {
        label: {
          'sign-up': `Sign Up`,
          'value-moment': `Value Moment`,
        },
        tooltip: {
          'sign-up': `This is the event where a new user—free or paid—is acquired by creating a new account. Your event might be called "Account created" instead of "Sign up." Don't have this type of event? Try using "Session start" or a similar event. [Learn More]({{urlLink}})`,
        },
      },
      benchmark: {
        label: {
          'sign-up': `Sign Up`,
          'value-moment': `Value Moment`,
          industry: `Industry`,
        },
      },
      'product-launch': {
        label: {
          'feature-name': `Feature Name`,
          'launch-date': `Launch Date`,
          'new-feature': `New Feature`,
          'feature-value-moment': `Feature Value Moment`,
        },
        tooltip: {
          'new-feature': `This is the event that captures the adoption of your new feature. For adding reviews to a streaming service, the event could be publishing a review.`,
        },
        placeholder: {
          'feature-name': `Ex. Reactions on Comments`,
        },
      },
      'life-cycle': {
        label: {
          'value-moment': `Value Moment`,
        },
      },
      'marketing-kpi': {
        label: {
          activation: `Activation Event`,
          conversion: `Conversion Event`,
        },
        tooltip: {
          activation: `This is the event that marks a user as "active" for you. This could be viewing a webpage or entering an email address, or opening an app.`,
          conversion: `This is the desired user behavior. For a social media platform, this could be creating a post, for e-commerce it might be completing a purchase.`,
        },
        'info-text': {
          activation: `Will default to "Any Event" if none selected`,
        },
      },
      ecommerce: {
        label: {
          'purchase-cart-price': `Purchase, Cart, and Price`,
          'add-to-cart-cart-category': `Add to Cart, Cart, and Category`,
          purchase: `Purchase`,
          'add-to-cart': `Add to Cart`,
          'product-viewed': `Product Viewed`,
          'cart-property': `Cart Property`,
          cart: `Cart Property`,
          price: `Price Property`,
          category: `Category Property`,
          'sign-up': `Sign Up`,
          'ad-spend': `Ad Data and Cost`,
          'ad-cost-property': `Ad Cost Property`,
        },
        tooltip: {
          'purchase-cart-price': dedent(
            `Adding a purchase event, cart property, and price property helps us uncover user purchase behaviors.\n
            In order to use this template we require a cart property with your purchase event. If you don't have one set up, learn how to set it up [here]({{urlLink}}).`,
          ),
          'add-to-cart-cart-category': dedent(
            `Adding an Add to Cart event, cart property, and category property helps us uncover user purchase behaviors.\n
            In order to use this template we require a cart property with your Add to Cart event. If you don't have one set up, learn how to set it up [here]({{urlLink}}).`,
          ),
          'product-viewed': `The Product Viewed Event helps us see users behavior across the entire purchasing funnel. [Learn More]({{urlLink}})`,
          'sign-up': `This is the event where a new user—free or paid—is acquired by creating a new account. Your event might be called "Account created" instead of "Sign up." Don't have this type of event? Try using "Session start" or a similar event. [Learn More]({{urlLink}})`,
          'ad-spend': `Event and Property that tracks advertising spend for a day. Used for calculating ROI.`,
        },
        tutorial: {
          'sign-up': `First, add your **$t(boards.rhs.ecommerce.label.sign-up) Event** . This is the moment a user first registers on your website.`,
          purchase: `Now add your **$t(boards.rhs.ecommerce.label.purchase) Event** . This is the moment a user transacts on your website.`,
          cart: `Now add your **$t(boards.rhs.ecommerce.label.cart)**  \n*Example names: Shopping bag, Cart, Basket, Checkout*`,
          price: `Now add your **$t(boards.rhs.ecommerce.label.price)**. This is a numeric property that represents how much an individual item costs.`,
          'add-to-cart': `Now add your **$t(boards.rhs.ecommerce.label.add-to-cart) Event**. This is the action of a user moving a product into their shopping cart or bag.`,
          category: `Now add your **$t(boards.rhs.ecommerce.label.category)**. This property can be anything to help further segment your products.  \n*Ex. "Category" "Brand" "Type"*`,
          'product-viewed': `Now add your **$t(boards.rhs.ecommerce.label.product-viewed) Event**. This represents when a user views a specific item on your app or site.`,
          'ad-spend': `Now add your **$t(boards.rhs.ecommerce.label.ad-spend) Event**. This represents when a money is spent for advirtising purposes.`,
          'ad-cost': `Lastly, add your **$t(boards.rhs.ecommerce.label.ad-cost) Property**. This is the property within the **$t(boards.rhs.ecommerce.label.ad-spend) Event** that is the amount of money spent.`,
          finished: `You've completed the E-commerce template walkthrough! &#127881;`,
        },
        'info-text': {
          'ad-spend': `How do you track your ad spend?`,
          'ad-cost-property': `The Ad Cost property is associated with the amount of money spent on advertising.`,
        },
      },
      'web-analytics': {
        label: {
          'page-loaded': `Page Loaded`,
          'page-name': `Page Name`,
        },
        tooltip: {
          'page-loaded': `This is how Page Views are tracked on your website.`,
          'page-name': `This is how Page Names are tracked on your website.`,
        },
        'info-text': {
          'page-loaded': `What do you consider a page view?`,
          'page-name': `How do you identify the name of a page?`,
        },
      },
      'saas-company-kpi': {
        label: {
          mrr: `Monthly recurring revenue`,
          'plan-type': `Plan Type`,
          segment: `Segment`,
        },
      },
      'ai-company-kpi': {
        label: {
          'llm-prompt-event-text': `AI Prompt Sent and Prompt Text`,
          'llm-prompt-text': `Prompt Text`,
          'llm-response-event': `AI Response Sent`,
          'llm-tokens-used': `API Tokens Used`,
          'api-cost': `API Cost`,
          'llm-response-property': `Property on the AI Response Sent event`,
          'response-time': `API Response Time`,
          'tokens-used': `API Tokens Used`,
          'start-agent': `Launch AI`,
          'agent-dismissed': `AI Dismissed`,
          'api-error': `API Error`,
          conversion: `Conversion Event`,
          'user-feedback-event': `User Feedback`,
          'user-feedback-property': `Property on the User Feedback event`,
          'user-feedback-sentiment': `User Feedback Sentiment`,
          'user-feedback-text': `User Feedback Text`,
        },
        tooltip: {
          'llm-prompt-event-text': dedent(
            `Tracks when a user sends a message to your AI feature. Capturing this event and its text property helps you analyze engagement, understand user intent, and measure AI conversion funnels.\n
            Need help setting it up? [Learn More]({{urlLink}}).`,
          ),
          'llm-response-event': dedent(
            `Tracks when your AI feature returns a response. Helps you understand AI output volume and user interaction patterns. This may be the same event as AI Prompt Sent.\n
            Need help setting it up? [Learn More]({{urlLink}}).`,
          ),
          'llm-response-cost': `Tracks the cost of each LLM API call. Add this property to your AI Response event to monitor usage costs and optimize for efficiency. [Learn More]({{urlLink}})`,
          'llm-response-time': `Tracks how long the LLM takes to respond. Add this property to your AI response event to monitor latency and improve performance.`,
          'llm-response-tokens': `Tracks the number of tokens used by the API call. Add this property to your AI response event to monitor usage and improve costs.`,
          'user-feedback-event-sentiment-text': dedent(
            `Tracks when a user leaves feedback on your AI feature. Use this event and text property to capture user sentiment and gather insights for improvement.\n
            Need help setting it up? [Learn More]({{urlLink}}).`,
          ),
          'user-feedback-text': `Property on the User Feedback event that captures the user's feedback on their interaction with the AI product in a free-text format.`,
          'user-feedback-sentiment': dedent(
            `Property on the User Feedback event that captures how the user felt about their interaction with the AI product.\n
            Use values like Positive, Negative, Neutral, or Thumbs Up/Down to track sentiment at scale.`,
          ),
          'sign-up': dedent(
            `Tracks when a new user—free or paid—is acquired by creating a new account. Your event might be called “Account Created” instead of “Sign Up.”\n
            Don't have this type of event? Try “Session Start” or a similar event. [Learn More]({{urlLink}})`,
          ),
          'start-agent': dedent(
            `Tracks when a user launches an AI-powered feature in your product.\n
            Use this event to measure AI engagement—identify power users, understand feature adoption, and analyze retention patterns based on AI usage. [Learn More]({{urlLink}})`,
          ),
          'api-error': `Tracks when a user encounters an error while using an AI feature. Helps monitor failure rates and diagnose reliability issues. [Learn More]({{urlLink}})`,
          'agent-dismissed': `Tracks when a user closes an AI feature. Helps you understand disengagement points and improve feature adoption. [Learn More]({{urlLink}})`,
          conversion: `For a social media platform, it could be creating a post; for a streaming service, it's watching a video; for e-commerce, it might be completing a purchase. [Learn More](https://docs.mixpanel.com/docs/what-to-track)`,
        },
      },
    },
    selector: {
      project: `Project`,
      'this-project': `This Project`,
      'no-projects': `No other projects`,
      'no-projects-found': `No projects match your search`,
      'nesting-allowed-plan': `Nesting boards within a project is available in the Enterprise Plan. [See Pricing Page](/pricing/)`,
      'no-boards': `You do not have any boards yet.`,
      'no-boards-found': `No boards found.`,
      'new-board-title': `New board title`,
      'move-out-all': `Move out of all boards`,
      'move-to-your-boards': `Move to **Your Boards**`,
      'create-new': `Create New Board`,
      manage: `Manage Boards`,
      'no-move-access': `You do not have Move to access in the current project. Switch projects to move the board`,
      'nested-disabled': `Nested boards is not enabled for the selected project`,
      'editor-access-needed': `You don't have editor access on this board`,
      'edit-access-needed': `You don't have edit access on this board`,
      'nesting-limit-restriction': `Can't move to this board due to nesting depth limits`,
      'circular-nesting-restriction': `Can't move to this board because it is a descendant`,
      'create-linked-copy-title': `Edit once, update everywhere`,
      'create-linked-copy-description': `Changes made to linked reports are automatically reflected across all of them`,
    },
    'query-bar': {
      'breakdown-by': `Breakdown by`,
      'any-value': `Any`,
      'filter-values-unused': `Selected values will not appear on your filter.`,
      'filter-settings': `Filter Settings`,
      'filter-limit-values': `Limit filter values to selection above`,
      'filter-default-empty': `Start filter with nothing selected`,
    },
    'ai-summary-panel': {
      title: `{{contentType}} AI Summary`,
      error: `Unable to generate summary.`,
      loading: `Analyzing your {{contentType}}. This may take a minute...`,
    },
    components: {
      'benchmark-banner': {
        header: `2024 Benchmarks`,
        body: `This template currently includes metrics from our 2024 Benchmarks campaign for you to view side by side with your own data. Download our official PDF to view even more insights. `,
        'download-pdf': `Download PDF`,
      },
    },
    'digest-mode': {
      'unexpected-failure': `Unexpected load failure`,
    },
    'manage-digests': {
      'manage-subscription': `Manage Subscription`,
      'fetch-subscriptions-error': `Failed to fetch subscriptions.`,
    },
    'public-mode': {
      'passcode-protected': `Passcode Protected`,
      'enter-passcode': `Enter the Passcode`,
      'passcode-placeholder': `e.g. ••••••••`,
      'access-button': `Access Public Board`,
      'no-reports': `There are no Reports on this Board`,
      'invalid-board': `Error: Not a valid public board`,
      'powered-by': `Powered By`,
      'report-abuse': {
        button: `Report abuse`,
        title: `Report abuse`,
        description: `Let us know why you're reporting this public board. Our team will review your report.`,
        'details-label': `Details`,
        'details-placeholder': `Please describe the issue`,
        'email-label': `Email`,
        'email-placeholder': `you@example.com`,
        'email-invalid': `Please enter a valid email address`,
        submit: `Submit report`,
        cancel: `Cancel`,
        'success-title': `Thank you for your report`,
        'success-message': `Our team will review this public board.`,
        'error-message': `Something went wrong submitting your report. Please try again.`,
        'rate-limit-message': `You've reported too many times. Please try again later.`,
      },
    },
    'starter-board': {
      'browse-templates': `Browse our templates`,
      feedback: {
        'thank-you': `Thank you! Please tell us more.`,
        rate: `How would you rate the 🌱 Starter Board?`,
        poor: `Poor`,
        excellent: `Excellent`,
      },
    },
  },
  canvases: {
    actions: {
      delete: {
        label: `Delete`,
        success: `$t(mixology.metric-tree) deleted successfully`,
        fail: `Failed to delete $t(mixology.metric-tree)`,
      },
      duplicate: {
        label: `Duplicate`,
        success: `$t(mixology.metric-tree) duplicated successfully`,
        fail: `Failed to duplicate $t(mixology.metric-tree)`,
      },
      export: {
        label: `Export`,
        success: `$t(mixology.metric-tree) exported successfully`,
        load: `Exporting $t(mixology.metric-tree)...`,
        warn: `$t(mixology.metric-tree) should be fully loaded before exporting`,
        fail: `Failed to export $t(mixology.metric-tree)`,
      },
      'sample-metric-tree': {
        label: `Sample Metric Tree`,
      },
      favorite: {
        label: `Favorite`,
        success: `$t(mixology.metric-tree) favorited successfully.`,
        fail: `Failed to add $t(mixology.metric-tree) to favorites.`,
      },
      unfavorite: {
        label: `Favorited`,
        success: `$t(mixology.metric-tree) unfavorited successfully.`,
        fail: `Failed to remove $t(mixology.metric-tree) from favorites.`,
      },
      pin: {
        label: `Pin for Project`,
        success: `$t(mixology.metric-tree) pinned for project successfully.`,
        fail: `Failed to add $t(mixology.metric-tree) to project pins.`,
      },
      unpin: {
        label: `Unpin for Project`,
        success: `$t(mixology.metric-tree) unpinned for project successfully.`,
        fail: `Failed to remove $t(mixology.metric-tree) from project pins.`,
      },
      filter: `Filter`,
      'show-hide': `Show/Hide`,
      'show-metric-value': `Show Metric Value`,
      'show-sparkline': `Show Metric Chart`,
      'show-correlation': `Show Correlation`,
      'show-multiplayer-cursors': `Show Multiplayer Cursors`,
    },
    edges: {
      'connect-limit': `Cannot connect more than 100 edges`,
      correlation: {
        'strong-positive': `+{{score}} Strong`,
        'moderate-positive': `+{{score}} Moderate`,
        'weak-positive': `+{{score}} Weak`,
        'strong-negative': `{{score}} Strong`,
        'moderate-negative': `{{score}} Moderate`,
        'weak-negative': `{{score}} Weak`,
        unknown: `Unknown`,
      },
    },
    tooltip: {
      'save-to-canvas': `Saving to $t(mixology.metric-tree) makes changes visible to everyone.`,
      'hub-pin': `Pinned`,
      'hub-logbook': `Logbook`,
      'hub-experiments': `Experiments`,
      'hub-verified': `*Verified*\non {{date}} by\n{{user}}`,
      'hub-avatar-no-owner': `No owner`,
      'hub-avatar-not-save-metric': `Save this Metric to assign an owner`,
    },
    panels: {
      'filter-panel': {
        now: `Now`,
        'as-of-now': `As of now`,
        'as-of-date': `As of date...`,
      },
      'tool-bar': {
        disabled: {
          tooltip: `This tool is not available with view only permission`,
        },
        hand: {
          tooltip: `Hand Tool`,
        },
        select: {
          tooltip: `Select Tool`,
        },
        metric: {
          text: `Metric`,
          tooltip: `Metric Tool`,
        },
        strategy: {
          text: `Strategy`,
          tooltip: `Strategy Tool`,
        },
        note: {
          text: `Note`,
          tooltip: `Note Tool`,
        },
        text: {
          text: `Text`,
          tooltip: `Text Tool`,
        },
        media: {
          text: `Media`,
          tooltip: `Media Tool`,
        },
      },
      'sidebar-panel': {
        context: `Context`,
        query: `Query`,
        settings: `Settings`,
        goals: {
          title: `Goal`,
          'title-single': `Active Goal`,
          'title-plural': `Active Goals`,
          hidden: `hidden`,
          label: `Label`,
          Direction: {
            'greater-than': `Greater than`,
            'lower-than': `Lower than`,
          },
          Value: `Value`,
          'start-date': `Start Date`,
          'end-date': `End Date`,
          'add-goal': `Add a Goal`,
          'view-all': `View All Goals`,
          'disabled-no-permission': `Saved metric owners can manage goals`,
          'disabled-unsaved-changes': `You have unsaved changes for this saved metric`,
          errors: {
            'target-date-past': `Target date needs to be in the future.`,
          },
        },
        chart: {
          title: `Chart`,
          'preferred-direction': `Preferred Direction`,
          'preferred-direction-info': `Set whether rising values are positive or negative. This affects green and red coloring in comparisons.`,
          'direction-up': `Up`,
          'direction-down': `Down`,
        },
        logbook: {
          'title-single': `Logbook Entry`,
          'title-plural': `Logbook Entries`,
          'view-all': `View All Logbook Entries`,
        },
        experiments: {
          'section-title-single': `Experiment`,
          'section-title-plural': `Experiments`,
          'overlay-title': `Experiments`,
          'overlay-draft': `Draft`,
          'overlay-active': `Active`,
          'overlay-completed': `completed`,
          users: `users`,
          add: `Add Experiments`,
          'empty-title': `No Experiments`,
          'empty-desc': `Experiments that are linked to this metric`,
          'view-all': `View All Experiments`,
        },
        pinned: {
          'view-all': `View All Pinned Content`,
        },
      },
      'hub-panel': {
        title: `Metric Context`,
        description: `Context is a hub for all of the learnings related to this metric`,
        logbook: `Logbook`,
        pinned: `Pinned Content`,
        'add-logbook': `Add Logbook`,
        'edit-logbook': `Edit Logbook`,
        'logbook-search-placeholder': `Search logbook`,
        'logbook-no-results': `No Logbook Entries`,
        'logbook-no-results-description': `Logs are insights or hypotheses related to this Metric`,
        'logbook-no-results-button-text': `Create Logbook`,
        'pinned-search-placeholder': `Search pinned`,
        'pinned-no-results': `No Pinned Content`,
        'pinned-no-results-description': `Pin relevant boards and reports to this Metric`,
        'pinned-no-results-button-text': `Add Pin`,
        'owned-by': `Owned By`,
        'inline-empty-state': {
          logbook: {
            description: `Keep track of insights or hypotheses`,
            button: `Add Logbook Entry`,
          },
          pinned: {
            description: `Pin relevant boards and reports`,
            button: `Add Pinned Content`,
          },
          experiments: {
            description: `Experiments that are linked to this metric`,
          },
        },
      },
      'settings-panel': {
        visualization: `Visualization`,
        direction: {
          label: `Target Direction`,
          up: `Up`,
          down: `Down`,
        },
      },
      'time-ranges-panel': {
        'ranges-and-comparisons': `Ranges and comparisons`,
        title: `Title`,
        'time-range': `Time Range`,
        'compare-to': `Compare to`,
        interval: `Interval`,
        'add-time-range': `Add Time Range`,
        'apply-to-all-metrics': `Apply to All Metrics`,
      },
      'template-picker': {
        title: `Not sure where to start?`,
        description: `A template can help you map out an outline of your tree.`,
        'view-example': `View Example`,
        'learn-more': `Learn More`,
        kpi: {
          title: `KPI Tree`,
          description: `Maps metrics to goals, show how KPIs drive biz objectives`,
        },
        'north-star': {
          title: `North Star Framework`,
          description: `Aligns teams to one key metric, a leading indicator for outputs`,
        },
        'ai-template-button': `Generate with AI`,
        'ai-template-modal': {
          title: `Build with AI`,
          'title-selected': `Create {{templateName}}`,
          description: `You may optionally input custom instructions to help define your metric tree.`,
          placeholder: `Enter custom instructions (optional)`,
          cancel: `Cancel`,
          submit: `Submit`,
          close: `Close`,
        },
      },
    },
    'delete-modal': {
      'metrics-node': `metrics node`,
      'strategy-node': `strategy node`,
      'title-single': `Delete Node`,
      'description-single': `Are you sure you want to delete "{{nodeTitle}}" {{nodeType}}?`,
      'description-single-no-title': `Are you sure you want to delete this {{nodeType}}?`,
      'title-multiple': `Delete Nodes`,
      'description-multiple': `Are you sure you want to delete the selected nodes?`,
      'confirm-button': `Delete`,
    },
    'delete-goal-modal': {
      description: `Are you sure you want to delete {{goalLabel}}?`,
      title: `Delete Goal`,
      'confirm-button': `Delete`,
    },
    'metrics-node': {
      'delete-modal-title': `Delete Metrics Node`,
      'delete-modal-description': `Are you sure you want to delete "{{nodeTitle}}" metrics node?`,
      'delete-modal-description-empty': `Are you sure you want to delete this metrics node?`,
      'delete-modal-confirm-button': `Delete`,
      'update-owner-modal-title': `Update Owner`,
      'update-owner-modal-description': `Are you sure you want to update the owner for this metric?`,
      'update-owner-modal-confirm-button': `Save`,
      'update-owner-success': `Owner updated successfully`,
      'update-owner-failure': `Owner update failed`,
      'update-owner-loading': `Updating owner...`,
    },
    goal: {
      status: {
        at_risk: `At Risk`,
        on_track: `On Track`,
        achieved: `Achieved`,
        ahead: `Ahead`,
        missed: `Missed`,
        unknown: `Unknown`,
      },
      section: {
        progress: `PROGRESS`,
        settings: `SETTINGS`,
      },
      content: {
        'empty-state': {
          title: `No Goals`,
        },
        'inline-empty-state': {
          description: `Track your progress toward targets`,
          'dirty-description': `You have unsaved changes for this saved metric. Discard or push changes to view hidden goals.`,
          'global-filter-description': `Remove global filters to view goals.`,
          button: `Add Goal`,
        },
        sections: {
          active: `ACTIVE`,
          completed: `COMPLETED`,
          future: `FUTURE`,
        },
        'open-goals-section': `Open goals section`,
        'delete-success': `Goal deleted successfully`,
        'delete-error': `Failed to delete goal. Please try again.`,
      },
      form: {
        'edit-goal': `Edit Goal`,
        'untitled-goal': `Untitled Goal`,
        label: {
          text: `Label`,
          placeholder: `Goal label`,
        },
        'date-range': {
          text: `Date Range`,
          placeholder: `Select date range`,
        },
        'baseline-value': {
          text: `Baseline Value`,
          placeholder: `Enter baseline value`,
          loading: `loading...`,
          error: `something went wrong, please try again.`,
          'no-data': `No data`,
          helper: `updates based on date range`,
        },
        'target-type': {
          text: `Target Type`,
          absolute: `Absolute value`,
          relative: `Relative change`,
        },
        'target-value': {
          text: `Target Value`,
          placeholder: `Enter absolute value`,
        },
        'target-change': {
          text: `Target Change`,
          placeholder: `Enter relative change`,
        },
        direction: {
          text: `Direction`,
          'greater-than': `Greater than`,
          'less-than': `Less than`,
        },
        'time-interval': `Time Interval`,
        buttons: {
          saving: `Saving...`,
          'update-goal': `Update Goal`,
          'save-goal': `Save Goal`,
          'delete-goal': `Delete Goal`,
        },
        validation: {
          label: `Please provide a label`,
          'select-date-range': `Please select a date range`,
          'baseline-value': `Please enter a valid baseline value`,
          'enter-valid-target': `Please enter a valid target value`,
          'relative-target-must-be-greater-than-minus-one': `Relative target change must be greater than -100%`,
          'invalid-form': `Invalid form`,
          'range-from-in-the-future': `Please select a start date in the past`,
        },
        success: {
          'goal-updated': `Goal updated successfully`,
          'goal-created': `Goal created successfully`,
        },
        error: {
          'failed-to-save': `Failed to save goal. Please try again.`,
        },
      },
      'push-changes-modal': {
        title: `Push Changes to Saved Metric`,
        description: `To push these changes please choose whether to keep or delete the {{goalCount}} goals that are attached to this metric.`,
        buttons: {
          cancel: `Cancel`,
          'delete-goals': `Delete Goals`,
          'keep-goals': `Keep Goals`,
        },
      },
    },
    feedback: {
      title: `Metric Trees Feedback`,
      description: `Rate your satisfaction with metric trees`,
      placeholder: `Additional feedback (Optional)`,
    },
    'copy-clipboard': {
      success: `Node(s) and Edge(s) copied to clipboard`,
      fail: `There was an error copying the nodes and edges to your clipboard.`,
    },
  },
  'experiment-reporting': {
    empty: {
      body: `Learn more on [Intro to Experiments]({{basicLink}})`,
      header: `Select an experiment and two variant groups to get started.`,
    },
    'failed-to-load': `Failed to load board reports. If this issue persists please [contact support](https://mixpanel.com/get-support)`,
  },
  flows: {
    steps: `Steps`,
    'add-steps': `Add Steps`,
    'add-1-step': `Add 1 Step`,
    'add-3-steps': `Add 3 Steps`,
    'add-5-steps': `Add 5 Steps`,
    'remove-step': `Remove Step`,
    'remove-steps': `Remove Steps`,
    'add-entry-tooltip': `Select an event to see the most common things users do before/after the event.`,
    'expand-events-by-prop': `Expand Events by Property`,
    'expand-events-tooltip': `Zoom in on specific details about each step in the flow.`,
    'view-funnel': `View as Funnel`,
    'conversion-percent': `overall conversion`,
    'step-range': {
      description: `Choose the number of steps to show before and after the event.`,
      'steps-before_one': `{{count}} Step before`,
      'steps-before_other': `{{count}} Steps before`,
      'steps-after_one': `{{count}} Step after`,
      'steps-after_other': `{{count}} Steps after`,
      before: `Before`,
      after: `After`,
    },
    alignment: {
      'analyze-before': `Analyze Events Before`,
      'analyze-after': `Analyze Events After`,
      disabled: {
        'top-paths-incompatible': `Alignment not available on Top Paths view.`,
        'minimum-steps': `Alignment available with 2 or more anchor steps.`,
      },
    },
    cardinality: {
      'rows-label': `{{count}} rows`,
      'rows-title': `Rows of Events`,
      'rows-description': `The number of events in each column`,
    },
    'advanced-options': {
      collapse: `Collapse Repeated`,
      'collapse-tooltip': `If an event happens twice back to back, we'll collapse them into one.`,
      'custom-events': `Show Custom Events`,
      'custom-events-tooltip': `Bundle custom events into a single event. [Learn more](https://docs.mixpanel.com/docs/analysis/reports/flows#custom-events-in-flows)`,
      'time-percentiles': `Show Time Percentiles`,
      breakdown: `Always Breakdown`,
    },
    empty: {
      body: `Learn [Flows Basics]({{basicLink}}) or explore [Example Flows Reports]({{exampleLink}})`,
      header: `Select at least one step to begin a flow.`,
    },
    'counting-sessions-requires-session-window': `Counting Sessions requires a conversion window of 1 session`,
  },
  funnels: {
    control: `Control`,
    table: {
      'total-tooltip': `Percent who converted all steps`,
      'stat-sig-tooltip': `Statistical significance is calculated using a hypergeometric test, which measures whether this segment's conversion rate differs from the overall population.`,
      'stat-sig-tooltip-control': `Statistical significance is calculated using a z-test comparing the conversion rates of the control and treatment groups.`,
      'stat-sig-tooltip-control-experiment': `Calculated using frequentist method, matching the default method of [Experiment Reports](https://docs.mixpanel.com/docs/experiments), so results are directly comparable across both. Note: Funnels displays 1-p (confidence), while the Experiments report displays the p-value directly. [Learn More](https://docs.mixpanel.com/docs/experiments#statistical-significance-calculation).`,
      'step-user-count-tooltip': `Number who performed this step`,
      'prev-step-tooltip': `Conversion from previous step`,
      'first-step-tooltip': `Conversion from first step`,
      'prev-step-avg-time-tooltip': `Average time from previous step`,
      'first-step-avg-time-tooltip': `Average time from first step`,
      'add-metrics-tooltip': `Add metrics to view them here`,
    },
    empty: {
      body: `Learn [Funnels Basics]({{basicLink}}) or explore [Example Funnels Reports]({{exampleLink}})`,
      header: `Select two funnel steps to get started.`,
    },
  },
  impact: {
    'launch-event': `Launch Event`,
    'impacted-events': `Impacted Events`,
    'user-def': `User Definition`,
    'users-who-did': `Count users who did`,
    'did-on-average': `did on average`,
    'only-impacted': `only the impacted event`,
    'any-event': `any event`,
    'events-count_one': `1 event`,
    'events-count': `{{count}} events`,
    'impact-prefix': `Launch impact on`,
    impact: `Impact`,
    'pre-launch': `Pre-launch`,
    'post-launch': `Post-launch`,
    adopters: `Adopters`,
    'adopters-of': `Adopters of`,
    'non-adopters': `Non-Adopters`,
    'non-adopters-comparison': `per user per day than non-adopters`,
    'adopters-delta': `Adopters - Non-adopters Delta`,
    'non-adopters-delta': `Non-adopters - Adopters Delta`,
    'delta-from-non-adopters': `Delta from non-adopters`,
    'delta-from-adopters': `Delta from adopters`,
    'first-use': `First use`,
    'days-before-first_one': `1 day before first use`,
    'days-before-first': `{{count}} days before first use`,
    'days-after-first_one': `1 day after first use`,
    'days-after-first': `{{count}} days after first use`,
    'had-a': `had a`,
    greater: `greater`,
    more: `more`,
    less: `less`,
    'ate-confidence-interval': `Average Treatment Effect Confidence Interval`,
    'uca-adopters': `Unobserved Counterfactual of Adopters (UCA)`,
    'loading-header': `This might take some time...`,
    'loading-body': `Reports can take a few minutes to generate.`,
    'impact-no-change': `The launch of <bold>{{launchEvent}}</bold> contributed to <highlight>no change</highlight> on the metric <bold>{{metricName}}</bold>.`,
    'impact-change': `The launch of <bold>{{launchEvent}}</bold> contributed to a <highlight>{{delta}} ({{deltaPercent}}) change</highlight> in the {{metricAggregateLabel}} <bold>{{metricName}}</bold> per day.`,
    'adoption-did-event_one': `<users>{{count, number}}</users> unique user did <bold>{{launchEvent}}</bold> <daterange>{{date}}</daterange> (<highlight>{{percentChange}}</highlight> of all users).`,
    'adoption-did-event_other': `<users>{{count, number}}</users> unique users did <bold>{{launchEvent}}</bold> <daterange>{{date}}</daterange> (<highlight>{{percentChange}}</highlight> of all users).`,
    'adoption-didnt-event_one': `<users>{{count, number}}</users> unique user did not do <bold>{{launchEvent}}</bold>.`,
    'adoption-didnt-event_other': `<users>{{count, number}}</users> unique users did not do <bold>{{launchEvent}}</bold>.`,
    'adoption-change-aggavg-avg-did-event': `For these users, the weighted average of <bold>{{metricName}}</bold> changed by an average of <highlight>{{delta}}</highlight> per user per day after they did <bold>{{launchEvent}}</bold>.`,
    'adoption-change-aggavg-avg-didnt-event': `For these users, the weighted average of <bold>{{metricName}}</bold> changed by an average of <highlight>{{delta}}</highlight> per user per day.`,
    'adoption-change-aggavg-proportion-did-event': `For these users, the weighted average of the proportion of users who did <bold>{{metricName}}</bold> changed by <highlight>{{delta}}</highlight> per day after they did <bold>{{launchEvent}}</bold>.`,
    'adoption-change-aggavg-proportion-didnt-event': `For these users, the weighted average of the proportion of users who did <bold>{{metricName}}</bold> changed by <highlight>{{delta}}</highlight> per day.`,
    'adoption-change-aggsum-avg-did-event': `For these users, the average sum of <bold>{{metricName}}</bold> changed by an average of <highlight>{{delta}}</highlight> per user per day after they did <bold>{{launchEvent}}</bold>.`,
    'adoption-change-aggsum-avg-didnt-event': `For these users, the average sum of <bold>{{metricName}}</bold> changed by an average of <highlight>{{delta}}</highlight> per user per day.`,
    'adoption-change-aggsum-proportion-did-event': `For these users, the average sum of the proportion of users who did <bold>{{metricName}}</bold> changed by <highlight>{{delta}}</highlight> per day after they did <bold>{{launchEvent}}</bold>.`,
    'adoption-change-aggsum-proportion-didnt-event': `For these users, the average sum of the proportion of users who did <bold>{{metricName}}</bold> changed by <highlight>{{delta}}</highlight> per day.`,
    'adoption-change-noagg-avg-did-event': `For these users, <bold>{{metricName}}</bold> changed by an average of <highlight>{{delta}}</highlight> per user per day after they did <bold>{{launchEvent}}</bold>.`,
    'adoption-change-noagg-avg-didnt-event': `For these users, <bold>{{metricName}}</bold> changed by an average of <highlight>{{delta}}</highlight> per user per day.`,
    'adoption-change-noagg-proportion-did-event': `For these users, the proportion of users who did <bold>{{metricName}}</bold> changed by <highlight>{{delta}}</highlight> per day after they did <bold>{{launchEvent}}</bold>.`,
    'adoption-change-noagg-proportion-didnt-event': `For these users, the proportion of users who did <bold>{{metricName}}</bold> changed by <highlight>{{delta}}</highlight> per day.`,
    'propensity-banner-header': `Get even more accurate results with Propensity Modeling`,
    'propensity-banner-body': `Propensity modeling uses a causal ML model to measure your impact with more granularity. Choose the Causal Impact chart option to view this analysis. This may take some time.`,
    'view-causal': `View Causal Impact`,
    'propensity-title-noagg-effect': `<bold>{{launchEvent}}</bold> <highlight>does have</highlight> a statistically significant effect on <bold>{{metricName}}</bold>.`,
    'propensity-title-noagg-noeffect': `<bold>{{launchEvent}}</bold> <highlight>does not have</highlight> a statistically significant effect on <bold>{{metricName}}</bold>.`,
    'propensity-title-agg-effect': `<bold>{{launchEvent}}</bold> <highlight>does have</highlight> a statistically significant effect on the {{propertyAggregation}} <bold>{{propertyName}}</bold> property of <bold>{{metricName}}</bold>.`,
    'propensity-title-agg-noeffect': `<bold>{{launchEvent}}</bold> <highlight>does not have</highlight> a statistically significant effect on the {{propertyAggregation}} <bold>{{propertyName}}</bold> property of <bold>{{metricName}}</bold>.`,
    'propensity-info-negative-agg_one': `On average, a user who adopted <bold>{{launchEvent}}</bold> had a <bold>{{propertyName}}</bold> of <estimate>{{valueEstimate}}</estimate> unit <highlight>change</highlight> per <bold>{{metricName}}</bold> event per day than a non-adopter (within <confidence>{{confidenceInterval}}</confidence> at 95% confidence.`,
    'propensity-info-negative-agg_other': `On average, a user who adopted <bold>{{launchEvent}}</bold> had a <bold>{{propertyName}}</bold> of <estimate>{{valueEstimate}}</estimate> units <highlight>change</highlight> per <bold>{{metricName}}</bold> event per day than a non-adopter (within <confidence>{{confidenceInterval}}</confidence> at 95% confidence.`,
    'propensity-info-negative-noagg_one': `On average, a user who adopted <bold>{{launchEvent}}</bold> did <estimate>{{valueEstimate}}</estimate> <highlight>change</highlight> <bold>{{metricName}}</bold> event per day than a non-adopter (within <confidence>{{confidenceInterval}}</confidence> at 95% confidence.`,
    'propensity-info-negative-noagg_other': `On average, a user who adopted <bold>{{launchEvent}}</bold> did <estimate>{{valueEstimate}}</estimate> <highlight>change</highlight> <bold>{{metricName}}</bold> events per day than a non-adopter (within <confidence>{{confidenceInterval}}</confidence> at 95% confidence.`,
    'propensity-info-positive-agg_one': `On average, a user who adopted <bold>{{launchEvent}}</bold> had a <bold>{{propertyName}}</bold> of <estimate>{{valueEstimate}}</estimate> unit <highlight>more</highlight> per <bold>{{metricName}}</bold> event per day than a non-adopter (within <confidence>{{confidenceInterval}}</confidence> at 95% confidence.`,
    'propensity-info-positive-agg_other': `On average, a user who adopted <bold>{{launchEvent}}</bold> had a <bold>{{propertyName}}</bold> of <estimate>{{valueEstimate}}</estimate> units <highlight>more</highlight> per <bold>{{metricName}}</bold> event per day than a non-adopter (within <confidence>{{confidenceInterval}}</confidence> at 95% confidence.`,
    'propensity-info-positive-noagg_one': `On average, a user who adopted <bold>{{launchEvent}}</bold> did <estimate>{{valueEstimate}}</estimate> <highlight>more</highlight> <bold>{{metricName}}</bold> event per day than a non-adopter (within <confidence>{{confidenceInterval}}</confidence> at 95% confidence.`,
    'propensity-info-positive-noagg_other': `On average, a user who adopted <bold>{{launchEvent}}</bold> did <estimate>{{valueEstimate}}</estimate> <highlight>more</highlight> <bold>{{metricName}}</bold> events per day than a non-adopter (within <confidence>{{confidenceInterval}}</confidence> at 95% confidence.`,
    prelaunch: `Pre-launch`,
    postlaunch: `Post-launch`,
    event: `Event`,
    events: `Events`,
    group: `Group`,
    users: `Users`,
    'total-users': `Total users`,
    'group-description': `The effect on Users who did your launch event`,
    'change-in-avg': `Change in Average Value`,
    'table-placeholder': `Select a launch event and a metric event.`,
    'upsell-header': `Understand which launches increase user engagement`,
    'upsell-description': `Find causal relationships between changes in product KPIs and user behavior, and feature launches so you can build products that move the needle. Account for biases and external factors that might degrade your analysis—all without bothering your data science team.`,
    'upsell-bold': `Impact is available on Growth & Enterprise. When you upgrade to a Growth plan, you also get:`,
    'upsell-callout1': `Unlimited saved reports & cohorts`,
    'upsell-callout2': `Data modeling`,
    'upsell-callout3': `Group Analytics & Data Pipelines add-ons, & more...`,
    'delete-confirm-title': `Delete Report`,
    'delete-confirm-body': `Are you sure you want to delete this report? This action is permanent and cannot be undone.`,
    delta: `Delta`,
    'relative-delta': `Relative Delta`,
    confidence: `Confidence`,
    'counterfactual-adopters': `Unobserved Counterfactual of Adopters`,
    'actual-adopters': `Actual Adopters`,
    ate: `Average Treatment Effect`,
    '95-confidence-interval': `95% CI`,
    att: `Average Treatment Effect on the Treated`,
    'adopters-avg': `Adopters Avg.`,
    'non-adopters-avg': `Non-Adopters Avg.`,
    'avg-values': `Average Values`,
    'days-from-launch': `Days from Launch`,
    'avg-sum-per-user': `avg. sum per user`,
    'weighted-avg-per-user': `weighted avg. per user`,
    'avg-events-per-user': `avg. events per user`,
    'avg-proportion-of-users': `avg. proportion of users`,
    'on-all': `on all`,
    per: `per`,
    'per-day': `per day`,
    'unit-per-day': `{{unit}} per day`,
    errors: {
      'insufficient-overlap': `There is not enough overlap between adopters and non-adopters of {{metricName}}. Try filtering to just your power users.`,
      'sample-size': `Insufficient Sample Size for Modeling. Please try increasing the date range, or waiting until enough user data comes in.`,
      'modeling-failure': `Propensity modeling failed.`,
      'negative-window': `Invalid input: must be a positive integer`,
      'max-window': `Please select a day window below {{count}} days`,
      'max-history': `Update your plan to query beyond {{count}} days`,
      'failed-board': `Failed to load board reports. If this issue persists please [contact support](https://mixpanel.com/get-support)`,
    },
    window: {
      days_one: `{{count}} day before and after`,
      days: `{{count}} days before and after`,
      weeks_one: `{{count}} week before and after`,
      weeks: `{{count}} weeks before and after`,
      months_one: `{{count}} month before and after`,
      months: `{{count}} months before and after`,
    },
    empty: {
      body: `Learn more on [Intro to Impact]({{basicLink}})`,
      header: `Select a launch event and the events you'll use to measure its impact.`,
    },
  },
  insights: {
    empty: {
      body: `Learn [Insights Basics]({{basicLink}}) or explore [Example Insights Reports]({{exampleLink}})`,
      header: `Select an Event or Cohort to get started.`,
    },
    'truncated-export': `Results have been truncated to the first {{count}} results for export.`,
    'show-distribution-by-time-to-convert': `Show distribution of total conversions by time to convert.`,
    'time-to-convert-requires-funnel-metrics': `Time to Convert is only available when there are funnel metrics.`,
    'multi-attribution-incompatible-count-types': `Multi attribution could not be added to some metrics due to incompatible count types.`,
    'funnel-steps-chart-requires-one-funnel-metric': `Funnel steps chart is only available when there is one funnel metric only`,
    'retention-curve-requires-retention-metrics': `Retention curve chart is only available when all metrics are retention metrics`,
    'rolling-cumulative-requires-line-or-column-chart': `Rolling or cumulative calculations are only supported for line and column charts.`,
    'any-value-not-supported-for-line-or-column-charts': `The any value option for profile properties is not supported for line or column charts.`,
    'funnel-conversion-rate-not-supported-on-chart': `Funnel conversion rate metric is not supported on {{chart}} chart.`,
    'funnel-time-to-convert-not-supported-on-chart': `Funnel time to convert metric is not supported on {{chart}} chart.`,
    'retention-rate-not-supported-on-chart': `Retention rate metric is not supported on {{chart}} chart.`,
    'no-data': {
      reason: {
        default: `We couldn't find any data for your query.`,
        'retention-no-data': `We couldn't find any data for your query.`,
        empty: `No data for this report`,
        'empty-project': `Your project doesn't have data`,
        'future-date-range': `This report has no data because its date range is fully in the future.`,
        'beyond-as-of-date': `This report has no data because its date range is after your project's date of last import.`,
        timeout: `Sorry, the query timed out.`,
      },
      remedy: {
        default: `Please try a different date range, event or remove some filters.`,
        'empty-project': `Please track some events to get started.`,
        'future-date-range': `Please try a different date range or wait until the dates come to pass.`,
        'beyond-as-of-date': `Please try a different date range or wait for more data to be imported.`,
      },
    },
  },
  'lookup-tables': {
    empty: {
      body: `Learn more with [Introduction to Lookup Tables]({{basicLink}}) or watch a [quick video]({{exampleLink}})`,
      header: `Import a lookup table to enrich properties with extra data.`,
    },
  },
  'public-boards': {
    empty: {
      body: `No reports have been added yet`,
      header: `This board is empty.`,
    },
  },
  retention: {
    retention: `Retention`,
    'retention-tooltip': `**Retention Analysis** How many users return every day, week, or month?`,
    frequency: `Frequency`,
    'frequency-of': `Frequency of {{event}}`,
    'frequency-tooltip': `**Frequency Analysis** How frequently do your users return to your product?`,
    'cumulative-frequency': `Cumulative Frequency`,
    'cumulative-frequency-tooltip': `Measures users who did the event in at least X unique intervals.\n[$t(common.learn-more)](https://docs.mixpanel.com/docs/analysis/reports/retention#frequency-view)`,
    'non-cumulative-frequency': `Non-cumulative Frequency`,
    'non-cumulative-frequency-tooltip': `Measures users who did the event in exactly X unique intervals.\n[$t(common.learn-more)](https://docs.mixpanel.com/docs/analysis/reports/retention#frequency-view)`,
    'retention-criteria': `Retention Criteria`,
    'frequency-criteria': `Frequency Criteria`,
    'group-by-date': `Group by date`,
    'group-by-date-tooltip': `Enable calendar date intervals instead of the default rolling intervals.\nE.g. {{today}}, {{tomorrow}} instead of Day 1, Day 2`,
    cohortize: `Cohortize`,
    'cohortize-tooltip': `Create cohorts based on day of first event.`,
    dropped: `dropped`,
    retained: `retained`,
    empty: {
      body: `Learn [Retention Basics]({{basicLink}}) or explore [Example Retention Reports]({{exampleLink}})`,
      header: `Select two events to get started.`,
    },
  },
  sampling: {
    'query-sampling': {
      enable: `Enable Query Sampling.`,
      description: `Shorten the time it takes to load results\nby querying only a subset of data.`,
      confirm: `Got it`,
      'enable-with-link': `$t(sampling.query-sampling.enable) [$t(common.learn-more)](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#query-time-sampling)`,
      disable: `Disable Query Sampling`,
      'enable-success': `Query sampling enabled successfully`,
      'disable-success': `Query sampling disabled succesfully`,
      'approx-warning': `Using 10% of data. Results are approximate.`,
      sampled: `Sampled`,
      'turn-off': `Try turning off Query Sampling to query all results.`,
    },
    'report-mode-sampling-notice': `Using {{samplingPercentage}} of data. Results are approximate.\n[Learn More](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#query-time-sampling)`,
    'sampled-at': `Sampled at {{samplingPercentage}}%`,
  },
  signal: {
    'qb-for': `For`,
    'qb-show-me': `Show me how`,
    'qb-correlated-with': `is correlated with`,
    'qb-event-clause-noevent': `<users></users> <addThatDid></addThatDid> <text>in the</text> <timeRange></timeRange>`,
    'qb-event-clause-event': `<users></users> <text>that did</text> <thatDid></thatDid> <text>in the</text> <timeRange></timeRange>`,
    'event-filtering-disabled': `Filtering can only be applied to "All Users"`,
    'add-correlation-disabled-top': `To add specific correlation events, select something other than "Your Top Events"`,
    'add-correlation-disabled-none': `Select an event above before adding additional events`,
    'add-correlation-disabled-max': `You can only select up to {{maxEvents, number}} events to correlate`,
    'add-correlation-disabled-breakdown': `You can only select one correlation event when breaking down by a property`,
    'correlation-tooltip': `Choose one or more events to correlate with your performance metric`,
    'no-events-title': `We haven't received any events from you yet`,
    'no-events-body': `You'll need to install Mixpanel's library on your site and start tracking your user's actions.`,
    'no-events-cta': `Install Mixpanel`,
    'show-event-details': `Show details for`,
    details: `Details`,
    event: `Event`,
    goal: `Goal`,
    'goal-users': `Goal users`,
    'goal-tooltip': `Select your performance metric`,
    'users-label': `Users`,
    'users-in-label': `Users in`,
    'cohort-tooltip': `Identify your target audience`,
    'cohort-in': `in`,
    'cohort-not-in': `not in`,
    'at-least': `at least`,
    'count-time_plural': withComment(`as in 1 time`, `time`),
    'count-time': `times`,
    'retention-2week': `2nd Week Retention`,
    'retention-3week': `3rd Week Retention`,
    'retention-4week': `4th Week Retention`,
    'retention-2month': `2nd Month Retention`,
    'cohort-all-users': `All Users`,
    'all-users': `All Users`,
    'cohort-new-users': `New Users`,
    'select-item': `Select an item...`,
    'load-more-results': `Load more results`,
    'correlate-conditions': `To break down your results by a property:`,
    'correlate-condition-new-users': `Select a target group other than "New Users"`,
    'correlate-condition-one': `Select only one correlation event at a time`,
    'correlate-condition-no-top': `Select an event other than "Your Top Events"`,
    'qb-frequency_one': `<text>at least</text> <frequency></frequency> <text>time</text>`,
    'qb-frequency_other': `<text>at least</text> <frequency></frequency> <text>times</text>`,
    'qb-filter': `<text>where</text> <conditions></conditions>`,
    'qb-breakdown': `<text>broken down by</text> <properties></properties>`,
    'sort-opportunity': `Sort by Opportunity`,
    'sort-opportunity-description': `Mixpanel's measure of potential opportunity`,
    'sort-strength': `Sort by Correlation Strength`,
    'correlation-event': `Correlation event`,
    'key-findings': `Key findings`,
    'optimal-action': `Optimal action`,
    'sort-strength-description': `Correlation of the optimal action with your goal`,
    'correlation-with-event': `Correlation with {{eventName}}`,
    'correlation-with-goal': `Correlation with goal event`,
    'property-breakdown': `broken down by {{propertyName}}`,
    'docs-tooltip': `How do I interpret Signal results? Learn more.`,
    'export-csv': `Export CSV`,
    'export-upsell': `Upgrade your plan to export reports as CSV`,
    'error-no-access': `Contact your Admin to request access.`,
    'error-timeout': `Please try again after shortening the date range, adding filters, or selecting fewer correlation events.`,
    'loading-header': `This might take some time...`,
    'loading-body': `Sometimes reports can take up to 1-2 minutes to generate.`,
    'optimal-correlation-data': `Optimal correlation data`,
    'detailed-stats': `Detailed Statistics`,
    precision: `Precision`,
    'precision-description_one': `Goal conversion rate of users who did this event ≥ {{count, number}} time within {{intervals, number}} days.`,
    'precision-description_other': `Goal conversion rate of users who did this event ≥ {{count, number}} times within {{intervals, number}} days.`,
    'false-omission': `False omission rate`,
    'false-omission-description_one': `Goal conversion rate of users who did this event < {{count, number}} time within {{intervals, number}} days.`,
    'false-omission-description_other': `Goal conversion rate of users who did this event < {{count, number}} times within {{intervals, number}} days.`,
    recall: `Recall`,
    'recall-description_one': `Percent of the converted users who did this event ≥ {{count, number}} time within {{intervals, number}} days.`,
    'recall-description_other': `Percent of the converted users who did this event ≥ {{count, number}} times within {{intervals, number}} days.`,
    fallout: `Fall-out`,
    'fallout-description_one': `Percent of the unconverted users who did this event ≥ {{count, number}} time within {{intervals, number}} days.`,
    'fallout-description_other': `Percent of the unconverted users who did this event ≥ {{count, number}} times within {{intervals, number}} days.`,
    correlation: `Correlation`,
    'correlation-description_one': `Association between doing this event ≥ {{count, number}} time within {{intervals, number}} days and goal conversion.`,
    'correlation-description_other': `Association between doing this event ≥ {{count, number}} times within {{intervals, number}} days and goal conversion.`,
    'conversion-table': `Conversion table`,
    'formulas-tooltip': `Formulas shown in blue`,
    'frequency-short': `Freq.`,
    'frequency-within-days': `Within (Days)`,
    'frequency-context': `<keyword>≥{{numEvents}}x</keyword> within <keyword>{{numDays}} days</keyword>`,
    'did-goal': `Did goal`,
    'didnt-do-goal': `Didn't do goal`,
    'range-bar': {
      'strong-positive': `Strong positive`,
      positive: `Moderate positive`,
      weak: `Weak`,
      negative: `Moderate negative`,
      'strong-negative': `Strong negative`,
      complex: `Complex`,
    },
    strength: {
      strong: `Strong`,
      moderate: `Moderate`,
      weak: `Weak`,
      'weak-to-none': `Weak - None`,
      optimal: `Optimal`,
    },
    rarity: {
      'vast-majority': `Performed by a vast majority of your user group.`,
      majority: `Done by a majority of your user group.`,
      minority: `Done by a minority of your user group.`,
      rare: `Very rarely performed among your user group.`,
      never: `Never performed by your user group.`,
    },
    conversion: {
      'too-high': `This event may be too good to be true?`,
      'very-positive': `Optimal action linked to dramatically increased conversion to your goal.`,
      positive: `Optimal action linked to increased conversion to your goal.`,
      neutral: `Optimal action linked to similar conversion to your goal.`,
      negative: `This action is linked to decreased conversion to your goal.`,
      'very-negative': `This action is linked to dramatically decreased conversion to your goal.`,
      'too-low': `This action is linked to suspiciously low conversion.`,
      'not-useful': `Unlikely to be useful.`,
      none: `No users converted to goal.`,
    },
    recommendation: {
      avoid: `Always avoid.`,
      'avoid-early': `Avoid doing too early.`,
      'avoid-possible': `As few times as possible.`,
      none: `No strong correlations found.`,
      'couple-times': `Only a couple times, not too early.`,
      'couple-early': `Only a couple times early.`,
      'couple-within-days': `Only a couple times within a few days.`,
      'few-times': `Just a few times, not too early.`,
      'few-early': `Just a few times early.`,
      'few-within-days': `Just a few times within a few days.`,
      'many-times': `Many times, not too early.`,
      'many-early': `Many times early.`,
      'many-within-days': `Many times within a few days.`,
      many: `Many times.`,
      'many-one-day': `Many times within a day.`,
      'many-num-days': `Many times within {{numDays}} days.`,
      once: `Just once.`,
      'once-one-day': `Just once within a day.`,
      'once-num-days': `Just once within {{numDays}} days.`,
      'at-least': `At least {{numEvents}} times.`,
      'at-least-one-day': `At least {{numEvents}} times within a day.`,
      'at-least-num-days': `At least {{numEvents}} times within {{numDays}} days.`,
    },
    'time-label': `Time`,
    'time-60-days': `Last 60 days`,
    'time-1-quarter': `Last quarter`,
    'time-6-months': `Last 6 months`,
    'time-1-year': `Last year`,
    'time-choose-custom': `Choose a date range...`,
    'minimum-time-error': `Minimum valid date range is {{minimum}} days. (Currently {{count}} days.)`,
    correlate: `Correlate`,
    'formula-1': `a ÷ (a + b)`,
    'formula-2': `c ÷ (c + d)`,
    'formula-3': `a ÷ (a + c)`,
    'formula-4': `b ÷ (b + d)`,
    'formula-5': `(a × d - b × c) ÷ √`,
    'formula-6': `(a + c) × (a + b) × (b + d) × (c + d)`,
    empty: {
      body: `Learn more on [Intro to Signal]({{basicLink}})`,
      header: `Enter your question to discover actions correlated with your goal.`,
    },
  },
  subscriptions: {
    title: `Subscriptions`,
    'create-title': `Create a Subscription`,
    'edit-title': `Edit a Subscription`,
    'alert-any': `Any type`,
    'alert-email': `Email`,
    'alert-slack': `Slack`,
    subscribers: `Subscribers`,
    'count-subscribers_one': `{{count}} subscriber`,
    'count-subscribers': `{{count}} subscribers`,
    'count-subscriptions': `{{count}} subscribers`,
    'channel-placeholder': `Select a channel`,
    'repeat-any': `Any frequency`,
    'repeat-daily': `Daily`,
    'repeat-weekly': `Weekly`,
    'repeat-monthly': `Monthly`,
    'repeat-quarterly': `Quarterly`,
    'repeat-asap': `ASAP`,
    'search-placeholder': `Search by name or email`,
    'search-all-placeholder': `Search for subscription, board or subscriber`,
    'loading-placeholder': `Loading data...`,
    'no-active-user': `The user who created this board or subscription is no longer active. Please create a new subscription to continue receiving updates.`,
    'no-email-subscriptions': `No email subscriptions found for this board.`,
    'no-slack-subscriptions': `No Slack subscriptions found for this board.`,
    'no-slack-channels': `No slack channels`,
    'delete-email_one': `There is 1 active email address subscribed to this board. Are you sure you want to delete this subscription?`,
    'delete-slack_one': `There is 1 active Slack channel subscribed to this board. Are you sure you want to delete this subscription?`,
    'delete-email': `There are {{count}} active email addresses subscribed to this board. Are you sure you want to delete this subscription?`,
    'delete-slack': `There are {{count}} active Slack channels subscribed to this board. Are you sure you want to delete this subscription?`,
    'send-test': `Send Test`,
    'proceed-test': `Proceed with Test`,
    'add-mixpanel-to-slack': `Add Mixpanel to Slack`,
    'reauthorize-slack-app': `Reauthorize Slack App`,
    'view-all': `View All`,
    'create-new': `Create New`,
    subscribe: `Subscribe`,
    subscribed: `Subscribed`,
    first: `First`,
    last: `Last`,
    'any-board': `Any board`,
    'any-creator': `Any creator`,
    column: {
      board: `Board`,
      'data-view': `Data View`,
      type: `Type`,
      schedule: `Schedule`,
      creator: `Creator`,
      subscribers: `Subscribers`,
      subscribed: `Subscribed`,
    },
    notice: {
      'create-success': `Subscription created`,
      'create-error': `There was an error creating the subscription.`,
      'delete-error': `There was an error deleting the subscription.`,
      'delete-success': `Subscription deleted`,
      'delete-title': `Delete Subscription`,
      'email-no-results': `No existing users found. You can enter an email address to add an external subscriber.`,
      'missing-permissions': `You don't have the proper permissions to create or manage subscriptions.`,
      'send-test-email-success': `Your test email will be sent shortly.`,
      'send-test-slack-success': `Your test Slack message will be sent shortly.`,
      'send-test-email-confirmation': `This will send an email to the recipients you have selected. The content of this email may contain sensitive data.`,
      'send-test-slack-confirmation': `This will send a message to the Slack channels you have selected. You may not have permissions in Slack to delete the message afterward.`,
      'last-subscriber-prompt': `Removing the last subscriber will delete the subscription. Are you sure you want to delete it?`,
      'update-error': `There was an error updating the subscription.`,
      'update-success': `Subscription updated`,
    },
  },
  'data-groups': {
    'add-group': `Add Group`,
    'add-groups': `Add Groups`,
    'analyze-by': `Analyze Uniques by`,
    'analyze-by-users-tooltip': `Analyze uniques as individual users`,
    'no-groups-configured-tooltip': `Analyze uniques as individual users.\n [Learn more about configuring Groups.](https://docs.mixpanel.com/docs/data-structure/group-analytics#implementation)`,
    'analyze-by-dg-tooltip': `Analyze uniques as a group`,
    'analyze-by-cohort-group': `Group is set in the cohort\n[$t(common.learn-more)](https://help.mixpanel.com/hc/en-us/articles/360025333632)`,
  },
  users: {
    search: `Search profiles`,
    'search-props': `Search profile properties`,
    'no-perms': `You don't have permission to add or edit.`,
    'no-results': `No users matched your query.`,
    'profiles-deleted_one': `{{count}} profile deleted`,
    'profiles-deleted_other': `{{count}} profiles deleted`,
    exporting: `Downloading CSV...`,
    'export-cap-exceeded': `CSV exports are limited to {{limit}} profiles. Your filter matches {{matched}}. Narrow your filter and try again.`,
    'parse-error': `An unexpected parsing error has occurred. If this continues please [contact-support](/get-support)`,
    'export-failed': `Sorry, something went wrong. Please contact support if this problem persists.`,
    'delete-failed': `Profiles couldn't be deleted`,
    'data-group-selector': {
      'changed-alert': `Incompatible custom events, properties, or cohorts were removed`,
    },
    'user-type-all-user-tooltip': `Anyone whose data is tracked in Mixpanel as an event or a user profile property`,
    'user-type-all-dg-tooltip': `Any {{dataGroupName}} that has data tracked in Mixpanel as an event or {{dataGroupName}} profile property`,
    'user-type-dg': `{{dataGroupName}} with Profiles`,
    'user-type-dg-tooltip': `Only the {{pluralDataGroupName}} for whom you collect or set {{dataGroupName}} profile properties in Mixpanel`,
    'inline-modal': {
      title: `Create or Modify Profile`,
      'select-type': `Select Profile Type`,
      create: `Create Profile`,
      update: `Update Profile`,
      import: `Import from CSV`,
      'name-placeholder': `Enter Property Name`,
      'value-placeholder': `Enter Value`,
      view: `View Profile`,
      'prop-name': `Property Name`,
      'prop-name_other': `Property Names`,
      value: `Value`,
      'add-prop': `Add Property`,
      'create-success': `Profile created. Changes may take a minute to process and display in your project.`,
      'update-success': `Profile updated. Changes may take a minute to process and display in your project.`,
      'save-failed': `Failed to save profile.`,
      csv: {
        banner: `Give each column a name (e.g. $name, $phone, Industry). If you do not include a {{profileIdField}} column, it will be generated randomly for each profile.`,
        progress: `{{percent}}% of profiles imported`,
        cta: `Upload CSV File`,
        processing: `Processing your file`,
        'name-placeholder': `Enter a name for column #{{index}}`,
        'email-warning': `Changing this column name from \n {{emailProp}} may prevent you from being \n able to send e-mails to your users.`,
        'email-detected': `Email Column Detected`,
        'col-values': `{{columnName}} Values`,
        'col-index-values': `Column {{index}} Values`,
        'import-success_one': `{{count, number}} profile successfully saved. Changes may take a minute to process and display in your project.`,
        'import-success_other': `{{count, number}} profiles successfully saved. Changes may take a minute to process and display in your project.`,
        import_zero: `Import profiles`,
        import_one: `Import {{count}} profile`,
        import_other: `Import {{count}} profiles`,
        'upload-file-limit': `Please upload a file with less than 1M profiles.`,
        'upload-failed': `Failed to upload profile(s).`,
      },
    },
    actions: {
      'add-edit-profile': `Add/Edit Profile`,
    },
    empty: {
      body: `Failed to load users, please try again later`,
    },
    cohorts: {
      'create-title': `Create New Cohort`,
      'edit-title': `Edit Cohort`,
      'confirm-unsaved': `Changes made will not be applied.`,
      'cohort-id': `Cohort ID:`,
      'name-placeholder': `Cohort name`,
      'save-as-new': `Save as New`,
      create: `Create Cohort`,
      'create-and-save': `Create & Save Cohort`,
      edit: `Edit Cohort`,
      'edit-and-save': `Edit & Save Cohort`,
      'upgrade-cta': `Upgrade to Save`,
      'upgrade-description': `When you upgrade your plan, cohorts can be created and saved to use across all reports in a project.`,
      'sharing-updated': `Cohort sharing status updated successfully`,
      'view-users': `View Cohort Users`,
      'user-count': `{{number}} users in cohort`,
      'group-count': `{{number}} {{entityName}} in cohort`,
      error: {
        'cant-edit-attribution': `Cohort definitions created using Attribution cannot be edited`,
        'cant-edit-period-filters': `Cohort definitions created using First or Last Time in Period filters cannot be edited`,
        'cant-edit-report': `Cohort definitions created from {{reportName}} reports cannot be edited`,
        'failed-create': `Failed to create cohort`,
        'failed-save': `Failed to save cohort`,
        'failed-share': `Failed to update cohort sharing`,
        'no-permission': `You do not have permission to edit this cohort.`,
        'try-save-as-new': `Try "$t(users.cohorts.save-as-new)" to create a new copy.`,
        'empty-name': `Enter a name for your cohort.`,
        'name-exists': `A cohort with that name already exists`,
      },
    },
  },
  views: {
    teams: `Team(s)`,
    orgs: `Org(s)`,
  },
  home: {
    reports: `reports`,
    boards: `boards`,
    greeting: {
      morning: `Good Morning`,
      afternoon: `Good Afternoon`,
      evening: `Good Evening`,
    },
    'your-recents': withComment(`the users recent reports`, `Your Recents`),
    'your-recents-tooltip': `Your Recents shows your most recently viewed boards`,
    'your-product': `Your Product`,
    'your-product-tooltip': `Your Product is comprised of your metrics, users, events. All customizations to your metrics can only be seen by you.`,
    'top-boards': `Top Boards`,
    'top-boards-tooltip': `Top Boards is a list of the most popular boards in your project`,
    'see-all': `See All`,
    'recent-users': `Recent Users`,
    'recent-users-tooltip': `This list consists of users from the last week for whom you collect or set user profile properties in Mixpanel`,
    'latest-replays': `Replays`,
    'latest-replays-tooltip': `This list consists of replays recorded from the last week for your project`,
    'saved-heat-maps': `Heat Maps`,
    'saved-heat-maps-tooltip': `This list consists of heat maps saved for your project`,
    'view-more-replays': `See All`,
    'view-all-users': `View All Users`,
    'events-feed': `New Events`,
    'events-feed-tooltip': `This table of events showcases the last events added to your project and who has been querying them. You can view more events in [$t(mixology.app.lexicon)]({{link}})`,
    'view-all-events': `View $t(mixology.app.events) in $t(mixology.app.lexicon)`,
    'new-homepage-tooltip': `Welcome to your new home page. Your project owner created a custom home page for your project`,
    'suggested-creators': {
      title: `Suggested Creators`,
      'title-tooltip': `Suggested Creators are people in your project that have built reports and boards that we recommend for you to get inspiration or learn from.`,
      invite: `Invite`,
      'no-teammates': `No teammates yet`,
      'has-teammates': `Invite more teammates`,
      'body-text': `Mixpanel works better together.`,
      'invite-others': `Invite others to join you in Mixpanel.`,
    },
    resources: {
      title: `Resources`,
      'help-docs': `Help Docs`,
      'mixpanel-support': `Mixpanel Support`,
      'view-demo': `View Demo Project`,
      'feedback-heading': `How was your experience with $t(mixology.app.home)?`,
    },
    implementation: {
      'unlock-insights': `Connect your data to get started`,
      'quick-setup': `Setup is quick, and we'll guide you through it.`,
      'quick-setup-startup': `Setup is quick, and we'll guide you through it. Implement by {{implementationDeadline}} to stay in the startup program.`,
      'continue-setup': `Connect Your Data`,
    },
    'no-data-state': {
      users: `No users seen in the last week`,
      events: `No events found. Finish implementing Mixpanel to see your events.`,
      boards: `No boards found.`,
      creators: `No creators found. <inviteLink>Invite your team.</inviteLink>`,
      replays: `No replays found. <docsLink>Start sending replays</docsLink> to see them here.`,
      'heat-maps': `No heatmaps or suggested heatmaps found.  <docsLink>Start sending heatmaps</docsLink> to see them here.`,
    },
    welcome: {
      title: `Welcome to Mixpanel`,
      'connect-title': `Connect your data`,
      'connect-subtitle': `Get your data flowing and start discovering insights about your users. Your current plan includes up to one million monthly events.`,
      'connect-button': `Connect Data`,
      'invite-title': `Invite an engineer`,
      'invite-description': `Not the one setting up tracking? Invite a teammate who can connect your data source \u2014 it only takes a few minutes.`,
      'invite-button': `Invite a Teammate`,
      'help-title': `Need some help?`,
      'help-description': `There are plenty of helpful tools if you get stuck. Check out a few popular resources below.`,
      'help-chat': `Open Help Chat`,
      'demo-project': `View Demo Project`,
      docs: `Go to Docs`,
      'no-events': `No Events Detected`,
      'connect-illustration-alt': `Connect your data illustration`,
    },
    'trouble-loading-data': `We had trouble loading your data.`,
  },
  comments: {
    'show-resolved-comments': `Show resolved comments`,
    'sort-newest': `Sort by newest`,
    'sort-oldest': `Sort by oldest`,
    'unpin-comment': `Unpin Comment`,
    'pin-comment': `Pin comment`,
    'pinned-comment': `Pinned Comment`,
    'comment-pinned': `Comment pinned`,
    'comment-unpinned': `Comment unpinned`,
    comments: `Comments`,
    'comments-loading': `Loading Comments...`,
    'add-comment-instruction': `Give feedback, ask a question, or just leave a note. Leave a comment and tag anyone you want to notify.`,
    'write-comment': `Write a comment...`,
    'reply-to-thread': `Reply to comment`,
    'mention-user': `Mention user`,
    cancel: `Cancel`,
    send: `Send`,
    'add-mention': `Add mention`,
    'add-emoji': `Add emoji`,
    'comments-disabled': `Comments are disabled on this report`,
    'enable-comments': `Enable comments`,
    'disable-comments': `Disable comments`,
    'no-emoji-found': `No emoji found`,
    search: `Search...`,
    'select-emoji': `Select an emoji`,
    bold: `Bold`,
    italic: `Italic`,
    feedback: {
      'give-feedback': `How are comments working for you?`,
      title: `Comments Feedback`,
      tooltip: `Send Mixpanel feedback about the comments`,
      'sentiment-heading': `Are you satisfied with the comments?`,
    },
    subscription: {
      'tooltip-report': `Notification settings for this report`,
      'tooltip-board': `Notification settings for this board`,
      'tooltip-canvas': `Notification settings for this canvas`,
      header: `Get notified about`,
      'all-comments': `All comments`,
      'all-comments-description': `Every new comment and reply`,
      'mentions-and-replies': `Mentions & replies`,
      'mentions-and-replies-description': `When you're @mentioned or replied to`,
    },
  },
  'version-history': {
    title: `Version History`,
    loading: `Loading version history...`,
    'loading-more': `Loading...`,
    'load-more': `Load more`,
    empty: `No version history available`,
    'error-loading': `Failed to load version history`,
    'action-created': `Created`,
    'action-updated': `Updated`,
    preview: `Preview`,
    feedback: {
      'give-feedback': `How is version history working for you?`,
      title: `Version History Feedback`,
      tooltip: `Send Mixpanel feedback about version history`,
      'sentiment-heading': `Are you satisfied with version history?`,
    },
  },
  notifications: {
    title: `Notifications`,
    'bell-aria-label': `Notifications`,
    'mark-all-read': `Mark all as read`,
    loading: `Loading notifications...`,
    error: `Failed to load notifications`,
    'error-hint': `Please try again later`,
    empty: `No notifications yet`,
    'empty-hint': `You'll be notified when someone mentions you or replies to your comments.`,
    'empty-unread': `No unread notifications`,
    'empty-read': `No read notifications`,
    'show-more': `Show more`,
    filter: {
      all: `All`,
      unread: `Unread`,
      read: `Read`,
    },
    feedback: {
      'give-feedback': `How are notifications working for you?`,
      tooltip: `Send Mixpanel feedback about notifications`,
      title: `Notifications Feedback`,
      'sentiment-heading': `Are you satisfied with notifications?`,
    },
    settings: {
      title: `Settings`,
      'email-label': `Email notifications`,
      'email-description': `Receive an email when someone mentions you or replies to your comments`,
      'slack-label': `Slack notifications`,
      'slack-description': `Receive a Slack DM when someone mentions you or replies to your comments`,
      'slack-not-connected': `If you haven't already, <slackLink>connect the Mixpanel Slack app</slackLink> to receive notifications`,
      'in-app-disclaimer': `In-app notifications are always on`,
      'no-channels': `More delivery options like email and Slack will appear here`,
    },
  },
  'query-builder': {
    'report-query-tab': `Query`,
    'chart-customization-tab': `Chart`,
    'annotations-tab': `Annotations`,
    'comments-tab': `Comments`,
    'add-metric': `Add Metric`,
    'add-metric-tooltip': `Add a metric. Analyze events, funnels, cohorts, retention and more.\n[See Insights Overview]({{url}})`,
    'add-metric-tooltip-disabled-too-many-metrics': `You have reached the limit on the number of metrics in a report. Remove a metric to add another.`,
    'add-property': `Add Property`,
    'all-events-disabled-reason': {
      'above-date-range': `All Events cannot be selected when the date range is greater than {{allEventsDateLimit}} days. Please reduce the date range or use a custom event to group a subset of events`,
    },
    'value-placeholder': `Select value…`,
    'numeric-value-placeholder': `Value…`,
    'search-placeholder': `Search...`,
    'matching-any': `Matching Any`,
    'any-operator-deprecated': `The "Any" operator is deprecated and cannot be changed for this report`,
    offset: {
      'as-of-start-of-interval': `As of start of Interval`,
      'as-of': `As of`,
      ago: `ago`,
    },
    'revenue-change': {
      'change-type-is': `Change Type is`,
    },
    'behavioral-lookback-window': {
      reset: `Reset`,
      cancel: `Cancel`,
      apply: `Apply`,
    },
    behavior: {
      on: `on`,
    },
    'filter-types': {
      'list-value-placeholder': `value`,
      and: `and`,
    },
    formula: {
      'save-formula': `Save Formula`,
      'definition-placeholder': `eg. (A+B)/(A-B) * 100`,
    },
    'export-dashboard-modal': {
      'loading-data': `Loading data...`,
      'search-by-name-or-email': `Search by name or email`,
      cancel: `Cancel`,
      'export-board': `Export Board`,
      'external-recipients-warning': `It looks like the following recipients might not be members of your project or organization:`,
      'edit-recipients': `Edit Recipients`,
      proceed: `Proceed`,
    },
    'subscribe-to-boards-menu': {
      owner: `Owner`,
    },
    'annotations-panel': {
      'all-tags': `All Tags`,
      tags: `Tags`,
      creators: `Creators`,
      'filter-by-tag': `Filter by Tags`,
      'filter-by-creator': `Filter by Creators`,
      'all-creators': `All Creators`,
      'show-annotations': `Show Annotations`,
      'hide-annotations': `Hide Annotations`,
      'sort-newest': `Sort by newest`,
      'sort-oldest': `Sort by oldest`,
      'annotations-hidden': `Annotations are hidden on this report. Control annotation visibility in the menu above.`,
      'no-results': `No annotations in time range.`,
      'no-tags-selected': `(None)`,
    },
    switcher: {
      insights: `Insights Report`,
      retention: `Retention Report`,
      funnels: `Funnels Report`,
      flows: `Flows Report`,
    },
    metric: `Metric`,
    metric_other: `Metrics`,
    behaviors: {
      update: `Update Behavior`,
      save: `Save Behavior`,
      'save-as-new': `Save as New Behavior`,
      reset: `Reset Behavior`,
      'behavior-cannot-edit': `You don't have the proper permissions to edit this behavior. Please contact your admin.`,
      'unsaved-changes-tooltip': `Behavior modified from original. Reset to the original state or push changes from the overflow menu [**⋮**] at the end of this row.`,
    },
    metrics: {
      'new-definition': `New Definition`,
      behavior: `Behavior`,
      event: `Event`,
      'event-tooltip': `Add a metric based on a single event.`,
      cohort: `Cohort`,
      'cohort-tooltip': `Add a metric measuring the size of a defined cohort.`,
      profile: `Profile`,
      'all-profiles': `All User Profiles`,
      'profile-tooltip': `Add a metric based on user or group profiles.`,
      funnel: `Funnel`,
      'funnel-tooltip': `Add a funnel metric or behavior. You can select any previously saved or create your own`,
      'funnel-tooltip-behavior': `Add a metric based off a funnel behavior. Select a saved funnel behavior or define your own, then choose how to measure it.`,
      retention: `Retention`,
      simple: `Simple`,
      'reset-tooltip': `Reverts the saved metric to its previously saved state.`,
      metric: `Metric`,
      'retention-tooltip': `Add a retention metric or behavior. You can select any previously saved or create your own.`,
      'retention-tooltip-behavior': `Add a metric based off a retention behavior. Select a saved retention behavior or define your own, then choose how to measure it.`,
      'simple-tooltip': `Add a simple metric or behavior. You can select any previously saved or create your own.`,
      formula: `Formula`,
      'formula-tooltip': `Add a metric that is a function of other metrics. You can select a saved formula or define your own.`,
      'formula-cannot-save-empty': `Enter a formula definition to save`,
      'formula-cannot-save-nested': `Cannot reference formulas in formulas`,
      'formula-cannot-save-multi-attribution': `Cannot save formula because it references metric(s) using attribution`,
      'formula-cannot-save-count-users-once': `Cannot save formula because it references metric(s) using Unique Users - First/Last Time`,
      'formula-cannot-save-current-plan': `Cannot save formula with your current plan. [Learn more](https://mixpanel.com/pricing/).`,
      'metric-cannot-edit': `You don't have the proper permissions to edit this metric. Please contact your admin.`,
      'saved-metric': `Saved Metric`,
      'unsaved-metric': `Unsaved Metric`,
      'no-unsaved-changes': `No unsaved changes.`,
      'saved-metric-tooltip': `Add a previously saved metric. You can select a metric or define your own.`,
      'unsaved-changes-tooltip': `Metric modified from original. Reset to the original state or push changes from the overflow menu [**⋮**] at the end of this row.`,
      'custom-event': `Custom Event`,
      'custom-event-tooltip': `Add a metric based on a custom event.`,
      'exclusion-steps': `Exclusion Steps`,
      'add-event': `Add Event`,
      'add-step': `Add Step`,
      name: `{{aggregationLabel}} of {{metricLabel}}`,
      'name-aggregation': `{{aggregationLabel}} on {{metricLabel}}`,
      'name-profiles': `User Profiles`,
      'name-funnel': `{{firstStep}} through {{lastStep}}`,
      'name-funnel-steps': `{{behaviorLabel}} - {{stepLabel}}`,
      'name-funnel-comparison': `Step 1 through Step {{count}}`,
      'name-retention': `{{firstStep}} and then {{lastStep}}`,
      'name-retention-frequency': `Frequency of {{firstStep}}`,
      'name-simple': `{{firstStep}} or {{lastStep}}`,
      'name-simple-multiple': `{{firstStep}} or {{lastStep}} or {{count}} others`,
      unknown: `Unknown`,
    },
    apply: `Apply`,
    'add-item': `Add {{item}}`,
    filter: `Filter`,
    filter_other: `Filters`,
    'filter-tooltip': `Filter to include or exclude specific data.`,
    breakdown: `Breakdown`,
    breakdown_other: `Breakdowns`,
    'new-breakdown': `New Breakdown`,
    'new-filter': `New Filter`,
    'breakdown-tooltip': `Segment to group your data into different categories.`,
    'incompatible-removed': `Incompatible breakdowns and filters were removed`,
    'data-groups-sanitized': `The data groups did not match across the report. Incompatible metrics, breakdowns, or filters were removed.`,
    'attribution-mismatch': `Removed attribution filter {{filterName}} because it does not match breakdown property {{propertyName}}.`,
    'conversion-window': `Conversion Window`,
    'conversion-window-description': `The window of time a user has to complete all steps once they enter the funnel.`,
    errors: {
      'failed-to-resolve-formula': `Failed to resolve formula - does it exist in this project?`,
    },
    actions: {
      remove: `Remove`,
      'save-behavior': `Save Behavior`,
      'save-behavior-tooltip': `Save this sequence of events to reuse and standardize key flows across reports.\n [Learn More](http://mixpanel.com/saved-metrics-hub)`,
      'save-metric': `Save Metric`,
      'save-metric-tooltip': `Save this measurement to track and reuse your key KPIs anywhere in Mixpanel.\n [Learn More](http://mixpanel.com/saved-metrics-hub)`,
      'show-metric': `Show Metric`,
      'hide-metric': `Hide Metric`,
      'show-trendline': `Show Trendline`,
      'hide-trendline': `Hide Trendline`,
      'add-filter': `Add Filter`,
      'add-breakdown': `Add Breakdown`,
      collapse: `Collapse`,
      expand: `Expand`,
      save: `Save`,
      reset: `Clear Changes`,
      update: `Push Changes`,
      filter: `Filter`,
      rename: `Rename`,
      'customize-range': `Customize Range`,
      'customize-buckets': `Customize Buckets`,
      'group-data': `Group Data`,
      'customize-buckets-tooltip-numeric': `Select each numeric range to display`,
      'customize-buckets-tooltip-datetime': `Select each time interval to display`,
      'customize-buckets-tooltip-string': `Select which value(s) to display [Learn More](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#string-property-use-cases)`,
      attribution: `Attribution`,
      'attribution-tooltip': `Attribute credit to multiple touch-points in a user-journey that lead to conversion.`,
      'filter-touchpoints': `Filter Touchpoints`,
      'filter-touchpoints-tooltip': `Filter events with selected property values from being considered touchpoints for multi-touch attribution.`,
      'event-comparison': `Event Comparison`,
      'event-comparison-tooltip': `Choose multiple events per step to compare conversion rates.\n[More on comparison events](https://docs.mixpanel.com/docs/analysis/reports/funnels#comparison-events)`,
      'toggle-any-order': `Toggle Any Order`,
      'toggle-any-order-tooltip': `Toggle whether the funnel step can be completed in any order. [Learn more.](https://docs.mixpanel.com/docs/reports/funnels#ordering)`,
      'map-lookup-table': `Map Lookup Table`,
      'map-lookup-table-tooltip': `Enrich property with additional data using Lookup tables.\n[Learn more](https://docs.mixpanel.com/docs/tracking/how-tos/lookup-tables)`,
      'unmap-lookup-table': `Unmap Lookup Table`,
      typecast: `Data Type`,
      'profile-history-type': `History Type`,
      'set-lookback-period': `Set Lookback Period`,
      'unset-lookback-period': `Remove Lookback Period`,
      duplicate: `Duplicate`,
      'duplicate-disabled-tooltip': `You have reached the limit on the number of metrics in a report. Remove another metric to duplicate this one.`,
      'first-time-filter': `First Time Filter`,
      'first-time-filter-tooltip': `Only count a user's first time ever doing this event\n[More on 1st time filters](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#analyzing-first-time-users)`,
    },
    'rename-modal': {
      'save-metric-suggestion': `Consider saving this metric so you may reuse it.`,
    },
    'data-type': {
      string: `String`,
      number: `Number`,
      boolean: `True / False`,
      date: `Date`,
      list: `List`,
      object: `Object`,
      'custom-type': `Custom {{type}}`,
      'list-of-type': `List of {{type}}`,
      'changed-to-type': `Data type changed to {{type}}`,
    },
    'context-menu': {
      'view-in-lexicon': `View in Lexicon`,
      'view-in-users': `View on Users Page`,
      'view-sample-events': `View Sample Events`,
      new: `New`,
      creator: `Creator`,
      owner: `Owner`,
      owner_plural: `Owners`,
      'cohort-id': `Cohort ID`,
      'cohort-edit': `Edit Cohort`,
      'cohort-view': `View Cohort`,
      'cohort-save': `Save custom property to share`,
      platforms: `Platforms`,
      'edit-custom-event': `Edit Custom Event`,
      'tracked-as': `Tracked as`,
      'edit-no-access': `You have view-only access and cannot edit`,
      share: `Share`,
      example: `Example`,
      'property-edit': `Edit Property`,
      'property-save': `Save custom property to share`,
      'property-is-local': `Local custom property, only visible in this report.`,
      'share-no-access': `You have view-only access and cannot share`,
      'lookup-table-info': `More about Lookup Tables`,
      'from-lookup-table': `From {{lookupTableName}}`,
      'from-borrowed-property': `Borrowed from {{borrowedEventName}}`,
      'from-history-table': `Imported from history table`,
      'automatically-created': `Automatically created by Mixpanel`,
      'automatically-tracked': `Automatically tracked by Mixpanel`,
      'used-in-reports': `Used in`,
      'codeless-tracking': `Tracked through Codeless tracking`,
      'usage-count_one': `Used by {{count, number}} teammate in the last 30 days`,
      'usage-count_other': `Used by {{count, number}} teammates in the last 30 days`,
      'usage-times_one': `Used by teammates {{count, number}} time in the last 30 days`,
      'usage-times_other': `Used by teammates {{count, number}} times in the last 30 days`,
      'users-usage_one': `{{count, number}} user with this property in the last 30 days`,
      'users-usage_other': `{{count, number}} users with this property in the last 30 days`,
      'events-usage_one': `{{count, number}} event with this property in the last 30 days`,
      'events-usage_other': `{{count, number}} events with this property in the last 30 days`,
      'query-count_one': `Queried {{count, number}} time in the last 30 days`,
      'query-count_other': `Queried {{count, number}} times in the last 30 days`,
      'query-rank': `#{{rank, number}} most queried event in your project`,
      'track-count_one': `Tracked this event {{count, number}} time in the last 30 days`,
      'track-count_other': `Tracked this event {{count, number}} times in the last 30 days`,
      'report-backlinks_one': `{{count, number}} report`,
      'report-backlinks_other': `{{count, number}} reports`,
      'recently-used': `Recently used by you`,
      'unsaved-changes': `Metric in use is modified from original. To reset or push changes, use the overflow menu at the end of the row.`,
      'description-add-placeholder': `Add Description...`,
      'description-placeholder': `Please add a description`,
      'user-query-scd-notice': `For user queries, the latest value will be used.`,
      'no-data': `There is no associated data.`,
      'verified-tooltip': `Verified on {{date}} by {{name}}`,
      'verified-by': `Verified by`,
      measurement: `Measurement`,
      'data-group': `Group`,
      'session-replay': `Recent Replay`,
      'merged-event-name': `Merged to {{mergedToEventName}}`,
      'merged-event-description': `This event has been merged into {{mergedToEventName}} and is no longer tracked separately.`,
      'merged-property-name': `Merged to {{mergedToPropertyName}}`,
      'merged-property-description': `This property has been merged into {{mergedToPropertyName}} and is no longer tracked separately.`,
    },
    'no-items': `No items found`,
    'no-search-results': `No results found`,
    search: `Search...`,
    create: `Create`,
    refresh: `Refresh`,
    'dg-user': `User`,
    'dg-user_other': `Users`,
    'dg-group': `Group`,
    'dg-group_other': `Groups`,
    default: `Default`,
    advanced: `$t(common.advanced)`,
    'edit-controls': {
      'save-entity': `Save {{entityType}}`,
      'save-new-entity': `Save as New {{entityType}}`,
      'no-unsaved-changes': `No unsaved changes`,
      'incomplete-behavior': `Cannot save incomplete behavior`,
      'reset-tooltip': `Reverts the behavior to its previously saved state`,
      reset: `Reset`,
      saving: `Saving...`,
      saved: `Saved`,
    },
    'step-selection': {
      title: `Step Selection`,
      tooltip: `Select the steps to measure on`,
      'all-steps': `All Steps`,
      'all-steps-tooltip': `Measure against completing the whole funnel.`,
      'all-steps-disabled-tooltip': `All steps is not supported for this chart.`,
      'entering-funnel': `Entering Funnel`,
      'step-from-to': `Step {{from}} \u2192 {{to}}`,
      'step-from-to-long': `Step {{from}} \u2192 Step {{to}}`,
      'step-entering-tooltip': `Measure against entering the funnel. Typically only applies to measuring uniques, totals or sessions.`,
      'step-range-tooltip': `Measure against a step range. When measuring uniques, totals or sessions, counts instances of reaching step {{to}}.`,
      'only-all-steps-tooltip': `Step selection disabled in Funnel Steps visualization.`,
      'entering-disabled-tooltip': `Entering Funnel is disabled from conversion rate and time to convert measurements.`,
    },
    'segment-selection': {
      'breakdown-on': `Breakdown on {{segment}}`,
      'filter-on': `Filter on {{segment}}`,
      'breakdown-and-filter-on': `Breakdown + Filter on {{segment}}`,
      'step-number': `Step {{number}}`,
      'last-step': `Last Step Defined`,
      'last-step-tooltip': `Use the property value from the last step reached to assign conversion. [$t(common.learn-more)](https://docs.mixpanel.com/docs/analysis/reports/funnels#first-touch-vs-last-touch)`,
      'first-step': `First Step Defined`,
      'first-step-tooltip': `Use the property value from the first step it was seen to assign conversion. [$t(common.learn-more)](https://docs.mixpanel.com/docs/analysis/reports/funnels#first-touch-vs-last-touch)`,
    },
    'count-menu': {
      measuring: `Measuring`,
      'select-property': `Select Property`,
      'select-revenue-property': `Select Revenue Property`,
      'unique-dg': `Unique {{dataGroupName}}`,
      'unique-dg-retention-tooltip': `Count {{dataGroupName}} who were retained within window`,
      'unique-dg-funnels-tooltip': `The number of unique users who performed the specified steps of the funnel.`,
      'unique-dg-tooltip': `The number of {{dataGroupName}} who performed an event`,
      'total-events': `Total Events`,
      total: `Total`,
      'total-profiles': `Total Profiles`,
      'total-profiles-tooltip': `The total number of profiles`,
      'total-events-tooltip': `The total number of events performed`,
      'total-sessions': `Total Sessions`,
      'total-sessions-funnels-tooltip': `The total number of sessions the specified steps of the funnel was performed.`,
      'total-sessions-tooltip': `The total number of sessions that contain the selected event`,
      'total-conversions': `Total Conversions`,
      'total-conversions-tooltip': `The total number of times the specified steps of the funnel was performed.`,
      'frequency-per-dg': `Frequency per {{dataGroupName}}`,
      'frequency-per-dg-tooltip': `The number of events performed per {{dataGroupName}}`,
      'agg-prop': `Aggregate Property`,
      'agg-prop-tooltip': `Aggregate property value across all instances of an event`,
      'agg-prop-group-tooltip': `Aggregate property value across all instances of an event per {{dataGroupName}}`,
      'agg-prop-per-dg': `Aggregate Property per {{dataGroupName}}`,
      'number-of-users-who-did': `Number of Users who did...`,
      'retention-rate': `Retention Rate`,
      'retention-rate-tooltip': `Count the percentage rate for users who were retained within window`,
      'conversion-rate-uniques': `Conversion Rate (Uniques)`,
      'conversion-rate-total': `Conversion Rate (Totals)`,
      'conversion-rate-sessions': `Conversion Rate (Sessions)`,
      'conversion-rate': `Conversion Rate`,
      'conversion-rate-tooltip': `The percentage of funnels that complete the specified steps of the funnel.`,
      'time-to-convert': `Time to Convert`,
      'time-to-convert-tooltip': `Aggregate the time to convert for the specified steps of the funnel across all occurrences of the funnel.`,
      'agg-of-prop': `{{aggregationOperator}} of {{propertyName}}`,
      'freq-dist': `Frequency Distribution`,
      'freq-dist-per-group': `Frequency Distribution per {{dataGroupName}}`,
      'agg-freq': `{{aggregationOperator}} Frequency`,
      'agg-freq-per-group': `{{aggregationOperator}} Frequency per {{dataGroupName}}`,
      'agg-prop-without-group': `{{aggregationOperator}} of ({{aggregationOperatorPerUser}} {{propertyName}})`,
      'agg-prop-per-group': `{{aggregationOperator}} of ({{aggregationOperatorPerUser}} {{propertyName}} per {{dataGroupName}})`,
      'property-sum': `Property Sum`,
      'property-sum-funnels-tooltip': `The sum of a property on each funnel step through the specified steps of the funnel.`,
      'property-sum-tooltip': `The sum of a property in each interval.`,
      'property-avg': `Property Average`,
      'property-avg-tooltip': `The average of the per user sum of a property in each interval.`,
      calculations: {
        title: `Calculation Options`,
        tooltip: `Calculation overrides based on time. Active chart will become Line or Stacked Line.`,
        'rolling-avg': `Rolling Average`,
        'rolling-avg-tooltip': `Rolling smooths trends by calculating the average value over previous {{windowSize}} {{windowUnit}} period`,
        'rolling-avg-label': `{{windowSize}} {{timeUnit}} rolling avg`,
        'cumulative-sum': `Cumulative Sum`,
        'cumulative-sum-tooltip': `Cumulative Sum adds each new period's value to the sum of previous periods.`,
      },
      'sub-menu': {
        'count-method': `Counting method`,
        'count-method-tooltip': `Choose what user activity will count towards conversion rates.`,
        'agg-by': `Aggregate by`,
        'agg-group-by': `Aggregate per {{dataGroupName}} by`,
        'unique-group-tooltip': `Select how the number of {{dataGroupName}} who perform an event are calculated.`,
        'per-group-tooltip': `Select how the number of events per {{dataGroupName}} is calculated across all instances of that event.`,
        'prop-values-tooltip': `Select how property values from each instance of an event are calculated.`,
      },
      'unique-aggs': {
        interval: `Interval (Default)`,
        'interval-tooltip': `The number of {{dataGroupName}} who performed an event in the defined chart interval range`,
        daily: `Daily Active {{dataGroupName}}`,
        'daily-tooltip': `The number of {{dataGroupName}} who performed an event within the last 24 hours`,
        weekly: `Weekly Active {{dataGroupName}}`,
        'weekly-tooltip': `The number of {{dataGroupName}} who performed an event within the last 7 days`,
        monthly: `Monthly Active {{dataGroupName}}`,
        'monthly-tooltip': `The number of {{dataGroupName}} who performed an event within the last 30 days`,
        cumulative: `Cumulative {{dataGroupName}}`,
        'cumulative-tooltip': `The cumulative number of {{dataGroupName}} who performed an event in the defined chart range`,
      },
      'conversion-aggs': {
        uniques: `Uniques`,
        'uniques-tooltip': `Count a single entry per user.`,
        totals: `Totals`,
        'totals-tooltip': `Count multiple entries per user. User may re-enter after converting or dropping off.`,
        sessions: `Sessions`,
        'sessions-tooltip': `Count the number of sessions that convert.`,
      },
      'count-users-once': {
        'users-title': `Count Unique Users`,
        'groups-title': `Count Unique Groups`,
        'breakdown-required': `Breakdown required`,
        'no-every-attribution': `Unique users can't be counted in Every Segment for attribution metrics`,
        'count-every-segment': `Count Every Segment`,
        'every-segment': `Every Segment`,
        'every-segment-tooltip': `Count users once in every segment they appear {{timePeriod}}.`,
        'count-last-segment': `Count Last Segment`,
        'last-segment': `Last Segment`,
        'last-segment-tooltip': `Count users in the last segment they appear {{timePeriod}}. This ensures no user is double-counted.`,
        'count-first-segment': `Count First Segment`,
        'first-segment': `First Segment`,
        'first-segment-tooltip': `Count users in the first segment they appear {{timePeriod}}. This ensures no user is double-counted.`,
      },
      operators: {
        sum: `Sum`,
        'sum-group-tooltip': `The total of numeric property values per {{dataGroupName}}`,
        'sum-event-tooltip': `The total of numeric property values across all instances of an event`,
        'sum-count-tooltip': `Sum of the property value counts per {{dataGroupName}}`,
        'sum-tooltip': `Counts the total of a numeric property value across all profiles`,
        avg: `Average`,
        'avg-tooltip': `Average across all users of the event count per {{dataGroupName}}`,
        'avg-event-tooltip': `Average of a numeric property value across all instances of an event`,
        'avg-count-tooltip': `Average of property value counts per {{dataGroupName}}`,
        'avg-group-tooltip': `Average of numeric property values per {{dataGroupName}}`,
        'avg-agg-tooltip': `Adds the numeric property values across all profiles and divides by the total count`,
        median: `Median`,
        'median-tooltip': `Median of the event count per {{dataGroupName}}`,
        'median-count-tooltip': `Median of property values per {{dataGroupName}}`,
        'median-agg-tooltip': `Counts the numeric property value across all profiles in order and finds the middle value`,
        min: `Minimum`,
        'min-tooltip': `Minimum of the number of time a {{dataGroupName}} performed an event`,
        'min-group-tooltip': `Minimum of all the numeric property values per {{dataGroupName}}`,
        'min-count-tooltip': `Minimum of property value counts per {{dataGroupName}}`,
        'min-agg-tooltip': `The smallest value of a numeric property across all profiles`,
        max: `Maximum`,
        'max-tooltip': `Maximum of the number of time a {{dataGroupName}} performed an event`,
        'max-group-tooltip': `Maximum of all the numeric property values per {{dataGroupName}}`,
        'max-agg-tooltip': `The greatest value of a numeric property across all profiles`,
        'max-count-tooltip': `Maximum of property value counts per {{dataGroupName}}`,
        distinct: `Distinct Count`,
        'distribution-count-tooltip': `A distribution of {{dataGroupName}} by property value counts`,
        'distinct-group-tooltip': `The number of distinct property values per {{dataGroupName}}`,
        'distinct-tooltip': `The number of distinct property values across all profiles`,
        distribution: `Distribution`,
        p: `Percentile`,
        p25: `25th Percentile`,
        'p25-tooltip': `The 25th percentile of the event count per {{dataGroupName}}`,
        'p25-count-tooltip': `The 25th percentile of property value counts per {{dataGroupName}}`,
        'p25-agg-tooltip': `Ranks the 25th percentile of the numeric property value across all profiles`,
        p75: `75th Percentile`,
        'p75-tooltip': `The 75th percentile of the event count per {{dataGroupName}}`,
        'p75-count-tooltip': `The 75th percentile of property value counts per {{dataGroupName}}`,
        'p75-agg-tooltip': `Ranks the 75th percentile of the numeric property value across all profiles`,
        p90: `90th Percentile`,
        'p90-tooltip': `The 90th percentile of the event count per {{dataGroupName}}`,
        'p90-agg-tooltip': `Ranks the 90th percentile of the numeric property value across all profiles`,
        'p90-count-tooltip': `The 90th percentile of property value counts per {{dataGroupName}}`,
        p99: `99th Percentile`,
        'p99-tooltip': `The 99th percentile of the event count per {{dataGroupName}}`,
        'p99-agg-tooltip': `Ranks the 99th percentile of the numeric property value across all profiles`,
        'custom-percentile': `Nth Percentile`,
        'custom-percentile-short': `P{{percentile}}`,
        'custom-percentile-tooltip': `Specify a custom percentile (0-100) to rank the numeric property value per {{dataGroupName}}`,
        'custom-percentile-agg-tooltip': `Specify a custom percentile (0-100) to rank the numeric property value across all profiles`,
        'distribution-tooltip': `A distribution of the number of times an event is performed per {{dataGroupName}}`,
        'disabled-tooltip': `Not available in {{queryOrigin}}`,
        unique: `Interval (Default)`,
        dau: `Daily Active Users`,
        wau: `Weekly Active Users`,
        mau: `Monthly Active Users`,
        'cumulative-users': `Cumulative Users`,
      },
      'select-unique': `Select Unique Count`,
      'select-count': `Select Counting Method`,
      'select-agg': `Select Aggregation`,
      'custom-percentile-modal': {
        title: `Custom Percentile`,
        label: `Enter percentile value (0-100)`,
        placeholder: `e.g., 95`,
        'error-invalid-number': `Invalid number`,
        'error-out-of-range': `Number must be between 0 and 100`,
      },
    },
    'events-menu': {
      'search-placeholder': `Search Events, use # to filter to tags`,
      'create-tooltip': `Create Custom Event`,
      'create-new': `Create New`,
      'no-tags': `No tags in use`,
      'no-tags-learn-more': `No tags in use. Learn more about`,
      'adding-tags-to-events': `adding tags to events`,
      'select-tag': `Select Tag`,
      'section-recent-events': `Recents`,
      'section-suggested-events': `Suggested Events`,
      'section-suggested-events-info': `These suggestions have been made by AI and are based on your project's Lexicon data.`,
      'section-all-events': `All Events`,
      'sidebar-tags': `Tags`,
      'sidebar-all': `All`,
      'sidebar-verified': `Verified`,
    },
    'multi-metrics-menu': {
      'create-tooltip': `Create Custom Event, Formula, Funnel, Retention, or Cohort`,
      'create-formula': `Create Formula`,
      'create-funnel': `Create Funnel`,
      'create-retention': `Create Retention`,
      'create-cohort': `Create Cohort`,
      'search-placeholder': `Search Metrics, use # to filter to tags`,
      'section-recents': `Recents`,
      'section-saved-metrics': `Metrics`,
      'section-saved-formulas': `Formulas`,
      'section-profile': `Profile`,
      'section-historical-profile-props': `Historical Profile Properties`,
      'section-cohorts': `Cohorts`,
      'section-events': `Events`,
      'section-formulas': `All Formulas`,
      'section-funnel-behaviors': `All Funnel Behaviors`,
      'section-funnel-metrics-and-behaviors': `All Funnel Metrics and Behaviors`,
      'section-retention-behaviors': `All Retention Behaviors`,
      'section-retention-metrics-and-behaviors': `All Retention Metrics and Behaviors`,
      'section-simple-behaviors': `All Simple Behaviors`,
      'section-simple-metrics-and-behaviors': `All Simple Metrics and Behaviors`,
      'analyze-profiles': `Analyze Profiles`,
      verified: {
        'section-formulas': `Formulas`,
        'section-funnel-metrics-and-behaviors': `Funnel Metrics and Behaviors`,
        'section-retention-metrics-and-behaviors': `Retention Metrics and Behaviors`,
        'section-simple-metrics-and-behaviors': `Simple Metrics and Behaviors`,
      },
      'create-new': `Create New`,
      'create-new-description': {
        simple: `How many x do y?`,
        funnel: `Who makes it through this sequence?`,
        retention: `Who does x and comes back to do y?`,
        profile: `How many x are there?`,
        cohort: `How many users match x criteria?`,
        formula: `Calculate using arithmetic operators.`,
        'custom-event': `Build your own event definition.`,
      },
    },
    'properties-menu': {
      'recent-props': `Recents`,
      'all-props': `All Properties`,
      'all-events': `All Events`,
      'all-datagroup-props': `All {{dataGroupName}} Properties`,
      'all-event-props': `All Event Properties`,
      'all-cohorts': `All Cohorts`,
      'all-user-props': `All User Properties`,
      'hidden-props': `Hidden Properties`,
      'hidden-event-props': `Hidden Event Properties`,
      'event-props': `Event Properties`,
      'profile-props': `Profile Properties`,
      experiments: `Experiments`,
      'hidden-profile-props': `Hidden Profile Properties`,
      'your-cohorts': `Your Cohorts`,
      'sidebar-all': `All`,
      'sidebar-all-tooltip': `All properties and cohorts, including event, user and computed properties.`,
      'sidebar-event': `Event`,
      'sidebar-event-tooltip': `Properties describing details of events.\n[Learn more](https://docs.mixpanel.com/docs/tracking/how-tos/events-and-properties)`,
      'sidebar-user': `User`,
      'sidebar-user-tooltip': `Properties describing details on user profiles.\n[Learn more](https://docs.mixpanel.com/docs/tracking/how-tos/events-and-properties)`,
      'sidebar-users-who-did': `Users who did...`,
      'sidebar-users-who-did-tooltip': `Virtual property describing groups of users defined by a chosen set of criteria, like a shared property or sequence of events.\n[Learn more](https://docs.mixpanel.com/docs/analysis/advanced/group-analytics)`,
      'sidebar-computed': `Computed`,
      'sidebar-computed-tooltip': `Properties that are derived from a calculation, typically based on the events users did.`,
      'sidebar-cohorts': `Cohorts`,
      'sidebar-cohorts-tooltip': `Properties based on whether a user is in or out of a cohort.\n[Learn more](https://docs.mixpanel.com/docs/analysis/advanced/cohorts)`,
      'sidebar-experiments': `Experiments`,
      'sidebar-experiments-tooltip': `Properties describing experiment variant membership for user profiles or events.`,
      'sidebar-datagroup-tooltip': `Properties describing details of {{dataGroupName}} profiles.\n[Learn more](https://docs.mixpanel.com/docs/analysis/advanced/group-analytics)`,
      'create-new': `Create New`,
      'create-tooltip': `Create Custom Property or Cohort`,
      'create-event-prop': `Custom Event Property`,
      'create-user-prop': `Custom User Property`,
      'create-group-prop': `Custom Group Property`,
      'create-runtime-prop': `Runtime Property`,
      'create-behavioral-prop': `Behavioral Property`,
      'create-cohort': `Cohort`,
      'in-cohort': `In Cohort`,
      'num-selected': `{{count}} Selected`,
      'go-back': `Go Back`,
      clear: `Clear`,
      reset: `Reset`,
      select: `Select`,
      'select-under-limit': `Select {{count}} or more`,
      'select-over-limit': `Select {{count}} or fewer`,
      'create-new-description': {
        'event-property': `Transform event properties using functions.`,
        'user-property': `Transform user properties using functions.`,
        'group-property': `Transform {{dataGroupName}} properties using functions.`,
        'runtime-property': `Custom property evaluated at runtime.`,
        'behavioral-property': `Build your own behavioral property.`,
        cohort: `How many users match x criteria?`,
      },
    },
    'disabled-behaviors-xau': `Disabled with Rolling XAU analysis, remove Rolling XAU to enable`,
    'disabled-behaviors-cohort': `Disabled with cohort analysis, remove cohorts to enable`,
    'select-metric': `Select Metric`,
    'select-step': `Select Step`,
    'select-event': `Select Event`,
    'select-behavior': `Select Behavior`,
    'select-cohort': `Select Cohort`,
    'select-formula': `Select Formula`,
    'select-profile': `Select User Profile`,
    'select-property': `Select Property`,
    'add-step': `Add Step`,
    'add-step-tooltip': `Analyze users who performed each event.\n[Read Intro to Funnels]({{link}})`,
    'add-simple-behavior-tooltip': `Analyze users who performed one of the selected events.`,
    'first-time-ever': `First time ever`,
    simple: {
      'cannot-save': `Cannot save metric with your current plan. [Learn more](https://mixpanel.com/pricing/).`,
      'label-new': `New Simple Behavior`,
      'label-select': `Select Simple Behavior`,
    },
    funnels: {
      'cannot-save': `Cannot save funnel metric with your current plan. [Learn more](https://mixpanel.com/pricing/).`,
      'event-comparison-unsupported': `Event comparison is not supported for saved funnel behaviors`,
      'event-comparison-report-only': `Event comparison is only available in funnels report`,
      'label-new': `New Funnel Behavior`,
      'label-select': `Select Funnel Behavior`,
    },
    retention: {
      'label-new': `New Retention Behavior`,
      'label-select': `Select Retention Behavior`,
      criteria: {
        'on-or-after': `On or After`,
        on: `On`,
        'on-or-before': `On or Before`,
        'consecutive-forward': `Streak`,
        tooltip: {
          'on-or-after': `Count users who perform the 2nd event during or after the time interval.\n[Learn more](https://docs.mixpanel.com/docs/reports/retention#on-or-after-time-interval)`,
          on: `Count users who perform the 2nd event during the interval.\n[Learn More](https://docs.mixpanel.com/docs/reports/retention#on-time-interval)`,
          'on-or-before': `Count users who perform the 2nd event during or before the time interval.\n[Learn more](https://docs.mixpanel.com/docs/reports/retention#on-or-before-time-interval)`,
          'consecutive-forward': `Count users who perform the 2nd event during the time interval and during each prior interval.\n[Learn More](https://docs.mixpanel.com/docs/reports/retention#streak-mode)`,
        },
      },
      'each-interval': `Each {{interval}}`,
      interval: {
        day: `Day`,
        week: `Week`,
        month: `Month`,
        custom: `Custom`,
        'custom-menu': {
          title: `Custom Retention`,
          'bracket-by': `Bracket by`,
          'bracket-number': `Bracket {{number}}`,
          'returning-users': `Returning users on days`,
          daily: `Daily`,
          weekly: `Weekly`,
          monthly: `Monthly`,
          error: {
            'not-specified': `{{bracket}} is not specified`,
            'not-integer': `{{bracket}} must be an integer`,
            'not-positive': `{{bracket}} must be greater than 0`,
            'max-days': `Cannot exceed {{count}} days`,
            'max-months': `Cannot exceed {{count}} months`,
            'max-years': `Cannot exceed {{count}} years`,
          },
        },
        tooltip: {
          day: `Calculate users that came back in day intervals`,
          week: `Calculate users that came back in week intervals`,
          month: `Calculate users that came back in month intervals`,
          custom: `Select custom intervals`,
        },
        label: {
          day: `Each Day`,
          'calendar-day': `Each Calendar Day`,
          week: `Each Week`,
          'calendar-week': `Each Calendar Week`,
          month: `Each Month`,
          'calendar-month': `Each Calendar Month`,
        },
      },
      groups: {
        title: `Retention groups`,
        tooltip: `View how specific Retention Groups trend over time. [$t(common.learn-more)](https://docs.mixpanel.com/docs/analysis/reports/retention#retention-trends)`,
        all: `All Groups`,
      },
    },
    'conversion-criteria': `Conversion Criteria`,
    conversion: {
      'any-order': `Any Order`,
      'any-order-tooltip': `Users can complete unanchored funnels steps in any order to convert.`,
      'specific-order-tooltip': `Users must complete the funnel in step order to convert.`,
      'exclude-who-did': `Exclude users who did...`,
      'exclude-who-did-tooltip': `Exclude users in between steps who performed exclusion events.`,
      hpc: `Holding property constant...`,
      'hpc-tooltip': `A property that must retain a single value across all funnels steps for a user to convert through the funnel.`,
      'hpc-disabled-tooltip': `A maximum of 3 properties can be held constant at the same time.`,
      'optimized-reentry': `Optimized Reentry`,
      'optimized-reentry-tooltip': `User can enter the funnel multiple times. [$t(common.learn-more)](https://docs.mixpanel.com/docs/reports/funnels/funnels-advanced#optimized-re-entry)`,
      within: `Window`,
    },
    'conversion-window-tooltip': `The time window a user has to complete a funnel from the time they enter.`,
    'conversion-window-label': {
      day_one: `Within {{count}} day`,
      day_other: `Within {{count}} days`,
      hour_one: `Within {{count}} hour`,
      hour_other: `Within {{count}} hours`,
      minute_one: `Within {{count}} minute`,
      minute_other: `Within {{count}} minutes`,
      week_one: `Within {{count}} week`,
      week_other: `Within {{count}} weeks`,
      second_one: `Within {{count}} second`,
      second_other: `Within {{count}} seconds`,
      month_one: `Within {{count}} month`,
      month_other: `Within {{count}} months`,
      session_one: `Within {{count}} session`,
      session_other: `Within {{count}} sessions`,
    },
    buckets: {
      'prompter-placeholder': `How do you want to organize this data?`,
      'column-name': `Column Name`,
      'add-column-name': `Add Column Name`,
      groups: `Groups`,
      'add-group': `Add Group`,
      'no-groups': `No groups defined.`,
      'generating-groups': `Generating groups`,
      'generate-groups': `Generate Groups with AI`,
      'generate-groups-description': `Intelligently organize your data into meaningful groups with an AI prompt.`,
      'generate-groups-hint': `Try grouping by sentiment, severity, feature name, etc.`,
      'generate-groups-example': `Group by Sentiment`,
      'generate-groups-placeholder': `Refine groups with another prompt`,
      'feedback-request': `Help us improve this feature`,
      'apply-groups': `Apply Groups`,
      'untitled-group': `Untitled Group`,
      'no-description': `No description`,
      'mutually-exclusive': `Mutually exclusive`,
      'group-remaining': `Group remaining`,
      'numeric-bucket': `Numeric Bucket...`,
      'bucket-by': `Bucket by...`,
      'bucket-by-groups': `Bucket by **{{groups}}**`,
      'by-time-period': `by **{{timePeriod}}**`,
      'time-unit': `Unit`,
      'time-unit-tooltip': `Group date values by time units`,
      'iso-time': `ISO-8601`,
      'iso-time-tooltip': `Group date values by ISO-8601 format`,
      'else-is': `Else Is`,
      'else-contains': `Else Contains`,
      'else-remaining': `Else remaining values`,
      'remaining-tooltip': `All other values will be\nincluded in this group`,
      'size-tooltip': `![Sizes]({{sizeImg}})\nSize defines an evenly\ndistributed range of values for\neach bucket.`,
      'evenly-divided': `Even`,
      'evenly-divided-tooltip': `Group values by equal sized groups`,
      'varied-sizes': `Varied`,
      'varied-sizes-tooltip': `Group values by specific ranges`,
      'no-buckets': `None`,
      'no-buckets-tooltip': `Remove all grouping`,
      min: `MIN`,
      max: `MAX`,
      size: `SIZE`,
      value: `VALUE`,
    },
    attribution: {
      model: {
        'first-touch': `First Touch`,
        'last-touch': `Last Touch`,
        linear: `Linear`,
        participation: `Participation`,
        'time-decay': `Time Decay`,
        'u-shaped': `U-Shaped`,
        'j-shaped': `J-Shaped`,
        'inverse-j_shaped': `Inverse J-Shaped`,
        custom: `Custom`,
        description: {
          'first-touch': `Gives 100% credit to the first touchpoint within the attribution lookback window.`,
          'last-touch': `Gives 100% credit to the last touchpoint within the attribution lookback window.`,
          linear: `Gives equal credit to every touchpoint seen leading up to a conversion within the attribution lookback window.`,
          participation: `Gives 100% credit to every unique touchpoint seen within attribution window. The total number of conversions is inflated compared to other attribution models. For example with 5 property values, each would receive 100% credit showing 5 conversions.`,
          'time-decay': `Follows an exponential decay with a custom half-life parameter, where the default is 7 days. The weight of each channel depends on the amount of time that passed between the touch point initiation and the eventual conversion. The formula used to determine credit is 2^(-t/halflife), where t is the amount of time between a touch point and a conversion. All touch points are then normalized to 100%.`,
          'u-shaped': `Gives 40% credit to the first touchpoint, 40% credit to the last touchpoint, and divides the remaining 20% to any touch points in between. With 2 touchpoints, the credit is normalized (50%, 50%). With 6 touch points the middle 4 touch points would share the 20% (40%, 5%, 5%, 5%, 5%, 40%).`,
          'j-shaped': `Gives 20% credit to the first touchpoint, 60% credit to the last touchpoint, and divides the remaining 20% to any touch points in between. With 2 touchpoints, the credit is normalized (25%, 75%). With 6 touch points the middle 4 touch points would share the 20% (20%, 5%, 5%, 5%, 5%, 60%).`,
          'inverse-j_shaped': `Gives 60% credit to the first touchpoint, 20% credit to the last touchpoint, and divides the remaining 20% to any touch points in between. With 2 touchpoints, the credit is normalized (75%, 25%). With 6 touch points the middle 4 touch points would share the 20% (60%, 5%, 5%, 5%, 5%, 20%).`,
          custom: `Customize the weightage to be given to the first and last touchpoint, and all other touchpoints in between based on your use-case.`,
        },
      },
      'advanced-models': `Advanced Attribution Models`,
      'upgrade-cta': `Try out the power of advanced attribution models by upgrading your <upgrade-link>Mixpanel plan</upgrade-link>.`,
      'model-header': `Model`,
      'name-header': `Name`,
      'custom-model-num': `Custom model {{number}}`,
      'weight-first': `First %`,
      'weight-middle': `Middle %`,
      'weight-last': `Last %`,
      'lookback-window': `Lookback Window`,
      'lookback-duration': `{{duration}} Lookback`,
      'lookback-description': `The time window where a user's events with this attribution property are counted towards the calculation. The window ends when the conversion metric happens.`,
      'touchpoints-exclude': `Touchpoints exclude`,
      'touchpoints-include': `Touchpoints include`,
      'remove-touchpoint-filter': `Remove touchpoint filter`,
      remove: `Remove Attribution`,
      error: {
        'count-type': `This attribution model is not supported with your current count type.`,
        'weight-value': `Weight values must be between 0 and 100.`,
        'weight-sum': `Attribution does not equate to 100%. Please update the attribution percentages to generate a result.`,
        'integer-lookback': `Lookback days must be an integer value.`,
        'lookback-max': `Lookback Windows up to {{count}} days are supported. Please choose a value within the range to generate a result.`,
        'session-limit': `Sessions is currently limited to 1`,
      },
    },
    'behavior-interval': {
      'per-interval': `Per Interval`,
      'between-steps': `Between Steps`,
      'between-funnel-steps': `Between Funnel Steps`,
      'after-step-1': `After Step 1`,
      'number-users-who-did-tooltop': `Count number of users who did the event per each interval in the chart`,
    },
    'behavior-agg': {
      'most-frequent': `Most Frequent`,
      'most-frequent-tooltip': `The most frequent property value across all instances of an event`,
      'first-value': `First Value`,
      'first-value-tooltip': `The first property value across all instances of an event`,
    },
    'property-agg': {
      'menu-label': `Aggregate by`,
      sum: `Sum`,
      'sum-tooltip': `The total of a numeric property value across all instances of an event\n[Learn more](https://docs.mixpanel.com/docs/analysis/reports/insights#measurements)`,
      average: `Average`,
      'average-tooltip': `Average of a numeric property value across all instances of an event\n[$t(common.learn-more)](https://docs.mixpanel.com/docs/analysis/reports/insights#measurements)`,
      median: `Median`,
      'median-tooltip': `Median of a numeric property value across all instances of an event\n[Learn more](https://docs.mixpanel.com/docs/analysis/reports/insights#measurements)`,
      'distinct-count': `Distinct Count`,
      'distinct-count-tooltip': `The number of distinct property values across all instances of an event\n[Learn more](https://docs.mixpanel.com/docs/analysis/reports/insights#measurements)`,
      p25: `25th Percentile`,
      'p25-tooltip': `The 25th percentile of a numeric property value across all instances of an event\n[Learn more](https://docs.mixpanel.com/docs/analysis/reports/insights#measurements)`,
      p75: `75th Percentile`,
      'p75-tooltip': `The 75th percentile of a numeric property value across all instances of an event\n[Learn more](https://docs.mixpanel.com/docs/analysis/reports/insights#measurements)`,
      p90: `90th Percentile`,
      'p90-tooltip': `The 90th percentile of a numeric property value across all instances of an event\n[Learn more](https://docs.mixpanel.com/docs/analysis/reports/insights#measurements)`,
      p99: `99th Percentile`,
      'p99-tooltip': `The 99th percentile of a numeric property value across all instances of an event\n[Learn more](https://docs.mixpanel.com/docs/analysis/reports/insights#measurements)`,
      minimum: `Minimum`,
      'minimum-tooltip': `Minimum of a numeric property value across all instances of an event\n[Learn more](https://docs.mixpanel.com/docs/analysis/reports/insights#measurements)`,
      maximum: `Maximum`,
      'maximum-tooltip': `Maximum of a numeric property value across all instances of an event\n[Learn more](https://docs.mixpanel.com/docs/analysis/reports/insights#measurements)`,
    },
    cohorts: {
      count_one: `{{count}} cohort`,
      count_other: `{{count}} cohorts`,
      'all-dg': `All {{dataGroupName}}`,
      'not-in-cohort': `Not in {{cohortName}}`,
    },
    items: {
      'frequency-per-dg': {
        name: `Frequency per {{dataGroupName}}...`,
        description: `A behavioral property that tracks the frequency of an event per user. This type of behavioral property is useful for understanding how often users did an action and correlating it to other actions. [Learn more in our help docs](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#behavioral-properties)`,
      },
      'agg-prop-per-dg': {
        name: `Aggregate Property per {{dataGroupName}}...`,
        description: `A behavioral property that tracks the property aggregation of an event per user. This type of behavioral property is useful for understanding the value of an action and correlating it to other actions. [Learn more in our help docs](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#behavioral-properties)`,
      },
      'attributed-by': {
        prefix: `Attributed by `,
        name: `Attributed by...`,
        description: `Special property that associates touchpoints to the conversion metric. This type of segmentation is useful for understanding which touchpoints impacted conversion ultimately. [Learn More](https://docs.mixpanel.com/docs/analysis/advanced/attribution)`,
        'free-note': `Limited models for Free plans`,
      },
      'time-to-convert': {
        name: `Time to Convert`,
        description: `The time in seconds to convert between the 2 chosen steps. [Learn more](https://docs.mixpanel.com/docs/reports/funnels#time-to-convert)`,
      },
      conversion: {
        name: `Conversion`,
        description: `Find which events are more likely to lead to conversion`,
        'extra-info': `The flow report must have atleast two steps to do conversion analysis`,
      },
      duration: {
        name: `Duration`,
        description: `The duration between 2 instances of this event. This works best for events that occur continuously during a session such as Page View or Screen View.`,
      },
      'recurring-revenue-change': {
        name: `Recurring Revenue Change`,
        description: `Computed property that calculates the delta in revenue across the selected chart interval.  [Learn more here](https://docs.mixpanel.com)`,
      },
      'recurring-revenue-change-type': {
        name: `Recurring Revenue Change Type`,
        description: `Computed property that identifies the type of revenue change across the selected chart interval. [Learn more here](https://docs.mixpanel.com)`,
      },
      'number-of-users-who-did': {
        name: `Number of Users who did...`,
        description: `Computed property that counts the number of users who performed an action, aggregating on a per Group basis. [Learn more here](https://docs.mixpanel.com)`,
      },
      'list-sum': {
        name: `Sum`,
        description: `The total value for a property across all of the items in the list. [Learn More](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#computed-properties)`,
      },
      'list-unique': {
        name: `Distinct Count`,
        description: `The number of unique values for a property across all of the items in the list. [Learn More](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#computed-properties)`,
      },
      'list-length': {
        name: `Size`,
        description: `The number of items in the list. [Learn More](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#computed-properties)`,
      },
      'first-seen': {
        name: `First Seen`,
        description: `First Seen is a datetime profile property that captures the timestamp of the very first event for this user/group. This can be used to filter/segment reports by users who were "new" in a particular timeframe. [More about First Seen](https://docs.mixpanel.com/docs/data-structure/property-reference/reserved-properties)`,
      },
      'project-name': `Project Name`,
    },
    'hide-events': `Hide Events`,
    'hide-x-events_one': `Hide {{count}} event`,
    'hide-x-events_other': `Hide {{count}} events`,
    'hide-events-all-but': `all but {{count}}`,
    'number-of-users-who-did-label': `Number of Users who did {{event}}...`,
    'filter-inherited-from-board': `Filter is inherited from the parent board`,
    'breakdown-inherited-from-board': `Breakdown is inherited from the parent board`,
    'row-updated': `"{{name}}" has been updated`,
    'update-description-failed': `Failed to update the description`,
    'undo-description-failed': `Unable to undo the description`,
    'lookup-table-removed': `Removed temporary lookup table mapping`,
  },
  filters: {
    menu: {
      'search-placeholder': `Search`,
      'remove-values': `Remove Value(s)`,
      'rename-values': `Rename Value(s)`,
    },
    group: `Group`,
    'group-operator': {
      and: `and`,
      'and-all': `And all`,
      'and-any': `and any`,
      or: `or`,
      'or-all': `or all`,
      'or-any': `Or any`,
      then: `then`,
    },
    'date-operator': {
      last: `Last`,
      'not-in-last': `Not in the last`,
      between: `Between`,
      'not-between': `Not between`,
      on: `On`,
      'not-on': `Not on`,
      'before-last': `Before the last`,
      before: `Before`,
      since: `Since`,
      'in-the-next': `In the next`,
      tooltip: {
        last: `Includes only results within the last filter interval.`,
        'not-in-last': `Excludes results within the last filter interval.`,
        between: `Includes only results within the filter range.`,
        'not-between': `Excludes results within the filter range.`,
        on: `Includes only results from the filter date.`,
        'not-on': `Excludes results from the filter date.`,
        'before-last': `Includes only results from before the last filter interval.`,
        before: `Includes only results from before the filter date.`,
        since: `Includes only results since the filter date.`,
        'in-the-next': `Includes only results that occur in the next filter interval.`,
      },
    },
    'number-operator': {
      equals: `Equals`,
      'not-equal': `Not equal`,
      'greater-than': `Greater than`,
      'greater-than-equal': `Greater than or equal to`,
      'less-than': `Less than`,
      'less-than-equal': `Less than or equal to`,
      between: `Between`,
      'not-between': `Not between`,
      'is-numeric': `Is numeric`,
      'is-not-numeric': `Is not numeric`,
      tooltip: {
        equals: `Includes only results that exactly match the filter value(s).`,
        'not-equal': `Excludes results that exactly match the filter value(s).`,
        'greater-than': `Includes only results with values greater than the filter value(s).`,
        'greater-than-equal': `Includes only results with values greater than or equal to the filter value(s).`,
        'less-than': `Includes only results with values less than the filter value(s).`,
        'less-than-equal': `Includes only results with values less than or equal to the filter value(s).`,
        between: `Includes only results with values within the filter range.`,
        'not-between': `Excludes results with values within the filter range.`,
        'is-numeric': `Includes only results with a numeric value for the filter property.`,
        'is-not-numeric': `Excludes only results with a numeric value for the filter property.`,
      },
    },
    'boolean-operator': {
      'is-true': `is True`,
      'is-false': `is False`,
      true: `True`,
      false: `False`,
    },
    'string-operator': {
      is: `Is`,
      'is-not': `Is not`,
      contains: `Contains`,
      'does-not-contain': `Does not contain`,
      'is-set': `Is set`,
      'is-not-set': `Is not set`,
      tooltip: {
        is: `Includes only results that exactly match the filter value(s).`,
        'is-not': `Excludes results that exactly match the filter value(s).`,
        contains: `Includes only results that partially match the filter value(s).`,
        'does-not-contain': `Excludes results that partially match the filter value(s).`,
        'is-set': `Includes only results with any value for the filter property.`,
        'is-not-set': `Excludes results with any value for the filter property.`,
      },
    },
    'list-inclusion': {
      all: `Include All Items`,
      matching: `Include Matching Items`,
      tooltip: {
        all: `Return all items from the list in the results.`,
        matching: `Only return matching items from the list in the results.`,
      },
    },
    'list-operator': {
      'any-in-list': `Any in List`,
      'all-in-list': `All in List`,
      in: `In`,
      tooltip: {
        'any-in-list': `Any object in the list matches the filter criteria`,
        'all-in-list': `Every object in the list matches the filter criteria`,
      },
    },
    'scd-options-type': {
      latest: `At time of event`,
      any: `Any value`,
      fixed: `Most recent value`,
      tooltip: {
        latest: `Value at event time OR value at the end of the time interval`,
        any: `Checks if any value during the interval meets the condition`,
      },
    },
    'cohort-operator': {
      is: `Is`,
      'is-not': `Is Not`,
    },
    'user-operator': {
      who: `who`,
      where: `where`,
      did: `did`,
      'did-not': `did not do`,
    },
  },
  common: {
    active: `Active`,
    add: `Add`,
    'add-tags': `Add tags...`,
    advanced: `Advanced`,
    all: `All`,
    alpha: `Alpha`,
    analyze: `Analyze`,
    apply: `Apply`,
    archive: `Archive`,
    archived: `Archived`,
    auto: `Auto`,
    back: `Back`,
    beta: `Beta`,
    cancel: `Cancel`,
    'clear-all': withComment(`clear as in remove`, `Clear all`),
    close: `Close`,
    'coming-soon': `Coming Soon`,
    confirm: `Confirm`,
    continue: `Continue`,
    copy: `Copy`,
    'copy-url': `Copy URL`,
    create: `Create`,
    custom: `Custom`,
    dark: `Dark`,
    'dark-mode': `Dark Mode`,
    default: `Default`,
    delete: `Delete`,
    restore: `Restore`,
    disable: `Disable`,
    disabled: `Disabled`,
    discard: `Discard`,
    dismiss: `Dismiss`,
    done: `Done`,
    'dont-save': `Don't Save`,
    duplicate: `Duplicate`,
    edit: `Edit`,
    enable: `Enable`,
    enabled: `Enabled`,
    explore: `Explore`,
    filter: `Filter`,
    'finish-save': `Finish & Save`,
    hidden: `Hidden`,
    internal: `Internal`,
    label: `Label`,
    'learn-more': `Learn more`,
    light: `Light`,
    'light-mode': `Light Mode`,
    loading: `Loading...`,
    manage: `Manage`,
    'n-a': `N/A`,
    new: `New`,
    next: `Next`,
    no: `No`,
    'no-data': `No data`,
    none: `None`,
    off: `Off`,
    okay: `Okay`,
    optional: `Optional`,
    overall: `Overall`,
    'overflow-more': `more`,
    'read-docs': withComment(`"Docs" is short for documentation`, `Read the Docs`),
    retry: `Retry`,
    refresh: `Refresh`,
    remove: `Remove`,
    replace: `Replace`,
    'required-field': `This is a required field`,
    reset: `Reset`,
    save: `Save`,
    saved: `Saved`,
    'save-as-new': `Save as New`,
    'save-as': `Save as`,
    'save-for-everyone': `Save for Everyone`,
    saving: `Saving`,
    saving_suspense: `Saving...`,
    search: `Search`,
    'search-users': `Search users`,
    sensitive: `Classified`,
    show: `Show`,
    hide: `Hide`,
    unknown: `Unknown`,
    'select-all': `Select all`,
    share: `Share`,
    submit: `Submit`,
    'talk-to-sales': `Talk to Sales`,
    unique: `Unique`,
    uniques: `Uniques`,
    unsensitive: `Not Classified`,
    update: `Update`,
    upgrade: `Upgrade`,
    visible: `Visible`,
    'view-docs': withComment(`"Docs" is short for documentation`, `View Docs`),
    yes: `Yes`,
    you: `You`,
    recommended: `Recommended`,
    'generic-error': `Sorry, something went wrong. Please contact support if this problem persists.`,
    standard: `Standard`,
    'share-feedback': `Share Feedback`,
  },
  /* Mixpanel-specific terminology should go here. */
  [MIXOLOGY_KEY]: {
    app: {
      'agent-flows': `Automations`,
      boards: `$t(mixology.board_plural)`,
      canvases: `$t(mixology.metric-tree_plural)`,
      cohorts: `$t(mixology.cohort_plural)`,
      data: withDetail(`Data`, {excludeFromMixologyDict: true}),
      'data-audit': `Data Audit`,
      events: `$t(mixology.event_plural)`,
      experiments: withComment(`e.g. "A/B Experiments"`, `Experiments`),
      experimentation: `Experimentation`,
      'experiments-legacy': withComment(`e.g. "A/B Experiments"`, `Experiments`),
      'feature-flags': `Feature Flags`,
      flows: withComment(`e.g. "User Flows"`, `Flows`),
      funnels: withComment(`e.g. "User Conversion Funnels"`, `Funnels`),
      'heat-maps': `Heatmaps`,
      'heat-map-collections': `Heatmap Collections`,
      playlists: `Playlists`,
      home: `Home`,
      impact: withComment(`e.g. "Impact Analysis"`, `Impact`),
      insights: withComment(
        `e.g. "Product Insights" - analyses that reveal what users experience when interacting with a product`,
        `Insights`,
      ),
      integrations: `Integrations`,
      destinations: `Destinations`,
      jql: `JQL`,
      lexicon: withComment(
        `defined set of terms, vocabulary, or dictionary that is used to describe data, events, or metrics within a specific domain`,
        `Lexicon`,
      ),
      'metric-trees': `$t(mixology.metric-tree_plural)`,
      retention: withComment(`e.g. "User Retention"`, `Retention`),
      session_replay: `Session Replay`,
      signal: withComment(
        `e.g. "User Signals" - referring to the data or indicators that users generate while interacting with a product`,
        `Signal`,
      ),
      users: `$t(mixology.user_plural)`,
    },
    behavior: `Behavior`,
    behavior_plural: `Behaviors`,
    cohort: `Cohort`,
    cohort_plural: `Cohorts`,
    'custom-event': `Custom Event`,
    'custom-event_plural': `Custom Events`,
    'custom-event-property': `Custom Event Property`,
    'custom-profile-property': `Custom Profile Property`,
    'custom-property': `Custom Property`,
    'custom-property_plural': `Custom Properties`,
    'metric-tree': `Metric Tree`,
    'metric-tree_plural': `Metric Trees`,
    board: withComment(`short for "Dashboard"`, `Board`),
    board_plural: withComment(`short for "Dashboards"`, `Boards`),
    'data-group': `Data Group`,
    'data-group_plural': `Data Groups`,
    'data-point': `Data Point`,
    'data-point_plural': `Data Points`,
    'data-view': `Data View`,
    'data-view_plural': `Data Views`,
    'agent-flow': `Automation`,
    'agent-flow_plural': `Automations`,
    editor: `Editor`,
    event: `Event`,
    event_plural: `Events`,
    'event-property': `Event Property`,
    'event-property_plural': `Event Properties`,
    experiment: `Experiment`,
    experiment_plural: `Experiments`,
    'feature-flag': `Feature Flag`,
    'feature-flag_plural': `Feature Flags`,
    formula: `Formula`,
    funnel: `Funnel`,
    group: `Group`,
    group_plural: `Groups`,
    'heat-map': `Heatmap`,
    'heat-map-collection': `Heatmap Collection`,
    'lookup-table': `Lookup Table`,
    'lookup-table_plural': `Lookup Tables`,
    journey: `Journey`,
    journey_plural: `Journeys`,
    metric: `Metric`,
    metric_plural: `Metrics`,
    organization: `Organization`,
    organization_plural: `Organizations`,
    owner: `Owner`,
    people: `People`,
    'private-data-view': `Private Data View`,
    profile: `Profile`,
    playlist: `Playlist`,
    profile_plural: `Profiles`,
    project: `Project`,
    project_plural: `Projects`,
    'project-count_one': `1 $t(mixology.project)`,
    'project-count_other': `{{count}} $t(mixology.project_plural)`,
    'profile-property': `Profile Property`,
    'profile-property_plural': `Profile Properties`,
    property: `Property`,
    property_plural: `Properties`,
    report: `Report`,
    report_plural: `Reports`,
    'report-count_one': `1 $t(mixology.report)`,
    'report-count_other': `{{count}} $t(mixology.report_plural)`,
    user: `User`,
    user_plural: `Users`,
    'user-count_one': `1 $t(mixology.user)`,
    'user-count_other': `{{count}} $t(mixology.user_plural)`,
    // refers to user of Mixpanel webapp (rather than tracked user)
    'user-of-mixpanel': `User`,
    'user-of-mixpanel_plural': `Users`,
    'user-role': {
      role: `Role`,
      admin: `Admin`,
      analyst: `Analyst`,
      'billing-admin': `Billing Admin`,
      consumer: `Consumer`,
      member: `Member`,
      messenger: `Messenger`,
      owner: `Owner`,
    },
    'user-access': {
      creator: `Creator`,
      editor: `Editor`,
      viewer: `Viewer`,
      none: `None`,
    },
    viewer: `Viewer`,
    verified: `Verified`,
    workspace: `Workspace`,
    workspace_plural: `Workspaces`,
  },
  discover: {
    filter: {
      'filter-label': `Filter`,
      'clear-all': `Clear all`,
      creators: `Creators`,
      'created-by-anyone': `Created by anyone`,
      'created-by': `Created by`,
      'search-creator-placeholder': `Search creators...`,
      'types-label': `Types`,
      'events-label': `$t(mixology.app.events)`,
      'entry-btn-tooltip': `Show bookmarks using this event`,
      'entry-btn-label': `Add events`,
      'entry-label': `Select an event`,
    },
    results: {
      'results-with-count_one': `{{count}} result`,
      'results-with-count_other': `{{count}} results`,
      empty: {
        header: `No results`,
        body: `Not finding what you're looking for?`,
        link: `[Let us know what you expected]{{link}}`,
      },
    },
    'no-results': {
      title: `No results`,
      'no-entities': `You don't have any saved entities yet.`,
      'full-page': {
        title: `Monitor your metrics in one place`,
        body: `Looks like there aren't any boards or reports to browse yet.\nCreate your first board or use a template to get started.`,
        'subtitle-filtered': `Not finding what you're looking for?`,
        'body-filtered': ` Let us know what you expected`,
      },
      'add-mode': {
        title: `Looks like there aren't any reports to choose from`,
        subtitle: `Create a new report to add it to a board`,
        'subtitle-filtered': `You may want to try different keywords or check for possible typos.`,
      },
    },
    error: {
      title: `Something happened...`,
      subtitle: `We're thinking this isn't the result you want`,
      body: `If this error persists, please contact [support]({{supportLink}}) or check our [status page]({{statusLink}})`,
    },
    'suggested-creators': {
      title: `Suggested Creators`,
      board_one: `{{count}} board`,
      board_other: `{{count}} boards`,
      report_one: `{{count}} report`,
      report_other: `{{count}} reports`,
    },
  },
  'warning-hero': {
    'empty-board': {
      'header-text': `Create a board to monitor your metrics in one place.`,
      'board-new': `New Board`,
      'board-tooltip': `**New Board** generates a blank board so you can start building from scratch.`,
      'template-new': `Use a Template`,
      'template-tooltip': `**Use a Template** to add your events and Mixpanel will generate a starter board for you. This feature is currently in beta.`,
    },
    'contact-support': `If this issue persists, please [contact support](https://mixpanel.com/get-support).`,
    unsupported: `Unsupported Request`,
    timeout: `Query Timeout`,
    'timeout-remedy': `Your query is timing out because it's too large. Please try reducing the date range.`,
    'cardinality-limit': `Cardinality limit exceeded. Consider reducing the date range, removing breakdowns, or filtering out data.`,
    'too-many-arguments': `The query contains too many values. Try reducing the number of filters or filtered values.`,
    'precondition-failed': `Precondition failed. If this issue persists, please [contact support](https://mixpanel.com/get-support).`,
    'bad-request': `The client has issued a malformed request.`,
    'server-error': `Oops, something seems to have gone wrong. Please try again.`,
    'invalid-formulas': `One or more of the formulas are invalid`,
    'list-property-constant': `Cannot hold List Property constant.`,
    'hpc-limit-with-list': `Can only have one HPC when using a list property.`,
    'sensitive-data': `Report contains classified data`,
    'property-unavailable': `The property "{{property}}" is no longer available for use. \n Please use another property or contact your project admin`,
    'lookup-property-unavailable': `The lookup table property "{{property}}" is no longer available for use. \n Please use another property or contact your project admin`,
    'unauthorized-workspace': `Unauthorized for report's data view`,
    'cta-header': {
      sampling: `Use query sampling to get results faster`,
      'high-cardinality-multi-pass': `This breakdown has many unique values, so we're running a more precise calculation - hang tight.`,
      both: `This breakdown has many unique values, so we’re running a more precise calculation - hang tight. You can use query sampling to get results faster.`,
    },
    'sampling-cta': `Re-run with query time sampling`,
    'high-cardinality-multi-pass-cta': `Cancel and show me approximate data`,
  },
  events: {
    'no-matches': `No events matched your query.`,
    'no-matches-sentinel': `No events matched through {{sentinelTime}}.`,
    'showing-sentinel': `Showing {{count}} results through {{sentinelTime}}.`,
    'showing-count': `Showing **{{count}} most recent results**`,
    'showing-count-of': `Showing **{{count}} most recent results** of {{totalCount}} matches`,
    'open-as-page': `Open as page`,
    search: `Search events`,
    'search-tooltip': `Search common event properties such as _distinct_id_ and _$current_url_, common user properties such as _$name_ and _$email_, and any properties specified in the table below.`,
    'load-more': `Load more`,
    fetching: `Fetching events...`,
    'search-properties': `Search event properties`,
    'json-mode': `JSON mode`,
    'view-in-insights': `View in $t(mixology.app.insights)`,
    'view-in-lexicon': `View in $t(mixology.app.lexicon)`,
    'view-replay': `View Replay`,
    'property-key-filter': {
      'all-properties': `All Properties`,
      'your-properties': `Your Properties`,
      'mixpanel-properties': `Mixpanel Properties`,
    },
    export: {
      title: `Export Event Table`,
      description: `Modal for exporting events with format and property options`,
      confirm_one: `Export {{count}} event`,
      confirm_other: `Export {{count}} events`,
      'export-type': `Type`,
      'no-permission': `You don't have permission to export`,
      failed: `Failed to export events. Please try again or contact support if the issue persists.`,
      properties: `Properties on events`,
      'properties-selected': `Only selected columns`,
      'properties-all': `All event properties`,
    },
    actions: {
      'hide-filter': `Hide Filter`,
      'show-filter': `Show Filter`,
      'edit-columns': `Edit Columns`,
      export: `Export`,
    },
  },
  'map-lookup-table': {
    'lookup-table': `Lookup Table`,
    'create-title': `Create Lookup Table Mapping`,
    'import-title': `Import Lookup Table`,
    'select-placeholder': `Select Existing`,
    import: `Import CSV`,
    uploading: `Uploading file...`,
    'uploading-percent': `Uploading file ({{percent}}%)`,
    processing: `Processing file...`,
    applied: `Applied temporary lookup table mapping`,
    'applied-new': `Applied temporary mapping to new lookup table`,
    'map-to-prop': `Map to Property`,
    'existing-mapping': `This property has an existing mapping that will be overwritten.`,
    'learn-more': `Learn more about Lookup Tables`,
    overwrite: `Overwrite Property Mapping`,
    'overwrite-description': `The property chosen is already mapped. Overwrite?`,
  },
  lexicon: {
    'not-implemented-title': `We haven't received any data yet`,
    'not-implemented-description': `You'll need to install Mixpanel's library to start tracking your user's actions`,
    'not-implemented-cta': `Install Mixpanel`,
    'magic-merge': {
      'powered-by': `Powered by Mixpanel AI`,
      'has-groups-copy': `Your data could use some help`,
      'tidy-up-cta': `Tidy Up`,
      'tidying-cta': `Tidying up...`,
      'clean-copy': `Everything looks clean`,
      'view-details-cta': `View Details`,
      'tidy-up-popover-title': `Clean Up Your Data`,
      'reduce-clutter-title': `Reduce clutter`,
      'reduce-clutter-description': `Merge duplicate events and properties`,
      'alpha-tag': `Alpha`,
      modal: {
        title: `Merge Duplicate Events and Properties`,
        subtitle_one: `1 duplicate identified. Consider merging to reduce clutter.`,
        subtitle: `{{count}} duplicates identified. Consider merging to reduce clutter.`,
        'events-section': `Events`,
        'event-properties-section': `Event Properties`,
        'cancel-cta': `Cancel`,
        'primary-cta_one': `Merge {{count}} Duplicate`,
        'primary-cta': `Merge {{count}} Duplicates`,
        'more-chip': `+{{count}} More`,
        'less-chip': `View Less`,
        'canonical-banner': `This will become the main event. The other event(s) will be merged into this one.`,
        'success-toast_one': `{{count}} duplicate merged`,
        'success-toast': `{{count}} duplicates merged`,
        'dismiss-toast_one': `{{count}} duplicate dismissed`,
        'dismiss-toast': `{{count}} duplicates dismissed`,
        'error-toast': `Couldn't complete merge. Try again.`,
        'no-permission-tooltip': `You do not have permission to merge in this project.`,
        'empty-copy': `Your data looks clean.`,
        'error-copy': `Couldn't load suggestions. Try again later.`,
        'loading-aria': `Loading merge suggestions`,
        'edit-group-aria': `Edit group`,
        'done-cta': `Done`,
        'drag-to-reorder': `Drag to reorder`,
        'merge-into-badge': `Merge into`,
        'remove-from-group-aria': `Remove {{name}} from group`,
      },
    },
    usage: `Usage`,
    'event-replays': `Recent Replays`,
    'event-replays-more': `See More`,
    details: `Details`,
    'no-data': `There is no data for the selected tab.`,
    'no-permission-for-verify': `You do not have permission to verify this. Please contact your admin.`,
    'operation-disallowed': `Unable to do this operation at this time`,
    'error-saving': `Your change could not be saved. If this issue persists please [contact support](https://mixpanel.com/get-support).`,
    'custom-property-updated': `Custom property successfully updated`,
    'text-input-update-failed': `Failed to update text input`,
    audit: {
      description: `Comparing new data against the snapshot of data sent between {{startDate}} - {{endDate}}`,
      'x-unexpected_zero': `No unexpected events and properties in the last 7 days`,
      'x-unexpected_one': `{{count}} unexpected event or property in the last 7 days`,
      'x-unexpected_other': `{{count}} unexpected events and properties in the last 7 days`,
      tabs: {
        'unexpected-events': `Unexpected Events`,
        'unexpected-properties': `Unexpected Properties`,
        'missing-properties': `Missing Properties`,
        'unexpected-data-types': `Unexpected Data Types`,
      },
      'event-name': `Event Name`,
      event: `Event`,
      platform: `Platform`,
      version: `Version`,
      'app-version': `App Version`,
      count: `Count`,
      'count-7-days': `Count (Last 7 days)`,
      property: `Property`,
      name: `Name`,
      'data-type': `Date Type`,
      action: `Action`,
      type: `Type`,
      'type-tooltip': `Displays when the data type does not match the expected data type for a property in your current implementation`,
      'empty-restricted': `Data Audit is restricted ato events only for this project`,
      'empty-no-results': `No {{entityName}} in the last 7 days`,
      'empty-enable': `Enable Data Audit to view possible issues with your implementation`,
      'mark-expected': `Mark as Expected`,
      'mark-expected-no-perms': `You do not have permission to mark as expected`,
    },
    export: {
      export: `Export`,
      title: `Lexicon Export`,
      send: `Send CSV`,
      description: `We will email **{{userEmail}}** a CSV file of your selections.`,
      options: `Export Options`,
      'option-events': `Events & Properties`,
      'option-profiles': `User Profile Properties`,
      legacy: `Legacy Format`,
      'legacy-label': `Use legacy export format`,
      'zero-counts': `Zero Counts`,
      'zero-counts-label': `Include Zero Counts`,
      'zero-counts-tooltip': `If selected, the export will include ALL events and properties even if they have no values for the past 30 days. Note that selecting this option will increase the time it takes to generate the export and could result in failure if the export is too large.`,
    },
    'tracked-data': `Tracked Data`,
    'saved-definitions': `Saved Definitions`,
    tabs: {
      behaviors: `Behaviors`,
      'custom-events': `Custom Events`,
      'custom-event-properties': `Custom Event Properties`,
      'custom-profile-properties': `Custom Profile Properties`,
      'advanced-deletion': `Data Deletion`,
      'data-drop-and-deletion': `Data Drop and Deletion`,
      'data-standards': `Data Standards`,
      'data-rules': `Data Rules`,
      events: `Events`,
      'event-approval': `Event Approval`,
      'event-properties': `Event Properties`,
      formulas: `Formulas`,
      'lookup-tables': `Lookup Tables`,
      metrics: `Metrics`,
      'profile-properties': `Profile Properties`,
      cohorts: `$t(mixology.cohort_plural)`,
      'volume-monitoring': `Volume Monitoring`,
      'data-volume-monitoring': `Data Volume Monitoring`,
      issues: `Issues`,
      tags: {
        new: withComment(`translate it as a noun such as a tag for a new feature on a website`, `New`),
        alpha: `Alpha`,
        beta: `Beta`,
      },
    },
    'info-banner': {
      'tracked-description-suffix': `your team has implemented.`,
      'computed-description-suffix': `your team has created and saved.`,
      'behavior-description-prefix': `Custom behavior definitions`,
      'metric-description-prefix': `KPI Metrics and Formulas`,
      cta: `View Docs`,
    },
    import: `Import`,
    'import-wizard': {
      'choose-source-description': `Import your event and property definitions using the following options.`,
      'learn-more': `Learn more about Lexicon Import`,
      'segment-token': `Segment Token`,
      'segment-credentials-help': `How do I access credentials from Segment?`,
      'available-plans': `Available Plans`,
      'select-file': `Select File`,
      'processing-file': `Processing file...`,
      'mparticle-export-help': `How do I export JSON from mParticle?`,
      'external-integration-description': `Use your {{integrationName}} tracking plan as the source of truth for Lexicon. When you publish a new version of your {{integrationName}} tracking plan, Lexicon will automatically reflect those changes. See the documentation for details on setting up this integration.`,
      'integration-docs': `{{integrationName}} Integration Documentation`,
      property: `Property`,
      type: `Type`,
      description: `Description`,
      required: `Required`,
      examples: `Examples`,
      'required-value': `Required`,
      optional: `Optional`,
      'no-properties': `No properties found for this entity`,
      'event-name': `Event Name`,
      'number-of-properties': `Number of Properties`,
      search: `Search`,
      uploading: `Uploading to Lexicon...`,
      done: `Done!`,
      'back-to-lexicon': `Back to Lexicon`,
      'import-failed': `Import Failed`,
      'try-again': `Try Again`,
      'select-csv': `Select CSV`,
      review: `Review`,
      'input-credentials': `Input Credentials`,
      'select-plan': `Select Plan`,
      'select-json': `Select JSON`,
      'lexicon-import': `Lexicon Import`,
      'import-from': `Import from {{source}}`,
      back: `Back`,
      'replace-entries': `Replace Lexicon entries`,
      'replace-entries-tooltip': `Replace the display name and description on Lexicon with new entries.`,
      cancel: `Cancel`,
      'replace-all-title': `Replace All Lexicon Entries?`,
      continue: `Continue`,
      'replace-all-warning': `This will replace the display name and description on Lexicon with the new data for each event. This action once taken, cannot be undone.`,
      'replace-all-csv-note': `The new CSV will replace all existing entries.`,
      'replace-all-event-note': `Please note, replacing entries does not remove the event if triggered in the last 30 days. The event will still show up on Lexicon.`,
    },
    'lookup-table': `Lookup Table`,
    'mapped-lookup-table': `Mapped Lookup Table`,
    'event-prop-schema': `Event & Property Schema`,
    'import-schema': `Import Schema`,
    'manage-permissions': `Manage Data Permissions`,
    'manage-permissions-tooltip': `Manage in project settings`,
    'lookup-tables-upsell': {
      header: `Enrich data in seconds`,
      description: `Change existing property values or create new ones by uploading a list of properties into Mixpanel. Mixpanel's Lookup Tables let you enrich your data so you can run more ad-hoc analyses without worrying about ways to ingest missing data.`,
      'list-header': `When you upgrade to the Growth plan, you also get:`,
      'list-item-1': `Unlimited saved reports & cohorts`,
      'list-item-2': `Impact report with causal inference`,
      'list-item-3': `Option to purchase Group Analytics & Data Pipelines`,
    },
    banners: {
      enable: `Enable`,
      'data-volume-monitoring': {
        title: `Data Volume Monitoring`,
        description: `Get notified when an unexpected trend is discovered on any event you track.`,
      },
      'event-approval': {
        title: `Event Approval`,
        description: `Get notified on all new events entering your project to stay on top of your implementation.`,
      },
      'hidden-events-limit': {
        title: `Your project has reached the hidden event limit.`,
        description: `You have {{count, number}} hidden events. Up to 3000 events can be hidden from your reporting experience. You may start seeing hidden events appear in Flows or "All Events" queries.`,
      },
    },
    loading: `Loading...`,
    unknown: `Unknown`,
    columns: {
      name: `Name`,
      type: `Type`,
      description: `Description`,
      'description-placeholder': `Add Description...`,
      'your-access': `Your access`,
      'display-name': `Display name`,
      created: `Created`,
      'created-by': `Created by`,
      'query-30-days': `30 day queries`,
      'last-queried': `Last queried`,
      'first-queried': `First queried`,
      'last-edited': `Last edited`,
      definition: `Definition`,
      'recently-queried': `Recently queried by`,
      status: `Status`,
      queries: `Queries`,
      'verified-by': `Verified by`,
      tags: `Tags`,
      platforms: `Platforms`,
      image: `Image`,
      visibility: `Visibility`,
      editing: `Editing`,
      'cohort-name': `Cohort Name`,
      count: `Count`,
      goals: `Goals`,
    },
    'search-placeholder': `Search`,
    'view-insights': `View in Insights`,
    'view-report': `View in Report`,
    'analyze-event': `Analyze Event`,
    entity: `entity`,
    'entity-not-found': `The selected {{entityName}} could not be found.`,
    'row-updated': `"{{rowName}}" has been updated.`,
    'row-updated-error': `"{{rowName}}" could not be updated.`,
    'event-auto-approved': `Your event, "{{eventName}}", is auto-approved and no longer pending.`,
    'events-merged': `Events have been successfully merged`,
    'props-merged': `Properties have been successfully merged`,
    'events-updated': `Events updated`,
    'props-updated': `Properties updated`,
    'prop-viz-updated': `Property visibility updated`,
    'edit-perms': `Edit Permissions`,
    'loading-error': `Could not load {{entityName}}.`,
    'issue-persists': `If this issue persists please [contact support](https://mixpanel.com/get-support).`,
    behaviors: {
      details: `Behavior Details`,
      'update-success': `Updated behavior details successfully.`,
      deleted_one: `Behavior has been deleted`,
      deleted_other: `Behaviors have been deleted`,
      'delete-loading_one': `Deleting behavior...`,
      'delete-loading_other': `Deleting behaviors...`,
      'delete-error': `There was an issue deleting the behavior. Please try again.`,
      'delete-error-permission': `You do not have permission to delete this behavior.`,
      'delete-confirm-title': `Delete Behavior`,
      'delete-confirm-description': `Do you want to delete this behavior?`,
      'delete-confirm-button': `Delete`,
      'deleted-behavior': `Deleted behavior`,
      'error-deleting-behavior': `Error deleting behavior`,
      'error-loading': `Could not load behavior`,
      'error-updating-behavior': `Error updating behavior`,
      'contact-admin-delete': `Contact your admin to delete this behavior.`,
      'failed-to-load-behavior': `Failed to load behavior`,
      actions: {
        'delete-tooltip': `Remove behavior from UI`,
        'delete-confirm-title': `Delete Behavior(s)?`,
        'delete-confirm-description': `The behavior(s) will be removed from the behavior dropdown. Saved reports using the selected behavior(s) may be affected.`,
        'description-tooltip': `Explain what your behavior is, e.g. it's sequence of actions or how it gets activated...`,
        'verify-tooltip': `Verify behavior from UI`,
        'verify-confirm-title': `Verify Behavior(s)?`,
        'verify-confirm-description': `The behavior(s) will be marked as verified and will no longer appear in the unverified list.`,
        'verify-success': `Behavior(s) verified`,
      },
      'type-filter': {
        all: `All Types`,
        retention: `$t(mixology.app.retention)`,
        funnel: `$t(mixology.funnel)`,
        simple: `$t(query-builder.metrics.simple)`,
        'default-label': `Type`,
      },
    },
    cohorts: {
      'no-delete-permission': `You don't have permission to delete this Cohort`,
      'no-verify-permission': `You don't have permission to verify this Cohort`,
      'cohorts-count_other': `{{count}} cohorts`,
      'cohorts-count_one': `{{count}} cohort`,
      'other-cohorts-count_other': `{{count}} other cohorts`,
      'other-cohorts-count_one': `{{count}} other cohort`,
      'delete-cohorts-with-dependency': `$t(lexicon.cohorts.cohorts-count, {"count": {{dependencyCount}} }) will not be deleted because they are being referenced. $t(lexicon.cohorts.other-cohorts-count, {"count": {{deletableCount}} }) will be deleted.`,
      'create-cohort': `Create Cohort`,
      'cohort-duplicated': `Cohort successfully duplicated`,
      'cohort-created': `Cohort successfully created`,
      'cohort-updated': `Cohort successfully updated`,
      'cohort-update-failed': `Failed to update cohort`,
      'cohorts-load-failed': `Failed to load cohorts`,
      'cohort-not-restored': `Your cohort could not be restored`,
      'cohort-delete-failed': `Failed to delete cohort`,
      'delete-selected-tooltip': `Delete selected cohorts`,
      verify: `Verify`,
      'verify-selected-tooltip': `Verify selected cohorts`,
      'verify-confirm-title': `Verify cohorts?`,
      'verify-confirm-description': `Verifying cohorts indicates that you have reviewed and approved them for use in analysis.`,
      'verify-success': `Cohort verified successfully`,
      unverify: `Unverify`,
      'unverify-selected-tooltip': `Unverify selected cohorts`,
      'unverify-confirm-title': `Unverify cohorts?`,
      'unverify-confirm-description': `Unverifying cohorts will remove their verified status.`,
      'unverify-success': `Cohort unverified successfully`,
      'restore-deleted-failed_other': `Unable to restore deleted cohorts`,
      'restore-deleted-failed_one': `Unable to restore deleted cohort`,
      'cohorts-delete-in-progress': `Cohorts are being deleted...`,
      'cohorts-deleted_other': `Cohorts successfully deleted`,
      'cohorts-deleted_one': `Cohort successfully deleted`,
      'cohorts-delete-failed_other': `Failed to delete selected cohorts`,
      'cohorts-delete-failed_one': `Failed to delete selected cohort`,
      'cohorts-restored_other': `Cohorts successfully restored`,
      'cohorts-restored_one': `Cohort successfully restored`,
      'confirm-delete-entities': `Do you want to delete {{length}} {{entityTypeName}}?`,
      'confirm-delete-cohort': `Delete $t(mixology.cohort)?`,
      'confirm-you-delete-cohort': `Do you want to delete {{cohortName}}?`,
      'cohort-create-failed-explanation': `Failed to create cohort: {{message}}`,
      'no-share-permission': `You don't have permission to share this $t(mixology.cohort)`,
      'editing-unrestricted': `Unrestricted`,
      'export-to': `Export to...`,
      export: `Export`,
      'view-profile-types': `View {{profileTypes}}`,
      'view-replays': `View Replays`,
      'visualize-trend': `Visualize Trend`,
      'refresh-count': `Refresh Count`,
      'view-dependency': `View dependency`,
      'cannot-delete': `Cannot be deleted. Click to see details`,
      'active-integrations': `All Active Integrations`,
      'see-status': `See status`,
      'unable-to-connect-integration': `Unable to connect {{integrationDisplayName}}`,
      'disconnected-dynamic-export': `Successfully disconnected dynamic export from {{integrationDisplayName}}`,
      'unable-to-disconnect-integration': `Unable to disconnect {{integrationDisplayName}}`,
      'one-time-export-in-progress': `A one-time export is already in progress for this cohort and integration. Please wait for it to complete before starting another.`,
      disconnect: `Disconnect`,
      'disconnect-dynamic': `Disconnect dynamic export`,
      'confirm-stop-dynamic': `Are you sure you want to stop this dynamic export? By disconnecting, this cohort will no longer be dynamically updated in {{integrationDisplayName}}.`,
      'description-placeholder': `Add a description for this cohort`,
      'cohorts-moved-modal': {
        title: `Cohorts now live in Lexicon`,
        description: `You can find your Cohorts in Lexicon, alongside your other saved definitions like metrics, custom events, and custom properties.`,
        'dismiss-button-text': `Got It`,
      },
      'cohort-count-fetch-failed_other': `Unable to fetch cohorts count`,
      'cohort-count-fetch-failed_one': `Unable to fetch cohort count`,
      'some-cohort-count-fetch-failed': `Unable to fetch the count for the following cohorts: {{failedCohorts}}`,
      'cohorts-moved': `Cohorts has moved`,
      'previous-exports': `Previous exports`,
    },
    formulas: {
      'cant-delete': `Contact your admin to delete this formula.`,
      delete: `Delete Formula`,
      'delete-tooltip': `Delete the selected formulas.`,
      'delete-description': `Do you want to delete this formula?`,
      'delete-failure': `Failed to delete formula.`,
      'delete-success': `Formula deleted`,
      details: `Formula Details`,
      'load-failure': `Failed to load formula.`,
      search: `Search formulas`,
    },
    metrics: {
      'change-not-saved': `Your change could not be saved.`,
      'changes-not-saved': `Your changes could not be saved.`,
      'cant-delete': `Contact your admin to delete this metric.`,
      delete: `Delete Metric`,
      'delete-description': `Do you want to delete this metric?`,
      'delete-description-long_one': `Do you want to delete this metric? Saved reports using the selected metrics may be affected.`,
      'delete-description-long_other': `Do you want to delete these metrics? Saved reports using the selected metrics may be affected.`,
      'delete-failure': `Failed to delete metric.`,
      'delete-multiple-success': `Metrics deleted`,
      'delete-success': `Metric deleted`,
      verify: `Verify Metric`,
      'verify-tooltip': `Verify the selected metrics.`,
      'verify-confirm-description': `Do you want to verify this metric? Verified metrics will no longer appear in the unverified list.`,
      'verify-confirm-title': `Verify Metric`,
      'verify-success': `Metrics verified`,
      details: `Metric Details`,
      'load-failure': `Failed to load metric.`,
      type: `Type`,
      'type.metric': `Metric`,
      'type.formula': `Formula`,
      'type.behavior': `Behavior`,
      'type-filter.all': `All Types`,
      'type-filter.metric': `Metrics`,
      'type-filter.formula': `Formulas`,
    },
    actions: {
      apply: `Apply`,
      tag: `Tag`,
      'tag-tooltip': `Organize your events into tags`,
      hide: `Hide`,
      unhide: `Make Visible`,
      merge: `Merge`,
      unmerge: `Unmerge`,
      block: `Block`,
      unblock: `Unblock`,
      drop: `Drop`,
      undrop: `Undrop`,
      'map-lookup-table': `Map To Lookup Table`,
      'unmap-lookup-table': `Unmap From Lookup Table`,
      'mark-sensitive': `Mark Classified`,
      'mark-sensitive-tooltip': `Mark selected properties as classified`,
      'mark-sensitive-confirm-title': `Mark Classified?`,
      'mark-sensitive-confirm-description': `This operation prevents some users from viewing and analyzing properties in cohorts and saved reports. \n\n Only users granted access will be able to view Classified Data. You can change permissions on Project settings.`,
      'unmark-sensitive': `Unmark Classified`,
      'unmark-sensitive-tooltip': `Mark selected properties as unclassified`,
      'upgrade-to-map-to-lookup-table': `Upgrade To Map To Lookup Table`,
      filter: `Filter`,
      'bulk-actions': `Bulk Actions`,
      verify: `Verify`,
    },
    'merge-modal': {
      'events-title': `Merge Events`,
      'events-explanation': `Merging takes your selected events and combines them into a single event without affecting the raw data.`,
      'events-selected': `Selected Events`,
      'events-into': `Merge selected events into...`,
      'events-select': `Select an event`,
      'events-success': `Events have been successfully merged`,
      'event-properties-title': `Merge Properties`,
      'event-properties-explanation': `Merging takes your selected properties and combines them into a single property without affecting the raw data.`,
      'event-properties-selected': `Selected Properties`,
      'event-properties-into': `Merge selected properties into...`,
      'event-properties-select': `Select an event property`,
      'event-properties-success': `Properties have been successfully merged`,
      'undo-note': `Note: This can be undone at anytime`,
      volume: `Volume`,
    },
    'map-lookup-table-modal': {
      title: `Map Lookup Table`,
      select: `Select a Lookup Table`,
    },
    'custom-events': {
      columns: {},
      actions: {
        'delete-tooltip': `Remove custom event from UI`,
        'delete-confirm-title': `Delete custom event(s)?`,
        'delete-confirm-description': `The custom event(s) will be removed from event selection dropdowns. Cohorts and saved reports using the selected custom event(s) may be affected, and notifications that are targeted using one of these events will be paused.`,
        'verify-tooltip': `Verify the selected custom events.`,
        'verify-confirm-title': `Verify custom event(s)?`,
        'verify-confirm-description': `The custom event(s) will be marked as verified and will no longer appear in the unverified list.`,
      },
      type: {
        custom: `Custom Events`,
      },
      'load-failure': `Failed to load event definition`,
      'deleted-toast_one': `Custom event has been deleted`,
      'deleted-toast_other': `Custom events have been deleted`,
      'delete-error': `There was an issue deleting the custom event. Please try again.`,
      'delete-loading': `Deleting custom event...`,
      'verify-success': `Custom events verified`,
      'verify-error': `There was an issue verifying the custom event. Please try again.`,
    },
    properties: {
      status: {
        all: `All Status`,
        new: `New`,
        pending: `Pending`,
        visible: `Visible`,
        hidden: `Hidden`,
        blocked: `Blocked`,
        dropped: `Dropped`,
        merged: `Merged`,
        classified: `Classified`,
        'default-label': `Status`,
      },
      type: {
        all: `All Types`,
        your: `Your Properties`,
        mixpanel: `Mixpanel Properties`,
        custom: `Custom Properties`,
        'default-label': `Type`,
      },
      'event-tabs': {
        yours: `Your Events`,
        mixpanel: `Mixpanel Events`,
      },
      'icon-tooltip': {
        lookup: `Lookup property`,
        custom: `Custom property`,
      },
      'cannot-open-details': `Error: could not open property details. If this issue persists please [contact support](https://mixpanel.com/get-support).`,
    },
    'event-properties': {
      details: `Property Details`,
      'failed-to-load': `Failed to load property definition`,
      actions: {
        'hide-tooltip': `Hide selected properties from Mixpanel UI`,
        'hide-confirm-title': `Hide Properties?`,
        'hide-confirm-description': `Hiding properties cleans up the interface, so things you aren't using or sent by mistake don't clutter your dropdowns or charts. This action does not stop properties from being sent to Mixpanel and can be undone at any time.`,
        'hide-success_other': `Your properties were hidden.`,
        'hide-success_one': `Your property was hidden.`,
        'unhide-tooltip': `Make selected properties visible in Mixpanel UI`,
        'unhide-confirm-title': `Make Properties Visible?`,
        'unhide-confirm-description': `This will make these Properties visible in your dropdowns and charts`,
        'unhide-success_other': `Your properties were made visible.`,
        'unhide-success_one': `Your property was made visible.`,
        'block-tooltip': `Block selected properties at ingestion time`,
        'block-confirm-title': `Block Properties?`,
        'block-confirm-description': `This will prevent new data for the selected properties from being processed by Mixpanel. It may take up to a few hours for this change to take full effect. Caution: Blocked data cannot be recovered!`,
        'block-success_other': `Your properties were blocked.`,
        'block-success_one': `Your property was blocked.`,
        'unblock-tooltip': `Allow selected properties to be ingested into Mixpanel`,
        'unblock-success_other': `Your properties were unblocked.`,
        'unblock-success_one': `Your property was unblocked.`,
        'drop-tooltip': `Drop selected properties at ingestion time`,
        'drop-confirm-title': `Drop Properties?`,
        'drop-confirm-description': `This will prevent new data for the selected properties from being processed by Mixpanel. It may take up to a few hours for this change to take full effect. Caution: Dropped data cannot be recovered!`,
        'drop-success_other': `Your properties were dropped.`,
        'drop-success_one': `Your property was dropped.`,
        'drop-failed_one': `Error dropping property.`,
        'undrop-tooltip': `Allow selected properties to be ingested into Mixpanel`,
        'undrop-success_other': `Your properties were undropped.`,
        'undrop-success_one': `Your property was undropped.`,
        'delete-confirm-title': `Delete Custom Properties?`,
        'delete-confirm-description': `The custom properties will be removed from event property dropdowns. Cohorts and saved reports using the selected custom properties may be affected.`,
        'delete-success_other': `Your custom properties were deleted.`,
        'delete-success_one': `Your custom property was deleted.`,
        'delete-failure': `Failed to delete custom properties: {{failedNames}}`,
        'delete-tooltip': `Remove custom property from UI`,
        'merge-tooltip': `Merge selected properties at query time`,
        'merge-tooltip-disabled': `Properties can't be merged if they're Mixpanel-defined, dimensions, sensitive, or already merged.`,
        'unmerge-tooltip': `Unmerge selected properties`,
        'unmerge-success': `Your properties were unmerged`,
        'map-tooltip': `Map properties to Lookup Table`,
        'map-success_one': `Your property was mapped to Lookup Table`,
        'map-success_other': `Your properties were mapped to Lookup Table`,
        'unmap-confirm-title': `Unmap Lookup Table?`,
        'unmap-confirm-description': `This operation will impact saved reports and boards that are using this property.`,
        'unmap-confirm-button-label': `Unmap Lookup Table`,
        'unmap-tooltip': `Unmap properties from Lookup Tables`,
        'unmap-success_one': `Your property was unmapped from Lookup Tables`,
        'unmap-success_other': `Your properties were unmapped from Lookup Tables`,
        'upgrade-to-map-to-lookup-table-tooltip': `When you upgrade your plan, you can enrich your data with additional data within seconds with Lookup Tables.`,
        history: `Details History`,
        'history-tooltip': `View the changes made to this property`,
      },
      columns: {
        'description-tooltip': `Add a description for your property`,
        'display-name-placeholder': `Add Property Name...`,
        'display-name-tooltip': `The property name that displays in the Mixpanel interface`,
        'merged-properties': `Merged Properties`,
        'example-value': `Example Value`,
        'example-value-tooltip': `Add one or more sample values`,
        'example-value-placeholder': `Add Values...`,
        'event-count': `Events With Property`,
        'scd-type-string-option': `Historic`,
      },
      source: `Source`,
    },
    'profile-properties': {
      'failed-to-load': `Failed to load profile property definition`,
      columns: {
        'user-count': `Users With Property`,
      },
      'profile-type': {
        user: `User`,
      },
    },
    'lookup-tables': {
      'export-long': `Export Lookup Table`,
      'replace-long': `Replace Lookup Table`,
      'update-success': `Updated Lookup Table Details Successfully.`,
      'update-failure': `Failed to update Lookup Table Details.`,
      'file-imported': `File imported`,
      'file-imported-with-warning': `File imported - {{warning}}`,
      actions: {
        'delete-disabled-tooltip': `Contact your admin to delete this Lookup Table`,
        'delete-disabled-admin-tooltip': `You do not have edit permissions to delete this Lookup Table`,
        'delete-disabled-import-tooltip': `This Lookup Table is being imported from a warehouse. Please delete the import first.`,
        'delete-confirm-title': `Delete Lookup Table(s)?`,
        'delete-confirm-description': `The Lookup Table(s) will be removed.`,
        'delete-tooltip': `Remove Lookup Table`,
        export: `Export`,
        'export-tooltip': `Export Lookup Table as CSV`,
        import: `Import Lookup Table`,
        'import-success': `Lookup Table imported`,
        'import-success-cta': `View table`,
        delete: `Delete`,
      },
      deleted: `Lookup tables deleted`,
      'downloading-csv': `Downloading lookup table CSV`,
      'error-deleting': `Could not delete lookup table.`,
      exporting_one: `Exporting {{count}} lookup table`,
      exporting_other: `Exporting {{count}} lookup tables`,
      'export-limited': `Lookup table exports are limited to {{count, number}} rows each`,
      'export-error_one': `Could not export {{count}} lookup table`,
      'export-error_other': `Could not export {{count}} lookup tables`,
      'export-error-name': `could not export lookup table: {{tableName}}`,
      'details-header': `Lookup table details`,
      columns: {
        'last-edited': `Last Edited`,
        'properties-linked': `Properties Linked`,
      },
      'properties-table': {
        header: `Lookup Table Properties ({{rowCount}} Profiles)`,
        name: `Name`,
        'display-name': `Display Name`,
        'empty-message': `There are no properties to show.`,
      },
      details: {
        title: `Lookup table details`,
        deleted: `Deleted Lookup Table`,
        'load-failed': `Failed to load Lookup Table`,
        'properties-linked': `Properties Linked`,
        'profile-properties': `Lookup Table Properties`,
        'profile-properties_one': `Lookup Table Properties (1 Profile)`,
        'profile-properties_other': `Lookup Table Properties ({{count}} Profiles)`,
        'no-properties': `There are no properties to show.`,
        id: `ID`,
      },
    },
    goals: {
      'single-goal': `goal`,
      'plural-goals': `goals`,
    },
    tags: {
      'default-label': `Tags`,
      header: `Tags`,
      all: `All Tags`,
      untagged: `Un-Tagged`,
      search: `Search tags...`,
      loading: `Loading tags...`,
      'no-tags': `No tags found`,
      create: `Create new tag`,
    },
    'all-tags': `All Tags`,
    'un-tagged': `Un-Tagged`,
    events: {
      details: `Details`,
      columns: {
        name: `Event Name`,
        'merged-events': `Merged Events`,
        platforms: `Platforms`,
        'used-in': `Used in`,
        'description-tooltip': `Explain what your event is used for or how it is triggered`,
        'display-name-placeholder': `Add Event Name...`,
        'first-tracked': `First tracked`,
        'display-name-tooltip': `The event name that displays in the Mixpanel interface`,
        'volume-30-days': withComment(`number of events within last 30 days`, `30 day volume`),
        owners: `Owners`,
        owner: `Owner`,
      },
      actions: {
        'hide-tooltip': `Hide selected items from Mixpanel UI`,
        'hide-confirm-title': `Hide Events?`,
        'hide-confirm-description': `Hiding events cleans up the interface, so things you aren't using or sent by mistake don't clutter your dropdowns or charts. This action does not stop events from being sent to Mixpanel and can be undone at any time.`,
        'hide-success_other': `Your events were hidden.`,
        'hide-success_one': `Your event was hidden.`,
        'unhide-tooltip': `Make selected items visible in Mixpanel UI`,
        'unhide-tooltip-non-compliant': `You can't make non-compliant pending events visible until compliance issues are resolved.`,
        'unhide-confirm-title': `Make Events Visible?`,
        'unhide-confirm-description': `This will make these Events visible in your dropdowns and charts.`,
        'unhide-confirm-description-add-on': `This will only apply to {{numEventsToChange}} events. You can't make non-compliant pending events visible until compliance issues are resolved.`,
        'unhide-success_other': `Your events were made visible.`,
        'unhide-success_one': `Your event was made visible.`,
        'merge-tooltip': `Merge selected events at query time`,
        'unmerge-tooltip': `Unmerge selected items`,
        'unmerge-success': `Your events were unmerged`,
        'tag-events': `Tag Events`,
        'block-tooltip': `Block selected items at ingestion time`,
        'block-confirm-title': `Block Events?`,
        'block-confirm-description': `This will prevent new data for the selected event(s) from being processed by Mixpanel. It may take up to a few hours for this change to take full effect. Caution: Blocked data cannot be recovered!`,
        'block-success': `Events blocked`,
        'unblock-tooltip': `Allow selected items to be ingested into Mixpanel`,
        'unblock-success': `Events unblocked`,
        'drop-tooltip': `Drop selected items at ingestion time`,
        'drop-confirm-title': `Drop Events?`,
        'drop-confirm-description': `This will prevent new data for the selected event(s) from being processed by Mixpanel. It may take up to a few hours for this change to take full effect. Caution: Dropped data cannot be recovered!`,
        'drop-success': `Events dropped`,
        'drop-failed': `Error dropping event`,
        'undrop-tooltip': `Allow selected items to be ingested into Mixpanel`,
        'undrop-success': `Events undropped`,
        history: `Event History`,
        'history-tooltip': `View the changes made to this event`,
        'dismiss-warnings': `Dismiss Warnings`,
        'mark-expected': `Mark Expected`,
        'mark-expected-tooltip': `Mark this event as expected`,
        'edit-ownership': `Edit Ownership`,
        'add-tags': `Add tags...`,
        'add-platforms': `Add platforms...`,
        'verify-tooltip': `Mark selected events as verified`,
        'verify-confirm-title': `Verify Events?`,
        'verify-confirm-description': `Verifying events indicates that you have reviewed and approved them for use in analysis.`,
        'verify-success': `Your events were verified.`,
        'tag-update-success': `Tags updated successfully.`,
        'tag-update-failed': `Failed to update tags.`,
        'tag-deleted': `Tag deleted`,
        'tag-renamed': `Tag renamed`,
        'tag-delete-failed': `Failed to delete tag: {{errorMessage}}`,
        'tag-rename-failed': `Failed to rename tag: {{errorMessage}}`,
        'unknown-error': `Unknown error`,
        'manage-tag-tooltip': `Manage tag`,
        'delete-tag-title': `Delete tag?`,
        'delete-tag-description': `Removing this tag will delete it from Lexicon and from all events where it is applied.`,
        'edit-tag-title': `Rename tag`,
        'edit-tag-description': `Name`,
      },
      'filter-status': {
        all: `All Status`,
        visible: `Visible Events`,
        hidden: `Hidden Events`,
        dropped: `Dropped Events`,
        merged: `Merged Events`,
      },
      status: {
        'default-label': `Status`,
        all: `All Status`,
        pending: `Pending`,
        visible: `Visible`,
        hidden: `Hidden`,
        blocked: `Blocked`,
        dropped: `Dropped`,
        filtered: `Filtered`,
        'dropped-by-drop-filters-tooltip': `Event drop status is managed by drop filters. To drop or undrop this event, go to the Data Deletion tab and add or remove filters.`,
        merged: `Merged`,
        classified: `Classified`,
        'visible-merged': `$t(lexicon.events.status.visible), $t(lexicon.events.status.merged)`,
        'hidden-merged': `$t(lexicon.events.status.hidden), $t(lexicon.events.status.merged)`,
        'data-standard-issue-title': `Data Governance Issue`,
        'data-standard-issue-body': `The following issues were detected and affect the event status:`,
        'data-standard-detail-text': `Non-compliant, pending events can't be set to visible until compliance issues are resolved.`,
        'drop-filter-tooltip_one': `{{count}} drop filter applied to this event`,
        'drop-filter-tooltip_other': `{{count}} drop filters applied to this event`,
        'view-event-details': `View event details`,
      },
      type: {
        'default-label': `Type`,
        all: `All Types`,
        your: `Your Events`,
        mixpanel: `Mixpanel Events`,
      },
      'volume-warning': {
        all: `All Volume Warning Status`,
        warning: `Volume Warning Detected`,
        none: `No Warnings Detected`,
      },
      'property-tabs': {
        yours: `Your Properties`,
        mixpanel: `Mixpanel Properties`,
      },
      governance: {
        'default-label': `Compliance`,
        all: `All Compliance`,
        compliant: `Compliant`,
        'non-compliant': `Not Compliant`,
      },
      owner: {
        'default-label': `Owner`,
        all: `All Owners`,
        'no-owner': `No owner`,
        'empty-message': `No owners have been assigned to your events`,
      },
    },
    'event-details': {
      'event-owner-tooltip': `An event owner should be someone who can answer questions related to the event's context, such as implementation details or original purpose.`,
      'metric-owner-tooltip': `An owner is the person ultimately responsible for the performance of the metric`,
      'owner-update-failed': `Failed to update event owner.`,
      'assign-owner': `Assign teammate`,
      'assign-owner_plural': `Assign teammates`,
      'search-owner': `Search People ...`,
      'search-owner-or-team': `Search People or Teams ...`,
      'first-tracked-after': `First recorded tracking date may have been earlier. First tracked dates are only recorded after {{date}}.`,
      'first-tracked-prior': `This event was first tracked prior to {{date}}.`,
      'first-tracked-prior-two-years-window': `This event was first tracked prior to two years ago. [Learn more.]({{docsLink}})`,
      'first-tracked-after-two-years-window': `First recorded tracking date may have been earlier. First tracked dates look back two years. [Learn more.]({{docsLink}})`,
      'first-tracked-tooltip': `Over {{years}} years ago`,
      'unmerge-from-table': `Unmerge from the Lexicon table`,
      image: `Image`,
      'select-image': `Select Image`,
      'media-upload-error': `Failed to upload image. Please try again later.`,
      'media-deletion-error': `Failed to delete the image. Please try again later.`,
      'past-month-queries': `{{totalQueries}} queries in the past 30 days`,
      'loading-error': `Failed to load event definition.`,
      'owners-limit-warning': `Search People or Teams (10 max).`,
      'owners-limit-reached': `Maximum owners reached.`,
      'owners-limit-user-tooltip': `Only 10 owners can be assigned to an event. Create a team instead`,
      'owners-limit-team-tooltip': `Only 10 owners can be assigned to an event.`,
    },
    history: {
      'default-header': `{{type}} History`,
      'fetch-failed': `Failed to fetch history`,
      'first-date-info': `We began storing activity history on {{date}}. Activity that occurred before then may not be displayed.`,
      'search-placeholder': `Search {{type}} History`,
      'search-placeholder-general': `Search for a report or name...`,
      sort: `Sort`,
      'least-recent': `Least Recent`,
      'most-recent': `Most Recent`,
      'empty-message': `No {{type}} History available`,
      'previous-value': `Previous value`,
      'new-value': `New value`,
      dropped: `Dropped`,
      collected: `Collected`,
      merged: `Merged`,
      unmerged: `Unmerged`,
      edited: {
        description: `{{name}} edited the {{entityType}} description`,
        'display-name': `{{name}} edited the {{entityType}} display name`,
        verified: `{{name}} marked the {{entityType}} as verified`,
        unverified: `{{name}} marked the {{entityType}} as unverified`,
        hidden: `{{name}} hid the {{entityType}} from drop downs`,
        visible: `{{name}} made the {{entityType}} visible in drop downs`,
        dropped: `{{name}} marked the {{entityType}} to be dropped at ingestion`,
        collected: `{{name}} resumed collection of the {{entityType}}`,
        example: `{{name}} edited the {{entityType}} example value`,
        tags: `{{name}} edited the {{entityType}} tags`,
        platforms: `{{name}} edited the {{entityType}} platforms`,
        sensitive: `{{name}} marked the {{entityType}} as classified`,
        unsensitive: `{{name}} marked the {{entityType}} as not classified`,
        merged: `{{name}} merged the {{entityType}}`,
        'merged-into': `{{name}} merged the {{entityType}} into {{target}}`,
        unmerged: `{{name}} unmerged the {{entityType}}`,
        'unmerged-from': `{{name}} unmerged the {{entityType}} from {{target}}`,
      },
    },
    'singular-form-entity-types': {
      events: `event`,
      'custom-events': `custom event`,
      formulas: `formula`,
      metrics: `metric`,
      behaviors: `behavior`,
    },
    'verify-toast': {
      'verify-success': `Verified the {{entityType}}.`,
      'unverify-success': `Unverified the {{entityType}}.`,
      'verify-failed': `Verify failed.`,
      'unverify-failed': `Unverify failed.`,
    },
    'verify-tooltip': {
      event: `Verify this event <docsLink>Learn more</docsLink>`,
      behavior: `Verify this behavior <docsLink>Learn more</docsLink>`,
      formula: `Verify this formula <docsLink>Learn more</docsLink>`,
      metric: `Verify this metric <docsLink>Learn more</docsLink>`,
      cohort: `Verify this cohort <docsLink>Learn more</docsLink>`,
      undo: `Undo verification <docsLink>Learn more</docsLink>`,
    },
    'unavailable-creator-tooltip': `Creator information is unavailable`,
    'query-metadata-tooltip': `Earliest query information is only captured after {{date}}.`,
    'verification-status': {
      'default-label': `Verification`,
      all: `All Verifications`,
      verified: `Verified`,
      unverified: `Unverified`,
    },
    'data-actions': {
      title: `Data Actions`,
      'event-approval': `Event Approval`,
      'event-approval-description': `Maintain data quality by monitoring and approving newly tracked events. New events will stay **pending** and **hidden** until they are set to **visible** in Lexicon. Project Owners and Admins can review and approve events. Notify your team or slack channels about newly tracked events.`,
      'auto-approval': `Automate Event Approval with Data Standards`,
      'auto-approval-description': `Save time by auto-approving events that meet your data standards. Events that meet your data standards will automatically become **visible** and usable for your team. Events that do not comply will remain **pending** and **hidden** until fixed.`,
      'auto-approval-req-data-standards-tooltip': `Event Standards are required for this setting to work.`,
      'property-notification': `Property Notification`,
      'property-notification-description': `Get notified when new properties appear on new or existing events.`,
      'success-tooltip': `Data action setting updated successfully`,
      'error-tooltip': `Error updating data action setting`,
    },
    'data-standards': {
      settings: {
        title: `Data Rules`,
        description: `Set standards to enforce consistency and accuracy in your data. Review or automate approval for new incoming events. Project Owners can manage these settings. <helpLink>Learn more</helpLink>`,
        reset: `Reset`,
        save: `Save`,
        'confirm-title': `Save Data Standards`,
        'confirm-description': `Are you sure you want to save your changed data standards? These changes will take effect immediately after completing.`,
        'banner-warning': `{{percent}}% of your events ({{count}} total) fail to meet the data standards criteria below. [View non-compliant events]({{link}})`,
        'banner-success': `All events meet the data standards criteria below.`,
        'banner-loading': `Checking events for compliancy.`,
        'update-success': `Data standards settings have been saved.`,
        'update-error': `Data standards settings could not be saved.`,
        'calculation-fail': `Failed to calculate non-compliant events.`,
        'no-edit-permission': `You do not have permission to edit data standards for this project.`,
        'loading-error': `Could not load data standards settings. Please try again.`,
        'retry-load': `Retry`,
      },
      'event-standards': {
        title: `Data Standards`,
        'event-name': `Event Naming Convention`,
        'event-name-description': `Display names are typically more descriptive and separate from a tracked name`,
        'description-name': `Event Descriptions`,
        'description-description': `Require descriptions to add additional context for an event`,
        'owner-name': `Require Owner`,
        'owner-description': `Require an owner for all events`,
        'image-name': `Require Image`,
        'image-description': `Visual elements can provide better context for an event.`,
        'regex-pattern': `Regex Pattern`,
        'test-event-name': `Test Display Name`,
        'missing-media': `Missing Media`,
        'missing-description': `Missing Description`,
        'missing-owner': `Missing Owner`,
        'wrong-name-format': `Wrong Name Format`,
        'regex-help-text': `Need help? Try this <regexGeneratorLink>RegEx generator</regexGeneratorLink>`,
        'regex-match': `Matched`,
        'required-format': `Required Format`,
      },
      warning: {
        event: `Fails to meet data standards for <settingsLink>event names</settingsLink>`,
        description: `Fails to meet data standards for <settingsLink>description</settingsLink>`,
      },
    },
    'data-drop-and-deletion': {
      'header-title': `Data Drop and Deletion`,
    },
    'data-drop': {
      'header-title': `Data Drop`,
      'create-filter-button': `Create Drop Filter`,
      'set-up-filters-header': `Set Up Drop Filters`,
      'set-up-filters-text': `Create drop filters to prevent data from being ingested. Please note, data cannot be recovered. The filters will take effect within 10 minutes of creation.`,
      'delete-filter-confirm-title': `Delete Drop Filter?`,
      'delete-filter-confirm-text': `Please note deleted filters cannot be recovered`,
      'drop-event-confirm-title': `Save Drop Filter?`,
      'drop-event-confirm-text': `Matching events will stop being ingested by Mixpanel. Filters may take up to 10 minutes to apply. Please note that dropped data cannot be recovered.`,
      'table-header': `{{numberOfDropFilters}} Drop Filters`,
      'single-table-header': `1 Drop Filter`,
      'modal-header': `Drop Filter`,
      'modal-description': `Create drop filters to prevent data from being ingested. Please note, data cannot be recovered. The filters will take effect within 10 minutes of creation.`,
      'drop-event': `Drop Events`,
      'drop-event-label': `Drop Event`,
      filter: `Filter`,
      'where-name-is': `where name is`,
      'event-filter': `Events`,
      'unsaveable-no-event-name': `Drop filters must specify an event name.`,
      'unsaveable-no-property-filter': `Drop filters must include at least one filter. To block the entire event from coming into Mixpanel, block it from Lexicon.`,
      'unsaveable-incomplete-property-filter': `Drop filters must specify a property name, operator, and value to drop`,
      save: `Save Drop Filter`,
      'delete-drop-filter': `Delete Drop Filter`,
      saving: `Saving...`,
      'created-by': `Created by {{user}}`,
      description: `{{eventName}} {{where}} {{propertyClauses}}`,
      'description-clause': `{{propertyName}} {{propertyCondition}} {{propertyValue}}.`,
      and: ` and `,
      'and-no-space': `and`,
      unknown: `Unknown`,
      'edit-filter': `Edit filter`,
      delete: `Delete`,
      'max-num-of-data-drop-filters-tooltip': `You have reached the maximum number of {{limit}} filters.`,
      'no-permissions-tooltip': `You do not have permission to drop events. You must be an owner to drop events and create drop filters.`,
      'toast-success-create': `Property filter successfully created`,
      'toast-success-update': `Property filter successfully updated`,
      'toast-success-delete': `Property filter successfully deleted`,
      where: `where`,
      undo: `Undo`,
    },
    'data-deletion': {
      'undo-success': `Event deletion for \`{{eventName}}\` has been successfully undone.`,
      'undo-error': `Something went wrong. Unable to undo your event deletion request.`,
      'event-count': `{{eventCount}} events`,
      'event-volumes-exceed-message': `Your current event volumes exceeds the 5 billion events per month limit.`,
      'max-num-of-deletes-header': `Maximum number of deletion requests for the month reached.`,
      'max-num-of-deletes-body': `Your project has reached the maximum number of deletion requests for this month. Please wait until next month before submitting another deletion request or undo a current request (if it was made in the last {{undoDays}} days) in order to create a new one.`,
      'no-permissions-message': `Please contact the project owner {{projectOwnerString}} about deleting data.`,
      'event-volumes-exceed-tooltip': `Your current event volumes exceed the maximum allowed.`,
      'max-num-of-deletes-tooltip': `You have reached the maximum number of {{deletesPerMonth}} deletes this month.`,
      'no-permissions-tooltip': `You do not have permission to delete events. You must be an owner or admin to delete events.`,
      'header-title': `Data Deletion`,
      'select-event-text': `Select Event...`,
      'select-date-text': `Between...`,
      'select-property-filters-text': `Select property filters...`,
      'select-property-filters-tooltip': `Filter to specific data`,
      'delete-request-filter-size-exceeded': `Your filter size exceeds the maximum allowed size. Please reduce the number of filters to proceed.`,
      'delete-request-requirements-text': `Delete requests must have an event, date range, and must match some existing data in order to be submitted.`,
      'delete-button': `Delete`,
      'cancel-button': `Cancel`,
      'chart-area-placeholder-text': `Choose your Criteria to preview data`,
      'empty-state-description-text': `Data Deletion allows you to permanently delete data from your project that is duplicative, spam, or sensitive. Data Deletions are reversible for 7 days; after this period, events are permanently deleted.`,
      'view-docs-button': `View Docs`,
      'give-feedback-button': `Give Feedback`,
      'event-deletion-request-button': `Event Deletion`,
      'delete-data-button': `Delete Data`,
      criteria: `Criteria`,
      'criteria-text': `Delete up to {{maxTimeWindowDays}} days of data for any event in your project. Data Deletions are reversible for {{undoPeriodInDays}} days; after this period, events are permanently deleted.`,
      'criteria-event': `Event`,
      'criteria-date-range': `Date Range`,
      'criteria-filter': `Filter`,
      'see-guide': `See guide`,
      'learn-more': `Learn more`,
      'load-more': `Load more`,
      'show-results-through': `Showing {{eventsLength}} of {{totalEvents}} results through {{lastEventTime}}`,
      'open-as-page': `Open as page`,
      loading: `Loading...`,
      'no-deleted-events': `No deleted events`,
      'prop-deletion-text': `Interested in using export/import as a workaround for property deletion?`,
      'check-out-guide': `Check out our guide`,
      'delete-event-confirm-title': `Delete event?`,
      'delete-event-confirm-text': `Data Deletion will be permanent in {{undoPeriodInDays}} days. Until then, you may undo the action. Deletion will apply to the data summarized below.`,
      'delete-event-confirm-input-text': `To confirm, type \`{{eventName}}\` below`,
      'undo-confirm-title': `Undo Deletion Request`,
      'undo-confirm-text': `Are you sure you want to remove this deletion request?`,
      'deletes-left-in-month': `You have {{requestsLeft}} {{deletionText}} available this month.`,
      'event-table-cols': {
        'event-name': `Event Name`,
        time: `Time`,
        'distinct-id': `Distinct ID`,
      },
      'table-cols': {
        event: `Event`,
        volume: `Volume Deleted`,
        criteria: `Criteria`,
        'request-date': `Request Date`,
        'deletion-date': `Deletion Date`,
        'requested-by': `Requested By`,
      },
    },
    'data-governance': {
      title: `Data Governance`,
      updated: `Data Governance Settings updated successfully`,
      'update-error': `Unable to update Data Governance Settings`,
      'volume-monitoring': `Volume Monitoring`,
      'data-volume-monitoring': `Data Volume Monitoring`,
      'data-volume-monitoring-title': `Receive notifications when an unexpected trend is discovered on tracked events.`,
      'data-volume-monitoring-description': `Catch & resolve potential data quality issues early to maintain the health of your data.`,
      'event-approval': `Event Approval`,
      'event-approval-title': `Notify admins, owners, or Slack channels of newly tracked events.`,
      'event-approval-description': `Newly tracked events will be set to “Pending” by default which completely removes them from the event menus until they are set to “Visible” in Lexicon. Project Admins or Owners can change the visibility of all events.`,
      'event-approval-max-error': `You have too many hidden events ({{current, number}} out of {{max, number}} allowed), you must unhide events to enable Event Approval`,
      'event-approval-go-to-data-rules-banner': `Event Approval has moved to Data Rules. [See Data Rules]({{link}}){:target="_parent"}`,
      'new-prop-notifications': `New Property Notifications`,
      'new-prop-notifications-description': `Get notified when new properties appear on new or existing events.`,
      'automate-event-approval': `Automate Event Approval with Data Standards`,
      'automate-event-approval-description': `Save time by auto-approving events that meet your data standards. Event that meet your data standards will automatically become visible and usable for your team. Event that do not comply will remain pending and hidden until fixed.`,
      'no-permissions-event-approval-tooltip-text': `You do not have permission to edit event approval settings for this project`,
      'no-permissions-vol-monitoring-tooltip-text': `You do not have permission to edit data volume monitoring settings for this project`,
      'enable-event-approval-tooltip-text': `Event approval must be enabled to use this setting`,
      'notification-settings': `Notification Settings`,
      'notification-settings-tooltip': `Mixpanel will automatically send email or Slack notifications.`,
      email: `Email`,
      'invalid-emails': `Some recipient email addresses are invalid`,
      'slack-channels': `Slack Channels`,
      'detection-settings': `Detection Settings`,
      'detection-settings-tooltip': `By default, Mixpanel looks back 1 day for volume anomalies.`,
      lookback: `Lookback days`,
      'x-days': `{{count}} day`,
      'x-days_other': `{{count}} days`,
      'go-to-lexicon-banner': `Data Governance has moved to Lexicon. [See Data Governance]({{link}}){:target="_parent"}`,
      'warehouse-monitoring': {
        title: `Enable monitoring for data warehouse syncs`,
        description: `Receive notifications when warehouse syncs have issues or higher than average volumes. [Learn more about warehouse monitoring](#).`,
        'tooltip-text': `Threshold and overage monitoring is only available for projects using warehouse connectors.`,
        'email-label': `Project Owners and Admins will receive warehouse monitoring notifications by default`,
        'detection-settings': {
          title: `Volume Detection Settings`,
          description: `When a warehouse sync’s usual volume exceeds the set threshold, a notification will be sent to selected recipients. Usual volume is determined by calculating the rolling average of a sync over the previous 30 days. To learn more about this project’s event volume, visit the [Billing Page]({{link}}).`,
          notify: {
            'field-name': `Notify and Sync`,
            tooltip: `This threshold must be lower than the notify and pause limit.`,
            prefix: `Send a notification when a warehouse sync exceeds`,
            suffix: `increased volume. The sync will continue running.`,
          },
          pause: {
            'field-name': `Notify and Pause`,
            tooltip: `This threshold must be higher than the notify and sync limit.`,
            prefix: `Pause the sync and send a notification when a warehouse sync exceeds`,
            suffix: `increased volume.`,
          },
        },
      },
      'null-property-monitoring': {
        name: `Null Property Monitoring`,
        title: `Receive notifications when property values stop being sent or properties deviate from their expected type.`,
        description: `Catch & resolve potential data quality issues early to maintain the health of your data.`,
      },
      'property-type-monitoring': {
        name: `Property Type Monitoring`,
        title: `Receive notifications when Properties start sending unexpected types.`,
        description: `Catch & resolve potential data quality issues early to maintain the health of your data.`,
      },
    },
    'decimal-precision': {
      decrease: `← .0`,
      increase: `.00 →`,
    },
    'governance-issues': `Governance Issues`,
    'data-rules': {
      'notify-teammates': `Notify Teammates`,
      'notify-teammates-description': `Notify specific teammates about new data`,
      'notify-slack-channel': `Notify a Slack Channel`,
      'notify-slack-channel-description': `Notify a Slack channel about new data`,
    },
    'drop-filter': {
      'editing-watch-time-filter': `Editing Watch Time Filter`,
      'drop-abnormal-watch-time': `drop events with abnormal watch time`,
      cancel: `Cancel`,
      save: `Save`,
      title: `Drop Filters`,
      'watch-time-filter': `Watch Time Filter`,
      'edit-rule': `Edit Rule`,
    },
  },
  issues: {
    'issue-description': `Issue Description`,
    location: `Location`,
    context: `Context`,
    'higher-volume': `Higher volume than usual`,
    'lower-volume': `Lower volume than usual`,
    'more-null-volume': `More null values than usual`,
    date: `Date`,
    actions: `Actions`,
    'view-in-insights': `View in Insights`,
    'root-cause': `Root Cause`,
    status: `Status`,
    unresolved: `Unresolved`,
    resolved: `Resolved`,
    dismissed: `Dismissed`,
    'sort-by': `Sort By`,
    'sort-by-most-recent': `Sort By: Most Recent`,
    'sort-by-oldest': `Sort By: Oldest`,
    'high-event-volume': `High event volume`,
    'low-event-volume': `Low event volume`,
    'property-type-drift': `Unexpected property type`,
    'property-type-drift-plural': `Unexpected property types`,
    'property-type-drift-context-and': ` and `,
    'property-type-drift-context': `Property type changed from {{previousType}} to {{driftTypes}}`,
    'high-formula-volume': `High formula volume`,
    'low-formula-volume': `Low formula volume`,
    'high-null-property-volume': `High null property volume`,
    'anomaly-type': `Anomaly Type`,
    'all-anomalies': `All{{countValue}}`,
    'event-anomalies': `Event Anomalies{{countValue}}`,
    'property-anomalies': `Property Anomalies{{countValue}}`,
    'formula-anomalies': `Formula Anomalies{{countValue}}`,
    'table-empty-message': `Nice work! No anomalies detected`,
    'table-empty-message-no-dvm': `Nothing to see… yet. Set up monitoring to stay ahead of governance issues`,
    dismiss: `Dismiss`,
    'invalid-option': `Unable to perform action`,
    'error-dismiss': `Unable to dismiss issue`,
    'success-dismiss': `Issue dismissed successfully`,
    'sidenav-count-updating': `Updating...`,
    'property-anomaly-conjunction': `on`,
  },
  components: {
    'input-group': {
      submit: `Save`,
    },
    'items-menu': {
      'select-all': `Select all`,
      'select-all-tooltip': `Includes all values from the list for the property, including undefined or (not set) values.`,
      'select-all-list': `Select all in list ({{count}})`,
      'select-all-matching': `Select all matching in list ({{count}})`,
      'select-all-defined': `Select all defined values`,
      'select-all-defined-tooltip': `Includes all the values that have been defined for this property. If the property is not defined or empty for an event, it will be excluded from the selection.`,
      'deselect-all': `Deselect all`,
      'show-items': `Show {{items}}`,
      'hide-items': `Hide {{items}}`,
    },
    modal: {
      'close-window': `Close Window`,
      close: `Close`,
    },
    banners: {
      'startup-plan-blocked': {
        cta: `Contact Sales`,
        'feature-flags': {
          title: `We have temporarily disabled your ability to enable, or modify enabled feature flags (other than disabling them).`,
          body: `Your account has exceeded the Startup Plan quota, so we've temporarily restricted functionality of feature flags. Don't worry though - you can still disable preexisting feature flags and regain full access by upgrading to a paid plan. If you think this is an error, please contact: support@mixpanel.com`,
        },
        experiments: {
          title: `We have temporarily disabled your access to Experiments.`,
          body: `Your account has exceeded the Startup Plan quota, so we've temporarily turned off Experiments. Don't worry though - you can regain access to your Experiments/Feature Flags by upgrading to a paid plan. If you think this is an error, please contact: support@mixpanel.com`,
        },
      },
      'metrics-limit': {
        warning: `Your report has many metrics combined with features which may cause degraded performance. Limit the number of metrics referenced in formulas or turn off time comparison (if enabled) to improve performance`,
      },
      'segmentation-limits': {
        'segment-alignment-ineligible-message': `This query has a large number of segments so the results here are approximate. Apply filters to see exact results for {{uxLimit}} specific segments, or export for approximate results for up to {{exportLimit}} segments via CSV. [Learn more](https://docs.mixpanel.com/docs/reports#segment-limits-and-ordering).`,
        'segment-alignment-ineligible-no-export-message': `This query has a large number of segments so the results here are approximate. Apply filters to see exact results for {{uxLimit}} specific segments. [Learn more](https://docs.mixpanel.com/docs/reports#segment-limits-and-ordering).`,
        'segment-alignment-failed-retryable-message': `This query has a large number of segments and the results here are approximate as exact results couldn't be fetched. Click Retry to try fetching exact results for the segments shown. Alternatively, apply filters to see exact results for {{uxLimit}} specific segments, or export for approximate results for up to {{exportLimit}} segments via CSV. [Learn more](https://docs.mixpanel.com/docs/reports#segment-limits-and-ordering).`,
        'segment-alignment-failed-retryable-no-export-message': `This query has a large number of segments and the results here are approximate as exact results couldn't be fetched. Click Retry to try fetching exact results for the segments shown. Alternatively, apply filters to see exact results for {{uxLimit}} specific segments. [Learn more](https://docs.mixpanel.com/docs/reports#segment-limits-and-ordering).`,
        'segment-alignment-cancelled-message': `You chose to skip the precise calculation so the results here are approximate. Click Retry to fetch exact results for the segments shown. Alternatively, apply filters to see exact results for {{uxLimit}} specific segments, or export for approximate results for up to {{exportLimit}} segments via CSV. [Learn more](https://docs.mixpanel.com/docs/reports#segment-limits-and-ordering).`,
        'segment-alignment-cancelled-no-export-message': `You chose to skip the precise calculation so the results here are approximate. Click Retry to fetch exact results for the segments shown. Alternatively, apply filters to see exact results for {{uxLimit}} specific segments. [Learn more](https://docs.mixpanel.com/docs/reports#segment-limits-and-ordering).`,
        'segment-alignment-successful-message': `We limit the number of segments to {{uxLimit}}. Apply filters to see hidden segments, or export for up to {{exportLimit}} segments via CSV. [Learn more](https://docs.mixpanel.com/docs/reports#segment-limits-and-ordering).`,
        'segment-alignment-successful-no-export-message': `We limit the number of segments to {{uxLimit}}. Apply filters to see hidden segments. [Learn more](https://docs.mixpanel.com/docs/reports#segment-limits-and-ordering).`,
      },
      'query-sampling-notice': {
        message: `This report is sampled at {{samplingPercentage}}%. Query results are an approximation. `,
        tooltip: {
          text: `Learn More`,
          'tooltip-text': `Results use a percentage of event data determined by the sampling rate of selected events, date range, and whether query time sampling is enabled.`,
        },
      },
      'workspace-filter': {
        title: `Your data is being filtered`,
        message: `This data view has a filter applied and contains a subset of all data.`,
      },
      'session-replay-ingestion-warn': {
        title: `You have reached your session replay limit`,
        message: `You've hit your session replay limit. Your project will begin dropping replays soon unless you increase your limit. To avoid interruption, please contact sales.`,
      },
      'session-replay-ingestion-drop': {
        title: `You have reached your session replay ingestion limit`,
        message: `Your project will not capture any more session replays until your next billing period. To increase your session replay volume, talk to sales.`,
      },
      'session-replay-usage-dismissible': {
        header: `You've used 85% of your session replay volume for this month.`,
        'body-free-growth': `Replays will pause once you hit the limit. Avoid interruptions by adding more replays before you hit the cap.`,
        'body-enterprise': `Your access to session replays will be limited when your volume is reached. To increase your session replay volume, talk to sales.`,
      },
      'session-replay-usage-non-dismissible': {
        header: `More insights are waiting for you`,
        'body-free-growth': `You’ve used all of your monthly replays. Add up to 500K additional monthly replays to keep capturing valuable insights.`,
        'body-enterprise': `To increase your session replay volume, talk to sales.`,
      },
      'session-replay-cta': {
        'add-more-replays': `Add More Replays`,
        'talk-to-sales': `Talk to Sales`,
        dismiss: `Dismiss`,
      },
      'events-retention-changed-warning': {
        postfix: `events older than {{count}} days will be permanently deleted. Please contact your organization admin or [Mixpanel support](https://mixpanel.com/get-support) with any questions.`,
        'sku-reduced': {
          title: `Your data retention period has been reduced to {{count}} days.`,
          message: `On {{date}}, $t(components.banners.events-retention-changed-warning.postfix)`,
        },
        'sku-lost': {
          title: `Your custom data retention plan has ended.`,
          message: `Your retention period has reverted to {{count}} days. On {{date}}, $t(components.banners.events-retention-changed-warning.postfix)`,
        },
        'billing-flag-lost': {
          title: `Your 5-year data retention free plan has ended.`,
          message: `Your retention period has reverted to {{count}} days. On {{date}}, $t(components.banners.events-retention-changed-warning.postfix)`,
        },
      },
      'project-entitlement': {
        'over-quota': {
          title: `Project over its {{buyableName}} allocation`,
          message: `This project has used more {{buyableName}} than allocated to it. Usage above the allocation may draw from your organization's shared pool or accrue overage.`,
        },
        blocked: {
          title: `Project blocked: {{buyableName}} allocation exhausted`,
          message: `This project has exhausted its {{buyableName}} allocation and further usage is blocked. Contact your organization admin to raise the allocation.`,
        },
      },
      'eu-forwarding-deprecation': {
        title: `Action Required: US-to-EU Data forwarding has ended`,
        message: `As of July 1, 2026, we have sunset US-to-EU data forwarding. To demonstrate the impact on your project, events still being sent to US servers are temporarily hidden from your UI. These events are safe and will be unhidden on August 1, 2026. Update your implementation to "api-eu.mixpanel.com" to prevent permanent data loss after August 1, 2026. Contact [support](https://mixpanel.com/get-support) to opt out of this preview or with questions.`,
      },
      'in-forwarding-deprecation': {
        title: `Action required: stop data loss before August 31, 2026`,
        message: `This project is still sending data to "api.mixpanel.com" or "api-js.mixpanel.com" (Mixpanel's US servers). Forwarding to Mixpanel's India data center ends on August 31, 2026 — after that, any events still pointed at the US will be permanently dropped. Update your implementation to "api-in.mixpanel.com" now to avoid losing data. Reach out to your account manager with any questions or concerns.`,
      },
      'custom-roles-coming-soon': {
        title: `Custom Roles Coming Soon`,
        message: `Custom Roles and updated permissions are coming to all Enterprise plans on June 1, 2026. Want Early Access? Enable now for your Organization. Learn more in [Docs](https://docs.mixpanel.com/docs/orgs-and-projects/roles-and-permissions#custom-roles).`,
      },
    },
    'experiment-row': {
      started: `Started`,
      users: `users`,
    },
    'last-updated': {
      updated: `Updated {{relativeTime}}`,
    },
    'filter-bar': {
      'x-results_one': `{{count, number}} result`,
      'x-results_other': `{{count, number}} results`,
      'x-selected': `{{count, number}} selected`,
      'select-item': `Select {{itemType}}`,
      label: `Filter by`,
      reset: `Reset Filter`,
    },
    'upsell-modal-cta': `Upgrade Your Plan`,
    'hide-tour': `Do not show again`,
    'resizable-panel': {
      collapse: `Collapse`,
      'collapse-qb': `Collapse Query Panel`,
      'collapse-side-nav': `Collapse Side Nav`,
      'collapse-table': `Collapse Data Table`,
      show: `Show`,
      'show-qb': `Show Query Panel`,
      'show-side-nav': `Show Side Nav`,
      'show-table': `Show Data Table`,
      'align-left': `Align to Left`,
      'align-right': `Align to Right`,
      'align-bottom': `Align to Bottom`,
      'align-top': `Align to Top`,
    },
    table: {
      empty: `This table is empty. Let's make a fresh start.`,
      'no-results': `Your search returned no results`,
      'last-edited': `Last edited {{relativeTime}}`,
      'selection-limit-notice': `Select up to {{limit}} items.`,
      'selection-limit-reached': `You can't select more than {{limit}} items.`,
      'column-selection-title': `Select columns to display`,
      'column-selection-button-label': `Edit Columns`,
    },
    'copy-to-clipboard': {
      'url-copied': `URL Copied!`,
      copied: `Copied!`,
      'url-copy-error': `Something went wrong copying the URL`,
      'copy-error': `Something went wrong copying`,
      copy: `Copy`,
    },
    'create-entity': {
      create: `Create New`,
      edit: `Edit`,
      'save-behavior': `Save Behavior`,
      'save-behavior_funnel': `Save Funnel Behavior`,
      'save-behavior_retention': `Save Retention Behavior`,
      'save-before-sharing': `Save Before Sharing`,
      description: `Description`,
      'description-placeholder': `Enter a short description`,
      'description-placeholder_custom property': `Enter a short description for your custom property`,
      'description-placeholder_workspace': `Enter a short description for your data view`,
      'display-name': `Display name`,
      'display-name-placeholder': `Enter a display name`,
      name: `Name`,
      untitled: `Untitled`,
      untitled_workspace: `Untitled Data View`,
      'customize-access': `Customize Access`,
      'save-and-share': `Save and Share`,
      'url-copied': `URL copied!`,
      'url-not-copied': `Could not copy the URL`,
      'who-can-see': `Who Can See This?`,
      'group-property': `Group Identifier`,
      'group-property-placeholder': `eg. company_id`,
      'group-property-description': `Type an event property to use as a Unique Group Identifier`,
      'group-description': `The group key name that displays in the Mixpanel interface`,
      'report-limit-title': `{{count}} of {{limit}} Saved Reports`,
      'report-limit-description': `Your current plan allows {{limit}} saved reports per user per project. Upgrade your plan to save unlimited reports.`,
      'cohort-save-upgrade': `Upgrade to create and save cohort criteria`,
      'cohort-save-upgrade-description': `When you upgrade your plan, cohorts can be created and saved to use across all reports in a project`,
      sharing: {
        everyone: `This will be available to everyone in the project`,
        everyone_cohort: `This cohort will be available to everyone in the project`,
        'everyone_custom property': `This custom property will be available to everyone in the project`,
        everyone_report: `This report will be available to everyone in the project`,
        share: `This will be private until you share with others`,
        share_cohort: `This cohort will be private until you share with others`,
        'share_custom property': `This custom property will be private until you share with others`,
        share_report: `This report will be private until you share with others`,
        private: `This will be private due to your project role ({{role}})`,
        private_cohort: `This cohort will be private due to your project role ({{role}})`,
        'private_custom property': `This custom property will be private due to your project role ({{role}})`,
        private_report: `This report will be private due to your project role ({{role}})`,
      },
      errors: {
        'name-exists_workspace': `A data view with that name already exists`,
        'name-required_workspace': `Name is required for saving a data view`,
        'name-exists': `Name already exists`,
        'name-required': `Name is required for saving`,
        'name-field-length': `Name must be less than {{limit}} characters ({{length}})`,
        'description-field-length': `Description must be less than {{limit}} characters ({{length}})`,
      },
    },
    'edit-data-group': {
      'header-title': `Edit Group Key`,
      'set-is-company-key': `Set as B2B Company Key`,
      'set-is-company-key-description': `Enables company revenue analytics and company profiles.`,
      'company-name-placeholder': `eg. Company Name`,
      'company-name-label': `Company name identifier`,
      'company-name-description': `Profile property to reference`,
      'company-revenue-key-placeholder': `e.g. "account_mrr"`,
      'company-revenue-key-label': `Company Revenue (MRR) identifier`,
      'company-revenue-key-description': `Profile property to reference. This identifier enables revenue analysis.`,
      'company-profile-info': `For B2B businesses, add company details to one group key to enable more Company Profiles, revenue analysis and more`,
      'disabled-company-group-reason': `Another group is already set as the B2B Company Group`,
    },
    sharing: {
      title: `Share this with Others`,
      title_bookmarks: `Share this Report`,
      title_cohorts: `Share this Cohort`,
      'title_custom-events': `Share this Custom Event`,
      'title_custom-properties': `Share this Custom Property`,
      title_dashboards: `Share this Board`,
      'title_lookup-tables': `Share this Lookup Table`,
      title_metric: `Share this Metric`,
      'title_metric-trees': `Share this $t(mixology.metric-tree)`,
      title_behavior: `Share this Behavior`,
      'give-everyone': `Give access to everyone in the project`,
      invite: `Invite Teammates`,
      'invite-placeholder': `Enter People or Team Names...`,
      loading: `Loading current shares...`,
      'superadmin-access': `You have not been granted access to edit this item, but as a Project Owner you can make changes.`,
      'superadmin-access_bookmarks': `You have not been granted access to edit this Report, but as a Project Owner you can make changes.`,
      'superadmin-access_cohorts': `You have not been granted access to edit this Cohort, but as a Project Owner you can make changes.`,
      'superadmin-access_custom-events': `You have not been granted access to edit this Custom Event, but as a Project Owner you can make changes.`,
      'superadmin-access_custom-properties': `You have not been granted access to edit this Custom Property, but as a Project Owner you can make changes.`,
      'superadmin-access_dashboards': `You have not been granted access to edit this Board, but as a Project Owner you can make changes.`,
      'superadmin-access_lookup': `You have not been granted access to edit this Lookup Table, but as a Project Owner you can make changes.`,
      'superadmin-access_metric': `You have not been granted access to edit this Metric, but as a Project Owner you can make changes.`,
      'superadmin-access_behavior': `You have not been granted access to edit this Behavior, but as a Project Owner you can make changes.`,
      you: `You`,
      off: `Off`,
      remove: `Remove`,
      members_one: `1 person`,
      members: `{{count, number}} people`,
      people: `People`,
      teams: `Teams`,
      project: `Project`,
      advanced: `$t(common.advanced)`,
      'tag-added': `Added`,
      'tag-updated': `Updated`,
      'upsell-description': `On your current plan, {{planName}}, everything you create is visible and editable by others in the project. Upgrade your plan to gain access to advanced sharing and additional privacy controls.`,
      'url-description': `Project members with this URL can view`,
      'url-tooltip': `Copy the current URL`,
      boards: {
        'data-view': `Data View`,
        'data-view-tooltip': `Learn more about Data Views`,
        'empty-project': `Your project has no data! Start sending events today to share this board publicly.`,
        enabled: `Enabled`,
        'make-public': `Make Board Public`,
        'no-public-urls': `No public board URLs`,
        'public-board-details': `Public Board Details`,
        'public-url': `Public URL`,
        'public-access': `Non Project members can access a <bold>read-only</bold> instance of a Public Board with a link. <break /> Learn more about Public Boards on our Help docs.`,
        'public-links_one': `This Board <bold>"{{boardName}}"</bold> has 1 active Public URL`,
        'public-links': `This Board <bold>"{{boardName}}"</bold> has {{count, number}} active Public URLs`,
        'share-publicly': `Share boards with others outside your project. \n Project Owners can enable Public Boards. \n\n [Enable Public Boards]({{link}})`,
        'superadmin-access': `You have not been granted access to edit this Board, but as a Project Owner you can make changes.`,
      },
      error: {
        viewer: `You have view-only access and cannot edit sharing. You can copy the URL.`,
        consumer: `Due to your project role (Consumer), your reports are always hidden and cannot be shared with others. You can copy the URL.`,
        default: `You cannot edit sharing. You can copy the URL.`,
        'no-custom-data-perm': `Your project role prevents you from editing and saving analysis. You have view-only access on this {{entityType}}.`,
      },
      'role-prevents-editing-single': `This user's project role prevents them from editing and saving {{entityType}}. They can only be granted viewer access.`,
      'role-prevents-editing-multi': `One or more user's project role prevents them from editing and saving {{entityType}}. They can only be granted viewer access.`,
      'role-prevents-editing-existing': `This user's project role prevents them from editing and saving analysis. They have view-only access on this {{entityType}}.`,
      'owner-filter': {
        all: `Created By Anyone`,
        yours: `Created By You`,
        'not-yours': `Not Created By You`,
        shared: `Shared With You`,
        'default-label': `Created By`,
      },
    },
    calendar: {
      'next-month': `Next Month`,
      'prev-month': `Previous Month`,
    },
    'behavior-definition-panel': {
      conjunction: {
        or: `or`,
        then: `then`,
      },
    },
    'data-table': {
      'deselect-all': `Deselect All`,
      'select-top-segments': `Select Top Segments`,
      'dynamic-segments': `Dynamic Segments`,
      'dynamic-segments-tooltip': `Automatically select up to {{selectionLimit}} segments to show in the chart, based on the current sort.`,
      'manual-segments': `Manual Segments`,
      'manual-segments-tooltip': `Manually choose up to {{selectionLimit}} segments to display in the chart.`,
      'n-more': `{{count}} more`,
      overall: `Overall`,
      'empty-search': `Your search returned no results`,
    },
    'segment-filter': {
      'dynamic-segments': `Dynamic Segments`,
      'manual-segments': `Manual Segments`,
      'dynamic-segments-tooltip': `Automatically select segments to show in the chart, based on current chart sorting selections.`,
      'manual-segments-tooltip': `Manually choose segments to display in the chart.`,
    },
    'view-n-selector': {
      'showing-all': `Showing all`,
      'showing-max': `Showing max`,
      'choose-num-segments': `Choose the number of segments you want to see in the chart.`,
      'choose-num-rows': `Choose the number of rows you want to see in the chart.`,
      'choose-up-to-num-segments': `Choose the number of segments you want to see in the chart, up to {{maxViewNLimit}}.`,
      'choose-up-to-num-rows': `Choose the number of rows you want to see in the chart, up to {{maxViewNLimit}}.`,
      'show-all-segments': `Show all segments`,
      'show-max-segments': `Show max segments (up to {{maxViewNLimit}})`,
      'show-all-rows': `Show all rows`,
      'show-max-rows': `Show max rows (up to {{maxViewNLimit}})`,
      segments: `Segments`,
      values: `Values`,
      rows: `Rows`,
      'sorted-by-asc': `Sorted by {{fieldName}} ascending.`,
      'sorted-by-desc': `Sorted by {{fieldName}} descending.`,
      'grouped-root-selection-info': `The {{sortOrder}} {{viewNLimit}} {{segmentName}}(s) are selected.`,
      'grouped-nested-selection-info': `The {{sortOrder}} {{viewNLimit}} {{segmentName}}(s) within each {{parentSegmentName}} are selected.`,
      'row-selection-info': `The {{sortOrder}} {{viewNLimit}} row(s) are selected.`,
      'sort-order': {
        first: `First`,
        last: `Last`,
        bottom: `Bottom`,
        top: `Top`,
      },
    },
    'report-auto-name': {
      untitled: `Untitled`,
      duplicate: `Duplicate of`,
    },
    'clamped-text': {
      less: `less`,
      more: `more`,
    },
    'download-wrapper': {
      source: `Source:`,
      mixpanel: `Mixpanel`,
    },
    'email-recipients': {
      'invalid-email': `Invalid email address`,
      'duplicate-user': `Duplicate user`,
      'duplicate-email': `Duplicate email address`,
      'no-recipients': `No recipients`,
    },
    'file-uploader': {
      'click-to-upload': `Click to upload`,
      'or-drag-and-drop': `or drag and drop`,
    },
    'complex-filter': {
      filter: `Filter`,
    },
    'product-tour': {
      'image-alt': `Tour Modal Image`,
      'step-counter': `{{stepIndex}} of {{totalSteps}}`,
    },
    sidenav: {
      new: `New`,
    },
    tag: {
      'remove-tag': `remove tag`,
    },
    'tiptap-editor': {
      'remove-link': `Remove Link`,
      link: `Link`,
    },
  },
  'data-views': {
    loading: `Loading Data Views....`,
    description: `Explore and join data views in this project and manage your current data view subscriptions.`,
    create: `Create Data View`,
    yours: `Your Data Views`,
    created: `Data View {{workspaceName}} Created`,
    'yours-empty-title': `You don't have any data views in this project yet`,
    'yours-empty-subtitle': `Join one of the public data views below to get started`,
    'public-data-views': `Public Project Data Views`,
    'delete-confirm-title': `Delete Data View`,
    'delete-confirm-description': `Are you sure want to delete the data view: {{workspaceName}}?\n\nAny Boards, Reports, Cohorts, Custom Properties, or Custom Events created with this data view will still be available.`,
    'leave-title': `Leave Data View`,
    'leave-unable': `Unable to Leave Data View`,
    'leave-confirm-description': `Are you sure you want to leave this data view?`,
    'leave-confirm-description-private': `Since **{{workspaceName}}** is private, the Owner will have to invite you if you wish to rejoin in the future.`,
    'leave-confirm-description-multiple': `You have multiple invite grants to **{{workspaceName}}**. You can remove your individual membership; however, you are unable to leave the data view due to group invites from the following:`,
    'leave-confirm-description-group': `You are unable to leave **{{workspaceName}}** due to group invites from the following:`,
    leave: `Leave`,
    'remove-individual-member': `Remove Individual Membership`,
    'no-description': `No description yet.`,
    'no-members': `No members yet`,
    'created-by': `Created by: {{creatorName}}`,
    settings: `Settings`,
    'switch-to': `Switch to this Data View`,
    join: `Join`,
    'join-unavailable': `Unavailable at this time`,
    'join-failed': `Failed to join the selected data view`,
    'leave-failed': `Failed to leave the selected data view`,
    'create-failed': `Failed to create a data view`,
    'already-exists': `Data View already exists`,
  },
  jql: {
    'custom-properties-banner-header': `Learn More about Custom Properties`,
    'custom-properties-banner-body': `Do you want to create new properties from existing properties using Excel-like formulas like IF, REGEX_MATCH, DATEDIF and a lot more?`,
    'dont-save': `Don't save`,
    'unsaved-changes': `Unsaved changes`,
    'unsaved-changes-description': `If you navigate away from this page you will lose your changes. Would you like to continue?`,
    'no-saved-queries': `There are no saved JQL Queries.`,
    'create-tip': `Click '$t(common.new)' to create a new one.`,
    query: `JQL Query`,
    creator: `Creator`,
    'last-modified': `Last Modified`,
    action: `Action`,
    'delete-confirm-title': `Are you sure you want to delete this query?`,
    'delete-confirm-description': `Deleting a query is permanent and cannot be undone.`,
    search: `Search queries`,
    documentation: `Docs`,
    'saved-queries': `Your saved queries`,
    run: `Run query`,
    'save-and-run': `Save & run query`,
    export: `Export`,
    'slow-query': `This might take a little longer than we anticipated.`,
    truncated: `Only the first {{count}} objects were loaded.`,
    'no-results': `Your query returned no results.`,
    'results-placeholder': `You haven't run a query yet. Try running one above, or <docLink>check out our documentation</docLink> for help.`,
    table: `Table`,
    json: `JSON`,
    error: `Error`,
    'classified-data': {
      header: `JQL Only Available to Users with Classified Data Access`,
      'body-write': `You may want to give yourself and others classified access`,
      'body-read': `Contact your admin to request access`,
      manage: `Manage Classified Data Permissions`,
      'go-to-insights': `Go to Insights`,
    },
    samples: {
      byo: `Build your own`,
      segmentation: `Segmentation`,
      'uniques-by-prop': `Uniques by property`,
      dau: `Daily active users`,
      'failed-conversions': `Failed conversions`,
      funnel: `Funnel analysis`,
    },
    modals: {
      'choose-new': {
        header: `Get started`,
        description: `A few examples to get you started`,
      },
      intro: {
        header: `Write powerful queries with JQL`,
        description: `JQL lets you use the power of JavaScript to analyze the raw data that already exists in Mixpanel to answer any question you have about your business.`,
        cta: `Write your first query`,
      },
      upsell: {
        header: `You're limited to analyzing {{count}} days of historical data`,
        description: `You can see beyond 10 years or more of historical data by upgrading your account.`,
      },
      'restricted-report-upsell': {
        header: `JQL is only available if you upgrade your plan`,
        description: `JQL is a powerful JavaScript query language you can use to complete complex analysis of your data in fewer lines of code within Mixpanel.`,
      },
    },
  },
  'jql-console': {
    'request-error': `We weren't able to complete your request due to a complication on our end. We've logged and are investigating the issue.`,
    'error-with-support': `If the problem continues, please <supportLink>reach out to our wonderful customer support team</supportLink>.`,
  },
  profile: {
    'alias-error': `Unable to display all distinct ids`,
    'group-profile': `Group Profile`,
    'user-profile': `User Profile`,
    'distinct-id': `Distinct ID`,
    location: `Location`,
    'updated-at': `Updated at`,
    'email-unsubscribed': `Email Unsubscribed`,
    delete: `Delete Profile`,
    'delete-confirm-body': `This action is permanent and cannot be undone.`,
    'delete-error-no-perms': `Only an Admin or Owner can delete a profile.`,
    'delete-error-no-props': `Cannot delete because profile properties are not set on this profile.`,
    'prop-created': `Property created.`,
    'prop-updated': `Property updated.`,
    'prop-deleted': `Property '{{propertyName}}' deleted.`,
    'propagation-info': `Changes may take a minute to process and display in your project.`,
    'group-profile-props': `Group Profile Properties`,
    'user-profile-props': `User Profile Properties`,
    'create-prop': `Create Profile Property`,
    'prop-name': `Property Name`,
    'prop-value': `Property Value`,
    'no-props': `No Profile Properties`,
    'setup-props': `Setup Profile Properties`,
    'search-props': `Search properties`,
    'create-no-perms': `You don't have permission to create.`,
    'activity-feed': `Activity Feed`,
    'watch-replays': `Watch Replays`,
    'watch-replays-tooltip': `Watch recordings for this user.`,
    'replays-loading': `Loading recordings for this user.`,
    'replays-none': `No session recordings found for this user.`,
    'view-in-insights': `View in Insights`,
    'no-activity': `No activity in the time period specified`,
    'activity-error-header': `Unable to Display Activity Feed`,
    'activity-error-body': `We were unable to display this user's activity due to an error. Please try again later.`,
    'properties-error-header': `Unable to Display Properties`,
    'properties-error-body': `Please try again later.`,
    'show-more': `Show more`,
    'exposure-title': `Feature Flags`,
    'exposure-subtitle': `Showing exposed and enabled Mixpanel flags`,
    'search-exposures': `Search feature flags`,
    exposures: {
      empty: `No exposed and enabled flags`,
      'flags-link': `Go to Feature Flags`,
      'open-experiment': `Open Experiment`,
      'open-flag': `Open Feature Flag`,
      users: `Users`,
      'last-seen': `last seen`,
    },
  },
  'session-replay': {
    'activity-tab': `Activity`,
    'auto-play': `Enable Auto Play`,
    'collapse-panel': `Collapse Panel`,
    'concurrent-replay-default-title': `Replay {{replayIndex}}`,
    'details-tab': `Details`,
    'usage-tab': `Usage`,
    'event-properties-tab': `Event Properties`,
    'details-duration': `Duration`,
    'details-event-count': `Event Count`,
    'details-browser': `Browser`,
    'details-os': `Operating System`,
    'details-timestamp': `Replay Start Time`,
    'details-entry-url': `Entry URL`,
    'details-exit-url': `Exit URL`,
    'details-user': `User`,
    'details-source-sdk': `Source SDK`,
    'details-replay-id': `Replay ID`,
    'keyboard-shortcuts': {
      title: `Keyboard Shortcuts`,
      next: `Next Replay`,
      previous: `Previous Replay`,
      'play-pause': `Play/Pause Replay`,
      fullscreen: `Toggle Fullscreen`,
      'next-keys': `⇧ + →`,
      'previous-keys': `⇧ + ←`,
      'play-pause-keys': `Spacebar`,
      'fullscreen-keys': `F`,
    },
    'grouped-replay-header-title': `Recording {{index}}/{{total}}`,
    replays: `Replays`,
    'replays-count': `{{number}} replays`,
    'replays-count-subtotal': `Showing {{subtotal, number}} replays of {{total}} matches.`,
    'replays-count-concurrent-subtotal': `Showing {{subtotal, number}} journeys from {{total}} recordings.`,
    'replays-count-subtotal-loading': `Showing {{subtotal, number}} replays (retrieving total matches...)`,
    'replays-count-concurrent-subtotal-loading': `Showing {{subtotal, number}} journeys from — recordings.`,
    'replays-count-fail': `Showing {{number}} replays. Unable to retrieve total matches.`,
    'replays-count-info': `Number of replays are updated as more replays are fetched when filtering by events.`,
    'replays-journey-tooltip': `Journeys are comprised of multiple recordings from the same user session.`,
    'rewatch-replay': `Rewatch Replay`,
    feedback: `Replay Feedback`,
    'feedback-description': `Rate your satisfaction with this replay`,
    'feedback-placeholder': `Additional feedback (Optional)`,
    'feedback-tooltip': `Provide feedback`,
    'collapse-replay': `Collapse Replay`,
    'copy-link': `Copy Replay Link`,
    'copy-link-position': `Copy Replay Link at {{position}}`,
    'enable-stitching': `Enable Stitching`,
    'enable-stitching-tooltip': `Show replays via events that weren't captured with the same SDK initialization that captured the replay. [Learn more]({{docsLink}})`,
    'enable-console': `Enable Console`,
    'enable-console-tooltip': `Show console logs and errors captured during this replay.`,
    'event-observed': `Event is observed in replay`,
    'event-stitched': `This event is "stitched" to this replay using user ID and timestamp, but may not belong to this exact session`,
    'expand-panel': `Expand Panel`,
    'event-filter': `Filter by Events`,
    'event-filter-desc': `View replays where specific events + property combinations occurred.`,
    'property-filter': `Replay Property Filters`,
    'property-filter-desc': `View replays that matched certain replay attributes, like device, browser, session length, and more.`,
    'property-filter-info': `Please note 'or' logic is not supported for user/cohort filters.`,
    'replay-stitched': `This is a stitched replay`,
    'search-placeholder': `Search for Replays`,
    'search-placeholder-events': `Search events or pinned properties`,
    'share-replay': `Share Replay`,
    'loading-events': `Loading Events...`,
    'loading-replays': `Loading Replays...`,
    'loading-row-title': `Loading new replays`,
    'loading-row-subtitle': `Items will be sorted once loaded.`,
    'event-value-not-set': `(Not set)`,
    'events-count_one': `1 event`,
    'events-count': `{{count, number}} events`,
    'matching-events-count_one': `1 matching event`,
    'matching-events-count': `{{count, number}} matching events`,
    'more-events_one': `and 1 more event`,
    'more-events': `and {{count, number}} more events`,
    'event-group-label': `{{name}} and {{count, number}} others`,
    'exit-url': `Exit URL:`,
    'expand-replay': `Expand Replay`,
    'failed-to-load-player': `Failed to load player`,
    'failed-to-load-player-error': `It looks like we weren't able to load the player for Session Replays. Please try turning off any ad blockers or <docsLink>see docs</docsLink>.`,
    'home-item-summary': `visited for`,
    'home-item-summary-subtitle': `on`,
    'integrate-replay': `No session replays received yet. [See documentation.]({{docsLink}})`,
    'plan-does-not-have-access': `Session replay is not available with your plan. [See how to get started here.]({{docsLink}})`,
    'load-error': `We had trouble loading your data`,
    'next-replay': `Next Replay`,
    'no-related-events': `No Related Events Found`,
    'no-replay-found': `No replay found.`,
    'no-replay-found-error': `This replay may have been deleted or is still being processed. See <docsLink>our docs</docsLink> for further details.`,
    'no-replays-found': `No replays found.`,
    'no-recent-replays': `No recent replays`,
    'no-replays-available': `No replays available`,
    'no-replays-available-reason': `This may be due to the sampling rate or the current filters that have been applied.`,
    'no-replays-available-stitching-off': `Stitching is turned off, so replays stitched from events are hidden. Turn it back on to see more replays.`,
    'turn-on-stitching': `Turn on stitching`,
    'generic-load-error': `Failed to load replays`,
    'generic-load-error-details': `Something went wrong while loading replays. Please try again or contact support.`,
    'no-replays-description': `We couldn't find any replays for the specified conditions. See <docsLink>our docs</docsLink> for further details.`,
    'no-page-view-events': `Not seeing the right URL?  Make sure you're sending page view events following our [docs](https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript#track-page-views).`,
    'num-filters-set': `Filters • {{count}}`,
    'preview-hover': `View More Replays`,
    'playlist-load-error': `Playlist could not be loaded. It may have been deleted or you may not have access.`,
    'preview-load-error': `An error occurred loading this preview.`,
    'playlist-no-preview-available': `No recordings are available for this playlist.`,
    'player-stitched-timeline-info': `Events by this user captured separately from the SDK that recorded this replay. [Learn more](https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript/javascript-replay#server-side-stitching)`,
    'player-main-timeline-info': `Events captured with same SDK that recorded this replay. Thick track indicates on-screen activity detected; thin track indicates no on-screen activity detected.`,
    'player-insightful-timeline-info': `Indications of user frustration derived from user behavior.`,
    'player-insightful-timeline-tooltip': `Interesting user insights including console errors and sentiment signals like rage clicks.`,
    'rate-satisfaction': `Rate your satisfaction with this replay`,
    refresh: `Refresh`,
    'starting-url': `Entry URL:`,
    'unknown-time': `Unknown time`,
    'unknown-date': `Unknown date`,
    'timezone-tooltip': `Timezone: {{label}}`,
    'unknown-duration': `Unknown Duration`,
    'unknown-exit-url': `Unknown exit URL`,
    'unknown-source': `Unknown source`,
    'unknown-starting-url': `Unknown entry URL`,
    'unknown-os': `Unknown operating system`,
    'unknown-browser': `Unknown browser`,
    sort: {
      'most-recent': `Most Recent`,
      'least-recent': `Least Recent`,
      'longest-duration': `Longest Duration`,
      'shortest-duration': `Shortest Duration`,
      'most-events': `Most Events`,
      'least-events': `Least Events`,
      recency: `Recency`,
      duration: `Duration`,
      activity: `Activity`,
    },
    'sort-tooltip': `Sort Replays`,
    'summary-tab': `Summary`,
    'toggle-fullscreen': `Toggle Fullscreen`,
    'toggle-fullscreen-tooltip': `Toggle Fullscreen (F)`,
    'play-pause-tooltip': `Play/Pause Replay (Spacebar)`,
    'next-replay-tooltip': `Next Replay (⇧ + →)`,
    'bookmark-replay': `Bookmark this replay`,
    'unbookmark-replay': `Remove bookmark`,
    'bookmark-filter-tooltip': `Show bookmarked replays`,
    'bookmark-filter-active-tooltip': `Filters are not applied while viewing bookmarked replays`,
    'bookmark-requires-playlist': `Save a playlist to bookmark replays`,
    'expired-journeys': `Expired Journeys`,
    'expired-replay-tooltip': `This journey is beyond the {{retentionDays}} day retention window limit. Add a custom retention window to persist replays beyond this limit.`,
    'customize-properties': `Pin Properties`,
    'view-in-lexicon': `View in Lexicon`,
    'analyze-in-insights': `Analyze in Insights`,
    'jump-to-event': `Jump to Event`,
    filter: `Filter`,
    'collapse-filters': `Collapse filters`,
    'expand-filters': `Expand filters`,
    'tracked-events': `Tracked Events`,
    'stitched-events': `Stitched Events`,
    'user-sentiment': `User Sentiment`,
    console: `Console`,
    'collapse-console': `Collapse console options`,
    'expand-console': `Expand console options`,
    'console-errors': `Errors`,
    'console-warnings': `Warnings`,
    'console-logs': `Logs`,
    'console-message': `Message`,
    'stack-trace': `Stack Trace`,
    network: `Network`,
    'enable-network-tooltip': `Show network requests captured during the session`,
    'collapse-network': `Collapse network options`,
    'expand-network': `Expand network options`,
    'network-fetch': `Fetch`,
    'network-xhr': `XHR`,
    'network-other': `Other`,
    'network-method': `Method`,
    'network-status': `Status`,
    'network-duration': `Duration`,
    'network-initiator-type': `Initiator Type`,
    'network-request-headers': `Request Headers`,
    'network-response-headers': `Response Headers`,
    'network-request-body': `Request Body`,
    'network-response-body': `Response Body`,
    'network-url': `URL`,
    'network-general': `General`,
    'network-headers': `Headers`,
    'network-payload': `Payload`,
    'network-response': `Response`,
    'network-headers-not-captured': `Headers not captured`,
    'network-headers-not-captured-description': `Request and response headers are not captured by default. To enable header capture, configure the record_network_options in your Mixpanel SDK initialization.`,
    'network-body-not-captured': `Body not captured`,
    'network-request-body-not-captured-description': `Request bodies are not captured by default. To enable request body capture, configure the record_network_options in your Mixpanel SDK initialization.`,
    'network-response-body-not-captured-description': `Response bodies are not captured by default. To enable response body capture, configure the record_network_options in your Mixpanel SDK initialization.`,
    'network-no-headers': `No headers`,
    'network-no-body': `No body`,
    'network-learn-more': `Learn more`,
    'expand-event-details': `View event details`,
    'event-name': `Event Name`,
    'event-time': `Event Time`,
    'event-quantity': `Quantity`,
    'event-properties': `Pinned Properties`,
    'event-display-name': `Display Name`,
    'event-description': `Description`,
    'event-tags': `Tags`,
    'event-platforms': `Platforms`,
    'volume-30-days': `Volume (30 days)`,
    'query-count-30-days': `Query Count (30 days)`,
    'occurrences-in-replay': `Occurrences in this replay`,
    'recently-queried-by': `Recently Queried By`,
    'no-usage-data': `No usage data available`,
    'no-properties-data': `No properties data available`,
    'no-properties-found': `No properties found for this event`,
    'failed-to-load-properties': `Failed to load event properties`,
    'property-name': `Name`,
    'property-display-name': `Display Name`,
    'property-description': `Description`,
    'property-example': `Example Value`,
    na: `N/A`,
    'skip-inactivity': `Skip Inactivity`,
    'skipping-inactivity': `Skipping Inactivity`,
    'ai-summary-button': `Summary`,
    'ai-summary-title': `AI Summary`,
    'ai-summary-no-recording-selected': `Select a replay to generate an AI summary`,
    'ai-summary-error': `Failed to generate AI summary`,
    'ai-summary-no-events': `Not enough data to generate an AI summary`,
    'ai-summary-toggle': `Toggle AI Summary`,
    'ai-summary-spark-disabled': `Enable AI in your organization settings to use AI features.`,
    'ai-summary-open-settings': `Open Settings`,
    'ai-summary-info': `Powered by Mixpanel AI`,
    'ai-summary-feedback-prompt': `Was this summary helpful?`,
    'ai-summary-feedback-link': `Share feedback`,
    'key-actions': `Key Actions`,
    'estimated-duration': `Estimated Duration`,
    'events-analyzed': `{{count}} events analyzed`,
    'generating-ai-summary': `Generating AI summary...`,
    'home-upsell-title': `Understand the "why" and get the complete picture in Mixpanel.`,
    'home-upsell-desc-1': `Seamlessly move between reports, replays, and heatmaps.`,
    'home-upsell-desc-2': `Filter replays by your trusted events, properties, or cohorts.`,
    'home-upsell-desc-3': `Watch replays across web, IOS, Android.`,
    'home-upsell-self-serve': `Get Started Free`,
    'home-upsell-contact': `Contact Sales`,
    'home-upsell-troubleshooting': `Having trouble seeing replays? Check out the <docsLink>docs</docsLink> for troubleshooting issues.`,
    'home-enabled-title': `Session Replay is enabled for this project`,
    'home-enabled-desc': `No extra setup needed. Start sending replay data from your app and recordings will appear here.`,
    'home-enabled-cta': `Start sending data`,
    'home-view-docs': `View Docs`,
    'home-increase-sampling-rate': `Increase Sampling Rate`,
    'available-properties': `Available Properties to Pin`,
    'no-description': `No description`,
    loading: `Loading...`,
    'add-property': `Pin property to event feed`,
    'remove-property': `Unpin property from event feed`,
    'max-properties-reached': `Maximum properties pinned`,
    'pinned-property': `This property is always shown`,
    'new-journeys': `New journeys since you last viewed`,
    'no-filters-applied-for-playlist-message': `Please select at least one filter to save your playlist.`,
    'save-playlist': `Save Playlist`,
    'playlist-saved': `Playlist Saved`,
    'playlist-delete-header': `Delete Playlist`,
    'playlist-delete-body': `Are you sure you want to delete this playlist? This action cannot be undone.`,
    'playlist-delete-success-toast': `The playlist has been deleted.`,
  },
  'heat-maps': {
    'heat-map': `Heatmap`,
    'click-map': `Clickmap`,
    'goal-map': `Goalmap`,
    'scroll-map': `Scrollmap`,
    'heat-maps': `Heatmaps`,
    'new-heat-map': `New Heatmap`,
    'no-heat-maps-found': `No heatmaps found.`,
    'search-placeholder': `Search for heatmaps`,
    'card-placeholder': `Create a heatmap or select one from the list on the left to view it here.`,
    'card-loading-placeholder': `Loading your heatmaps...`,
    'backdrop-placeholder': `Select a URL to generate your heatmap.`,
    backdrop: `UI States`,
    'backdrop-explainer': `A UI state represents the target page at a specific state. <read-more-link>Read more here.</read-more-link>`,
    'url-search-placeholder': `Search for URLs, try using * as a wildcard to match multiple URLs...`,
    'url-current-item': `Did you know you can use * as a wildcard in your URL to match multiple pages?`,
    'heat-map-search-placeholder': `Search for Heatmaps`,
    'no-elements-found': `No clicked elements found.`,
    'no-elements-found-error': `This UI state may not match the page the clicks were made on. See <docsLink>our docs</docsLink> for further details.`,
    'no-backdrop-options-found': `No UI state options found.`,
    'no-backdrop-options-found-error': `We couldn't find an eligible UI state. See <docsLink>our docs</docsLink> for further details.`,
    'not-enough-replays-banner': `Not enough replays to build this heatmap. Replays provide click and backdrop data for heatmaps. Remove filters, or increase your session replay sampling rate.`,
    'low-sample-size-banner': `This heatmap has a small sample size. Results may not be statistically significant. Increase your session replay quota to capture more interactions for confident insights.`,
    'no-data-for-url': `No data was found for this URL.`,
    'no-data-for-url-error': `Try one of the suggested URLs. See <docsLink>our docs</docsLink> for further details.`,
    'no-heat-map-urls-found': `No URLs found with click data. Have you implemented Mixpanel session replay with heat maps?`,
    'failed-to-load-player': `Failed to load the Heat Maps player`,
    'failed-to-load-player-error': `It looks like we weren't able to load the player for Heat Maps. Please try turning off any ad blockers or <docsLink>see docs</docsLink>.`,
    'no-replay-found': `The UI state was not found.`,
    'no-replay-found-error': `The UI state may have expired and been deleted.  Please pick a new one.  See <docsLink>our docs</docsLink> for further details.`,
    'data-points-available': `{{count, number}} click events, captured via session replays`,
    'url-exists': `A heatmap for this URL already exists.`,
    'home-item-no-url': `No URL selected`,
    'url-required-to-save': `A URL is required to save this heatmap.`,
    'sessions-loading': `Loading sessions...`,
    'sessions-available': `{{count, number}} sessions`,
    'home-item-summary-events': `for`,
    'home-item-suggested': `Suggested`,
    'home-item-suggested-tooltip': `This heatmap URL is suggested based on available click data.`,
    'clicks-title': `{{totalClickCount}} Clicks`,
    'clicks-subtitle': `Users clicked this {{tagName}} element {{count, number}} times. This represents {{percent}}% of {{pageClickCount}} clicks found on this page.`,
    'conversions-title': `Drove {{convertedUserCount}} users converting to {{eventName}}`,
    'conversions-subtitle': `Conversion from clicks on this {{tagName}} element to {{eventName}} within {{time}} days.`,
    element: `This element`,
    page: `On this page`,
    'converted-users': `Converted Users`,
    'total-users': `Total Users`,
    'conversion-rate': `Conversion Rate`,
    'property-conversions-title': `Drove {{convertedUserCount}} users converting to {{eventName}}`,
    'property-conversions-subtitle': `Conversion from clicks on this {{tagName}} element to {{eventName}} within {{time}} days. Conversions account for a sum of {{convertedPropertySum}} of {{propertyName}}.`,
    'generic-api-error': `Something went wrong. Please review the error message and contact support if this problem persists.`,
    'get-list-api-error': `Sorry, something went wrong while loading your heatmaps. Please contact support if this problem persists.`,
    'create-api-error': `Sorry, something went wrong while creating your heatmap. Please contact support if this problem persists.`,
    'update-api-error': `Sorry, something went wrong while updating your heatmap. Please contact support if this problem persists.`,
    'delete-api-error': `Sorry, something went wrong while deleting your heatmap. Please contact support if this problem persists.`,
    'backdrop-replay-not-found': `Your existing UI state may have expired or been deleted. A new one will be selected for you if available.`,
    'backdrop-replay-not-found-no-edit': `The existing UI state may have expired or been deleted.  Edit permissions are required to select a new one.`,
    feedback: `Heatmaps Feedback`,
    'feedback-description': `Rate your satisfaction with this heatmap`,
    'feedback-placeholder': `Additional feedback (Optional)`,
    'feedback-tooltip': `Provide feedback`,
    'delete-heat-map-title': `Delete Heatmap`,
    'delete-heat-map-text': `Are you sure you want to delete the {{heatMapName}} heatmap? This action is permanent cannot be undone.`,
    'delete-heat-map-collection-title': `Delete Heatmap Collection`,
    'delete-heat-map-collection-text': `Are you sure you want to delete the {{heatMapCollectionName}} heatmap collection? This action is permanent cannot be undone.  The heatmaps within this collection will not be deleted.`,
    'created-new': `Created new heatmap.`,
    'review-replays': `Review Replays`,
    'view-replays': `View Replays`,
    'view-replays-tooltip': `View replays for this heatmap.`,
    'create-new': `Create`,
    'create-new-tooltip': `Create a new Heatmap`,
    'view-heat-map': `View Heatmap`,
    'filter-limit-reached': `You have reached the maximum number of filters for this heatmap. Please remove some filters to add more.`,
    loading: `Loading...`,
    'interactive-mode': `Customize UI State`,
    'interactive-mode-explainer': `Interact with the state to scroll and adjust it to your needs.  Some interactions (such as navigating to other pages) will not work.`,
    'disabled-with-interactive-mode': `Disabled while in interactive mode`,
    'not-seeing-ui-state': `Not seeing the UI state you're looking for?`,
    'interact-with-current-state': `Adjust Current UI State`,
    'interact-with-current-state-tooltip': `Use this to scroll within elements that have their own independent scroll, like panels, sidebars, or embedded lists. Click interactions are not supported. Full page scroll is available by default on the heatmap.`,
    'save-ui-state': `Save UI State`,
    'open-heat-map-tooltip': `Open this heatmap in a new tab`,
    compare: `Compare`,
    comparison: `Comparison`,
    'compare-new': `Create new heatmap`,
    'compare-duplicate': `Duplicate current heatmap`,
    'compare-index': `Showing heatmap {{index}} of {{total}} in this collection`,
    copy: `Copy`,
    goal: `Goal`,
    'goal-event-explainer': `A user action deemed valuable to a business, such as a "Purchase" or "Sign Up" event.`,
    'goal-property-explainer': `An optional numeric property that is used to weight conversions, such as a "Purchase Amount" or "Order Value" property.`,
    'analyze-conversions-tooltip': `Analyze conversions to a specific event from clicks on this heatmap.`,
    'analyzing-conversions-tooltip': `Analyzing conversions to {{eventName}} from clicks on this heatmap.`,
    'conversion-goal': `Conversion Goal`,
    'goal-event': `Goal Event`,
    'starting-event': `Clicks on this heatmap`,
    'goal-event-placeholder': `Select a goal event...`,
    'goal-property': `Conversion Weight Property (Optional)`,
    'goal-property-placeholder': `Select a property...`,
    'property-filter': `Property Filters`,
    'property-filter-desc': `View heatmap clicks that matched certain attributes, like device, browser, and more.`,
    'scroll-map-fold-line-desc': `{{percentage}}% of content is seen without scrolling on average.`,
    'scroll-map-fold-line-label': `Average fold`,
    'scroll-map-heat-zone': `{{percentage}}% of users scrolled to this zone. ({{total}} total)`,
    'loading-more-tooltip': `Loading additional click data...`,
    'load-more-data': `Load More Data`,
    'load-more-tooltip-no-chunks': `More click data is available for this heatmap`,
    'load-more-tooltip-with-progress': `{{percent}}% of available clicks loaded`,
    columns: {
      name: `Name`,
      url: `URL`,
      'event-count': `Events`,
      'session-count': `Sessions`,
    },
    select: `Select`,
    selected: `Selected`,
    'select-a-backdrop': `Select a state`,
    'backdrops-pagination': `Viewing states {{start}} - {{end}} ({{total}} total)`,
    'backdrops-thumbnail-load-error': `State failed to load`,
  },
  signup: {
    docs: `Docs`,
    'contact-support': `Contact Support`,
    name: {
      title: `Hi there, what's your name?`,
      subtitle: `Help us personalize your experience by answering three quick questions`,
      'short-subtitle': `Help us personalize your experience.`,
      'first-name': `First Name`,
      'last-name': `Last Name`,
      'first-name-placeholder': `First name`,
      'last-name-placeholder': `Last name`,
      later: `Remind me later`,
    },
    work: {
      title: `What kind of work do you do?`,
      subtitle: `Help us personalize your experience.`,
      placeholder: `How would you describe your work?`,
      option: {
        product: `Product`,
        engineering: `Engineering`,
        marketing: `Marketing`,
        growth: `Growth`,
        data: `Data`,
        design: `Design`,
        operations: `Operations`,
        sales: `Sales`,
        'customer-success': `Customer Success`,
        other: `Other`,
      },
    },
    role: {
      title: `What is your role?`,
      subtitle: `Help us personalize your experience.`,
      option: {
        'c-suite': `C-Suite`,
        executive: `Executive`,
        'department-lead': `Department Lead`,
        'team-manager': `Team Manager`,
        'individual-contributor': `Individual Contributor`,
        contractor: `Contractor / Freelancer`,
      },
    },
    thanks: {
      title: `Thanks {{firstName}}!`,
      subtitle: `This will help us better serve you`,
      finish: `Finish`,
    },
    org: {
      'create-org': `Create your organization`,
      'pending-invitation': `Someone from your organization has already sent you an invitation. Please check your email to accept it.`,
      'team-collab-msg': `This is where your team can work and collaborate on projects.`,
      'anyone-can-join': `Anyone with your domain can join`,
      'auto-join-msg': `Checking this will allow anyone with your domain to join your organization automatically. You may change permissions later in settings.`,
      'org-name': `Organization Name`,
      'acme-inc': `Acme Inc.`,
      'existing-redirect': `Existing organization found. Redirecting...`,
      'create-org-error': `{{message}}`,
      'data-center': `Data Center`,
      'use-eu-dc': `If you are a Mixpanel customer in the EU, you have the option to send your data to Mixpanel's EU data center. You do not lose any product capability if you choose to store your data in the EU.`,
      // OpenAI messes up the flag emoji, so passing it in as a param
      'united-states': `{{flagEmoji}} United States`,
      'european-union': `{{flagEmoji}} European Union`,
      'join-existing-org': `Join an Existing Organization`,
      requested: `Requested`,
      'request-sent': `Your request is sent!`,
      'join-org-error': `{{message}}`,
      'explore-mixpanel': `Explore Mixpanel`,
      join: `Join`,
      owner: `Owner: {{ownerEmail}}`,
      'create-new-org': `Create a new organization`,
      'company-size-question': `How large is your company?`,
      'company-industry-question': `What best describes your company’s industry?`,
      'project-description-question': `What best describes this project?`,
      'core-product': `This is our core product`,
      'internal-tool': `This is an internal tool`,
      'new-initiative': `This is a new initiative`,
      'side-project': `This is a side project`,
      'migrate-data-question': `Do you plan to migrate over existing data?`,
      'migrate-from': `Yes, from {{dataSource}}`,
      'migrate-from-other': `Yes, from another source`,
      'not-sure': `I'm not sure yet`,
      industry: {
        b2b: `B2B / SaaS`,
        ecommerce: `Ecommerce`,
        social: `Social`,
        media: `Media`,
        gaming: `Gaming`,
        consumer: `Consumer`,
        marketplace: `Marketplace`,
        finance: `Finance`,
        education: `Education`,
        healthcare: `Healthcare`,
        'agency-consulting': `Agency / Consulting`,
        other: `Other`,
      },
    },
    intro: {
      skip: `Skip`,
      next: `Next {{emoji}}`,
      'hi-no-name': `Hi,`,
      'hi-first-name': `Hi {{firstName}},`,
      welcome: `welcome to Mixpanel`,
      'mixpanel-overview': `Mixpanel is a tool that helps you understand how your users interact with your app.`,
      'key-concepts': `Let's spend a few minutes to walk through a couple concepts to get you started.`,
      'get-started': `Get Started`,
      'unique-user': `Every individual is a unique user`,
      'cafe-owner': `Imagine for a moment, you own a café.`,
      'cafe-customer': `Every customer who visits your cafe is unique. Details like their name, age, favorite coffee, and most recently purchased pastry are what make every customer unique.`,
      'customer-details': `In Mixpanel, these customers are called **users**, and their details are called **profile properties**. We keep track of this information to help you better understand your audience.`,
      'track-action': `Any action can be tracked as an event`,
      'purchase-details': `Every time a customer purchases a coffee at your café, there are details that describe the purchase the moment it happens. Actions like purchasing a coffee can be tracked as an **event** in Mixpanel. All details describing the event in the moment are called **event properties**.`,
      'user-action': `User Action = **Event**`,
      'action-details': `Details = **Event Properties**`,
      'profiles-purpose': `Profiles bring everything together`,
      'mixpanel-consolidates': `With profile properties keeping track of who a user is, and events keeping track of what a user does, Mixpanel consolidates this information into a **profile** for every user.`,
      'uncover-insights': `Having all this customer information available allows you to uncover your café's most popular pastry or the average amount spent by each customer.`,
      'great-work': `Great work {{emoji}}`,
      'ready-to-start': `Now that you’re familiar with Mixpanel building blocks, you’re ready to get started.`,
      'boards-overview': `Boards are the homebase for you to collaborate with your team.`,
      'view-demo': `View Demo`,
      'go-to-board': `Go to Your Board`,
      'go-to-mixpanel': `Go to Mixpanel`,
    },
  },
  onboarding: {
    'table-title': `Your team's planned events`,
    'planned-edited-by': `Edited by <typograph>{{editorName}}</typograph> on {{modifiedDate}}`,
    'completion-duration_one': `{{count, number}} minute`,
    'completion-duration_other': `{{count, number}} minutes`,
    completed: `Completed`,
    back: `Back`,
    next: `Next`,
    skip: `Skip`,
    sidenav: {
      'setup-guide': `Set Up Guide`,
      'up-and-running': `Up and running in 20 minutes`,
      'get-started': `Get Started`,
      'personalize-setup': `Personalize Setup`,
      'connect-data': `Connect Data`,
      'verify-connection': `Verify Connection`,
      'explore-your-data': `Explore Your Data`,
      'skip-for-now': `Skip for Now`,
      'go-back': `Go Back`,
      'finish-setup': `Finish Set Up`,
    },
    header: {
      'step-one': `Step 1`,
      'step-two': `Step 2`,
      'step-three': `Step 3`,
      'step-four': `Step 4`,
    },
    'install-js-sdk': {
      tabs: {
        snippet: `snippet`,
        npm: `npm`,
        yarn: `yarn`,
      },
      toggles: {
        autocapture: `Autocapture`,
        'session-replay': `Session Replay`,
      },
      'copy-to-clipboard': {
        copy: `Copy`,
        copied: `Copied`,
        'copy-error': `Something went wrong copying`,
      },
      'initialize-heading': `Initialize`,
      'initialize-description': `Start tracking events in just minutes with Autocapture. Paste the snippet below into your project’s page header to get started.`,
      'configure-heading': `Configure precision tracking`,
      'configure-subheading': `Optional`,
      'configure-description': `Autocapture will automatically start tracking events, but you can also define specific events you want Mixpanel to capture. To proceed with precision tracking, <docLink>read our docs</docLink>.`,
      'configure-description-no-autocapture': `You’ve disabled Autocapture-- you’ll need to follow a few additional steps to connect Mixpanel. To proceed with identifying users and tracking events, <docLink>follow along with our docs</docLink>.`,
      'command-line-heading': `Open command line`,
      'command-line-description': `Navigate to your project directory and enter the npm command below.`,
      'disabled-autocapture-heading': `Heads up, you’ve removed Autocapture from your Mixpanel install`,
      'disabled-autocapture-description': `Re-enable Autocapture and paste in the snippet again for the fastest way to connect your data, or follow along below to connect your data without using Autocapture.`,
      'code-comments': {
        'paste-before-closing-tag': `Paste this right before your closing head tag`,
        'create-instance': `Create an instance of the Mixpanel object, your token is already added to this snippet`,
      },
    },
    'invite-banner': {
      'need-help': `Need help from your team?`,
      'invite-your-team': `Invite a teammate to complete this step—you have unlimited seats.`,
      'button-text': `Invite a Teammate`,
    },
    chart: {
      'bar-default-title': `How many users did these actions this year?`,
      'funnel-title': `Where do customers drop off?`,
      'funnel-breakdown-default-title': `Where do mobile vs desktop users drop off?`,
      'line-default-title': `What is the trend over the last month?`,
      'breakdown-custom-title': `{{selectedEvent}} by {{property}}`,
    },
    events: {
      'events-header': `Events`,
      'property-help-text': `You can <boldText>edit</boldText> these properties to suit your needs`,
      'event-help-text': `Select an <boldText>event</boldText> to view its properties`,
      'select-property-help-text': `Select a <boldText>property</boldText> to view a preview`,
      'selected-event-properties': `{{selectedEvent}}'s Properties`,
      'add-own-property': `Add your own`,
      'planned-events': `Your planned events`,
      'teamate-help': `Need help from a teammate?`,
      'send-plan-guide': `Send your planned events and our 15 minute connection guide to a teammate`,
      'missing-invite-access': `You don’t have access to inviting. Ask a teammate to change your account’s permissions.`,
      'connect-data': `Connect your data`,
      'view-demo-project': `View a demo project`,
      'invite-teammate': `Invite a teammate`,
      'new-to-analytics': `New to event based analytics?`,
      'learn-analytics': `Learn how it works`,
      'choose-one-event': `You must choose at least one event`,
      'add-own-event': `Add Your Own Event`,
      'tracking-suggestion': `We suggest tracking 6 or fewer events to start`,
      'one-step-away': `Great work! Insights are one step away.`,
      'ready-to-connect': `You are ready to connect your data to Mixpanel`,
      'choose-events': `Choose your first events`,
      'chose-events-subtitle': `Events are meaningful actions users take in your product. The events below are a good starting point for your industry. You may edit this list to fit your needs.`,
      'choose-event-properties': `Choose the properties you want track with each event`,
      'popular-event-properties': `Below are popular selections — in some cases Mixpanel will track these by default.`,
      'personalize-setup': `Personalize Setup`,
      'step-two': `step 2`,
    },
    implement: {
      'listening-for-data': `Listening for events and users...`,
      'no-data-detected': `No data detected - Just now`,
      'data-detected': `Data detected!`,
      'click-for-details': `Click Events or Users for more details`,
      'view-events': `View Events`,
      'view-users': `View Users`,
      'copy-token-error': `Something went wrong copying the link`,
      'remaining-owners': `{{remainingOwners}} more`,
      copied: `Copied`,
      'copy-token': `Copy project token`,
      'token-tooltip': `This token is unique to the project this guide is setting up. \n\nA project is a container of your analytics data.`,
      'no-permission-tooltip': `You don't have permission to access this\n\nContact a {{orgName}} owner or admin to have your role changed:\n\n{{ownersString}}`,
      you: `You`,
      'planned-events': `Planned events: {{eventList}}`,
      'last-edited': `Last edited by {{lastEditedUser}} on {{lastEditedDate}}`,
      'view-planned-events': `View Planned Events`,
      'implemented-cdp': `I already collect data with another tool`,
      popular: `Popular`,
      'more-options': `More Options`,
      'first-analytics-tool': `Mixpanel is my first product analytics tool`,
      'sdk-includes-autocapture': `We now offer Autocapture in our Javascript SDK! <docLink>Learn more here</docLink>`,
      'finish-and-close': `Finish and Close`,
      'connect-data-header': `Connect Data`,
      'step-three': `step 3`,
      'choose-connection-method': `Choose a connection method`,
      'implementation-guide': `Not sure which method to use? <guideLink>Check out our guide</guideLink>`,
      'no-user-data-callout': {
        heading: `Mixpanel is not detecting any users with profiles at this time`,
        description: `To configure Mixpanel to profile anonymous visitors please <guideLink>follow along with the docs.</guideLink>`,
      },
    },
    welcome: {
      'start-here': `Start Here`,
      'quick-start': `Quick Start`,
      'quick-start-description': `We’ll walk you through a quick setup to connect and track your data.`,
      'quick-start-label': `Most Popular`,
      'step-one': `step 1`,
      'not-ready': `Not ready to start?`,
      'view-demo-project': `View a demo project`,
      'how-analytics-help': `How Analytics Helps Businesses`,
      'how-mixpanel-works': `Learn how Mixpanel works`,
      'no-invite-permission': `You don’t have access to inviting. Ask a teammate to change your account’s permissions.`,
      'modal-error': `{{message}}`,
      'invite-teammate': `Invite a teammate`,
      'choose-start': `How do you want to get started with Mixpanel?`,
      'choose-start-description': `Not sure which method to use? <guideLink>Check out our guide</guideLink>.`,
      'install-sdk': `Install the Javascript SDK`,
      'select-dif-sdk': `Select a different SDK`,
      'mixpanel-will-help': `Mixpanel will help you better understand your customers and answer questions about your product`,
      'new-to-analytics': `I'm new to product analytics`,
      'quick-setup': `Mixpanel will walk you through a quick setup to track and connect data`,
      'personalize-setup': `Personalize Setup`,
      'already-use-cdp': `I already collect data in a tool`,
      'already-use-cdp-description': `Seamlessly integrate with a number of popular data tools like Segment, Snowflake, BigQuery, and more.`,
      'example-cdps': `Such as Segment, Snowflake, BigQuery, Redshift, Rudderstack, etc...`,
      'connect-data': `Connect Data`,
      'unlock-insights': `Unlock product insights by connecting your data`,
      'connect-guide': `This guide will show you the best way to connect`,
      'connect-with-existing-tool': `Integrate with an existing tool`,
      'coding-agent-title': `Use your Coding Agent`,
      'coding-agent-beta': `Beta`,
      'coding-agent-description': `Add Mixpanel tracking to your codebase using your favorite AI coding tool`,
      'coding-agent-cta': `Get Started`,
      'coding-agent-feature-1': `Supports Claude Code, Cursor, ChatGPT Codex, Replit, and other popular coding tools`,
      'coding-agent-feature-2': `Works with any language, framework, or existing tracking setup`,
    },
    connect: {
      'configure-tracking': `Configure precision tracking`,
      'configure-tracking-description': `Autocapture will automatically start tracking events, but you can also define specific events you want Mixpanel to capture. To proceed with precision tracking, <docLink>read our docs</docLink>.`,
      'copy-prompt-instruction': `Paste this prompt into your preferred coding agent to get started:`,
      'install-with-ai': `Use your Coding Agent`,
      integrations: `Integrations`,
      'integrations-description': `If you are already collecting data with another tool, select it below to view the guide. If you can't find your tool, you can also leverage our HTTP APIs directly.`,
      'mixpanel-skill': `Mixpanel Skill`,
      'mixpanel-skill-description': `Select your tool, copy the prompt, and run it in your coding agent.`,
      'prompt-help-text': `Your agent will scan your codebase and walk you through adding tracking step by step. Then return to Mixpanel to verify your data is connected.`,
      sdks: {
        'ios-swift': `iOS (Swift)`,
        javascript: `Javascript`,
        nodejs: `NodeJS`,
        python: `Python`,
        'react-native': `React Native`,
      },
      'select-coding-tool': `Select the tool you plan to use:`,
      'select-sdk': `Select SDK`,
      'select-sdk-description': `Select the framework you'd like to use to install Mixpanel and follow along with the instructions provided. Once you have completed set up, continue on to verify the connection.`,
      tools: {
        bigquery: `BigQuery`,
        redshift: `Redshift`,
        rudderstack: `RudderStack`,
        segment: `Segment`,
        snowflake: `Snowflake`,
      },
    },
    verify: {
      'verify-connection': `Verify that your data is connected`,
      'verify-connection-description': `After installing Mixpanel, your project should begin ingesting data. Once we detect traffic, you’ll see it appear below.`,
      'need-help': `Need help troubleshooting?`,
      'autocapture-docs': `Autocapture docs`,
      'session-replay-docs': `Session Replay docs`,
      'contact-support': `Contact support`,
      'retry-button-text': `Retry`,
      'status-box': {
        headings: {
          success: `Connection Verified!`,
          loading: `Listening for Data...`,
          'some-connections-found': `Some connections found`,
          'still-listening-for-data': `Still Listening...`,
          'no-connections-found': `No connection found`,
        },
        descriptions: {
          success: `Mixpanel is now receiving data from your site.`,
          timeout: `We’re still listening, but we're missing some data—<docLink>check out our docs</docLink> to verify your implementation.`,
          error: `We’re missing some data. Please refer to the troubleshooting links below or <docLink>visit our docs</docLink>.`,
          errors: `We’re not detecting any data. Please refer to the troubleshooting links below or <docLink>visit our docs</docLink>.`,
          apiError: `There was an error connecting with your data. Please try again later.`,
        },
        tallies: {
          events: `Events`,
          users: `Users`,
          replays: `Replays`,
        },
      },
    },
    explore: {
      'your-data': `🎉 Your data is flowing -- Discover your first insights`,
      'your-data-not-verified': `Check back once your data is connected`,
      'use-template': `Use a template to quickly generate valuable insights`,
      'template-suggestions': `Suggested for you`,
      'template-usefulness': `You may find this template useful based on your event data`,
      'trusted-numbers': `Trusted by {{count}} customers this month`,
      'starter-board': `Starter Board`,
      'instant-snapshot': `Get an instant snapshot of your user activity, total events, and customer retention across your entire product.`,
      'see-data': `See your data in action`,
      'explore-more': `Explore more templates`,
      'templates-description': `Templates are built and maintained by Mixpanel to use industry best practices & benchmarks`,
    },
    'mixpanel-logo': `Mixpanel Logo`,
    mobile: {
      'email-limit-reached': `Email limit reached. Please try again later`,
      'login-link-sent': `Login link sent successfully`,
      'login-link-error': `Error sending login link`,
      'bigger-is-better': `Bigger is better`,
      'best-on-computer': `Mixpanel is best viewed on a larger screen. We can send you a login link to open on your computer.`,
      'email-login-link': `Email me a login link`,
      resend: `Resend`,
    },
    example: {
      industry: {
        b2b: `B2B / SaaS`,
        consumer: `Consumer`,
        social: `Social`,
        media: `Media`,
        gaming: `Gaming`,
        education: `Education`,
        healthcare: `Healthcare`,
        ecommerce: `E-Commerce`,
        agency: `Agency`,
        finance: `Finance`,
        marketplace: `Marketplace`,
        other: `Other`,
      },
      event: {
        'add-to-cart': `Add to Cart`,
        'complete-search': `Complete Search`,
        'content-view': `Content View`,
        'create-item': `Create Item`,
        engage: `Engage`,
        'finish-course': `Finish Course`,
        'finish-level': `Finish Level`,
        'finish-module': `Finish Module`,
        'funded-account': `Funded Account`,
        'new-appointment': `New Appointment`,
        'page-view': `Page View`,
        'purchase-media': `Purchase`,
        'purchase-ecommerce': `Purchase`,
        'request-demo': `Request Demo`,
        'select-bank': `Select Bank`,
        share: `Share`,
        'sign-up': `Sign Up`,
        'start-course': `Start Course`,
        'start-level': `Start Level`,
        transaction: `Transaction`,
        'view-cart': `View Cart`,
        'view-item': `View Item`,
      },
      property: {
        'account-type': `Account Type`,
        amount: `Amount`,
        'appointment-type': `Appointment Type`,
        'area-of-interest': `Area of Interest`,
        'bank-name': `Bank Name`,
        cart: `Cart`,
        category: `Category`,
        'completion-time': `Completion Time`,
        'contact-method': `Contact Method`,
        'content-type': `Content Type`,
        country: `Country`,
        'course-id': `Course ID`,
        'course-name': `Course Name`,
        'creation-method': `Creation Method`,
        'day-of-week': `Day of Week`,
        'engagement-type': `Engagement Type`,
        'final-score': `Final Score`,
        'funding-amount': `Funding Amount`,
        'funding-source': `Funding Source`,
        'game-mode': `Game Mode`,
        genre: `Genre`,
        'hearts-remaining': `Hearts Remaining`,
        'item-id': `Item ID`,
        'item-type': `Item Type`,
        'level-number': `Level Number`,
        location: `Location`,
        'module-number': `Module Number`,
        'original-content-type': `Original Content Type`,
        os: `OS`,
        'page-name': `Page Name`,
        'payment-portal': `Payment Portal`,
        price: `Price`,
        'privacy-setting': `Privacy Setting`,
        'purchase-type': `Purchase Type`,
        'recipient-type': `Recipient Type`,
        'referrer-url-path': `Referrer URL Path`,
        'result-shown': `Result Shown`,
        'search-click': `Search Click`,
        'search-query': `Search Query`,
        'share-method': `Share Method`,
        source: `Source`,
        'total-amount': `Total Amount`,
        'total-duration': `Total Duration`,
        'transaction-type': `Transaction Type`,
        'view-duration': `View Duration`,
      },
      'property-value': {
        'united-states': `United States`,
        brazil: `Brazil`,
        india: `India`,
        android: `Android`,
        ios: `iOS`,
        pc: `PC`,
        social: `Social`,
        organic: `Organic`,
        email: `Email`,
        home: `Home`,
        about: `About`,
        pricing: `Pricing`,
        '/home': `/home`,
        '/products': `/products`,
        '/contact': `/contact`,
        form: `Form`,
        phone: `Phone`,
        producta: `ProductA`,
        productb: `ProductB`,
        productc: `ProductC`,
        photo: `Photo`,
        video: `Video`,
        text: `Text`,
        app: `App`,
        web: `Web`,
        'third-party': `Third Party`,
        like: `Like`,
        comment: `Comment`,
        share: `Share`,
        'in-product': `In Product`,
        copy: `Copy`,
        image: `Image`,
        article: `Article`,
        public: `Public`,
        private: `Private`,
        'friends-only': `Friends Only`,
        movie: `Movie`,
        show: `Show`,
        clip: `Clip`,
        comedy: `Comedy`,
        action: `Action`,
        documentary: `Documentary`,
        full: `Full`,
        partial: `Partial`,
        brief: `Brief`,
        subscription: `Subscription`,
        'one-time': `One Time`,
        'in-app': `In-App`,
        'single-player': `Single Player`,
        multiplayer: `Multiplayer`,
        course101: `Course101`,
        course205: `Course205`,
        course310: `Course310`,
        intro: `Intro`,
        'learning-101': `Learning 101`,
        'advanced-topics': `Advanced Topics`,
        '30-min': `30 min`,
        '60-min': `60 min`,
        '120-min': `120 min`,
        '2-days': `2 days`,
        '1-week': `1 week`,
        '3-weeks': `3 weeks`,
        symptoms: `Symptoms`,
        pharmacy: `Pharmacy`,
        diet: `Diet`,
        true: `true`,
        false: `false`,
        'new-patient': `New Patient`,
        general: `General`,
        emergency: `Emergency`,
        monday: `Monday`,
        tuesday: `Tuesday`,
        wednesday: `Wednesday`,
        virtual: `Virtual`,
        'in-office': `In-Office`,
        item123: `Item123`,
        item456: `Item456`,
        item678: `Item678`,
        clothing: `Clothing`,
        electronics: `Electronics`,
        'home-appliances': `Home Appliances`,
        '$0-$50': `$0-$50`,
        '$51-$100': `$51-$100`,
        'over-$100': `Over $100`,
        '[item1, item2]': `[Item1, Item2]`,
        '[item3, item4]': `[Item3, Item4]`,
        '[item3]': `[Item3]`,
        'bank-of-us': `Bank of US`,
        'crescent-bank': `Crescent Bank`,
        oakmont: `Oakmont`,
        checking: `Checking`,
        savings: `Savings`,
        investment: `Investment`,
        'bank-transfer': `Bank Transfer`,
        'credit-card': `Credit Card`,
        'digital-wallet': `Digital Wallet`,
        deposit: `Deposit`,
        withdrawal: `Withdrawal`,
        transfer: `Transfer`,
        'personal-bank': `Personal Bank`,
        business: `Business`,
        external: `External`,
        'property-a': `Property A`,
        'property-b': `Property B`,
        'property-c': `Property C`,
      },
    },
  },
  'onboarding-legacy': {
    'table-title': `Your team's planned events`,
    'planned-edited-by': `Edited by <typograph>{{editorName}}</typograph> on {{modifiedDate}}`,
    'completion-duration_one': `{{count, number}} minute`,
    'completion-duration_other': `{{count, number}} minutes`,
    completed: `Completed`,
    back: `Back`,
    next: `Next`,
    skip: `Skip`,
    sidenav: {
      'setup-guide': `Setup Guide`,
      'up-and-running': `Up and running in 20 minutes`,
      'start-here': `Start Here`,
      'personalize-setup': `Personalize Setup`,
      'connect-data': `Connect Data`,
      'skip-for-now': `Skip for Now`,
    },
    chart: {
      'bar-default-title': `How many users did these actions this year?`,
      'funnel-title': `Where do customers drop off?`,
      'funnel-breakdown-default-title': `Where do mobile vs desktop users drop off?`,
      'line-default-title': `What is the trend over the last month?`,
      'breakdown-custom-title': `{{selectedEvent}} by {{property}}`,
    },
    events: {
      'events-header': `Events`,
      'property-help-text': `You can <boldText>edit</boldText> these properties to suit your needs`,
      'event-help-text': `Select an <boldText>event</boldText> to view its properties`,
      'select-property-help-text': `Select a <boldText>property</boldText> to view a preview`,
      'selected-event-properties': `{{selectedEvent}}'s Properties`,
      'add-own-property': `Add your own`,
      'planned-events': `Your planned events`,
      'teamate-help': `Need help from a teammate?`,
      'send-plan-guide': `Send your planned events and our 15 minute connection guide to a teammate`,
      'missing-invite-access': `You don’t have access to inviting. Ask a teammate to change your account’s permissions.`,
      'connect-data': `Connect your data`,
      'view-demo-project': `View a demo project`,
      'invite-teammate': `Invite a teammate`,
      'new-to-analytics': `New to event based analytics?`,
      'learn-analytics': `Learn how it works`,
      'choose-one-event': `You must choose at least one event`,
      'add-own-event': `Add Your Own Event`,
      'tracking-suggestion': `We suggest tracking 6 or fewer events to start`,
      'one-step-away': `Great work! Insights are one step away.`,
      'ready-to-connect': `You are ready to connect your data to Mixpanel`,
      'choose-events': `Choose your first events`,
      'chose-events-subtitle': `Events are meaningful actions users take in your product. The events below are a good starting point for your industry. You may edit this list to fit your needs.`,
      'choose-event-properties': `Choose the properties you want track with each event`,
      'popular-event-properties': `Below are popular selections — in some cases Mixpanel will track these by default.`,
      'personalize-setup': `Personalize Setup`,
      'step-two': `step 2`,
    },
    implement: {
      'listening-for-data': `Listening for events and users...`,
      'no-data-detected': `No data detected - Just now`,
      'data-detected': `Data detected!`,
      'click-for-details': `Click Events or Users for more details`,
      'view-events': `View Events`,
      'view-users': `View Users`,
      'copy-token-error': `Something went wrong copying the link`,
      'remaining-owners': `{{remainingOwners}} more`,
      copied: `Copied`,
      'copy-token': `Copy project token`,
      'token-tooltip': `This token is unique to the project this guide is setting up. \n\nA project is a container of your analytics data.`,
      'no-permission-tooltip': `You don't have permission to access this\n\nContact a {{orgName}} owner or admin to have your role changed:\n\n{{ownersString}}`,
      you: `You`,
      'planned-events': `Planned events: {{eventList}}`,
      'last-edited': `Last edited by {{lastEditedUser}} on {{lastEditedDate}}`,
      'view-planned-events': `View Planned Events`,
      'implemented-cdp': `I already collect data with another tool`,
      popular: `Popular`,
      'more-options': `More Options`,
      'first-analytics-tool': `Mixpanel is my first product analytics tool`,
      'sdk-includes-autocapture': `We now offer Autocapture in our Javascript SDK! <docLink>Learn more here</docLink>`,
      'finish-and-close': `Finish and Close`,
      'connect-data-header': `Connect Data`,
      'step-three': `step 3`,
      'choose-connection-method': `Choose a connection method`,
      'implementation-guide': `Not sure which method to use? <guideLink>Check out our guide</guideLink>`,
    },
    welcome: {
      'start-here': `Start Here`,
      'step-one': `step 1`,
      'not-ready': `Not ready to start?`,
      'view-demo-project': `View a demo project`,
      'how-analytics-help': `How Analytics Helps Businesses`,
      'how-mixpanel-works': `Learn how Mixpanel works`,
      'no-invite-permission': `You don’t have access to inviting. Ask a teammate to change your account’s permissions.`,
      'invite-teammate': `Invite a teammate`,
      'choose-start': `Choose how you would like to start`,
      'mixpanel-will-help': `Mixpanel will help you better understand your customers and answer questions about your product`,
      'new-to-analytics': `I'm new to product analytics`,
      'quick-setup': `Mixpanel will walk you through a quick setup to track and connect data`,
      'personalize-setup': `Personalize Setup`,
      'already-use-cdp': `I already collect data in a tool`,
      'example-cdps': `Such as Segment, Snowflake, BigQuery, Redshift, Rudderstack, etc...`,
      'connect-data': `Connect Data`,
      'unlock-insights': `Unlock product insights by connecting your data`,
      'connect-guide': `This guide will show you the best way to connect`,
    },
    mobile: {
      'email-limit-reached': `Email limit reached. Please try again later`,
      'login-link-sent': `Login link sent successfully`,
      'login-link-error': `Error sending login link`,
      'bigger-is-better': `Bigger is better`,
      'best-on-computer': `Mixpanel is best viewed on a larger screen. We can send you a login link to open on your computer.`,
      'email-login-link': `Email me a login link`,
      resend: `Resend`,
    },
    example: {
      industry: {
        b2b: `B2B / SaaS`,
        consumer: `Consumer`,
        social: `Social`,
        media: `Media`,
        gaming: `Gaming`,
        education: `Education`,
        healthcare: `Healthcare`,
        ecommerce: `E-Commerce`,
        agency: `Agency`,
        finance: `Finance`,
        marketplace: `Marketplace`,
        other: `Other`,
      },
      event: {
        'add-to-cart': `Add to Cart`,
        'complete-search': `Complete Search`,
        'content-view': `Content View`,
        'create-item': `Create Item`,
        engage: `Engage`,
        'finish-course': `Finish Course`,
        'finish-level': `Finish Level`,
        'finish-module': `Finish Module`,
        'funded-account': `Funded Account`,
        'new-appointment': `New Appointment`,
        'page-view': `Page View`,
        'purchase-media': `Purchase`,
        'purchase-ecommerce': `Purchase`,
        'request-demo': `Request Demo`,
        'select-bank': `Select Bank`,
        share: `Share`,
        'sign-up': `Sign Up`,
        'start-course': `Start Course`,
        'start-level': `Start Level`,
        transaction: `Transaction`,
        'view-cart': `View Cart`,
        'view-item': `View Item`,
      },
      property: {
        'account-type': `Account Type`,
        amount: `Amount`,
        'appointment-type': `Appointment Type`,
        'area-of-interest': `Area of Interest`,
        'bank-name': `Bank Name`,
        cart: `Cart`,
        category: `Category`,
        'completion-time': `Completion Time`,
        'contact-method': `Contact Method`,
        'content-type': `Content Type`,
        country: `Country`,
        'course-id': `Course ID`,
        'course-name': `Course Name`,
        'creation-method': `Creation Method`,
        'day-of-week': `Day of Week`,
        'engagement-type': `Engagement Type`,
        'final-score': `Final Score`,
        'funding-amount': `Funding Amount`,
        'funding-source': `Funding Source`,
        'game-mode': `Game Mode`,
        genre: `Genre`,
        'hearts-remaining': `Hearts Remaining`,
        'item-id': `Item ID`,
        'item-type': `Item Type`,
        'level-number': `Level Number`,
        location: `Location`,
        'module-number': `Module Number`,
        'original-content-type': `Original Content Type`,
        os: `OS`,
        'page-name': `Page Name`,
        'payment-portal': `Payment Portal`,
        price: `Price`,
        'privacy-setting': `Privacy Setting`,
        'purchase-type': `Purchase Type`,
        'recipient-type': `Recipient Type`,
        'referrer-url-path': `Referrer URL Path`,
        'result-shown': `Result Shown`,
        'search-click': `Search Click`,
        'search-query': `Search Query`,
        'share-method': `Share Method`,
        source: `Source`,
        'total-amount': `Total Amount`,
        'total-duration': `Total Duration`,
        'transaction-type': `Transaction Type`,
        'view-duration': `View Duration`,
      },
      'property-value': {
        'united-states': `United States`,
        brazil: `Brazil`,
        india: `India`,
        android: `Android`,
        ios: `iOS`,
        pc: `PC`,
        social: `Social`,
        organic: `Organic`,
        email: `Email`,
        home: `Home`,
        about: `About`,
        pricing: `Pricing`,
        '/home': `/home`,
        '/products': `/products`,
        '/contact': `/contact`,
        form: `Form`,
        phone: `Phone`,
        producta: `ProductA`,
        productb: `ProductB`,
        productc: `ProductC`,
        photo: `Photo`,
        video: `Video`,
        text: `Text`,
        app: `App`,
        web: `Web`,
        'third-party': `Third Party`,
        like: `Like`,
        comment: `Comment`,
        share: `Share`,
        'in-product': `In Product`,
        copy: `Copy`,
        image: `Image`,
        article: `Article`,
        public: `Public`,
        private: `Private`,
        'friends-only': `Friends Only`,
        movie: `Movie`,
        show: `Show`,
        clip: `Clip`,
        comedy: `Comedy`,
        action: `Action`,
        documentary: `Documentary`,
        full: `Full`,
        partial: `Partial`,
        brief: `Brief`,
        subscription: `Subscription`,
        'one-time': `One Time`,
        'in-app': `In-App`,
        'single-player': `Single Player`,
        multiplayer: `Multiplayer`,
        course101: `Course101`,
        course205: `Course205`,
        course310: `Course310`,
        intro: `Intro`,
        'learning-101': `Learning 101`,
        'advanced-topics': `Advanced Topics`,
        '30-min': `30 min`,
        '60-min': `60 min`,
        '120-min': `120 min`,
        '2-days': `2 days`,
        '1-week': `1 week`,
        '3-weeks': `3 weeks`,
        symptoms: `Symptoms`,
        pharmacy: `Pharmacy`,
        diet: `Diet`,
        true: `true`,
        false: `false`,
        'new-patient': `New Patient`,
        general: `General`,
        emergency: `Emergency`,
        monday: `Monday`,
        tuesday: `Tuesday`,
        wednesday: `Wednesday`,
        virtual: `Virtual`,
        'in-office': `In-Office`,
        item123: `Item123`,
        item456: `Item456`,
        item678: `Item678`,
        clothing: `Clothing`,
        electronics: `Electronics`,
        'home-appliances': `Home Appliances`,
        '$0-$50': `$0-$50`,
        '$51-$100': `$51-$100`,
        'over-$100': `Over $100`,
        '[item1, item2]': `[Item1, Item2]`,
        '[item3, item4]': `[Item3, Item4]`,
        '[item3]': `[Item3]`,
        'bank-of-us': `Bank of US`,
        'crescent-bank': `Crescent Bank`,
        oakmont: `Oakmont`,
        checking: `Checking`,
        savings: `Savings`,
        investment: `Investment`,
        'bank-transfer': `Bank Transfer`,
        'credit-card': `Credit Card`,
        'digital-wallet': `Digital Wallet`,
        deposit: `Deposit`,
        withdrawal: `Withdrawal`,
        transfer: `Transfer`,
        'personal-bank': `Personal Bank`,
        business: `Business`,
        external: `External`,
        'property-a': `Property A`,
        'property-b': `Property B`,
        'property-c': `Property C`,
      },
    },
  },
  time: {
    'less-than-minute-past': `less than a minute ago`,
    'less-than-minute-future': `in less than a minute`,
  },
  destinations: {
    'empty-state': {
      'no-destinations': `No Destinations in this Project`,
    },
  },
  integrations: {
    'contact-admin-tooltip': `Ask a project admin to perform this action.`,
    destinations: {
      title: `Destinations`,
      'connect-destinations-image-alt': `Connect Destinations`,
      'add-destination': `Add Destination`,
      empty: {
        header: `No Destinations in this Project`,
        body: `Connecting to destinations will allow you to send your Mixpanel cohorts to the other apps you use at your company. Mixpanel supports 150+ popular destinations including CRMs like Salesforce and Ad platforms like Google Ads.`,
        'view-docs': `View Docs`,
      },
      'active-card': {
        'connected-label': `Connected`,
        'disconnected-label': `Disconnected`,
        'syncs-label': `Connected Syncs`,
        'status-label': `Status`,
        'type-cohort-sync': `Cohort Sync`,
        'type-pipeline': `Data Pipeline`,
        'type-notification-webhook': `Notification Webhook`,
        'type-real-time': `Real-time`,
        'realtime-status': {
          authenticated: `Active`,
          'auth-error': `Authentication Error`,
          disabled: `Disabled`,
        },
      },
      'platform-card': {
        'connected-destinations-label_one': `Connected Destination`,
        'connected-destinations-label': `Connected Destinations`,
      },
      'platform-list': {
        'empty-for-platform': `No connected destinations for this platform.`,
      },
      failed: {
        header: `Failed to load destinations`,
        body: `If this issue persists please [contact support](https://mixpanel.com/get-support)`,
      },
    },
    'destination-detail': {
      'not-found': `Not Found`,
      'tab-configuration': `Configuration`,
      'tab-syncs': `Syncs`,
      'add-cohort': `Add Cohort Sync`,
      pipeline: {
        delete: `Delete Pipeline`,
        'delete-modal': {
          title: `Delete Pipeline`,
          description: `Are you sure you want to delete the pipeline "{{name}}"? This is permanent and cannot be undone.`,
          cancel: `Cancel`,
          confirm: `Delete Pipeline`,
        },
        pause: `Pause`,
        'pause-tooltip': `Pause this pipeline.`,
        unpause: `Unpause`,
        'unpause-tooltip': `Resume this pipeline.`,
        'pause-error': `Failed to pause pipeline`,
        'unpause-error': `Failed to resume pipeline`,
        'pause-success': `Pipeline paused`,
        'unpause-success': `Pipeline resumed`,
        'delete-error': `Failed to delete pipeline`,
        'delete-success': `Pipeline deleted`,
        'status-active': `Active`,
        'status-paused': `Paused`,
        'view-error': `View Error`,
        'error-modal-title': `Error`,
        'configuration-title': `Pipeline Configuration`,
        'configuration-empty': `No properties.`,
        runs: {
          'col-start-time': `Start Time`,
          'col-end-time': `End Time`,
          'col-status': `Status`,
          'col-rows-exported': `Rows Exported`,
          'col-error': `Error`,
          'view-error': `View Error`,
          'error-modal-title': `Run Error`,
          'no-runs': `No runs yet`,
          'no-runs-body': `This pipeline has no run history.`,
          'fetch-error': {
            header: `Failed to load run history`,
            body: `If this issue persists, please [contact support](https://mixpanel.com/get-support).`,
          },
        },
      },
      failed: {
        header: `Failed to load destination`,
        body: `This destination may not exist or could not be loaded. If this issue persists, please [contact support](https://mixpanel.com/get-support).`,
      },
      configuration: {
        'connection-details-title': `Connection Details`,
        'platform-settings-title': `Configuration`,
        'empty-settings': `No platform-specific configuration.`,
        rows: {
          status: `Status`,
          'connection-type': `Connection Type`,
          'connected-on': `Connected On`,
          'connected-by': `Connected By`,
          'last-updated': `Last Updated`,
        },
        status: {
          connected: `Connected`,
          disconnected: `Disconnected`,
        },
        disconnect: `Disconnect`,
        'disconnect-tooltip': `Disconnect this destination`,
        'disconnect-tooltip-disabled': `This destination is already disconnected`,
        reauthenticate: `Re-Authenticate`,
        'reauthenticate-tooltip': `Re-authenticate this destination with new credentials`,
        'reauthenticate-tooltip-unsupported': `Re-authentication is not supported for this destination type`,
      },
      'disconnect-modal': {
        title: `Disconnect destination`,
        description: `Are you sure you want to disconnect "{{name}}"? Active syncs to this destination will stop.`,
        cancel: `Cancel`,
        confirm: `Disconnect`,
        'disconnect-success': `Destination disconnected`,
        'disconnect-error': `Failed to disconnect destination`,
      },
      'connect-oauth': {
        title: `Connect {{destination}}`,
        description: `You'll be redirected to {{destination}} to authorize Mixpanel.`,
        'sign-in-google': `Sign in with Google`,
        'continue-facebook': `Continue with Facebook`,
        cancel: `Cancel`,
      },
      'syncs-placeholder': `Syncs will be displayed here.`,
      syncs: {
        'cohort-name': `Cohort Name`,
        'search-placeholder': `Search cohort syncs`,
        frequency: `Frequency`,
        status: `Status`,
        'last-export-completed': `Last Export Completed`,
        'user-profiles-exported': `User Profiles Exported`,
        recurring: `Recurring`,
        'one-time': `One-time`,
        paused: `Paused`,
        complete: `Complete`,
        errored: `Errored`,
        actions: {
          pause: `Pause`,
          unpause: `Unpause`,
        },
        'pause-success': `Cohort sync paused`,
        'pause-error': `Failed to pause cohort sync`,
        'unpause-success': `Cohort sync resumed`,
        'unpause-error': `Failed to resume cohort sync`,
        'load-error': {
          header: `Failed to load syncs`,
          body: `Something went wrong while loading syncs. Refresh the page to try again.`,
        },
        'start-time': `Start time`,
        'user-profile-changes': `User Profile Changes`,
        succeeded: `Succeeded`,
        failed: `Failed`,
        pending: `Pending`,
        'total-users_one': `1 total user`,
        'total-users_other': `{{count, number}} total users`,
        'users-added_one': `1 user added`,
        'users-added_other': `{{count, number}} users added`,
        'users-removed_one': `1 user removed`,
        'users-removed_other': `{{count, number}} users removed`,
        'view-error': `View Error`,
        'error-modal-title': `Sync error`,
        'cohort-csv': `Cohort CSV`,
        'download-csv': `Export`,
        'csv-expiration': `Available until {{expirationTime}}`,
        'csv-expired': `Export expired on {{expirationTime}}`,
        'csv-run-failed': `Run failed. Please check the error for more information`,
        'csv-unavailable': `No export data available for this run`,
        'fetch-error': {
          header: `Failed to load sync history`,
          body: `Something went wrong while loading sync runs. Refresh the page to try again.`,
        },
        'not-found': {
          header: `Sync not found`,
          body: `This sync may have been deleted or no longer exists.`,
        },
        'cohort-size': `Cohort Size`,
        duration: `Duration`,
        'history-title': `{{cohortName}} Sync History`,
        empty: {
          header: `No cohort syncs for this destination`,
          body: `Get started by adding a cohort to export when you're ready`,
          'add-cohort-sync': `Add a Cohort Sync`,
          'image-alt': `Add a cohort sync`,
        },
      },
      'cohort-sync-modal': {
        title: `Create Sync to {{destination}}`,
        'results-count': `{{count}} results`,
        'col-cohort-name': `Cohort Name`,
        'col-count': `Count`,
        'col-created': `Created`,
        'col-last-edited': `Last Edited`,
        'col-description': `Description`,
        'cohort-label': `Cohort`,
        'users-banner': `{{exported}} of {{total}} users will be exported`,
        'docs-link': `Setting up the Mixpanel Cohort in {{destination}}`,
        'sync-type-label': `Sync Type`,
        'one-time-title': `One-time Export`,
        'one-time-description': `All users in the cohort will be updated one time.`,
        'one-time-quota': `Unlimited One-time syncs`,
        'recurring-title': `Recurring Sync`,
        'recurring-description': `Your {{destination}} cohort will be updated automatically to match users in your Mixpanel cohort. Update frequency varies by destination.`,
        'recurring-quota': `{{used}} of {{limit}} recurring syncs in use`,
        previous: `Previous`,
        'create-sync': `Create Sync`,
        creating: `Creating…`,
        'success-toast': `Created {{count}} cohort sync(s) to {{destination}}.`,
        empty: `No cohorts found.`,
        'cohorts-error': `Failed to load cohorts. Please refresh and try again.`,
        'user-counts-error': `Failed to load user counts.`,
        'limits-error': `Failed to load recurring sync limit.`,
        'duplicate-external-info-fallback': `This value is already in use by an existing export. Choose a different one.`,
        'unknown-error': `Unknown error`,
        'cohort-needs-attention': `{{name}} — needs attention`,
        'external-info': {
          'airship-label': `Airship Tag`,
          'airship-placeholder': `Tag to add to users in this cohort`,
          'marketo-label': `Marketo Custom Field Name`,
          'marketo-placeholder': `Custom field to update for users in this cohort`,
          'required-error': `{{cohortName}}: {{label}} is required`,
        },
      },
    },
    library: {
      header: `Destination Library`,
      'view-docs': `View Docs`,
      'request-destination': `Request Destination`,
      'search-placeholder': `Search`,
      'category-label': `Category`,
      'type-label': `Type`,
      'no-results': `No destinations found`,
      'no-results-description': `Try adjusting your search or filters`,
      'request-destination-modal': {
        title: `Request a Destination`,
        'destination-name-label': `Destination Name`,
        'destination-name-placeholder': `e.g., Braze, Intercom`,
        'destination-name-required': `Destination name is required`,
        'reason-label': `Reason`,
        'reason-placeholder': `Tell us why you need this destination...`,
        cancel: `Cancel`,
        submit: `Submit Request`,
        'success-toast': `Your destination request has been submitted.`,
      },
    },
    page: {
      'loading-destinations': `Loading destinations...`,
    },
    connector: {
      'pipline-configuration': `Pipeline Configuration`,
      'data-pipelines': `Data Pipelines`,
      'export-log': `Export Log`,
      'search-history': `Search history`,
      'sync-status': {
        pending: `Pending`,
        running: `Running`,
        failed: `Failed`,
        succeeded: `Succeeded`,
        canceled: `Canceled`,
        'timed-out': `TimedOut`,
        retried: `Retried`,
        paused: `Paused`,
      },
      'job-status': {
        active: `Active`,
        paused: `Paused`,
        stopped: `Stopped`,
        success: `Success`,
        failed: `Failed`,
        running: `Running`,
        scheduled: `Scheduled`,
        cancelled: `Cancelled`,
      },
      column: {
        'start-time': `Timestamp (UTC)`,
        'run-id': `Run ID`,
        status: `Status`,
        'num-rows-exported': `Rows Exported`,
        'num-rows-exported-tooltip': `Shows total events processed per batch (spanning multiple days) instead of events per day or hour`,
      },
      property: {
        'account-name': `Account Name`,
        'bq-dataset': `Dataset`,
        'bq-gcp-project-id': `GCP Project Id`,
        'bq-region': `Region`,
        bucket: `Bucket`,
        'container-name': `Container Name`,
        'created-at': `Created`,
        'data-format': `Data Format`,
        'data-source': `Data Type`,
        database: `Database`,
        events: `Events`,
        frequency: `Frequency`,
        'gcs-bucket': `Bucket`,
        'gcs-prefix': `Prefix`,
        'gcs-region': `Region`,
        'glue-database': `Glue Database`,
        'glue-role': `Glue Role`,
        'glue-table-prefix': `Glue Table Prefix`,
        'path-prefix': `Path Prefix`,
        prefix: `Prefix`,
        region: `Region`,
        's3-bucket': `S3 Bucket`,
        's3-encryption': `S3 Encryption`,
        's3-prefix': `S3 Bucket Prefix`,
        's3-region': `S3 Region`,
        's3-role': `S3 Role`,
        's3-kms-key-id': `S3 KMS Key Id`,
        schema: `Schema`,
        'redshift-workgroup-arn': `Redshift Workgroup ARN`,
        'redshift-region': `Redshift Region`,
        'redshift-database': `Redshift Database`,
        'redshift-schema-name': `Redshift Schema Name`,
        'redshift-table': `Redshift Table`,
        role: `Role`,
        'snowflake-share-with': `Share With`,
        'storage-account': `Storage Account`,
        'storage-integration': `Storage Integration`,
        sync: `Sync`,
        trial: `Trial Pipeline`,
        'use-glue': `Use Glue`,
        user: `User`,
        warehouse: `Warehouse`,
        where: `Where`,
      },
    },
    'connector-item': {
      salesforce: {
        description: `Import Salesforce data into Mixpanel, and send key sales-related Mixpanel events to Salesforce.`,
        'not-integrated-message-incoming': `Integrate Salesforce to analyze your customer relationship management information with the power of Mixpanel.`,
      },
      zendesk: {
        description: `Import Zendesk ticket data into Mixpanel, and export user actions and properties from Mixpanel into your ticket and chat windows.`,
        'not-integrated-message-incoming': `Integrate Zendesk to analyze your customers and how they behave when they need help using your product.`,
        'not-integrated-message-outgoing': `The Mixpanel App for Zendesk is available on the Zendesk Marketplace. Support Agents can view Mixpanel events and People Properties in real time for ticket and chat requestors, leading to faster responses and better customer experiences.`,
      },
      urbanairship: {
        description: `Export Mixpanel Cohorts to better engage your users. Once you have connected go to Mixpanel cohorts, create a cohort, and click on the export button to export the cohort.`,
        'not-integrated-message-outgoing': `The Airship integration allows user to export cohorts to Airship through audiences and tags. Both one-time static exports and recurring exports are available. Airship SDK is required.`,
      },
      marketo: {
        description: `Export Mixpanel Cohorts to better engage your users. Once you have connected go to Mixpanel cohorts, create a cohort, and click on the export button to export the cohort.`,
        'not-integrated-message-outgoing': `The Marketo integration allows user to export cohorts to Marketo through updates to custom objects. Both one-time static exports and recurring exports are available. Some additional setup in Marketo is required.`,
      },
      braze: {
        description: `Export Mixpanel Cohorts to better engage your users. Once you have connected go to Mixpanel cohorts, create a cohort, and click on the export button to export the cohort.`,
        'not-integrated-message-outgoing': `The Braze integration allows you to export cohorts to Braze as custom Segments. Both one-time static exports and recurring exports are available. The Braze SDK is required.`,
      },
      facebookads: {
        description: `Export Mixpanel Cohorts to create audiences on Facebook Ads. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to Facebook Ads" in the dropdown menu for that Cohort.`,
        'not-integrated-message-outgoing': `Export Mixpanel Cohorts to create audiences on Facebook Ads`,
      },
      googleads: {
        description: `Export Mixpanel Cohorts to create audiences on Google Adwords. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to Google Adwords" in the dropdown menu for that Cohort.`,
        'not-integrated-message-outgoing': `Export Mixpanel Cohorts to create audiences on Google Adwords`,
      },
      iterable: {
        description: `Export Cohorts to Iterable as Lists. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to Iterable" in the dropdown menu for that Cohort.`,
      },
      segment: {
        description: `Connect Mixpanel to Segment with one click.`,
      },
      testpartnerwebhook: {
        description: `Test Partner Webhook Integration for cohort exports`,
      },
      moengagewebhook: {
        description: `Export Mixpanel Cohorts to create audiences on MoEngage. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to MoEngage" in the dropdown menu for that Cohort.`,
      },
      insiderwebhook: {
        description: `Export Mixpanel Cohorts to create audiences on Insider. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to Insider" in the dropdown menu for that Cohort.`,
      },
      chameleonwebhook: {
        description: `Export Mixpanel Cohorts to create audiences on Chameleon. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to Chameleon" in the dropdown menu for that Cohort.`,
      },
      appcueswebhook: {
        description: `Export Mixpanel Cohorts to create audiences on Appcues. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to Appcues" in the dropdown menu for that Cohort.`,
      },
      leanplumwebhook: {
        description: `Export Mixpanel Cohorts to create audiences on Leanplum. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to Leanplum" in the dropdown menu for that Cohort.`,
      },
      onesignalwebhook: {
        description: `Export Mixpanel Cohorts to create audiences on OneSignal. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to OneSignal" in the dropdown menu for that Cohort.`,
      },
      kameleoonwebhook: {
        description: `Export Mixpanel Cohorts to create audiences on Kameleoon. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to Kameleoon" in the dropdown menu for that Cohort.`,
      },
      mailchimpwebhook: {
        description: `Export Mixpanel Cohorts to create audiences on Mailchimp. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to Mailchimp" in the dropdown menu for that Cohort.`,
      },
      taplyticswebhook: {
        description: `Export Mixpanel Cohorts to create audiences on Taplytics. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to Taplytics" in the dropdown menu for that Cohort.`,
      },
      vwowebhook: {
        description: `Export Mixpanel Cohorts to create audiences on VWO. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to VWO" in the dropdown menu for that Cohort.`,
      },
      webengagewebhook: {
        description: `Export Mixpanel Cohorts to create audiences on WebEngage. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to WebEngage" in the dropdown menu for that Cohort.`,
      },
      xtremepushwebhook: {
        description: `Export Mixpanel Cohorts to create custom Segments on Xtremepush. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to Xtremepush" in the dropdown menu for that Cohort.`,
      },
      apptimizewebhook: {
        description: `Export Mixpanel Cohorts to create custom Segments on Apptimize. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to Apptimize" in the dropdown menu for that Cohort.`,
      },
      clevertapwebhook: {
        description: `Export Mixpanel Cohorts to create custom Segments on CleverTap. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to CleverTap" in the dropdown menu for that Cohort.`,
      },
      customwebhook: {
        description: `Export Mixpanel Cohorts to a custom webhook integration. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to Custom webhook" in the dropdown menu for that Cohort.`,
      },
      segmentwebhook: {
        description: `Export Mixpanel Cohorts to create custom user traits in Segment. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to Segment" in the dropdown menu for that Cohort.`,
      },
      salesforcemc: {
        description: `Export Mixpanel Cohorts to data extension into Salesforce Marketing Cloud. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to Salesforce Marketing Cloud" in the dropdown menu for that Cohort.`,
      },
      launchdarklywebhook: {
        description: `Export Mixpanel Cohorts to a bigsegment in LaunchDarkly. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to LaunchDarkly" in the dropdown menu for that Cohort.`,
      },
      blitzllamawebhook: {
        description: `Export Mixpanel Cohorts to Blitzllama. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to Blitzllama" in the dropdown menu for that Cohort.`,
      },
      productboardwebhook: {
        description: `Export Mixpanel Cohorts to Productboard. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to Productboard" in the dropdown menu for that Cohort.`,
      },
      pushwooshwebhook: {
        description: `Export Mixpanel Cohorts to Pushwoosh. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to Pushwoosh" in the dropdown menu for that Cohort.`,
      },
      abtastywebhook: {
        description: `Export Mixpanel Cohorts to AB Tasty. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to AB Tasty" in the dropdown menu for that Cohort.`,
      },
      flagshipwebhook: {
        description: `Export Mixpanel Cohorts to Flagship. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to Flagship" in the dropdown menu for that Cohort.`,
      },
      mparticlewebhook: {
        description: `Export Mixpanel Cohorts to mParticle. Once you have connected the Integration, go to the Mixpanel Cohorts report, create or select a cohort, and click on "Export to mParticle" in the dropdown menu for that Cohort.`,
      },
    },
    'create-pipeline': {
      'pipeline-created': `Pipeline created successfully - please refresh the page to view the pipeline`,
      'pipeline-failed': `Unable to create pipeline: {{error}}`,
      'create-pipeline': `Create {{connectorType}} Pipeline`,
      'help-info': `Be sure to <learnMoreLink>set this up</learnMoreLink> before creating your connection. <learnMoreLink>Learn More</learnMoreLink>`,
      'json-pipeline-warning': `You are creating a JSON pipeline. If you wish to manage schematized pipelines, click the link below to learn more.`,
      'data-source': {
        events: `events`,
        identity: `identity`,
        people: `people`,
        'data-source': `Data Source`,
      },
      's3-form': {
        's3-bucket-info': `The S3 bucket to export the Mixpanel data to.`,
        bucket: `Bucket`,
        's3-bucket': `S3 Bucket`,
        'enter-s3-bucket': `Enter S3 bucket`,
        's3-role-info': `AWS Role the writer should assume when writing to S3 e.g. arn:aws:iam:::role/example-s3-role`,
        role: `Role`,
        's3-role': `S3 Role`,
        'enter-s3-role': `Enter role`,
        's3-region-info': `The region where the S3 bucket is located`,
        region: `Region`,
        's3-region': `S3 Region`,
        's3-encryption-info': `At rest encryption used by the S3 bucket.`,
        encryption: `Encryption`,
        's3-encryption': `S3 Encryption`,
        's3-kms-key-id-info': `If encryption is set to KMS, this field can specify the custom key id you desire to use.`,
        'kms-key-id': `KMS Key ID`,
        's3-kms-key-id': `S3 KMS Key ID`,
        'enter-s3-kms-key': `Enter KMS key id`,
      },
      'trial-pipeline': {
        trial: `Trial`,
        'trial-info': `A trial pipeline will be created. Please purchase the data warehouse export package to use this feature.`,
      },
      advanced: {
        advanced: `$t(common.advanced)`,
        sync: `Sync`,
        'sync-info': `Selecting this updates exported data with any changes that occur in your Mixpanel dataset. These changes include deletions, late data, and imports that fall into your export window.`,
        'backfill-info': `Select how far back you would like to backfill. Note: this will slow down the initial export.`,
        backfill: `Backfill Mode`,
        frequency: `Frequency`,
        daily: `daily`,
        hourly: `hourly`,
      },
      azure: {
        'storage-account-info': `Blob Storage Account where the data will be exported.`,
        'storage-account': `Storage Account`,
        'enter-storage-account': `Enter storage account`,
        'blob-container-info': `The Blob container within the account where data will be exported.`,
        'container-name': `Container Name`,
        'enter-container-name': `Enter container name`,
        'client-id-info': `Client id from the service principal credentials.`,
        'client-id': `Client Id`,
        'enter-client-id': `Enter client id`,
        'client-secret-info': `Client secret from the service principal credentials.`,
        'client-secret': `Client Secret`,
        'enter-client-secret': `Enter client secret`,
        'tenant-id-info': `Tenant id from the service principal credentials. This is specific to the active directory instance where the service principal resides.`,
        'tenant-id': `Tenant Id`,
        'enter-tenant-id': `Enter tenant id`,
      },
      bigquery: {
        'gcp-project-info': `The GCP project id where BigQuery dataset is present.`,
        'gcp-project': `Project`,
        'enter-gcp-project': `Enter GCP project`,
        'dataset-info': `Dataset created on the GCP project to which Mixpanel needs to export data.`,
        dataset: `Dataset`,
        'enter-dataset': `Enter dataset name`,
        region: `Region`,
        'region-info': `The region used for BigQuery.`,
      },
      gcs: {
        'gcs-bucket-info': `The GCS bucket to export the Mixpanel data to.`,
        'gcs-bucket': `Bucket`,
        'enter-gcs-bucket': `Enter GCS bucket`,
        'gcs-prefix-info': `The GCS path prefix of the bucket.`,
        'gcs-prefix': `Prefix`,
        'enter-gcs-prefix': `Enter GCS path prefix`,
        'gcs-region-info': `The GCS region for the bucket.`,
        region: `Region`,
      },
      redshift: {
        configuration: `Configuration`,
        'access-role': `Access Role`,
        'enter-access-role': `Enter access role`,
        database: `Database`,
        'enter-database-name': `Enter database name`,
        workgroup: `Workgroup`,
        'enter-workgroup-name': `Enter workgroup name`,
        region: `Region`,
        'glue-configuration': `Glue Configuration`,
        'glue-database': `Database`,
        'enter-glue-database': `Enter Glue database`,
        'glue-region': `Region`,
        'glue-role': `Role`,
        'enter-glue-role': `Enter Glue role`,
        's3-configuration': `S3 Configuration`,
      },
      snowflake: {
        password: `Password`,
        'key-pair': `Key Pair`,
        'authentication-type': `Authentication Type`,
        'account-name': `Account Name`,
        'enter-account-name': `Enter account name`,
        warehouse: `Warehouse`,
        'enter-warehouse': `Enter warehouse`,
        'storage-integration': `Storage Integration`,
        'enter-storage-integration': `Enter storage integration`,
        user: `User`,
        'enter-user': `Enter user`,
        role: `Role`,
        'enter-role': `Enter role`,
        database: `Database`,
        'enter-database': `Enter database`,
        schema: `Schema`,
        'enter-schema': `Enter schema`,
      },
      databricks: {
        'server-hostname': `Server Hostname`,
        'enter-server-hostname': `Enter server hostname`,
        'http-path': `HTTP Path`,
        'enter-http-path': `Enter HTTP path`,
        catalog: `Catalog`,
        'enter-catalog': `Enter catalog name`,
        schema: `Schema`,
        'enter-schema': `Enter schema name`,
        'service-principal-id': `Service Principal ID`,
        'enter-service-principal-id': `Enter service principal ID`,
        'service-principal-secret': `Service Principal Secret`,
        'enter-service-principal-secret': `Enter service principal secret`,
      },
    },
    'pipeline-item': {
      'sync-bq-description': `Sync your Mixpanel data on an hourly or daily frequency to your Google BigQuery data warehouse.`,
      'sync-gcs-description': `Sync your Mixpanel data on an hourly or daily frequency to your Google Cloud Storage buckets.`,
      'sync-redshift-description': `Sync your Mixpanel data on an hourly or daily frequency to your AWS Redshift Spectrum data warehouse.`,
      'sync-s3-description': `Sync your Mixpanel data on an hourly or daily frequency to your AWS S3 buckets`,
      'sync-snowflake-description': `Sync your Mixpanel data on an hourly or daily frequency to your Snowflake data warehouse.`,
      'sync-azure-description': `Sync your Mixpanel data on an hourly or daily frequency to your Microsoft Azure Blob Storage buckets`,
      'sync-databricks-description': `Sync your Mixpanel data on an hourly or daily frequency to your Databricks data warehouse.`,
      connected: `Connected`,
      'num-pipelines_one': `1 Pipeline`,
      'num-pipelines_other': `{{count}} Pipelines`,
    },
    'pipeline-status': {
      created: `Created`,
      'delete-pipeline': `Delete Pipeline`,
      'confirm-delete': `Are you sure you want to delete this pipeline? \n \n This will permanently remove the pipeline configuration from Mixpanel, but will not delete any data already exported to your warehouse/storage. If you only want to temporarily stop the pipeline, pause it instead`,
      'pipeline-configuration': `{{connectorType}} Pipeline Configuration`,
      'job-name': `Name: {{jobName}}`,
      'view-error': `View Error`,
      'view-developer-doc': `View Developer Documentation`,
      'create-pipeline': `+ Create Pipeline`,
      'pipeline-name': `Pipeline Name`,
      'data-type': `Data Type`,
      'sync-enabled': `Sync Enabled`,
      trial: `Trial?`,
      status: `Status`,
      'search-pipelines': `Search Pipelines`,
      'no-pipelines-message': `No Export Pipelines for this offering are currently running. You can create one by using our Data Pipelines API.`,
      'job-failed-error': `This job failed due to the following error:`,
      'error-detail': `Error Detail`,
      'unpause-pipeline': `Unpause Pipeline`,
      'pause-pipeline': `Pause Pipeline`,
      'view-configuration': `View Configuration`,
      'show-header_one': `Show 1 value`,
      'show-header_other': `Show {{count}} values`,
      'hide-header_one': `Hide 1 value`,
      'hide-header_other': `Hide {{count}} values`,
      'status-active': `Active`,
      'status-stopped': `Stopped`,
      'status-paused': `Paused`,
    },
    'integration-status': {
      name: `Name`,
      'syncing-type': `Syncing Type`,
      recurring: `Recurring`,
      'one-time': `One-time`,
      'last-exported-at': `Last Export Completed`,
      'never-run': `Never Run`,
      'not-run-yet': `Not Yet Run`,
      'num-exported-info': `The number of qualifying user profiles in the Cohort at the time of the last export. This may differ from the current size`,
      users: `{{numUsers}} Users`,
      'profiles-exported': `User Profiles Exported`,
      status: `Status`,
      'search-cohorts': `Search cohorts`,
      'not-synced-info': `No cohorts have been synced yet. You can go to the Cohorts page to add some.`,
      'view-in-explore': `View in Explore`,
      'unpause-export': `Unpause Recurring Export`,
      'limit-reach-info': `You've reached the limit of active, recurring cohort syncs. Pause some recurring syncs to add more.`,
      'pause-export': `Pause Recurring Export`,
      'dynamic-exports-info': `Only dynamic exports can be unpaused`,
      'contact-admin': `Please contact your project admin to update this integration`,
      'not-active': `Not Active`,
      'connector-disconnected': `Connector Disconnected`,
      'connected-paused': `Connector Paused`,
      active: `Active`,
      error: `Error`,
    },
    'integration-item': {
      'contact-admin': `Please contact your project admin to update this integration`,
      'add-connection': `+ Add Connection`,
      disconnected: `Disconnected`,
      connected: `Connected`,
      unknown: `Unknown`,
      'connection-state-unknown': `The state of this Connection is unknown.\nPlease contact support to resolve this.`,
      'exported-by': `Exported by:`,
      'view-dev-doc': `View Developer Documentation`,
      'view-settings': `View Settings`,
      'sign-in-google': `Sign in with Google`,
      'continue-facebook': `Continue with Facebook`,
      connect: `Connect`,
      'go-to-integration': `Go to {{integrationName}}`,
      disconnect: `Disconnect`,
      reauthorize: `Re-authorize`,
      reconnect: `Re-connect`,
      'connector-details': `Connector Details`,
      'connector-name': `Connector Name`,
      'active-connection_one': `1 active connection`,
      'active-connection_other': `{{count}} active connections`,
      'total-connection_one': `1 total connection`,
      'total-connection_other': `{{count}} total connections`,
      'active-cohort_one': `1 Active, Recurring Cohort`,
      'active-cohort_other': `{{count}} Active, Recurring Cohorts`,
      'not-synced': `not yet synced`,
      'last-synced': `last synced {{relativeDate}}`,
    },
    'integration-detail': {
      integrations: `Integrations`,
      'expired-time': `Expired: {{expirationTime}}`,
      'view-error': `View Error`,
      'total-user_one': `1 total user`,
      'total-user_other': `{{count, number}} total users`,
      'exported-user_one': `1 user added`,
      'exported-user_other': `{{count, number}} users added`,
      'removed-user_one': `1 user removed`,
      'removed-user_other': `{{count, number}} users removed`,
      export: `Export`,
      'expiration-time': `Expiration: {{expirationTime}}`,
      'view-in-explore': `View Cohort in Explore`,
      'connected-by': `Connected by {{user}} on {{integrationDate}}`,
      'export-log': `Export Log`,
      'timestamp-utc': `Timestamp (UTC)`,
      'run-id': `Run ID`,
      'cohort-size-info': `The number of qualifying user profiles in the Cohort at the time of the export.`,
      summary: `Summary`,
      'cohort-csv': `Cohort CSV`,
      status: `Status`,
      'search-logs': `Search logs`,
      'run-error': `Run {{runId}} failed due to the following error:`,
      'error-detail': `Error Detail`,
    },
    'debugging-detail': {
      'cohort-export-error': `An error occurred internally during this Cohort's Export Run.`,
      'staff-only': `[Staff Only]`,
      skipped: `Skipped`,
      'diff-progress': `Diff Progress: {{errorMessage}}`,
      'previous-cohort-file': `Previous Cohort File: {{prevCohortFile}}`,
      'current-cohort-file': `Current Cohort File: {{currentCohortFile}}`,
      'snapshot-id': `Snapshot ID: {{snapshotId}}`,
      timestamp: `Timestamp: {{timestamp}}`,
      left: `Left`,
      headers: `Headers`,
      body: `Body`,
      latency: `Latency: {{numSeconds}} seconds`,
    },
    webhook: `Webhook`,
    'connect-modal': {
      'user-id-property': `User ID Property`,
      'prop-to-partner-msg': `If supplied, we will pass the selected property to the partner to match users.`,
      'data-import-key': `Data Import Key`,
      'data-import-key-placeholder': `Your data import key`,
      'braze-instance': `Braze Instance`,
      'braze-instance-url': `Your instance URL (e.g. https://rest.iad-01.braze.com)`,
      'access-token': `Access Token`,
      'airship-data-center': `Airship Data Center`,
      'client-id': `Client ID`,
      'client-id-placeholder': `Your client ID`,
      'client-secret': `Client Secret`,
      'client-secret-placeholder': `Your client secret`,
      'rest-url': `Rest URL`,
      'rest-url-placeholder': `Your rest URL`,
      'zendesk-subdomain': `Zendesk subdomain`,
      'zendesk-subdomain-url': `Your subdomain (e.g. example.zendesk.com)`,
      'iterable-api-key': `Iterable API Key`,
      'iterable-api-key-placeholder': `Your Iterable API Key`,
      'iterable-data-center': `Iterable Datacenter`,
      'google-customer-id': `Google CustomerID`,
      'google-mobile-id': `Google Mobile ID`,
      'customer-id-msg': `CustomerIDs are usually in the form {{format}}.`,
      'data-key': `Data Key`,
      'project-id': `Project ID`,
      passcode: `Passcode`,
      'clevertap-datacenter': `CleverTap Datacenter`,
      'properties-to-export': `Properties To Export`,
      'props-or-default-msg': `If supplied, we will export the following properties to your destination. Otherwise, we export a default set of properties, which can be found in the documentation.`,
      username: `Username`,
      password: `Password`,
      'mo-engage-datacenter': `MoEngage Datacenter`,
      'api-key': `API Key`,
      'vwo-data-question': `Where is your VWO’s visitor data stored?`,
      'apptimize-token': `Apptimize Token`,
      'apptimize-datacenter': `Apptimize Datacenter`,
      'app-id': `APP Id`,
      url: `URL`,
      'http-auth-optional': `HTTP BasicAuth (Optional)`,
      'username-missing-msg': `If you specify a password, you must also specify a username`,
      'auth-webhook-msg': `If supplied, Mixpanel will provide these Basic Auth credentials to authenticate itself to your webhook server.`,
      'password-missing-msg': `If you specify a username, you must also specify a password`,
      'data-extension-key': `Data Extension Key`,
      'account-id': `Account ID`,
      subdomain: `Subdomain`,
      'launch-darkly-project-key': `LaunchDarkly Project Key`,
      'launch-darkly-env-key': `LaunchDarkly Environment Key`,
      'connector-name': `Connector Name`,
      'optional-title': `{{title}} (Optional)`,
      'required-field': `This field is required`,
      'set-up-integration': `Set up {{integrationName}}`,
      'settings-current-project': `These settings apply to your current project`,
      'connect-to-integration': `Connect to {{integrationName}}`,
      'connect-description': `Create a connection from Mixpanel to {{integrationName}}.`,
      'select-datacenter': `Select a Datacenter to export to`,
      'integration-help-question': `Where do I find this info?`,
      'segment-region': `Segment Region`,
      'connect-error': `Failed to connect to {{destinationName}}`,
      'destination-type': {
        heading: `Destination type`,
        cohorts: `Cohort Sync`,
        'cohorts-sublabel': `Export Mixpanel Cohorts to a custom webhook integration.`,
        realtime: `Real Time Events`,
        'realtime-sublabel': `Send real time events to a custom webhook integration.`,
      },
      'set-up-heading': `Set up`,
    },
    'fb-login': {
      'not-accepted-tos': `You have not yet accepted the Terms of Service for this Ad Account. Please accept the Terms and click "Continue" to try again.`,
      'failed-fetch-tos': `Failed to fetch the accepted Terms of Service associated with this Ad Account.`,
      accept: `I Accept`,
      'select-account': `Select Your Facebook Ad Account`,
      'facebook-documentation-msg': `To find your Ad Account ID, refer to Facebook's documentation here:`,
      'find-facebook-credentials-msg': `Find Facebook ad account and ad ID numbers`,
      'name-connector': `Name Your Connector`,
      'connector-name': `Connector Name`,
      'advertiser-id': `Advertiser ID`,
      'custom-audience-terms': `Custom Audience Terms`,
      'accept-audience-tos-msg': `You'll need to agree to Facebook's Custom Audience Terms of Service before you can create or edit an audience.`,
      'accept-audience-tos-link': `To accept, go to [Custom Audiences Terms](https://www.facebook.com/ads/manage/customaudiences/tos?act={{selectedAccountId}})`,
    },
    app: {
      'integration-auth-title': `{{integrationName}} Authorization`,
      'facebook-auth-tos': `By accepting this agreement, you are authorizing and instructing Mixpanel to send data to Facebook, Inc. on your behalf. You are representing and warranting to Mixpanel that you have sufficient rights to authorize such transmission, and have complied with all laws and regulations applicable to such transmission. You are also agreeing that your use of this integration is subject to your agreement with Mixpanel, and Facebook's [platform](https://www.facebook.com/legal/terms) and [commercial](https://www.facebook.com/legal/commercial_terms) terms.`,
      'iterable-auth-tos': `By accepting this agreement, you are authorizing and instructing Mixpanel to send data to Iterable, Inc. on your behalf. You are representing and warranting to Mixpanel that you have sufficient rights to authorize such transmission, and have complied with all laws and regulations applicable to such transmission. You are also agreeing that your use of this integration is subject to your agreement with Mixpanel.`,
      'google-integration-explanation': `Instructions for finding your Google CustomerID: [Find your customer ID](https://support.google.com/google-ads/answer/1704344)`,
      'zendesk-integration-description': `Export user action and properties from Mixpanel into your ticket and chat windows.`,
      'zendesk-not-integration-msg': `The Mixpanel App for Zendesk is available on the Zendesk Marketplace. Support Agents can view Mixpanel events and People Properties in real time for ticket and chat requestors, leading to faster responses and better customer experiences.`,
      'integration-disconnected': `{{integrationName}} disconnected successfully`,
      'integration-connected': `{{integrationName}} connected successfully`,
      'export-removed': `Successfully removed recurring export`,
      'export-removal-failed': `Failed to remove recurring export`,
      'export-resumed': `Successfully resumed recurring export`,
      'export-resume-failed': `Failed to resume recurring export`,
      'pipeline-deleted': `{{pipelineName}} deleted successfully - please refresh the page`,
      'pipeline-delete-failed': `Unable to delete {{pipelineName}}: {{errorMessage}}`,
      'pipeline-unpaused': `{{pipelineName}} unpaused successfully`,
      'pipeline-unpause-failed': `Unable to unpause {{pipelineName}}`,
      'pipeline-paused': `{{pipelineName}} paused successfully`,
      'pipeline-pause-failed': `Unable to pause {{pipelineName}}`,
      'issue-contact-support': `If this issue persists please [contact support](https://mixpanel.com/get-support).`,
      'rollup-projects-not-available': `Roll-up projects do not have access to Destinations or Data Pipelines at this time.`,
      'data-pipelines': `Data Pipelines`,
      'setup-free-trial': `To access this feature, setup a free trial or purchase the Data Pipelines package.`,
      'add-pipelines-package': `Add the Data Pipelines Package`,
      'num-recurring-exports': `Cohort Syncs: {{numRecurringExports}} of {{exportLimit}} recurring syncs`,
      'external-integrations': `Externally managed destinations`,
      disconnect: `Disconnect`,
      'disconnect-confirm': `Are you sure you want to disconnect? Disconnecting will un-authenticate this integration.`,
      'integration-load-failed': `Destinations could not be loaded.`,
      'integration-disconnect-failed': `Your integration could not be disconnected.`,
      'connecters-load-failed': `Connectors could not be loaded.`,
      'pipeline-history-load-failed': `The history of the pipeline could not be loaded.`,
      'run-error-load-failed': `The error information for this specific Run could not be loaded.`,
      'webhook-section-header': `Notification Webhook`,
      'webhook-title': `Notification Webhook`,
      'webhook-description': `Send outgoing payloads (such as alerts) to third-party platforms.`,
      'webhook-load-failed': `Failed to load webhooks.`,
      'webhook-test-sent': `Test webhook sent successfully.`,
      'webhook-test-failed': `Failed to send test webhook.`,
      'webhook-created': `Webhook created successfully.`,
      'webhook-updated': `Webhook updated successfully.`,
      'webhook-save-failed': `Failed to save webhook.`,
      'webhook-disconnected': `Webhook deleted successfully.`,
      'webhook-disconnect-failed': `Failed to delete webhook.`,
    },
    'webhook-modal': {
      'title-create': `Create Webhook`,
      'title-edit': `Edit Webhook`,
      'name-label': `Name`,
      'name-placeholder': `Name your webhook`,
      'url-label': `URL`,
      'username-label': `Username`,
      'username-placeholder': `Enter username`,
      'password-label': `Password`,
      'password-placeholder': `Enter password`,
      'test-connection': `Test connection`,
      'edit-hidden': `(hidden for security reasons)`,
      submit: `Submit`,
      'name-required': `Name is required`,
      'url-required': `URL is required`,
      'url-invalid': `Please provide a valid URL`,
    },
    'webhook-table': {
      name: `Name`,
      url: `URL`,
      created: `Created`,
      status: `Status`,
      enabled: `Valid`,
      disabled: `Invalid`,
      'delete-confirm-title': `Delete Webhook`,
      'delete-confirm-description': `Are you sure you want to delete this webhook? This action cannot be undone.`,
    },
    'webhook-status': {
      'add-webhook': `+ Add Webhook`,
    },
    'webhook-item': {
      'num-webhooks_one': `1 total connection`,
      'num-webhooks_other': `{{count}} total connections`,
    },
  },
  em: {
    cohorts: {
      view: `View`,
      'go-to-lexicon-banner': `Cohorts have moved to Lexicon. [See Cohorts]({{link}}){:target="_parent"}`,
      'token-expired': `Your access token has expired. Try disconnecting and reconnecting this \nIntegration from the Integrations page, then re-export.`,
      'never-run': `Never Run`,
      'not-yet-run': `Not Yet Run`,
      created: `Created`,
      success: `Success`,
      disconnect: `Disconnect`,
      'recurring-sync': `Recurring sync`,
      'one-time-export': `One-time export`,
      'created-by': `Created by {{creator}}`,
      'already-synced': `This cohort is already synced`,
      'recurring-limit-reached': `{{numRecurringExports}} of {{limit}} recurring syncs reached`,
      'field-required': `This field is required`,
      'marketo-field-error': `Marketo custom field must start with a letter and use only letters, numbers, and underscores`,
      'start-new-export': `Start new export`,
      'begin-sync': `Begin Sync`,
      account: `Account`,
      'select-account': `Select an Account`,
      'account-required': `An account is required`,
      'select-data-view': `Select a Data View`,
      'data-view-required': `A Data View is required`,
      'exported-users-count': `{{validUserCount}} of {{distinctIdCount}} users will be exported`,
      'export-type': `Export type`,
      'manage-syncs': `Manage your cohort syncs`,
      'last-export': `Last export`,
      creator: `Creator`,
      status: `Status`,
      type: `Type`,
      'export-to-integration': `Export to {{exportIntegrationName}}`,
      default: `Default`,
      'active-integrations': `All Active Integrations`,
      count: `Count`,
      'view-profile-types': `View {{profileTypes}}`,
      'view-replays': `View Replays`,
      'visualize-trend': `Visualize Trend`,
      'refresh-count': `Refresh Count`,
      'view-dependency': `View dependency`,
      'cannot-delete': `Cannot be deleted. Click to see details`,
      'export-to': `Export to...`,
      export: `Export`,
      'see-status': `See status`,
      'unable-to-connect-integration': `unable to connect {{integrationDisplayName}}`,
      'disconnected-dynamic-export': `Successfully disconnected dynamic export from {{integrationDisplayName}}`,
      'unable-to-disconnect-integration': `unable to disconnect {{integrationDisplayName}}`,
      'count-updated': `Cohort count updated`,
      'count-update-failed': `Failed to update cohort count`,
      'cohort-not-restored': `Your cohort could not be restored`,
      'cohort-deleted': `Cohort successfully deleted`,
      'cohort-delete-failed': `Failed to delete cohort`,
      'delete-referenced-cohort_other': `{{count}} cohorts will not be deleted because they are being referenced.`,
      'delete-referenced-cohort_one': `1 cohort will not be deleted because it is being referenced.`,
      'deletable-cohort_one': `{{count}} other cohort will be deleted.`,
      'deletable-cohort_other': `{{count}} other cohorts will be deleted.`,
      'delete-entity-type': `Delete {{entityTypeName}}`,
      'selected-referenced-delete_other': `Selected cohorts can not be deleted because they are being referenced.`,
      'selected-referenced-delete_one': `Selected cohort can not be deleted because it is referenced.`,
      'restore-deleted-failed_other': `Unable to restore deleted Cohorts`,
      'restore-deleted-failed_one': `Unable to restore deleted Cohort`,
      'cohorts-deleted': `Cohorts successfully deleted`,
      'cohorts-delete-failed': `Failed to delete selected cohorts`,
      'cohort-duplicated': `Cohort was duplicated`,
      'cohort-created': `Cohort was created`,
      'cohort-updated': `Cohort successfully updated`,
      'cohort-create-failed': `Failed to create cohort`,
      'cohort-create-failed-explanation': `Failed to create cohort: {{message}}`,
      'cohort-update-failed': `Failed to update cohort`,
      'cohorts-load-failed': `Failed to load cohorts`,
      'save-and-segment-groups': `Save and segment groups of users on behavior in seconds`,
      'save-segments': `Save different user segments by grouping them on actions they did or didn’t take. Save sophisticated cohort criteria, inspect profiles of users that qualify to find important events, and break down any Mixpanel report by any cohort.`,
      'save-cohorts-plan': `Saved Cohorts are available on Growth & Enterprise. When you upgrade to a Growth plan, you also get:`,
      'unlimited-saved': `Unlimited saved reports & cohorts`,
      'data-modeling': `Data modeling`,
      'groups-pipelines-addons': `Group Analytics & Data Pipelines add-ons, & more...`,
      'cannot-delete-cohort': `Cannot delete cohort`,
      'remove-references': `To delete this cohort, first remove it from the following items that reference it.`,
      'go-to-integrations': `Go to integrations`,
      'no-connected-integrations': `There are no connected integrations`,
      'set-up-integration': `Please navigate to your integrations page to set up and connect an integration.`,
      'disconnect-dynamic': `Disconnect dynamic export`,
      'confirm-stop-dynamic': `Are you sure you want to stop this dynamic export? By disconnecting, this cohort will no longer be dynamically updated in {{integrationDisplayName}}.`,
      integration: {
        'cohort-export-begun': `Your cohort export to {{integrationName}} has begun.`,
        'setting-up-integration': `Setting up the {{integrationName}} integration`,
        'only-indentified': `Only identified user profiles will be exported`,
        'all-exported': `All user profiles will be exported`,
        braze: {
          'setting-up-in': `Setting up the Mixpanel Cohort in Braze`,
          'no-user-profiles': `There are no user profiles with a Braze external user id in this cohort`,
          'only-profiles': `Only user profiles with a Braze external user id will be exported`,
        },
        marketo: {
          'custom-field-used': `This custom field has already been used`,
          'custom-field-name': `Custom field name`,
          'custom-field-update': `Marketo Custom Field to update`,
          'setting-up-custom-field': `Setting up Custom Fields in Marketo`,
          'max-export-size': `The maximum exportable cohort size is 30 million user profiles at this time.`,
          'no-profiles-with': `There are no user profiles with {{propertyName}} in this cohort`,
          'only-profiles': `Only user profiles with {{propertyName}} will be exported`,
        },
        airship: {
          'tag-already-exported': `This tag has already been used`,
          'tag-name': `Tag name`,
          'tag-to-add': `Airship tag to add`,
          tag: `Airship tag`,
          'setting-up-in': `Setting up the Mixpanel Tag Group in Airship`,
          'max-export-size': `The maximum exportable cohort size is 15 million user profiles at this time.`,
          'no-profiles-with': `There are no user profiles with an {{airshipChannelId}} or a {{namedUserId}} in this cohort`,
          'only-profiles': `Only user profiles with an {{airshipChannelId}} or a {{namedUserId}} will be exported`,
        },
        facebookads: {
          'only-profiles': `Only user profiles with an email, a phone number, a first name, and a last name will be exported`,
        },
        iterable: {
          'only-profiles': `Only user profiles with an email will be exported`,
        },
        googleads: {
          'only-profiles': `Only user profiles with an email and a phone number will be exported`,
        },
        testpartnerwebhook: {
          'no-help-article': `No help article for this Integration yet`,
          'only-profiles': `Only user profiles with an email will be exported`,
        },
        vwowebhook: {
          'only-profiles': `Only user profiles with a “{{propertyName}}” property will be exported`,
        },
        onesignalwebhook: {
          'only-profiles': `Only user profiles with a {{propertyName}} property will be exported`,
        },
        salesforcemc: {
          'all-key-exported': `All user profiles with the key used at setup will be exported`,
        },
        blitzllamawebhook: {
          'only-profiles': `Only user profiles with a {{propertyName}} property will be exported`,
        },
        productboardwebhook: {
          'only-profiles': `Only user profiles with an {{propertyName}} property will be exported`,
        },
      },
      'export-once': {
        'custom-segment': `All exported users will be sent as a {{integrationName}} custom segment.`,
        'exported-to': `All users will be exported to {{integrationName}}.`,
        braze: `All users in the cohort will be updated one time.`,
        marketo: `All users in the cohort will be updated with the custom field one time.`,
        urbanairship: `All users in the cohort will be updated with the tag one time.`,
        facebookads: `All users in the cohort will be uploaded to the Business Manager, replacing the old one.`,
        googleads: `All users in the cohort will be uploaded as a Custom Audience, replacing the old one.`,
        iterable: `All users in the cohort will be uploaded as an Iterable List, replacing the old one.`,
        testpartnerwebhook: `All users in the cohort will exported to the Webhook destination of choice.`,
        leanplumwebhook: `Users in the cohort that match successfully into Leanplum will be uploaded as a Leanplum Audience, replacing the old one.`,
        salesforcemc: `All users with the key used during setup will be exported to Salesforce Marketing Cloud.`,
        'limit-text': `Unlimited one-time syncs`,
        title: `One-time export`,
      },
      'export-recur': {
        braze: `Your Braze cohort will be updated every 15 minutes to match users in Mixpanel cohort.`,
        marketo: `The custom field will be updated every 2 hours to match users in the cohort.`,
        urbanairship: `Tag will be updated every 15 minutes to match users in the cohort.`,
        facebookads: `Your Facebook Custom Audience will be updated every 2 hours to match users in this cohort.`,
        googleads: `Your Google Custom Audience will be updated every 2 hours to match users in this cohort.`,
        iterable: `Your Iterable List will be updated every 15 minutes to match users in this cohort.`,
        testpartnerwebhook: `All users in the cohort will exported every 2 hours to the Webhook destination of choice.`,
        moengagewebhook: `Your MoEngage custom segment will be updated every 15 minutes with the most current set of users exported from the cohort.`,
        leanplumwebhook: `Your Leanplum Audience will be updated every 15 minutes with users in this cohort who successfully match into Leanplum.`,
        chameleonwebhook: `Your Chameleon custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
        insiderwebhook: `Your Insider custom segment will be updated every 15 minutes with the most current set of users exported from the cohort.`,
        appcueswebhook: `Your Appcues custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
        onesignalwebhook: `Your OneSignal custom segment will be updated every 15 minutes with the most current set of users exported from the cohort.`,
        kameleoonwebhook: `Your Kameleoon custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
        mailchimpwebhook: `Your Mailchimp custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
        taplyticswebhook: `Your Taplytics custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
        vwowebhook: `Your VWO custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
        webengagewebhook: `Your WebEngage custom segment will be updated every 30 minutes with the most current set of users exported from the cohort.`,
        xtremepushwebhook: `Your Xtremepush custom segment will be updated every 15 minutes with the most current set of users exported from the cohort.`,
        apptimizewebhook: `Your Apptimize custom segment will be updated every 15 minutes with the most current set of users exported from the cohort.`,
        clevertapwebhook: `Your CleverTap custom segment will be updated every 15 minutes with the most current set of users exported from the cohort.`,
        customwebhook: `Your Custom Webhook will be updated every 30 minutes with the most current set of users exported from the cohort.`,
        segmentwebhook: `Your Segment integration will be updated every 15 minutes with the most current set of users exported from the cohort.`,
        salesforcemc: `Your Salesforce Marketing Cloud integration will be updated every 15 minutes with the most current set of users exported from the cohort.`,
        launchdarklywebhook: `Your LaunchDarkly integration will be updated every 30 minutes with the most current set of users exported from the cohort.`,
        blitzllamawebhook: `Your Blitzllama integration will be updated every 15 minutes with the most current set of users exported from the cohort.`,
        productboardwebhook: `Your Productboard integration will be updated every 15 minutes with the most current set of users exported from the cohort.`,
        pushwooshwebhook: `Your Pushwoosh integration will be updated every 15 minutes with the most current set of users exported from the cohort.`,
        abtastywebhook: `Your ABTasty integration will be updated every 15 minutes with the most current set of users exported from the cohort.`,
        flagshipwebhook: `Your Flagship integration will be updated every 15 minutes with the most current set of users exported from the cohort.`,
        mparticlewebhook: `Your mParticle integration will be updated every 15 minutes with the most current set of users exported from the cohort.`,
        'limit-text': `{{numRecurringExports}} of {{limit}} recurring syncs in use.`,
        title: `Recurring sync`,
      },
    },
  },
  spark: {
    title: `Spark`,
    'prompt-sample-1': `Which countries have the most sign ups compared to last month?`,
    'prompt-sample-2': `What is my trend of weekly active users?`,
    'prompt-sample-3': `What do our users do after they log in?`,
    'prompt-sample-4': `What's the conversion rate on our activation funnel?`,
    'prompt-sample-5': `What % of my users use Android?`,
    'prompt-sample-6': `How long does it take for our users to convert on average?`,
    'prompt-sample-7': `What is my week 1 retention over time?`,
    'prompt-sample-8': `What are users doing before checkout?`,
    'prompt-sample-9': `What's the average number of minutes per video watched?`,
    'tip-1': `Try using text to filter, breakdown, or change the chart type`,
    'tip-2': `Have a question? Start typing to explore your data`,
    'show-history': `Show history`,
    'stop-generating-report': `Stop generating report`,
    'generate-report': `Generate report`,
    generate: `Generate`,
    generating: `Generating`,
    'feedback-tooltip-positive': `I am satisfied with this output`,
    'feedback-tooltip-negative': `I am not satisfied with this output`,
    'beta-feature': `Beta`,
    'card-displayed': `Displayed`,
    'go-back': `Go Back`,
    'go-back-prompt': `Go Back to Last Prompt`,
    'remaining-usage': `Your organization has {{count}} Spark responses remaining this month.`,
    'report-error-generic': `We couldn't complete your query. Please try again in a bit, or <support-link>Contact Support</support-link> if you continue to experience issues.`,
    'report-error-noselections': `We couldn't identify the requested events or properties. Please try rephrasing your prompt, or <lexicon-link>Go to Lexicon</lexicon-link> to view your event and property names.`,
    'report-error-limit': `Your organization has reached its monthly cap of {{count}} Spark responses.`,
  },
  'feature-flags': {
    upsell: {
      headline: `Ship new features without the risk.`,
      subhead: `Mixpanel Feature Flags give you cohort-based targeting, gradual rollouts, and kill switches, all wired into your analytics from day one.`,
      'talk-to-sales': `Talk to Sales`,
      'read-the-docs': `Read the Docs`,
    },
    new: {
      header: `New Feature Flag`,
      'flag-type': `What type of flag would you like to create?`,
      'flag-type-options': {
        shortened: {
          'feature-gate': `Gate`,
          experiment: `Experiment`,
          'dynamic-config': `Config`,
        },
        'feature-gate': `Feature Gate`,
        'feature-gate-description': `Toggle a feature on or off for targeted users or all users`,
        experiment: `Experiment`,
        'experiment-description': `Serve multiple variants with string keys to power Experiments`,
        'dynamic-config': `Dynamic Config`,
        'dynamic-config-description': `Deliver a single payload to remotely control behavior`,
      },
      'experiment-banner': `Start by creating an Experiment to generate the Feature Flag`,
      'experiment-limit-reached': `Free Limit Reached`,
      name: `Flag Name`,
      'name-placeholder': `e.g. Project Launch`,
      key: `Flag Key`,
      'key-placeholder': `e.g. project-launch`,
      'is-auto-generated': `Generated using flag name`,
      create: `Create Flag`,
    },
    list: {
      title: `Feature Flags`,
      new: `New Feature Flag`,
      'search-placeholder': `Search...`,
      'column-name': `Name`,
      'column-key': `Key`,
      'column-status': `Status`,
      'column-tags': `Tags`,
      'column-flag-type': `Flag Type`,
      'column-enabled-since': `Enabled Since`,
      'column-lastmodified': `Last Modified`,
      'column-creator': `Creator`,
      'column-daily-usage': `Daily Usage`,
      'column-created': `Created`,
      'filter-statuses': {
        archived: `Archived`,
        enabled: `Enabled`,
        disabled: `Disabled`,
        draft: `Draft`,
      },
      'free-trial': {
        title: `Launch with confidence using Feature Flags`,
        'title-limit': `$t(mixology.feature-flag) Limit Reached`,
        description: `Safely roll out changes, test ideas in production, and control who sees what—all without code deploys. Enterprise plans let you **create three flags for free.**`,
        'description-empty': `Safely roll out changes, test ideas in production, and control who sees what—all without code deploys.`,
        'description-limit': `You've used all free feature flags included in your plan — upgrade to create more.`,
      },
      'show-archived': `All`,
      'hide-archived': `Active`,
      'show-archived-only': `Archived`,
      archival: `Archival`,
    },
    'unsaved-changes': {
      title: `Unsaved Changes`,
      description: `This feature flag has unsaved changes, would you like to save before leaving to avoid losing these changes?`,
    },
    create: {
      label: `Create`,
      success: `Successfully created feature flag`,
      error: `Failed to create feature flag`,
      'name-conflict': `A feature flag with this name already exists`,
      'limit-exceeded': `You've used all {{limit}} feature flags included in your plan — upgrade to create more.`,
      'trial-limit-exceeded': `You've used all free feature flags included in your plan — upgrade to create more.`,
    },
    duplicate: {
      label: `Duplicate`,
      success: `Successfully duplicated feature flag`,
      error: `Failed to duplicate feature flag`,
      'name-conflict': `A feature flag with this name already exists`,
      'limit-exceeded': `You've used all {{limit}} feature flags included in your plan — upgrade to create more.`,
      'trial-limit-exceeded': `You've used all free feature flags included in your plan — upgrade to create more.`,
      'experiment-not-supported': `You cannot duplicate an experiment type feature flag`,
      'private-data-view': `You cannot duplicate a feature flag from private data view`,
    },
    update: {
      success: `Successfully updated feature flag`,
      error: `Failed to update feature flag`,
      'invalid-input': `Failed to update feature flag due to invalid input`,
      'limit-exceeded': `You've reached the limit of {{limit}} active feature flags on your plan — upgrade to enable more.`,
      'trial-limit-exceeded': `You've reached the limit of active feature flags on your plan — upgrade to enable more.`,
    },
    'contract-expired-message': `Your Feature Flags contract has expired. Please contact your account manager to renew.`,
    'contract-expiring-banner': `Your feature flag contract has ended. You have 30 days to renew before feature flag operations are disabled.`,
    'contract-expired-banner': `Your feature flag contract has expired.`,
    'contract-expired-tooltip': `Feature flag contract has expired. Contact support to renew.`,
    'startup-blocked': {
      'mutation-error': `This action is restricted because your account has exceeded the Startup Plan quota. Please upgrade to a paid plan or contact support@mixpanel.com.`,
      'action-disabled': `This action is restricted because your account has exceeded the Startup Plan quota.`,
    },
    page: {
      status: `Status`,
      overview: `Overview`,
      qa: `QA`,
      'qa-description': `Testers have early access to validate your flag`,
      'release-conditions': `Release Conditions`,
      'rollout-groups': `Rollout Groups`,
      'rollout-groups-description': `Rollout groups are accessed from top to bottom`,
      'flag-status': `This Flag is {{status}}`,
      'status-help': `Please ensure the code snippet has been added during setup. After setup, this toggle controls whether users are assigned one of the configured variants.`,
      'context-help': `Please specify the key to be used for rollout (eg. distinct_id)`,
      'serving-users': `{{count}} users are being served this feature`,
      'name-help': `Please specify the human readable name to be used for this flag`,
      'key-help': `Please specify the key to be used to identify the flag (to be used in the SDK)`,
      loading: `Loading`,
      delete: {
        label: `Delete`,
        success: `Successfully deleted feature flag`,
        error: `Failed to delete feature flag`,
        'trial-restriction-tooltip': `Feature Flags cannot be deleted while on a Free Trial`,
        'enabled-restriction-tooltip': `Disable this flag before deleting`,
        'delete-modal': {
          title: `Delete Feature Flag`,
          description: `Are you sure you want to delete "{{flagName}}"?`,
        },
      },
      archive: {
        label: `Archive`,
        success: `Successfully archived feature flag`,
        error: `Failed to archive feature flag`,
        'trial-restriction-tooltip': `Feature Flags cannot be archived while on a Free Trial`,
        'enabled-restriction-tooltip': `Disable this flag before archiving`,
        'archive-modal': {
          title: `Archive Feature Flag`,
          description: `Are you sure you want to archive "{{flagName}}"?`,
        },
      },
      restore: {
        label: `Restore`,
        success: `Successfully restored feature flag`,
        error: `Failed to restore feature flag`,
      },
      'switch-data-view': {
        title: `Switch to Linked Data View`,
        content: `This feature flag is tied to the <bold>{{workspaceName}}</bold> Data View. <break /><break /> Select the {{workspaceName}} Data View via the top left dropdown or click Switch Data View.`,
        'content-private': `This feature flag is tied to a <bold>Private Data View</bold> owned by <bold>{{owner}}</bold>. <break /><break /> You do not have permission to access this Data View.`,
        'content-private-unknown': `This feature flag is tied to a <bold>Private Data View</bold>. <break /><break /> You do not have permission to access this Data View.`,
        'learn-more': `Learn More`,
        'switch-data-view': `Switch Data View`,
        'view-all-feature-flags': `View All Feature Flags`,
        'navigating-to': `Navigating to {{workspaceName}} Data View...`,
      },
      banner: {
        draft: {
          title: `Draft`,
          description: `Complete configuration before this flag can be used`,
          cta: `Configure Flag`,
        },
        disabled: {
          title: `Disabled`,
          description: `Enable the flag to start serving the configured variants to users`,
          cta: `Enable Flag`,
        },
        enabled: {
          title: `Enabled`,
          description: `Enabled since {{enabledAt}}`,
          cta: `Disable Flag`,
        },
        archived: {
          title: `Archived`,
          description: `This feature flag is archived and inactive`,
        },
        'code-snippets': `Code Snippets`,
      },
      context: {
        'enter-values': `Search existing values or enter a new value`,
        'no-data-groups': `No data group keys found`,
        'device-id': `Device ID`,
      },
      variants: {
        title: `Variants`,
        add: `Add`,
        name: `Name`,
        key: `Variant name`,
        'key-tooltip': `The identifier used to reference this variant in your SDK code.`,
        value: `Value`,
        'value-tooltip': `Add a value to associate with each variant. The value can be either a string or a JSON object.`,
        'value-type': {
          string: `String`,
          dynamic: `JSON`,
        },
        description: `Description`,
        payload: `Payload`,
        'description-tooltip': `Optional description of this variant`,
        'payload-tooltip': `JSON data passed with this variant`,
        'allocations-percents': `Allocation percentages must add up to 100%`,
        split: `Split`,
        'split-tooltip': `The percentage allocation to each variant`,
        screenshot: `Image`,
        none: `None`,
        'add-screenshot': `Add image`,
        'remove-screenshot': `Remove image`,
        'full-screen': `Full screen`,
        sticky: `Sticky`,
        'sticky-tooltip': `If sticky, variant assignment persists, even if rules change`,
        'sticky-help': `If enabled, once a user enters a variant, they will always be served that variant. If disabled, users may be served different variants on different visits.`,
        'sticky-disabled-context-tooltip': `Sticky variants currently only work with User or Device variant assignment keys`,
        'sticky-disabled-control-tooltip': `Sticky variants do not work with control variant`,
        error: {
          'empty-names': `All variants must have names`,
          'duplicate-names': `Variant names must be unique`,
          'not-enough-variants': `At least two variants are required`,
          'too-many-variants': `At most {{maxVariants}} variants are allowed`,
          'invalid-splits': `All splits must be valid non-negative integers.`,
          'splits-not-100': `Total must be 100%`,
          'negative-splits': `Splits cannot be negative values`,
          'non-numeric-splits': `Splits must be numeric values`,
          'invalid-payload': `Payload must be valid JSON`,
          'payload-too-long': `Payload must be <= 32KB`,
          'mixed-types': `All variant values must be of the same type, either string or dynamic (valid json object).`,
        },
      },
      'qa-testers': {
        title: `QA Testers`,
        name: `Name`,
        detail: `Detail`,
        error: `Error loading data for QA tester`,
        variant: `Variant`,
        'add-testers': `Add Testers`,
        add: `Add`,
        empty: `You have no QA testers`,
        'load-error': `Failed to load QA testers`,
        'add-error': `Failed to add QA tester`,
      },
      edit: {
        title: `Setup Feature Flag`,
        error: {
          'empty-flag-name': `Flag name cannot be empty`,
          'empty-flag-key': `Flag key cannot be empty`,
          'empty-flag-context': `Context cannot be empty`,
          'duplicate-flag-key': `Flag key must be unique`,
        },
      },
      rollouts: {
        'new-user': `New User`,
        group: `Group {{groupNumber}}`,
        'add-rollout-group': `Add Rollout Group`,
        'add-assignment-group': `Add Assignment Group`,
        'rollout-title': `ROLLOUT`,
        'rollout-conditions-title': `ROLLOUT CONDITIONS`,
        'variant-split-title': `VARIANT SPLIT`,
        percentage: `Percentage`,
        'rollout-tooltip': `Rollout is managed by the flag's Assignment Key. This flag's assignment key is {{assignmentKey}}.\n\nYou can change the flag's Assignment Key in this flag's Settings.`,
        'save-as-title': `Save Cohort As`,
        'loading-cohort-count': `Loading cohort count`,
        'cohort-count-error': `Failed to load cohort count`,
        'cohort-user-count': `{{cohortCount}} users in cohort`,
        'cohort-entity-count': `{{cohortCount}} {{dataGroupName}} entities in cohort`,
        'runtime-evaluation': {
          and: `AND`,
          or: `or`,
          'all-users': `All instances at runtime`,
          'learn-more': `Learn More`,
          who: `who`,
          where: `where`,
          string: `String`,
          'key-placeholder': `Enter key...`,
          is: `is`,
          'is-true': `Is True`,
          'is-false': `Is False`,
          'value-placeholder': `Enter value...`,
          'add-condition': `Filter`,
          'add-rule-group': `Rule Group`,
          'rule-group': `Rule Group`,
          'rule-group-sublabel': `Filter by runtime property conditions`,
          'key-required': `Key is required`,
          'value-required': `Value is required`,
          'add-value': `Add "{{value}}"`,
          'event-filter': {
            header: `All instances at runtime`,
            did: `did`,
            'for-the': `for the`,
            'first-time': `First Time`,
            until: `until`,
            'select-event': `Select Event`,
            'search-events': `Search events...`,
            'time-window': {
              'while-flag-enabled': `While Flag Enabled`,
            },
            duration: {
              'end-of-session': `End of Session`,
              indefinitely: `Indefinitely`,
            },
            'overflow-menu': {
              filter: `Filter`,
              'first-time-during-filter': `First Time During Filter`,
              'first-time-during-filter-tooltip': `Only count a user's first time doing this event while this flag is enabled`,
            },
            'where-event-properties': `Where event properties`,
            match: `match`,
            'add-condition': `Add condition`,
            where: `where`,
            when: `when`,
            'add-filter': `Filter`,
            error: {
              'select-event': `Please select an event for your filter.`,
            },
          },
        },
        'cohort-user-count-mini': `{{cohortCount}} users`,
        'cohort-entity-count-mini': `{{cohortCount}} {{dataGroupName}} entities`,
        'checking-sync-status': `Checking sync status...`,
        'cohort-not-synced': `Indexing`,
        'last-synced_one': `Last indexed {{count}} minute ago`,
        'last-synced_other': `Last indexed {{count}} minutes ago`,
        'sync-status-error': `Error retrieving sync status`,
        'save-flag-to-start-syncing': `Save to start indexing`,
        'enable-flag-to-start-indexing': `Enable flag to start indexing`,
        'cohort-saved-success': `Cohort "{{name}}" saved`,
        'cohort-saved-error': `Failed to save cohort`,
        'cohort-edit-error': `Failed to load cohort for editing`,
        'variant-overridden': `Variant allocation override enabled. All users targeted by this group will receive <bold>{{variantKeyName}}</bold>.`,
        'runtime-rule': `Runtime Property`,
        'runtime-event': `Runtime Event`,
        'filter-runtime-event': `Mixpanel event evaluated at runtime`,
        'filter-by-cohort': `Filter by {{contextName}} Cohort`,
        'filter-at-runtime': `Filter at Runtime`,
        'filter-by': `Filter by`,
        'filter-at': `Filter at`,
        cohort: `Cohort`,
        runtime: `Runtime`,
        'rollout-by': `Rollout by`,
        'filter-tooltip': `Define who sees the flag using filters.\n\nFilters defined here are used to create a cohort, which may take a few minutes to generate and update.\n\nUser cohorts are used by default. To filter on a group key, change the experiment's Assignment Key.`,
        'runtime-filter-tooltip': `Define who sees the flag using filters.\n\nFilters defined here are applied instantly at runtime, based off the parameters provided to the SDK from your code (in the case of runtime properties), and any events generated by the SDK (in the case of event filters). See the Runtime Targeting section in the docs for more details.`,
        'filter-user-events-properties': `Filter user events or properties`,
        'filter-runtime-events-properties': `Filter events or properties at runtime`,
        'filter-group-events-properties': `Filter group events or properties`,
        'disabled-group-tooltip': `Assignment Key must be set to this group key to filter this group`,
        'disabled-user-tooltip': `Assignment Key must be set to User to filter users`,
        'data-group-cohort-not-available': `Data group cohort targeting not yet available`,
        'runtime-event-not-available': `Runtime event targeting not yet available`,
      },
      'exposure-stream': {
        title: `Exposure Stream`,
        'see-all': `See All`,
        time: `Time`,
        'distinct-id': `Distinct ID`,
        variant: `Variant`,
      },
      config: {
        name: `Name`,
        key: `Key`,
        'key-info': `The key used to identify this feature flag in your code base`,
        experiment: `Experiment`,
        environment: {
          'experiment-copy-disabled': `You cannot duplicate an experiment type feature flag`,
          'experiment-push-disabled': `You cannot push changes to an experiment type feature flag`,
        },
        'environment-help': `Flags may be duplicated to other Projects used as development environments. Duplicates are managed independently.`,
        'other-environments': `Other Environments`,
        'list-environments-failed': `Failed to load linked feature flags.`,
        'copy-to-environment-success': `Successfully copied feature flags.`,
        'copy-to-environment-failed': `Failed to copy feature flag.`,
        'push-to-environment-success': `Successfully pushed feature flag changes.`,
        'push-to-environment-failed': `Failed to push feature flag changes.`,
        'unlink-environment-success': `Successfully unlinked feature flag.`,
        'unlink-environment-failed': `Failed to unlink feature flag.`,
        'experiment-info': `The Mixpanel experiment connected to this Feature Flag`,
        'experiment-none': `None`,
        'experiment-select-placeholder': `Select an experiment`,
        'flag-type': `Flag Type`,
        'flag-type-info': `Determines the value returned by the flag and manages functionality related to the use case. <learnMoreLink>Learn more</learnMoreLink>`,
        status: `Enable flag?`,
        tags: `Tags`,
        context: `Assignment Key`,
        'context-info': `Defines the entity type (e.g. user, organization, etc) used for targeting. <learnMoreLink>Learn more</learnMoreLink>`,
        'context-disabled-info': `You cannot change the variant assignment key of a flag once it has been enabled`,
        'serving-method': `Mode`,
        'edit-settings': `Edit Settings`,
        settings: `Settings`,
        history: `History`,
        'unsaved-changes-pending': `Unsaved Changes Pending`,
        'next-history-page': `Load more results`,
        'flag-settings': `Flag Settings`,
        details: `Details`,
        'diff-header': `Changes Diff`,
        'history-event': {
          flag: `Flag`,
          changed: `Changed`,
          'updated-settings': `Updated Settings`,
          'linked-experiment': `Linked Experiment`,
          'unlinked-experiment': `Unlinked Experiment`,
          'paused-experiment': `Paused Experiment`,
          'started-experiment': `Started Experiment`,
          'changed-experiment': `Updated Experiment`,
          'updated-experiment': `Updated Experiment Settings`,
          'added-variant': `Added variant`,
          'updated-variant': `Updated variant`,
          'removed-variant': `Removed variant`,
          'added-metric': `Added metric`,
          'removed-metric': `Removed metric`,
          'added-testers': `Added testers`,
          'updated-testers': `Updated testers`,
          'removed-testers': `Removed testers`,
          'added-rollout': `Added rollout groups`,
          'removed-rollout': `Removed rollout groups`,
          'changed-rollout': `Edited rollout groups`,
          'updated-variant-splits': `Updated variant splits`,
          'updated-rollout-percentage': `Updated rollout percentage`,
          'tester-variant-changed': `Tester {{tester}}'s variant changed from {{oldVariant}} to {{newVariant}}`,
          'variant-key-changed': `Variant key changed from {{oldKey}} to {{newKey}}`,
          'variant-value-changed': `Variant {{variantKey}}'s value changed`,
          'flag-created': `Created flag`,
          'no-change': `Saved with no changes`,
          'experiment-status': `Experiment`,
          'experiment-no-change': `Experiment saved with no changes`,
          'made-decision': `Made decision`,
          'changed-decision': `Changed decision`,
        },
      },
      'config-tab': {
        flag: `Flag`,
        'control-key': `Control Key`,
        'control-key-info': `The control variant key for this feature flag`,
        'experiment-link': `Experiment Link`,
        more: `More`,
      },
      setup: {
        'assignment-key': `Assignment Key`,
        'assignment-key-info': `The Assignment Key defines how randomization is applied in a feature flag. It serves two purposes: <break /><break />1. Splitting eligible users into variants. <break />2. Applying rollout percentages within a rollout group. <break /><break /> <bold>For nearly all flags, <highlight>distinct_id</highlight> (aka User) is the right choice and can be left as the default.</bold> <break /><break /> <bold>Counter-example</bold>: If you sell software to companies, you may want everyone at the same company to have the same experience. Setting the Assignment Key to a field like <highlight><bold>company_id</bold></highlight> ensures all users from that company are assigned consistently.`,
        'select-key': `Select Key`,
        payload: `Payload`,
        'payload-label': `Specify a payload to deliver`,
        'payload-info': `Payload is a JSON-formatted set of values that you can modify in Mixpanel to dynamically configure your product’s behavior without redeploying code.`,
      },
      testers: {
        title: `Test Users`,
        variant: `Variant`,
        auto: `Auto Assigned`,
        users: `Users`,
        'recent-users': `Recent Users`,
        add: `Add`,
        searchPlaceholder: `Enter a name, email or ID`,
        remove: `Remove`,
      },
      confirm: {
        enable: {
          title: `Confirm Flag Enablement`,
          description: `Are you sure you want to enable this flag? Once enabled, changes will be applied within a few minutes`,
          confirm: `Turn Flag On`,
        },
        disable: {
          title: `Confirm Flag Disablement`,
          description: `Are you sure you want to disable this flag? Once disabled, changes will be applied within a few minutes`,
          confirm: `Turn Flag Off`,
        },
        update: {
          title: `Confirm Flag Changes`,
          description: `This flag is enabled. Once saved, your changes will be instantly applied and live for all testers and cohorts with non-zero rollouts.`,
          confirm: `Save and Apply Changes`,
          cancel: `Cancel`,
        },
      },
    },
    status: {
      archived: `Archived`,
      draft: `Draft`,
      disabled: `Disabled`,
      enabled: `Enabled`,
    },
    'serving-method': {
      server: `Server`,
      client: `Client`,
      remote_or_local: `Remote or Local`,
      remote_only: `Remote Only`,
    },
    feedback: {
      title: `Feature Flags Feedback`,
      description: `Rate your satisfaction with feature flags`,
      placeholder: `Additional feedback (Optional)`,
    },
    errors: {
      'cannot-copy-to-same-project': `Feature Flag cannot be copied to the same project.`,
      'invalid-target-project': `Invalid destination project for feature flag.`,
      'invalid-target-workspace': `Invalid target workspace for feature flag.`,
      'invalid-data-groups': `Feature flag contains data-group based configuration that is not compatible with destination project.`,
    },
  },
  tours: {
    'mixpanel-agent': {
      title: `Meet Mixpanel Agent`,
      description: `Your always-on AI analyst. Use it to generate insights, build analyses, and more. Just tell Mixpanel Agent what you want and let it take care of the rest!`,
      feedback: `Have feedback? Tell us with the thumbs up/down.`,
      'maybe-later': `Maybe later`,
      cta: `Try Mixpanel Agent`,
    },
    'mcp-ga': {
      title: `Faster answers with Mixpanel MCP`,
      description: `Connect to Mixpanel MCP in your favorite tools including Claude, ChatGPT, Gemini, Cursor, Notion and more. Ask product questions, create dashboards, explore Session Replays, update Lexicon, and more.\n\nEnable MCP in Organization Settings.`,
      cta: `Learn more`,
    },
    'mcp-connect-tools': {
      title: `Connect the Mixpanel MCP Server`,
      description: `Query your events, watch session replays, and explore your data right from the tools you already use.`,
      'go-to-docs': `Go To Docs`,
      'remind-me-later': `Remind Me Later`,
    },
    'custom-roles-enroll': {
      title: `Custom roles coming soon`,
      description: `Custom Roles and updated permissions are coming to all Enterprise plans on June 1, 2026.\n\nWant early access? Enable the new experience for your Organization. Learn more about the updates in [Docs](https://docs.mixpanel.com/docs/orgs-and-projects/roles-and-permissions#custom-roles).`,
      cta: `Enable Custom Roles`,
      'confirm-title': `Enable Custom Roles and Permission for this Organization?`,
      'confirm-description': `This update will apply to all Projects in your Organization.\n\nThe main updates are:\n- The ability to create Custom Project Roles\n- Sensitive data access is now managed via Custom Roles. Project Members who previously had sensitive data access will need to be granted access again through a Custom Role by June 1, 2026.\n- Service accounts with access to sensitive data will continue to work as is, but will also need to be granted access through a Custom Role by June 1, 2026.`,
      'confirm-description-no-sensitive-data': `This update will apply to all projects in your organization. Your organization will be automatically moved to the new permissions model on June 1, 2026. Enable now to start creating custom roles.\n\nWhat you get:\n- Create custom project roles with granular permissions\n- Assign custom roles to project users, service accounts, and teams`,
      'confirm-description-docs': `You can learn more about Custom Roles and the permission updates in [Docs](https://docs.mixpanel.com/docs/orgs-and-projects/roles-and-permissions#custom-roles).`,
      'confirm-warning': `Your organization has {{memberCount}} project members, {{serviceAccountCount}} service accounts, and {{teamCount}} teams that have sensitive data access. To help with the transition, we'll email you a list of these users after you enable.`,
      'confirm-cta': `Enable for this Organization`,
      'success-toast': `Custom Roles have been enabled for your organization.`,
      'error-toast': `Failed to enable Custom Roles. Please try again.`,
    },
    'comments-tour': {
      'step1-title': `Collaborate with Comments`,
      'step1-description': `Add comments to any report or board to share insights, ask questions, and discuss findings with your team.`,
      'step1-cta': `Next`,
      'step2-title': `Stay Updated with Notifications`,
      'step2-description': `Never miss important discussions. Get notified when teammates comment or reply on reports or boards you care about.`,
      'step2-cta': `Got it`,
    },
    'version-history-tour': {
      'step1-title': `Track Changes with Version History`,
      'step1-description': `View a timeline of changes made to your report. See who made edits and when, and restore previous versions with a single click.`,
      'step1-cta': `Got it`,
    },
  },
  'error-boundary': {
    header: `Something went wrong`,
    body: `We have been notified of this error and are working to fix it. Please reload the page.`,
    'body-minimal': `Error`,
  },
  upsells: {
    'advanced-sharing': {
      title: `Want more control over your sharing permissions?`,
      description: `Upgrade to the Growth plan to decide who can view and edit your boards.`,
    },
    'alert-limit': {
      title: `Upgrade for More Alerts`,
      description: `Your current plan allows for up to {{alertLimit}} active alerts. To create additional alerts and unlock more premium features, upgrade to the Enterprise plan.`,
    },
    'events-limit': {
      title: `You’ve reached your monthly events limit`,
      description: `You’re on a roll! Your current plan allows up to 1 million tracked events per month. To track more events and get the most out of Mixpanel, upgrade to the Growth plan.`,
    },
    'save-alert': {
      title: `Get Notified When Something Goes Wrong`,
      description: `Create alerts that let you know when your traffic is spiking, dropping off, or doing something notable by upgrading to Mixpanel’s Growth plan.`,
    },
    'save-cohort': {
      title: `Track Cohorts Across Reports`,
      description: `Segment and compare your user base with ease. Upgrade to save unlimited cohorts with Mixpanel’s Growth plan.`,
    },
    'save-formula': {
      title: `Stop Reinventing Every Report`,
      description: `Upgrade to save and reuse key analytical building blocks like formulas and metrics with Mixpanel’s Growth plan.`,
    },
    'save-metric': {
      title: `Stop Reinventing Every Report`,
      description: `Upgrade to save and reuse key analytical building blocks like formulas and metrics with Mixpanel’s Growth plan.`,
    },
    'save-report': {
      title: `Oh No! You’ve Hit Your Saved Report Limit`,
      description: `Your current plan provides up to 5 saved reports per user. To create unlimited reports and unlock premium features, upgrade to the Growth plan.`,
    },
    'session-replay-settings': {
      title: `Unlock Session Replay Remote Settings`,
      description: `Remote settings for Session Replay are available with the Session Replay add-on. Contact sales to manage sampling rules and conditional capture.`,
    },
    spark: {
      title: `Upgrade for Additional Spark AI Reports`,
      description: `Your current plan provides up to 30 Spark AI reports per month. Keep the fire going! To access additional Spark reports and premium features, upgrade to the Growth plan.`,
    },
    modal: {
      'data-governance': `Data governance controls`,
      'data-warehouse-ingestion': `Data warehouse ingestion`,
      'email-and-slack': `Real-time email & Slack alerts`,
      'free-to-growth-cta': `Upgrade Plan`,
      'free-to-growth-cta-exit': `Continue With Free Plan →`,
      'growth-to-enterprise-cta': `Contact Sales`,
      'growth-to-enterprise-cta-exit': `Continue With Growth Plan →`,
      'premium-support': `Premium support & services`,
      'reports-and-cohorts': `Unlimited reports & cohorts`,
      'session-replay': `Higher session replay volumes`,
      sharing: `Advanced sharing controls`,
      spark: `Increased Spark AI report limits`,
    },
  },
  copilot: {
    'chat-placeholder': `Search or ask anything...`,
    'new-chat': `New chat`,
    'close-chat': `Close chat`,
    'empty-chat': `Ask the Mixpanel Agent anything`,
    'empty-search-results': `No results found`,
    'chart-drag': `Drag this chart to a board`,
    'no-verified-data': `No data to display. The requested entities are not verified.`,
    'suggestions-title': `Suggestions`,
    'key-moments': `Key moments`,
    'replay-fallback-label': `Replay {{id}}…`,
    'user-chip': {
      'id-label': `User ID: {{id}}`,
    },
    artifacts: {
      title: `Artifacts`,
      toggle: `Toggle artifact`,
    },
    'empty-state': {
      heading: `How can the Mixpanel Agent help?`,
      'default-suggestion-1': `What can you help me with?`,
      'default-suggestion-2': `Generate a report for me`,
      'default-suggestion-3': `What should I look into today?`,
      'new-board-suggestion-1': `How do I build a new board?`,
      'new-board-suggestion-2': `Generate a report for this board`,
      'new-board-suggestion-3': `Help me build a new board`,
      'board-suggestion-1': `Explain this board to me`,
      'board-suggestion-2': `What are some insights from this board?`,
      'board-suggestion-3': `Investigate this board`,
      'board-suggestion-4': `Generate a report for this board`,
      'new-report-suggestion-1': `How do I build a new report?`,
      'new-report-suggestion-2': `Help me build a new {{reportName}} report`,
      'report-suggestion-1': `Explain this report to me`,
      'report-suggestion-2': `What insights can you draw about this report?`,
      'report-suggestion-5': `Run a root cause analysis on this report`,
      'experiment-list-suggestion-1': `Create an experiment for me`,
      'experiment-list-suggestion-2': `What makes a good experiment?`,
      'experiment-list-suggestion-3': `Summarize my recent experiments`,
      'flag-list-suggestion-1': `Create a feature flag for me`,
      'flag-list-suggestion-2': `What can I use feature flags for?`,
      'flag-detail-suggestion-1': `Explain this feature flag`,
      'flag-detail-suggestion-2': `Is this flag safe to roll out?`,
      'experiment-setup-suggestion-configure': `Help me configure this experiment`,
      'experiment-setup-suggestion-sample-size': `Check my sample size`,
      'experiment-setup-suggestion-guardrails': `What metrics should I use as guardrails?`,
      'experiment-results-suggestion-summarize': `Summarize these results`,
      'experiment-results-suggestion-significant': `Is this significant yet?`,
      'experiment-results-suggestion-stakeholder-update': `Draft a stakeholder update`,
      'experiment-results-suggestion-retrospective': `Draft a retrospective`,
    },
    'follow-up': {
      'session-replay-playlist-suggestion-1': `Summarize what users did in these replays`,
      'session-replay-playlist-suggestion-2': `Find common drop-off points across these replays`,
      'session-replay-playlist-suggestion-3': `Highlight the most interesting moments in these replays`,
    },
    'create-report': {
      building: `The Mixpanel Agent is building your report. We'll notify you when it's ready.`,
      ready: `Your Mixpanel Agent report is ready`,
      'view-report': `View report`,
      'failed-generic': `Failed to create report. Please try again.`,
      'failed-submit': `Something went wrong. Please try again.`,
      'notification-subtitle': `Your Mixpanel Agent report is ready`,
      'notification-still-preparing': `The Mixpanel Agent report is still being prepared. Try again in a moment.`,
      'notification-open-failed': `Could not open the Mixpanel Agent report.`,
      'notification-untitled': `Untitled report`,
    },
    'create-board': {
      'modal-title': `Build a board with Mixpanel Agent`,
      'modal-description': `Describe the dashboard you want and Mixpanel Agent will create a board for you.`,
      'modal-placeholder': `e.g. Create a board showing user engagement metrics over the last 30 days`,
      creating: `Creating your board, please don't close this window...`,
      'create-button': `Create Board`,
      building: `The Mixpanel Agent is building your board. We'll notify you when it's ready.`,
      ready: `Your Mixpanel Agent board is ready`,
      'view-board': `View board`,
      'failed-submit': `Something went wrong. Please try again.`,
      'notification-subtitle': `Your Mixpanel Agent board is ready`,
      'notification-still-preparing': `The Mixpanel Agent board is still being prepared. Try again in a moment.`,
      'notification-open-failed': `Could not open the Mixpanel Agent board.`,
      'notification-untitled': `Untitled board`,
    },
    'reasoning-duration': `Thought for {{seconds}} seconds`,
    'reasoning-generic': `Thought for some seconds`,
    tools: {
      'search-dashboards': `Dashboard Search "{{input}}"`,
      'search-query-context': `Search Query Context`,
      'search-reports': `Report Search "{{input}}"`,
      'search-events': `Event Search "{{input}}"`,
      'search-properties': `Property Search "{{input}}"`,
      'search-property-values': `Property Values Search "{{input}}"`,
      'search-metrics': `Metric Search "{{input}}"`,
      'search-experiments': `Experiment Search "{{input}}"`,
      'search-prior-experiments': `Prior Experiment Search`,
      'search-feature-flags': `Feature Flag Search "{{input}}"`,
      'search-docs': `Documentation Search`,
      'metric-result': `Metric Result`,
      'edit-metric': `Edit Report`,
      'create-board': `Create Board`,
      'save-board': `Save Board`,
      'create-cohort': `Create Cohort`,
      'create-tracking-plan': `Create Tracking Plan`,
      'create-implementation-snippet': `Create Implementation Snippet`,
      'business-priorities': `Business Priorities`,
      'edit-board': `Edit Board`,
      'fetch-metric': `Fetch Metric`,
      'fetch-report': `Fetch Report`,
      'fetch-board': `Fetch Board`,
      'query-bookmark': `Fetch Card Data`,
      'live-events': `Fetch Live Events`,
      'save-metric': `Save Metric`,
      'current-page-context': `Current Page Context`,
      'session-replay-update': `Update Session Replay`,
      'create-custom-property': `Create Custom Property`,
      'update-custom-property': `Update Custom Property`,
      'view-in-lexicon': `View in Lexicon`,
      'replay-moments': `Surface key moments`,
      'trigger-rca': `Root Cause Analysis`,
      'rca-key-findings': `Key findings`,
      'rca-next-steps': `Next steps`,
      'rca-starting': `Starting Root Cause Analysis…`,
      'rca-card-header': `Root Cause Analysis`,
      'rca-card-error': `Root Cause Analysis failed`,
      'rca-still-running-notice': `Still working — we'll send an inbox notification when it's done. You can also follow along on the dashboard.`,
      'rca-show-progress': `Show progress ({{count}})`,
      'rca-hide-progress': `Hide progress`,
      'get-magic-merge-suggestions': `Get suggestions for merging similar entities`,
      'merge-magic-merge-entity-group': `Merge magic merge entity group`,
      'dismiss-magic-merge-entity-group': `Dismiss magic merge entity group`,
      'magic-merge-show-more': `+{{count}} more`,
      'magic-merge-show-less': `Show less`,
      'get-experiment': `Viewing experiment "{{input}}"`,
      'get-feature-flag': `Viewing feature flag "{{input}}"`,
      'plan-experiment': `Planning experiment`,
      'experiment-update': `Updating experiment`,
      'experiment-interpret': `Interpreting results`,
      'plan-feature-flag': `Planning feature flag`,
      'feature-flag-update': `Updating feature flag`,
      'feature-flag-read': `Reading feature flag`,
      'create-experiment': `Create experiment "{{input}}"`,
      'get-business-context': `Read Business Context`,
      'get-data-standards': `Read Data Standards`,
      'get-project-token': `Get Project Token`,
      'set-project-business-context': `Update Project Business Context`,
      'set-organization-business-context': `Update Organization Business Context`,
      'create-feature-flag': `Create feature flag "{{input}}"`,
      'update-experiment': `Update experiment "{{input}}"`,
      'launch-experiment': `Launch experiment "{{input}}"`,
      'conclude-experiment': `Conclude experiment "{{input}}"`,
      'decide-experiment': `Decide experiment "{{input}}"`,
      'archive-experiment': `Archive experiment "{{input}}"`,
      'restore-experiment': `Restore experiment "{{input}}"`,
      'update-feature-flag': `Update feature flag "{{input}}"`,
      'enable-feature-flag': `Enable feature flag "{{input}}"`,
      'disable-feature-flag': `Disable feature flag "{{input}}"`,
      'archive-feature-flag': `Archive feature flag "{{input}}"`,
      'restore-feature-flag': `Restore feature flag "{{input}}"`,
      'ask-user-question': `Asked questions`,
      'ask-user-question-header': `Questions from Copilot`,
      'ask-user-question-dismiss': `Dismiss questions`,
      'ask-user-question-select-all': `Select all that apply`,
      'ask-user-question-submit': `Submit Answers`,
      'ask-user-question-custom-single': `Or type a custom answer...`,
      'ask-user-question-custom-multiple': `Or type additional answer...`,
      'ask-user-question-custom-answer': `✎ Custom answer`,
      'ask-user-question-text-placeholder': `Type your answer...`,
      'ask-user-question-recommended': `Recommended`,
      'ask-user-question-dismissed': `Dismissed without answering`,
      'ask-user-question-toggle-expand': `Show full options`,
      'ask-user-question-toggle-collapse': `Hide full options`,
      'ask-user-question-no-answer': `No answer`,
      'ask-user-question-progress': `Question {{current}} of {{total}}`,
      'ask-user-question-back': `Back`,
      'ask-user-question-skip': `Skip`,
      'ask-user-question-next': `Next`,
      'ask-user-question-send': `Send`,
      'ask-user-question-skipped': `Skipped`,
      'ask-user-question-answer-label': `Answer`,
      'ask-user-question-submitted': `Answers submitted`,
    },
    'tracking-plan': {
      title: `Tracking Plan`,
      'title-with-project': `Tracking Plan — {{projectName}}`,
      table: `Table`,
      schema: `Schema`,
      'event-name': `Event name`,
      description: `Description`,
      properties: `Properties`,
      'property-name': `Property name`,
      type: `Type`,
      empty: `No events in this tracking plan.`,
    },
    'live-events': {
      title: `Listening for Events`,
      'view-events': `View Events`,
      'event-name': `Event name`,
      user: `User`,
      location: `Location`,
      os: `OS`,
      time: `Time`,
      empty: `No recent events.`,
    },
    approval: {
      confirm: `Confirm`,
      cancel: `Cancel`,
      confirmed: `Confirmed`,
      cancelled: `Cancelled`,
      working: `Working…`,
    },
    confirmation: {
      description: `Mixpanel Agent wants to make this change. Allow?`,
      deny: `Deny`,
      'allow-once': `Allow once`,
      'allow-session': `Allow for session`,
      card: {
        'allowed-once': {
          title: `Allowed`,
          body: `Mixpanel Agent will apply this change.`,
        },
        'allowed-session': {
          title: `Allowed for session`,
          body: `Mixpanel Agent will auto-apply edits for the rest of this conversation.`,
        },
        denied: {
          title: `Denied`,
          body: `Mixpanel Agent will not apply this change.`,
        },
      },
    },
    'bookmark-edit': {
      'apply-error-title': `Could not apply edit`,
    },
    experiments: {
      control: `(control)`,
      exposures: `Exposures`,
      results: `Results`,
      metrics: `Metrics`,
      'no-results': `No results available yet`,
      'view-experiment': `View experiment`,
      'variant-count': `{{count}} variant`,
      'variant-count_plural': `{{count}} variants`,
      'metric-count': `{{count}} metric`,
      'metric-count_plural': `{{count}} metrics`,
      'metric-type': {
        primary: `Primary`,
        guardrail: `Guardrail`,
        other: `Other`,
      },
      'metric-error': `Error`,
      'metric-error-tooltip': `Failed to compute results for this metric`,
      'significance-significant': `sig`,
      'significance-not-significant': `not sig`,
      'results-freshness-live': `Live`,
      'results-freshness-cached': `Last refreshed {{time}}`,
      decision: {
        shipped: `Decision: {{variant}}`,
        'no-variant-shipped': `No variant shipped`,
        deferred: `Decision deferred`,
      },
      meta: {
        'start-date': `Start date`,
        'end-date': `End date`,
        creator: `Creator`,
        created: `Created`,
        'experiment-type': `Experiment type`,
        'feature-flag': `Feature flag`,
        variants: `Variants`,
        'not-set': `—`,
      },
      health: {
        'srm-warning': `SRM detected (p={{pValue}})`,
        'srm-passed': `SRM check passed`,
      },
      validation: {
        title: `Pre-launch checks`,
      },
      mutation: {
        create: {
          title: `Create draft experiment "{{name}}"`,
          unnamed: `Create draft experiment`,
          creating: `Creating experiment…`,
        },
        update: {
          config: {
            title: `Update experiment "{{name}}"`,
            unnamed: `Update experiment`,
            pending: `Updating experiment…`,
          },
          launch: {
            title: `Launch experiment "{{name}}"`,
            unnamed: `Launch experiment`,
            pending: `Launching…`,
          },
          conclude: {
            title: `Conclude experiment "{{name}}"`,
            unnamed: `Conclude experiment`,
            pending: `Concluding…`,
          },
          decide: {
            title: `Decide experiment "{{name}}"`,
            unnamed: `Decide experiment`,
            pending: `Recording decision…`,
          },
          'ship-variant': {
            title: `Ship variant on experiment "{{name}}"`,
            unnamed: `Ship variant`,
            pending: `Shipping variant…`,
          },
          'do-not-ship': {
            title: `Roll back to control on experiment "{{name}}"`,
            unnamed: `Roll back to control`,
            pending: `Reverting to control…`,
          },
          abandon: {
            title: `Abandon experiment "{{name}}"`,
            unnamed: `Abandon experiment`,
            pending: `Abandoning…`,
          },
          archive: {
            title: `Archive experiment "{{name}}"`,
            unnamed: `Archive experiment`,
            pending: `Archiving…`,
          },
          restore: {
            title: `Restore experiment "{{name}}"`,
            unnamed: `Restore experiment`,
            pending: `Restoring…`,
          },
        },
      },
      summary: {
        hypothesis: `Hypothesis`,
        description: `Description`,
        tags: `Tags`,
        'primary-metric': `Primary metric`,
        'primary-metric_plural': `Primary metrics`,
        'guardrail-metric': `Guardrail metrics`,
        'secondary-metric': `Secondary metrics`,
        duration: `Duration`,
        confidence: `Confidence`,
        'collection-method': `Collection method`,
        'testing-model': `Testing model`,
        'health-checks': `Health checks`,
        'duration-days': `{{count}} day`,
        'duration-days_plural': `{{count}} days`,
        'duration-sample-size': `{{count}} samples`,
        'collection-feature-flag': `Feature flag`,
        'collection-exposure-events': `Exposure events`,
        'testing-frequentist': `Frequentist`,
        'testing-sequential': `Sequential`,
        'srm-enabled': `SRM`,
        'bias-enabled': `Pre-experiment bias`,
        'saved-metric-fallback': `Saved metric #{{id}}`,
        variants: `Variants`,
        'decision-label': `Decision`,
        'decision-variant': `Decision: {{variant}}`,
        'decision-none': `Decision: None`,
        'decision-deferred': `Decision Deferred`,
        'decision-abandoned': `Abandoned`,
        'decide-message': `Note`,
        'decide-only-on-ff-hint': `Records the decision without changing the linked flag's rollout. To ship a variant or disable the flag, ask Mixpanel Agent to ship or abandon it.`,
        'conclude-end-date': `End date`,
        'conclude-confirm': `Mark the experiment as concluded.`,
        'launch-confirm': `Move from DRAFT to ACTIVE. Enables the linked feature flag.`,
        'archive-confirm': `Soft delete the experiment. Can be restored later.`,
        'restore-confirm': `Restore the archived experiment.`,
        name: `Name`,
        'ship-keep-cohort-targeting': `Preserve cohort targeting`,
        'ship-variant-action': `Roll variant "{{variant}}" to 100% on the linked flag.`,
        'ship-do-not-ship-action': `Revert to control (100%) on the linked flag.`,
        'ship-abandon-action': `Disable the linked flag.`,
      },
      'follow-up': {
        launch: `Launch this experiment now`,
        'check-results': `Check current results`,
        decide: `Help me decide on a winner`,
        'roll-out-winner': `Roll out the winner to 100%`,
      },
    },
    'feature-flags': {
      control: `(control)`,
      variants: `Variants`,
      'rollout-label': `Rollout: {{percent}}`,
      'view-feature-flag': `View feature flag`,
      'experiment-linked': `Linked to experiment`,
      'view-experiment': `View experiment`,
      status: {
        enabled: `Enabled`,
        disabled: `Disabled`,
        archived: `Archived`,
        restored: `Restored`,
      },
      meta: {
        key: `Key`,
        context: `Context`,
        created: `Created`,
        'enabled-at': `Enabled at`,
        creator: `Creator`,
        'serving-method': `Serving method`,
        'not-set': `—`,
      },
      mutation: {
        create: {
          title: `Create feature flag "{{name}}"`,
          unnamed: `Create feature flag`,
          creating: `Creating feature flag…`,
        },
        update: {
          config: {
            title: `Update feature flag "{{name}}"`,
            unnamed: `Update feature flag`,
            pending: `Updating feature flag…`,
          },
          enable: {
            title: `Enable feature flag "{{name}}"`,
            unnamed: `Enable feature flag`,
            pending: `Enabling…`,
          },
          disable: {
            title: `Disable feature flag "{{name}}"`,
            unnamed: `Disable feature flag`,
            pending: `Disabling…`,
          },
          archive: {
            title: `Archive feature flag "{{name}}"`,
            unnamed: `Archive feature flag`,
            pending: `Archiving…`,
          },
          restore: {
            title: `Restore feature flag "{{name}}"`,
            unnamed: `Restore feature flag`,
            pending: `Restoring…`,
          },
        },
      },
      summary: {
        name: `Name`,
        description: `Description`,
        variants: `Variants`,
        'rollout-percentage': `Rollout percentage`,
        status: `Status`,
        'enable-confirm': `Turn the flag on so it serves the configured rollout to users.`,
        'disable-confirm': `Kill switch. Stops serving the flag immediately, regardless of rollout percentage.`,
        'archive-confirm': `Soft delete. Removes the flag from the active list but keeps it restorable.`,
        'restore-confirm': `Restore the archived flag.`,
      },
      'follow-up': {
        enable: `Enable this flag`,
        disable: `Disable this flag`,
        restore: `Restore this flag`,
        view: `Open the flag in the UI`,
      },
    },
    'edit-board': {
      'add-text-card': `Add text card`,
      'add-report': `Add report:`,
      'add-playlist': `Add playlist:`,
      'untitled-playlist': `Untitled playlist`,
      'untitled-report': `Untitled report`,
      'update-card': `Update card #{{contentId}}`,
      'remove-card': `Remove {{contentType}} card #{{contentId}}`,
      'update-board-metadata': `Update board metadata`,
      field: {
        title: `Title:`,
        description: `Description:`,
        markdown: `Markdown:`,
        'chart-type': `Chart type:`,
        metrics: `Metrics:`,
        filters: `Filters:`,
        breakdowns: `Breakdowns:`,
        'date-range': `Date range:`,
        'board-title': `Board title:`,
        'board-description': `Board description:`,
      },
      value: {
        none: `(none)`,
        'filter-count': `{{count}} filter(s)`,
        updated: `updated`,
      },
    },
    loading: {
      dashboards: `Loading dashboards...`,
      reports: `Loading reports...`,
      properties: `Loading properties...`,
      events: `Loading events...`,
      metric: `Generating metric...`,
    },
    'chat-history': `Chat History`,
    'mcp-connections': `MCP Connections`,
    'no-values-found': `No values found.`,
    'response-error': `There was an error responding to your message.`,
    'thinking-timeout-error': `Your message timed out. Please try again, or consider simplifying your request or breaking it into smaller parts.`,
    'stopped-message': `Response interrupted`,
    'scroll-to-bottom': `scroll to bottom`,
    'mixpanel-agent': `Mixpanel Agent`,
    typing: `Mixpanel Agent is thinking...`,
    warning: `Mixpanel Agent can make mistakes. Please double check responses.`,
    feedback: {
      title: `Mixpanel Agent Feedback`,
      'sentiment-heading': `Are you satisfied with Mixpanel Agent?`,
    },
    history: {
      title: `Chat History`,
      heading: `Conversation history`,
      loading: `Loading...`,
      'loading-conversation': `Loading conversation…`,
      'load-error': `Failed to load conversations`,
      'load-conversation-error': `Failed to load conversation.`,
      'load-conversation-private': `This chat is private`,
      'delete-error': `Failed to delete conversation.`,
      empty: `No previous conversations`,
      untitled: `Untitled`,
      retry: `Retry`,
    },
    menu: {
      'new-chat': `New Chat`,
      'rename-chat': `Rename chat`,
      'chat-history': `Chat history`,
      'delete-chat': `Delete chat`,
      'share-link': `Share link`,
    },
    share: {
      title: `Share link to chat`,
      description: `Share your chat thread with other project users in a view-only mode.`,
      'keep-private': `Keep Private`,
      'keep-private-sublabel': `Only you have access`,
      shared: `Shared`,
      'shared-sublabel': `Anyone in the project with the link can view`,
      'include-current-page': `Include current page`,
      'include-current-page-sublabel': `Show the chat alongside the page you're on instead of opening in full view`,
      'copy-link': `Copy link`,
      minting: `Creating link…`,
      'link-copied': `Link copied to clipboard`,
      'mint-failed': `Couldn't create share link. Please try again.`,
      'share-failed': `Couldn't share chat. Please try again.`,
      'unshare-failed': `Couldn't make chat private. Please try again.`,
    },
    shared: {
      'view-only': `View only`,
    },
    rename: {
      'save-error': `Failed to rename chat.`,
    },
    delete: {
      title: `Delete chat`,
      description: `Are you sure you want to delete this chat?`,
    },
    'expand-chat': `Open in full view`,
    'collapse-chat': `Open in side view`,
    'full-page': {
      'welcome-back': `Welcome back, {{firstName}}`,
      'welcome-subtitle': `What should we build today?`,
      'show-history': `Show chat history`,
      'collapse-history': `Hide chat history`,
    },
    flows: {
      'chart-error': `Failed to load Flows chart`,
    },
    limits: {
      'user-turns-reached': `You've reached your monthly Mixpanel Agent message limit.`,
      'user-turns-hourly-reached': `You've reached your hourly Mixpanel Agent message limit. Please wait and try again.`,
      'user-turns-hourly-free-reached': `You've reached the hourly Mixpanel Agent message limit for the free plan. Upgrade your plan to raise this limit.`,
      'user-tokens-reached': `You've reached your monthly Mixpanel Agent token limit.`,
      'project-turns-reached': `This project has reached its monthly Mixpanel Agent message limit.`,
      'project-turns-free-reached': `This project has reached the monthly Mixpanel Agent message limit for the free plan. Upgrade your plan to raise this limit.`,
      'project-tokens-reached': `This project has reached its monthly Mixpanel Agent token limit.`,
    },
  },
  'first-class-metrics-info': {
    'feedback-text': `I would like to sign up for early access to Saved Metrics.`,
    'pre-beta': {
      button: `Coming Soon`,
      title: `Saved Metrics are Coming Soon!`,
      description: `A new way to save Metrics and Behaviors is on its way, featuring an updated query building experience.`,
      cta: `Learn More`,
    },
    beta: {
      button: `What's New`,
      title: `Saved Metrics`,
      description: `Create standardized metrics your team can align on using a refreshed query building experience.`,
      cta: `Learn More`,
    },
    'successful-save': `{{entityTypeName}} successfully saved`,
  },
  'text-analysis': {
    'text-analysis': `Text Analysis`,
    description: `Group open-ended text data like product feedback, prompts, and survey feedback into themes and insights using AI.`,
    'step-n': `Step {{stepNumber}}`,
    'select-event': `Select the Event & Property that represents the text data you want to analyze`,
    prompt: `AI Prompt`,
    'prompt-description': `Select from suggested prompts or create your own`,
    'prompt-sentiment': `Sentiment`,
    'prompt-severity': `Severity`,
    'prompt-feature': `Feature Name`,
    'prompt-custom': `Custom Prompt`,
    'learn-more': `Learn More about Text Analysis`,
    start: `Start AI Text Analysis`,
  },
  tags: {
    'default-label': `Tags`,
    header: `Tags`,
    all: `All Tags`,
    untagged: `Un-Tagged`,
    search: `Search tags...`,
    loading: `Loading tags...`,
    'no-tags': `No tags found`,
    'no-tags-selected': `No tags`,
    create: `Create new tag`,
    'type-to-create': `Type to create a tag`,
    'add-tags': `Add tags...`,
    'tag-deleted': `Tag deleted`,
    'tag-delete-failed': `Failed to delete tag`,
    'tag-renamed': `Tag renamed`,
    'tag-rename-failed': `Failed to rename tag`,
  },
  'audit-logs': {
    sidebar: {
      tagline: `Audit Log Event`,
      details: `Details`,
      timestamp: `Timestamp`,
      user: `User`,
      'raw-data': `Raw Data`,
    },
    refresh: `Refresh`,
    export: {
      label: `Export`,
      modal: {
        title: `Export Audit Logs`,
        description: `We will email {{email}} an export of the audit logs matching the currently applied filters.\n\nDepending on the volume of data, preparing your export can take up to an hour or more.`,
        'confirm-button-label': `Export`,
        'cancel-button-label': `Cancel`,
      },
      'disabled-tooltip-date-range': `You may only export up to one year of audit logs at a time. Please adjust your filters and try again.`,
      'disabled-tooltip-no-results': `Your filters do not match any audit logs.`,
      toast: {
        success: `Audit log export submitted. You will receive an email when it is ready to download.`,
        duplicate: `Export already in progress. Please wait before submitting another.`,
        error: `Failed to submit audit log export. Please try again or contact support if the problem persists.`,
      },
    },
    table: {
      action: `Action`,
      category: `Category`,
      columns: {
        'date-and-time': `Date and Time`,
        user: `User`,
        'project-or-org': `Project or Org`,
        'ip-address': `IP Address`,
      },
      filters: {
        'hide-users': `Hide Users`,
        'hide-users-placeholder': `Enter email to hide users...`,
        'hide-emails-matching': `Hide {{emailPattern}}`,
        'hide-users-empty-state': `No users hidden`,
        project: `Project`,
        categories: {
          /* eslint-enable sort-keys */
          authentication: `Authentication`,
          authorization: `Authorization`,
          'data-updates': `Data and Content Updates`,
          exports: `Exports`,
          notifications: `Notifications`,
          /* eslint-disable sort-keys */
        },
        'action-sections': {
          /* eslint-enable sort-keys */
          'agentic-workflows': `Agentic Workflows`,
          alerts: `Alerts`,
          'billing-info': `Billing Info`,
          'board-subscriptions': `Board Subscriptions`,
          boards: `Boards`,
          cohorts: `Cohorts`,
          'custom-roles': `Custom Roles`,
          'data-exports': `Data Exports`,
          'data-management': `Data Management`,
          'data-pipelines': `Data Pipelines`,
          experiments: `Experiments`,
          'feature-flags': `Feature Flags`,
          'mixpanel-admin': `Mixpanel Admin`,
          'organization-settings': `Organization Settings`,
          'project-settings': `Project Settings`,
          'realtime-destinations': `Real-Time Destinations`,
          reports: `Reports`,
          'service-accounts': `Service Accounts`,
          teams: `Teams`,
          'user-authentication': `User Authentication`,
          'user-management': `User Management`,
          users: `Users`,
          'warehouse-sources': `Warehouse Sources`,
          workspaces: `Data Views`,
          /* eslint-disable sort-keys */
        },
      },
      'empty-state-message': `No audit logs found`,
    },
    'system-user': `Mixpanel (System)`,
    'service-account-user': `{{username}} (Service Account)`,
    'api-user': `API Key`,
    'scim-user': `SCIM`,
    types: {
      /* eslint-enable sort-keys */
      'admin_action.performed': `Mixpanel Admin Action Performed`,
      'agent_flow.activated': `Agent Automation Activated`,
      'agent_flow.created': `Agent Automation Created`,
      'agent_flow.deleted': `Agent Automation Deleted`,
      'agent_flow.paused': `Agent Automation Paused`,
      'agent_flow.triggered': `Agent Automation Triggered`,
      'agent_flow.updated': `Agent Automation Updated`,
      'agentic_account_request.created': `Agentic Account Request Created`,
      'agentic_account_request.updated': `Agentic Account Request Updated`,
      'agentic_deep_link.logged_in': `Agentic Deep Link Login`,
      'agentic_provisioned_resource.created': `Agentic Provisioned Resource Created`,
      'agentic_provisioned_resource.deleted': `Agentic Provisioned Resource Deleted`,
      'agentic_provisioned_resource.updated': `Agentic Provisioned Resource Updated`,
      'alert.created': `Alert Created`,
      'alert.deleted': `Alert Deleted`,
      'alert.triggered': `Alert Triggered`,
      'alert.updated': `Alert Modified`,
      'audit_log_export.created': `Audit Log Export Created`,
      'audit_log_export.downloaded': `Audit Log Export Downloaded`,
      'billing_info.payment_info_updated': `Billing Payment Information Updated`,
      'billing_info.receipt_preferences_updated': `Billing Invoice Preferences Updated`,
      'billing_info.tax_info_updated': `Billing Tax Information Updated`,
      'board.created': `Board Created`,
      'board.deleted': `Board Deleted`,
      'board.duplicated': `Board Duplicated`,
      'board.exported': `Board Exported`,
      'board.public_access_updated': `Public Board Access Updated`,
      'board.shared': `Board Shared`,
      'board.unshared': `Board Unshared`,
      'board.updated': `Board Updated`,
      'board_subscription.created': `Board Subscription Created`,
      'board_subscription.deleted': `Board Subscription Deleted`,
      'bookmark.created': `Report Saved`,
      'bookmark.deleted': `Report Deleted`,
      'bookmark.moved': `Report Moved`,
      'bookmark.screenshot_downloaded': `Report Screenshot Downloaded`,
      'bookmark.updated': `Report Updated`,
      'cohort.created': `Cohort Created`,
      'cohort.deleted': `Cohort Deleted`,
      'cohort.shared': `Cohort Shared`,
      'cohort.unshared': `Cohort Unshared`,
      'cohort.updated': `Cohort Updated`,
      'cohort_sync.created': `Cohort Sync Created`,
      'cohort_sync.updated': `Cohort Sync Updated`,
      'custom_role.created': `Custom Role Created`,
      'custom_role.deleted': `Custom Role Deleted`,
      'custom_role.updated': `Custom Role Updated`,
      'data_pipeline.created': `Data Pipeline Created`,
      'data_pipeline.deleted': `Data Pipeline Deleted`,
      'data_pipeline.updated': `Data Pipeline Updated`,
      'event_definition.created': `Event Definition Created`,
      'event_definition.deleted': `Event Definition Deleted`,
      'event_definition.updated': `Event Definition Updated`,
      'event_deletion.canceled': `Event Deletion Canceled`,
      'event_deletion.created': `Event Deletion Requested`,
      'event_export.downloaded': `Events Exported`,
      'experiment.archived': `Experiment Archived`,
      'experiment.concluded': `Experiment Concluded`,
      'experiment.created': `Experiment Created`,
      'experiment.decided': `Experiment Decision Recorded`,
      'experiment.deleted': `Experiment Deleted`,
      'experiment.duplicated': `Experiment Duplicated`,
      'experiment.launched': `Experiment Launched`,
      'experiment.restored': `Experiment Restored`,
      'experiment.updated': `Experiment Updated`,
      'feature_flag.archived': `Feature Flag Archived`,
      'feature_flag.created': `Feature Flag Created`,
      'feature_flag.deleted': `Feature Flag Deleted`,
      'feature_flag.duplicated': `Feature Flag Duplicated`,
      'feature_flag.restored': `Feature Flag Restored`,
      'feature_flag.updated': `Feature Flag Updated`,
      'invitation.accepted': `Accepted Invitation`,
      'invitation.sent': `Invited User to Organization`,
      'organization.user_added': `User Added to Organization`,
      'organization.user_removed': `User Removed from Organization`,
      'organization_access_security.twofactor_disabled': `Organization Two-Factor Auth Disabled`,
      'organization_access_security.twofactor_enabled': `Organization Two-Factor Auth Enabled`,
      'organization_setting.updated': `Organization Settings Changed`,
      'organization_spark_setting.updated': `Organization AI Settings Changed`,
      'passkey.created': `Passkey Registered`,
      'passkey.deleted': `Passkey Removed`,
      'profile.batch_deleted': `Profiles Batch Deleted`,
      'profile.batch_updated': `Profiles Batch Updated`,
      'profile.deleted': `Profile Deleted`,
      'profile.updated': `Profile Updated`,
      'profile_export.downloaded': `Profile Data Exported`,
      'project.default_access_granted': `Default Project Access Granted`,
      'project.default_access_revoked': `Default Project Access Revoked`,
      'project.team_added': `Team Added to Project`,
      'project.team_removed': `Team Removed from Project`,
      'project.transferred': `Project Transferred`,
      'project.user_added': `User Added to Project`,
      'project.user_removed': `User Removed from Project`,
      'property_definition.created': `Property Definition Created`,
      'property_definition.deleted': `Property Definition Deleted`,
      'property_definition.updated': `Property Definition Updated`,
      'raw_event_export.requested': `Raw Event Export Requested`,
      'realtime_destination.created': `Real-Time Destination Created`,
      'service_account.added_to_project': `Service Account Added to Project`,
      'service_account.created': `Service Account Created`,
      'service_account.deleted': `Service Account Deleted`,
      'service_account.removed_from_project': `Service Account Removed from Project`,
      'service_account.role_changed': `Service Account Role Changed`,
      'session.logged_in': `User Logged In`,
      'session.logged_out': `User Logged Out`,
      'session.login_failed': `User Login Failed`,
      'team.created': `Team Created`,
      'team.deleted': `Team Deleted`,
      'team.service_account_added': `Service Account Added to Team`,
      'team.service_account_removed': `Service Account Removed from Team`,
      'team.updated': `Team Updated`,
      'team.user_added': `User Added to Team`,
      'team.user_removed': `User Removed from Team`,
      'user.passkey_login_disabled': `User Disabled Passkey Login`,
      'user.passkey_login_enabled': `User Enabled Passkey Login`,
      'user.role_changed': `User Role Changed`,
      'user.twofactor_enabled': `User Enabled Two-Factor Auth`,
      'user_data_deletion.canceled': `User Data Deletion Canceled`,
      'user_data_deletion.created': `User Data Deletion Requested`,
      'user_data_export.created': `User Data Export Requested`,
      'warehouse_source.created': `Warehouse Source Created`,
      'warehouse_source.deleted': `Warehouse Source Deleted`,
      'warehouse_source.updated': `Warehouse Source Updated`,
      'warehouse_source_sync.created': `Warehouse Source Sync Created`,
      'warehouse_source_sync.deleted': `Warehouse Source Sync Deleted`,
      'warehouse_source_sync.updated': `Warehouse Source Sync Updated`,
      'workspace.team_added': `Team Added to Data View`,
      'workspace.team_removed': `Team Removed from Data View`,
      'workspace.user_added': `User Added to Data View`,
      'workspace.user_removed': `User Removed from Data View`,
      // TODO: below are proposed audit log events, keeping for posterity:
      // 'session.mfa_disable': `MFA Disabled`,
      // 'session.sso_login_success': `SSO Login Succeeded`,
      // 'user.invite': `User Invited`,
      // 'user.invite_remove': `User Invite Removed`,
      // 'user.remove': `User Removed`,
      // 'user.email_change': `User Email Changed`,
      // 'user.organization_role_change': `Organization Role Changed`,
      // 'user.project_role_change': `Project Role Changed`,
      // 'user.access_request_create': `Access Request Created`,
      // 'user.access_request_invite_send': `Access Request Invite Sent`,
      // 'user.access_request_delete': `Access Request Deleted`,
      // 'report.create': `Report Created`,
      // 'report.delete': `Report Deleted`,
      // 'report.duplicate': `Report Duplicated`,
      // 'report.update': `Report Updated`,
      // 'report.export': `Report Exported`,
      // 'report.move': `Report Moved`,
      // 'report.rename': `Report Renamed`,
      // 'board.subscription_create': `Board Subscription Created`,
      // 'board.subscription_delete': `Board Subscription Deleted`,
      // 'board.share': `Board Shared`,
      // 'board.create': `Board Created`,
      // 'board.delete': `Board Deleted`,
      // 'board.duplicate': `Board Duplicated`,
      // 'board.update': `Board Updated`,
      // 'board.export': `Board Exported`,
      // 'cohort.export': `Cohort Exported`,
      // 'cohort.duplicate': `Cohort Duplicated`,
      // 'events.export': `Events Exported`,
      // 'users.export': `Users Exported`,
      // 'warehouse_source.create': `Warehouse Source Created`,
      // 'warehouse_source.edit': `Warehouse Source Edited`,
      // 'warehouse_source.delete': `Warehouse Source Deleted`,
      // 'warehouse_source_sync.create': `Warehouse Source Sync Created`,
      // 'warehouse_source_sync.edit': `Warehouse Source Sync Edited`,
      // 'warehouse_source_sync.delete': `Warehouse Source Sync Deleted`,
      // 'cohort_sync.integration_add': `Integration Added`,
      // 'cohort_sync.integration_disconnect': `Integration Disconnected`,
      // 'cohort_sync.create': `Sync Created`,
      // 'cohort_sync.pause': `Sync Paused`,
      // 'cohort_sync.resume': `Sync Resumed`,
      // 'org_settings.rename': `Org Renamed`,
      // 'org_settings.spark_change': `Spark Settings Changed`,
      // 'org_settings.mcp_change': `MCP Settings Changed`,
      // 'org_settings.billing_change': `Billing Info Changed`,
      // 'compliance.user_data_export': `User Data Exported`,
      /* eslint-disable sort-keys */
    },
  },
  'mcp-connections': {
    title: `MCP Connections`,
    'add-connection': `Add Connection`,
    added: `MCP Connection added successfully.`,
    'add-x-connection': `Add {{connectionName}} Connection`,
    'add-custom-connection': `Add Custom Connection`,
    'discovery-error': `Could not discover authentication method for this URL.`,
    discovering: `Discovering authentication configuration...`,
    'token-required': `This server requires you to provide a token for authentication.`,
    finalizing: `Finalizing connection...`,
    'auth-waiting': `Waiting for authorization...`,
    'auth-waiting-description': `Please complete the authorization steps in the popup window.`,
    connected: `Connected`,
    disconnected: `Disconnected`,
    'checking-status': `Checking connection status...`,
    'delete-confirm-title': `Delete MCP Connection`,
    'delete-confirm-description': `Are you sure you want to delete this connection?`,
    'server-url': `Server URL`,
    'auth-token': `Authentication Token`,
    'connection-name': `Connection Name`,
    'connection-status': `Connection Status`,
    connect: `Connect`,
    'custom-mcp-server': `Custom MCP Server`,
    redirect: {
      invalid: `Invalid redirect.`,
      connected: `Successfully connected`,
      'close-window': `You may close this window and return to Mixpanel to finish the connection setup process.`,
    },
  },
  'warehouse-import-builder': {
    'manual-sync-error': `Something went wrong triggering the manual sync.`,
    'column-select': {
      'static-value': `Static value`,
    },
    'api-frequency-message': {
      'check-back': `Check back here for the unique API link once the sync is created.`,
      'trigger-syncs': `To trigger syncs via API, send a PUT request.`,
      'view-docs': `View docs.`,
    },
    'column-form': {
      'fields-auto-completed-ga': `Fields are automatically completed for Google Analytics tables`,
      'group-key': `Group Key`,
      'event-time-subtitle': `Timestamps shown in project timezone`,
    },
    'complete-stage-sync-modal': {
      'ready-to-complete': `You're ready to complete your setup. We'll remove the test data and process a full backfill to finalize this import.`,
      'would-you-like-to-proceed': `Would you like to proceed?`,
      'sync-settings': `Sync Settings`,
    },
    'confirm-stage-modal': {
      'test-import': `Test Import`,
      'creating-test-import': `Creating a Test Import`,
      'should-only-take-seconds': `This should only take a few seconds`,
      'm-rows': `{{count}}M rows`,
      'test-import-description-prefix': `Up to `,
      'test-import-description-suffix': ` of data will be imported for this test. You will be able to use the data in any report once complete.`,
      'test-import-temporary-notice': `Tests are free and temporary — please complete or delete this sync within {{days}} days to avoid automatic deletion.`,
      'test-import-monthly-limit': `Your team may create a maximum of {{limit}} test imports per month. `,
      'learn-more': `Learn More`,
    },
    'frequency-select': {
      at: `at`,
      on: `on`,
    },
    form: {
      setup: `Setup`,
      status: `Status`,
      'test-import': `Test Import`,
      source: `Source`,
      type: `Type`,
      'map-columns': `Map Columns`,
      'sync-settings': `Sync Settings`,
    },
    'scd-unsupported-columns-warning': {
      'import-completed-but': `This import will be completed, but lists and objects are not supported. You will not be able to query the following columns:`,
    },
    'scheduling-section': {
      'sync-mode': `Sync Mode`,
      delay: `Delay`,
      'delay-description': `When Delay is not None, Mixpanel will ignore the most recent selected number days for event ingestion based on event timestamp.`,
      'manually-run-sync': `Manually Run Sync`,
    },
    index: {
      'plan-limited': `Your plan is limited to 20M events per month. To upgrade, see our [pricing page](/pricing/).`,
      'analyze-in-insights': `Analyze in Insights`,
      'refresh-data': `Refresh Data`,
      'test-import': `Test Import`,
      'not-sure-about-setup': `Not sure about your sync setup? Preview and import a portion`,
      'of-your-data': `of your data to use in Mixpanel before committing.`,
      'learn-more': `Learn more`,
      'delete-import': `Delete Import`,
      cancel: `Cancel`,
      'complete-import': `Complete Import`,
    },
    'sync-history': {
      'for-test-events': `For Test Events, Mixpanel runs a one time sampled import of the last 30 days of data.`,
      'review-your-data': `Review your event data before completing the import.`,
      'learn-more': `Learn More`,
      'complete-import': `Complete Import`,
      'analyze-in-insights': `Analyze in Insights`,
      'full-backfill': `Full Backfill`,
    },
    'run-details': {
      'mixpanel-import-id': `Mixpanel Import ID:`,
      'mixpanel-run-id': `Mixpanel Run ID:`,
      'anomaly-detected': `Anomaly detected - sync automatically paused`,
      'warehouse-monitoring-enabled': `This project has warehouse monitoring enabled. Your admin has opted in to sync pauses when a volume anomaly is detected. Check with your admin to unpause the sync.`,
    },
    'run-details-with-steps': {
      'mixpanel-import-id': `Mixpanel Import ID:`,
      'mixpanel-run-id': `Mixpanel Run ID:`,
      'refresh-data': `Refresh Data`,
    },
    'table-form': {
      table: `Table`,
      'duplicate-column-names': `Duplicate Column Names`,
      'column-names-match': `Column names in this table match the column names from an active user profile table.`,
      'if-you-plan-to-keep': `If you plan to keep both tables, you might consider mapping the column names to a unique name in the Map Columns step.`,
      duplicates: `Duplicates`,
      okay: `Okay`,
    },
    frequency: `Frequency`,
    'mixpanel-property': `Mixpanel Property`,
    status: `Status`,
    table: `table`,
  },
  widgets: {
    'create-cohort-button': {
      'cohort-deleted': `Cohort {{name}} was successfully deleted.`,
      'cohort-not-deleted': `Your cohort could not be deleted`,
      'cohort-created': `Cohort created`,
      'cohort-creation-error': `There was an error creating your cohort`,
    },
    'dismissible-banner': {
      'persist-error': `There was an error persisting the dismiss state. Please ensure cookies are enabled and try again.`,
    },
    'map-lookup-table': {
      'error-saving': `Error saving lookup table. Please try again.`,
    },
    'project-selector': {
      'create-error': `Failed to create new project. Error: {{message}}`,
    },
    'public-dashboard-password-row': {
      'embed-url-copied': `Embed URL copied!`,
      'embed-url-fallback': `Embed URL {{url}}`,
      'passcode-saved': `Public Board Passcode Saved`,
      'password-insecure': `The password cannot be used because it is on our commonly used/insecure list`,
      'save-error': `Something went wrong saving password`,
      'passcode-removed': `Public Board Passcode Removed`,
      'remove-error': `Something went wrong removing password`,
    },
    'slack-channel-selector': {
      'app-updated': `The Slack app has successfully updated`,
      'app-update-error': `The Slack App could not be updated. Please try again soon.`,
    },
    'warehouse-import-builder': {
      'import-created': `{{importType}} successfully created. Starting import.`,
      'changes-saved': `Changes saved.`,
      'test-import-error': `Error completing the test import: {{message}}`,
      'create-import-error': `Error creating import: {{message}}`,
      'update-import-error': `Error updating import: {{message}}`,
    },
    alerts: {
      'custom-alert-modal': {
        'failed-load-project-members': `Failed to load project members`,
        'failed-load-project-webhooks': `Failed to load project webhooks`,
      },
    },
    'annotation-tag-input': {
      'failed-create-tag': `Failed to create tag`,
    },
    'avatar-upload': {
      'avatar-updated': `Avatar updated`,
      'error-updating-avatar': `Error updating avatar`,
    },
    'stripe-cc-form': {
      'enter-payment-info': `Enter Payment Info`,
      'card-number': `Card number`,
      expiration: `Expiration`,
      cvv: `CVV`,
      country: `Country`,
      'postal-code': `Postal Code`,
      processing: `Processing`,
      update: `Update`,
      review: `Review`,
      'not-charged-today': `Your card will not be charged today, but we do require a card on file.`,
    },
    browse: {
      'starter-board-ready-title': `Your Starter Board is ready!`,
      'starter-board-ready-description': `View this board to learn about:`,
      'user-activity': `User activity`,
      'event-volume': `Event volume`,
      'user-retention': `User retention`,
      'view-starter-board': `View Starter Board`,
      'get-to-know-product-title': `Get to know your product`,
      'get-to-know-product-description': `The Starter Board automatically highlights user activity, total events, and customer retention across your entire product`,
    },
    'workspace-global-only-banner': {
      'only-available-in-global': `Only Available in the Global Data View`,
      'available-from-global': `Mixpanel {{appName}} {{isOrAre}} only available from {{workspaceName}} (Global Data View)`,
      'available-from-global-no-name': `Mixpanel {{appName}} {{isOrAre}} only available from the Global Data View`,
      'not-member-private': `You are not a member of the Global Data View and it is private.`,
      'contact-admin': `Contact your project Admin or Owner for access.`,
      'go-to-insights-data-view': `Go to Insights in this Data View`,
      'not-member-joinable': `You are not a member of the Global Data View but it can be joined using the Data View Library.`,
      'go-to-data-view-library': `Go to Data View Library`,
      'view-in-global': `View in Global Data View`,
    },
    'profile-editable-property': {
      'view-changes': `View Changes`,
      'no-permission-edit': `You don't have permission to edit.`,
      'editing-list-disabled': `Editing list properties is disabled.`,
      'delete-property-title': `Delete '{{propertyName}}' From Profile?`,
      'delete-property-description': `This property will no longer show on this profile.`,
      'property-changelog': `Property changelog`,
      'historical-properties-description': `Historical properties store changes over time, unlike standard properties which only retain the current value.`,
    },
  },
  'sankey-chart': {
    'no-data': `No Data`,
    of: `of`,
    'median-time-from-start': `Median time from start`,
    'median-time-from-previous-step': `Median time from previous step`,
    step: `Step`,
  },
  'rhs-panel': {
    'group-tooltip': `Pick a group profile that represents a customer account.`,
    'mrr-property-tooltip': `Profile property that represents the MRR (Monthly Recurring Revenue) of a customer account.`,
    'plan-type-tooltip': `Plan Type of group e.g. Basic, Pro, Enterprise.`,
    'segment-tooltip': `Segment of group e.g. MM+, Enterprise, SMB`,
  },
  'property-filter-menu': {
    match: `Match`,
    where: `Where`,
    equals: `Equals`,
    and: `and`,
  },
  'teammate-sidebar': {
    'no-recent-reports': `No recent reports or boards shared with you`,
    'not-member-of-teams': `Not a member of any teams within this organization`,
    inactive: `Inactive`,
  },
  'create-cohort-button': {
    'upgrade-to-create': `Upgrade to Create Cohort`,
    'upgrade-description': `When you upgrade your plan, cohorts can be created and saved to use across all reports in a project.`,
    'save-cohort': `Save Cohort`,
  },
  'bucket-pane': {
    'interval-size': `Interval Size`,
    minimum: `Minimum`,
    maximum: `Maximum`,
  },
  'warehouse-empty-state': {
    'view-docs': `View Docs`,
    'give-feedback': `Give Feedback`,
  },
  'user-list': {
    'recently-viewed-by': `Recently Viewed By:`,
    'last-edited': `Last edited`,
  },
  'replay-playlist': {
    'load-error': `We had trouble loading your data.`,
    refresh: `Refresh`,
  },
  'entity-collection': {
    'no-access': `No access`,
    'not-shared': `This content is not shared with you.`,
  },
  'date-range-picker': {
    and: `and`,
    'upgrade-query-beyond': `Upgrade your plan to query beyond`,
  },
  'dashboard-selector': {
    'placeholder-untitled': `Untitled board`,
    'placeholder-no-description': `No description`,
  },
  'create-annotation-form': {
    at: `at`,
    'placeholder-description': `Add a description`,
  },
  'countdown-panel-card': {
    'implementation-period': `Implementation Period`,
    'start-sending-data': `Start Sending Data`,
  },
  'bucket-sizes': {
    cancel: `Cancel`,
    apply: `Apply`,
  },
  'avatar-upload': {
    'update-photo': `Update Profile Photo`,
    save: `Save`,
  },
  'auth-container': {
    'terms-and-conditions': `Terms and Conditions`,
    'privacy-policy': `Privacy Policy`,
  },
  'upsell-modal': {
    'learn-more': `Learn More`,
  },
  sharing: {
    by: `by`,
    'url-copied': `URL copied!`,
    'url-copy-error': `Could not copy the URL`,
    'url-copy-error-full': `Something went wrong. Could not copy URL`,
    'public-board-enabled': `Public Board enabled for this Data View`,
    'public-board-no-reports': `This board doesn't meet the requirements to be made public.`,
    'something-went-wrong': `Something went wrong`,
    'public-url-deactivated': `Public URL deactivated.`,
  },
  'profile-activity': {
    since: `Since`,
  },
  'profile-exposures': {
    'unable-to-load': `Unable to Load Exposures`,
    'try-again-later': `Please try again later.`,
  },
  'org-select-modal': {
    'select-organization': `Select Organization`,
  },
  'map-lookup-table-modal': {
    or: `or`,
  },
  'embed-error': {
    'failed-header': `Embed failed to connect.`,
    'failed-body': `Please allow cookies to enable embeds. Visit our [Help Center](https://help.mixpanel.com/hc/en-us/articles/12538250505236-Collaborate-in-Figma-with-Mixpanel) for more information.`,
    'contact-support': `Contact Support`,
  },
  'embed-media': {
    'embed-link': `Embed Link`,
    placeholder: `Paste the video link...`,
  },
  'create-entity-modal': {
    'company-key-description': `Enables company revenue analytics and company profiles.`,
    'set-as-company-key': `Set as Company key`,
  },
  'company-profile-users-table': {
    users: `Users`,
  },
  '404-screen': {
    'page-not-found': `Page not found`,
    'not-the-page': `We're thinking this isn't the page you want...`,
    'nothing-at-url': `There is nothing at this URL. Try checking the address or go to:`,
    'your-board': `Your Board`,
    'help-center': `Help Center`,
    'mixpanel-community': `Mixpanel Community`,
  },
  'public-dashboard-password-row': {
    'require-passcode': `Require Passcode`,
    'minimum-length': `Minimum length of 8 characters.`,
    'passcodes-not-recoverable': `Passcodes can not be recovered after they are set.`,
    reset: `Reset`,
    'embed-link': `Embed Link`,
  },
  'find-your-team-modal': {
    title: `Find Your Organization`,
    subtitle: `A faster way to get all your teammates into Mixpanel`,
    description: `Find Your Organization lets you make your organization searchable, joinable or undiscoverable to new users with your shared email domain.`,
    'default-settings': `By default, team members can now join your org by request. You can modify these settings at any time.`,
    'edit-discoverability': `Edit Discoverability`,
  },
  'csv-export-modal': {
    'export-csv': `Export CSV`,
    'export-up-to': `Export up to`,
    'export-description': `Depending on the row quantity, your export speed may vary.`,
    'limit-description': `Modal for customizing CSV export limit`,
    'confirm-title': `Confirm Export`,
    'confirm-description': `Confirm CSV export`,
    'confirm-message': `You are about to export {{formattedCount}} rows. Continue?`,
    'confirm-message-generic': `Continue with CSV export?`,
    'confirm-export': `Export`,
    cancel: `Cancel`,
  },
  alerts: {
    'custom-alert-modal': {
      'in-a': `in a`,
      email: `Email`,
      slack: `Slack`,
      webhook: `Webhook`,
    },
    'custom-alert-readonly': {
      project: `Project`,
      'data-view': `Data View`,
      criteria: `Criteria`,
      when: `When`,
      'anomaly-detected': `Anomaly detected`,
      'alert-when': `Alert when`,
      segment: `segment`,
      'outside-of-expected-range': `outside of the expected range with`,
      confidence: `confidence`,
      'custom-threshold': `Custom threshold`,
      notify: `Notify`,
      'at-most': `At most`,
      email: `Email`,
      'no-recipient': `No Recipient`,
      'project-webhooks': `Project Webhooks`,
    },
  },
  'custom-api-domains': {
    title: `Custom API Domains`,
    'add-domain': `Add Domain`,
    'column-domain': `Domain`,
    'column-region': `Region`,
    'column-status': `Status`,
    dns: `DNS`,
    'delete-title': `Delete custom domain`,
    'delete-description': `Are you sure you want to delete "{{domain}}"? This will immediately stop serving tracking traffic on this domain. This action cannot be undone.`,
    delete: `Delete`,
    'status-pending-dns': `Pending DNS validation`,
    'status-provisioning': `Provisioning certificate`,
    'status-active': `Serving`,
    'status-error': `Certificate error`,
    'add-modal': {
      title: `Add Custom Domain`,
      domain: `Domain`,
      placeholder: `track.example.com`,
      'domain-required': `Domain is required`,
      'domain-invalid': `Enter a valid domain (e.g. track.example.com)`,
      region: `Region`,
      adding: `Adding...`,
    },
    'dns-records': {
      title: `DNS Records for {{domain}}`,
      instructions: `Add the following DNS records to your domain provider:`,
      type: `Type`,
      name: `Name`,
      value: `Value`,
      'help-text': `After adding both DNS records, the certificate will be provisioned automatically. Status will update on this page.`,
    },
  },
  oauth: {
    'authorization-error': `Authorization error`,
    'mcp-access-not-enabled': `You are not part of an Organization that has mcp access enabled.`,
    'trouble-authorizing': `We are having troubles authorizing the app. Please`,
    'contact-support': `contact support`,
    'with-errors-below': `with the errors shown below.`,
    'contact-org-admin': `If this sounds incorrect, please contact your Mixpanel Org admin.`,
    'authorize-app': `Authorize {{application_name}}?`,
    'logged-in-as': `Logged in as {{user_email}}.`,
    'revoke-access': `To revoke access at any time, go to the integrations page.`,
    'app-asking-to': `{{application_name}} app is asking to:`,
    'close-window': `You may close this window.`,
    'will-redirect-to': `You will be redirected to the following URL:`,
    'redirect-unverified': `Mixpanel doesn't recognize this redirect URL. Only continue if you trust this app.`,
  },
  'gdpr-banner': {
    'cookies-message': `Mixpanel uses cookies to run our site and improve its functionalities for a better browsing experience. To learn more about Mixpanel's use of cookies please review our`,
    'privacy-policy': `Privacy Policy`,
    accept: `Accept`,
    decline: `Decline`,
    'legacy-cookies-intro': `Mixpanel uses cookies to make it easier for you to use our website; read more about our `,
    'cookie-policy': `cookie policy`,
    'legacy-privacy-intro': `. We will also collect personal information about you as described in our `,
    'privacy-statement': `privacy statement`,
    'legacy-agreement': `. By using our services you agree to the use of cookies and to the collection of your personal information.`,
    reject: `Reject`,
  },
  'metric-tree': {
    correlation: {
      'same-time-range': `Correlation is only available for the same time range`,
    },
    'metric-node': {
      'no-data': `No data`,
      update: `Update`,
      'connect-metric': `Connect Metric`,
      explore: `Explore`,
      'assign-owner': `Assign an owner`,
      'metric-name': `Metric name`,
    },
    'note-node': {
      'add-text': `Add text`,
    },
    'strategy-node': {
      placeholder: `Write about your data...`,
    },
    ai: {
      'business-name': `Business Name`,
      'enter-business-name': `Enter your business name`,
      url: `URL`,
      'custom-instructions': `Custom instructions`,
      error: `Error`,
      success: `✓ Success`,
      'select-template': `Select an AI template to generate your metric tree`,
      cancel: `Cancel`,
    },
    'filter-panel': {
      'as-of': `As of`,
    },
    'time-ranges': {
      apply: `Apply`,
    },
    'template-picker': {
      'build-with-ai': `Build with AI`,
    },
    toast: {
      'clipboard-invalid-data': `Your clipboard does not contain valid metric tree data.`,
      'clipboard-read-error': `There was an error reading the data from your clipboard.`,
      'as-of-date-updated': `"As of" date updated to {{date}}`,
      'uploading-image': `Uploading image...`,
      'image-upload-failed': `Failed to upload image. {{errorMessage}}`,
    },
  },
} as const;
